// Security+ Concept Explanations Data
// Extracted from App.js for better maintainability and token efficiency
// This file contains all the concept explanations for the Security+ study app

export const conceptExplanations = {    'categories': {
      title: 'Categories',
      explanation: 'Broad groupings of security controls based on their nature and implementation method.',
      examples: 'Technical (firewalls), managerial (policies), operational (training), physical (locks).',
      useCase: 'A security manager evaluates and implements different types of security controls (technical, managerial, operational, physical) to create comprehensive defense-in-depth protection for the organization.',
      mnemonic: '📂 CATS = Classify All Types Systematically\nThink of folders - categorize controls.',
      visual: `
📂 CATEGORIES
│
├── Technical ✓
├── Managerial ✓
├── Operational ✓
├── Physical ✓
│
▼
✅ ORGANIZED`
    },
    'aaa': {
      title: 'Authentication, Authorization, and Accounting (AAA)',
      explanation: 'Framework for access control.',
      examples: 'Login (auth), permissions (authz), logs (accounting).',
      useCase: 'An IT administrator implements AAA framework using Active Directory to authenticate employees, authorize access to specific network resources based on roles, and generate audit logs for compliance reporting.',
      mnemonic: '🔑 AAA = Access All Areas\nThink of roadside service - verify, allow, track.',
      visual: `
🔑 AUTHENTICATE
│
🚺 AUTHORIZE
│
📝 ACCOUNT
│
▼
✅ SECURE`
    },
    'technical': {
      title: 'Technical Controls',
      explanation: 'Technology-based safeguards implemented through hardware, software, or firmware. These are automated controls that require minimal human intervention.',
      examples: 'Firewalls, antivirus software, encryption, access control systems',
      useCase: 'A network administrator deploys technical controls including next-generation firewalls, endpoint detection systems, and automated patch management to protect the corporate network infrastructure without requiring constant manual oversight.',
      mnemonic: '🔧 TECH = Tools Everyone Can Handle\nThink of a digital toolbox - these are your cyber tools that work automatically to protect systems.',
      visual: `
    ┌─────────────────┐
    │   FIREWALL      │ ← Automatic barrier
    │  ┌─────────┐    │
    │  │ SYSTEM  │    │ ← Protected by tech
    │  └─────────┘    │
    └─────────────────┘`
    },
    'managerial': {
      title: 'Managerial Controls',
      explanation: 'Administrative controls that define the policies, procedures, and guidelines for security management. These are paper-based or process-based controls.',
      examples: 'Security policies, risk assessments, training programs, incident response plans',
      mnemonic: '📋 MANAGE = Make All New Guidelines And Rules Effective\nThink of a manager with a clipboard - they create the rules and procedures.',
      visual: `
    👔 MANAGER
    │
    ├── Policy Document
    ├── Training Manual  
    ├── Risk Assessment
    └── Procedures Guide`
    },
    'operational': {
      title: 'Operational Controls',
      explanation: 'Day-to-day security measures implemented by people. These are human-driven controls that require ongoing attention and implementation.',
      examples: 'Security guards, awareness training, background checks, separation of duties',
      mnemonic: '👥 OPERATE = Only People Execute Real-time Actions That Ensure security\nThink of security guards on patrol - humans doing security work.',
      visual: `
    🚶‍♂️ GUARD PATROL
         │
    ┌────▼────┐
    │ SECURE  │
    │  AREA   │
    └─────────┘`
    },
    'physical': {
      title: 'Physical Controls',
      explanation: 'Tangible barriers and mechanisms that protect physical assets and prevent unauthorized physical access to facilities, equipment, and resources.',
      examples: 'Locks, fences, security cameras, biometric scanners, guard posts',
      mnemonic: '🏢 PHYSICAL = Put Hardware You See In Concrete Areas Locations\nThink of a fortress with walls, gates, and guards you can touch.',
      visual: `
    🏰 FORTRESS SECURITY
    ╔══════════════╗
    ║ 🚪 → 📹      ║ ← Camera
    ║    💳 → 🔒   ║ ← Card reader
    ║              ║
    ╚══════════════╝
         FENCE`
    },
    'preventive': {
      title: 'Preventive Controls',
      explanation: 'Proactive measures designed to prevent security incidents from occurring. They stop threats before they can cause damage.',
      examples: 'Firewalls, access controls, encryption, security awareness training',
      mnemonic: '🛡️ PREVENT = Proactive Response Eliminates Very Nasty Threats\nThink of a shield blocking arrows before they hit.',
      visual: `
    THREAT ——————→ 🛡️ ——→ ❌ BLOCKED
                    │
                PREVENTED`
    },
    'deterrent': {
      title: 'Deterrent Controls',
      explanation: 'Controls that discourage potential attackers from attempting malicious activities by making the consequences visible and severe.',
      examples: 'Warning signs, security cameras, visible security guards, audit logs',
      mnemonic: '⚠️ DETER = Dangerous Elements Think Exactly Right\nThink of a "Beware of Dog" sign - it makes attackers think twice.',
      visual: `
    👤 ATTACKER
    │
    ▼
    ⚠️ WARNING: MONITORED AREA
    📹 CAMERAS RECORDING
    │
    ▼
    🤔 "Maybe I shouldn't..."
    │
    ▼
    🏃‍♂️ LEAVES`
    },
    'detective': {
      title: 'Detective Controls',
      explanation: 'Controls that identify and alert when security incidents have occurred or are in progress. They help discover breaches and attacks.',
      examples: 'Security monitoring, intrusion detection systems, audit logs, security cameras',
      mnemonic: '🔍 DETECT = Discover Every Crime Through Electronic Tracking\nThink of Sherlock Holmes with a magnifying glass finding clues.',
      visual: `
    🔍 DETECTIVE CONTROL
    │
    ├── 📊 Monitor logs
    ├── 🚨 Alert system  
    ├── 📹 Review footage
    └── 📝 Document evidence`
    },
    'corrective': {
      title: 'Corrective Controls',
      explanation: 'Controls that fix problems and restore systems to normal operations after a security incident has been detected.',
      examples: 'Incident response procedures, system patches, malware removal, account recovery',
      mnemonic: '🔧 CORRECT = Clean Out Real Risks and Establish Control Today\nThink of a mechanic fixing a broken car.',
      visual: `
    💥 INCIDENT OCCURS
    │
    ▼
    🔧 CORRECTIVE ACTION
    │ ├── Patch system
    │ ├── Remove malware  
    │ └── Restore backup
    ▼
    ✅ SYSTEM RESTORED`
    },
    'compensating': {
      title: 'Compensating Controls',
      explanation: 'Alternative controls implemented when primary controls cannot be used or are insufficient. They provide equivalent protection through different means.',
      examples: 'Using additional monitoring when encryption cannot be implemented, manual reviews when automated controls fail',
      mnemonic: '⚖️ COMPENSATE = Create Other Methods Protecting Everything Now Since Alternative Tools Exist\nThink of a backup plan when Plan A fails.',
      visual: `
    🚫 PRIMARY CONTROL FAILS
    │
    ▼
    ⚖️ COMPENSATING CONTROL
    │ (Alternative protection)
    ▼
    ✅ SECURITY MAINTAINED`
    },
    'directive': {
      title: 'Directive Controls',
      explanation: 'Controls that guide or mandate actions through policies, procedures, and guidelines. They tell people what to do and how to do it.',
      examples: 'Security policies, mandatory training requirements, standard operating procedures',
      mnemonic: '📜 DIRECT = Documents Instruct Rules Everyone Can Take\nThink of a GPS giving turn-by-turn directions.',
      visual: `
    📜 POLICY DOCUMENT
    │
    ├── "You MUST do this"
    ├── "You SHALL NOT do that"  
    ├── "Follow these steps"
    └── "Report violations here"`
    },
    'cia-triad': {
      title: 'CIA Triad - Confidentiality, Integrity, Availability',
      explanation: 'The fundamental principle of information security. All security measures should protect one or more of these three core properties of information.',
      examples: 'Confidentiality: Encryption; Integrity: Digital signatures; Availability: Redundant systems',
      mnemonic: '🔺 CIA = Critical Information Always needs protection\nThink of a triangle - remove any side and it collapses.',
      visual: `
         🔒 CONFIDENTIALITY
        /                  \\
       /     INFORMATION    \\
      /        SECURITY      \\
     /                        \\
    📝 INTEGRITY ————————— ⚡ AVAILABILITY
    
    Each side protects information differently!`
    },
    'physical-security': {
      title: 'Physical Security',
      explanation: 'Protecting physical assets, facilities, and resources from threats, unauthorized access, and environmental hazards. Physical security forms the foundation of overall security.',
      examples: 'Barriers, surveillance cameras, access control vestibules, security guards, bollards, fencing, lighting',
      mnemonic: '🏢 PHYS SEC = Protect Hardware You See Securely Every Concern\nThink of building fortress - physical protection layers.',
      visual: `
🏢 PHYSICAL
│
▼
🛡️ SECURITY
├── Barriers ✓
├── Monitors ✓
├── Guards ✓
│
▼
✅ GUARDED`
    },
    'business-processes-security': {
      title: 'Business Processes Impacting Security Operation',
      explanation: 'Organizational processes and workflows that can affect the security posture during changes, operations, and decision-making. Understanding these processes is crucial for maintaining security during business operations.',
      examples: 'Approval processes, testing procedures, change management workflows, documentation requirements, version control',
      mnemonic: '💼 BUS PROC = Business Under Security Processes\nThink of workflow pipeline - business operations must consider security impact.',
      visual: `
💼 BUSINESS
│
▼
🔄 PROCESSES
├── Approval ✓
├── Testing ✓
├── Review ✓
│
▼
✅ SECURE`
    },
    'technical-implications': {
      title: 'Technical Implications',
      explanation: 'Technical effects and consequences that arise from implementing changes, new systems, or security measures. Understanding these implications helps in proper planning and risk mitigation.',
      examples: 'System downtime, compatibility issues, performance impact, configuration changes, network effects',
      mnemonic: '🔧 TECH IMP = Technical Every Change Has Implications Measured Properly\nThink of side effects - every technical change has consequences.',
      visual: `
🔧 TECH
│
▼
📉 IMPLICATIONS
├── Assess ✓
├── Mitigate ✓
├── Plan ✓
│
▼
✅ HANDLED`
    },
    'documentation': {
      title: 'Documentation',
      explanation: 'Recording and maintaining detailed information about changes, configurations, processes, and systems. Proper documentation is essential for security, compliance, and operational effectiveness.',
      examples: 'Change logs, system diagrams, process updates, configuration records, incident reports',
      mnemonic: '📝 DOC = Detail Operations Completely\nThink of journal - document everything important for future reference.',
      visual: `
📝 DOC
│
▼
🔄 CHANGE
├── Record ✓
├── Update ✓
├── Store ✓
│
▼
✅ LOGGED`
    },
    'authenticating-people': {
      title: 'Authenticating People',
      explanation: 'Verifying the identity of human users trying to access systems. Ensures the person is who they claim to be.',
      examples: 'Passwords, biometrics, smart cards, multi-factor authentication',
      mnemonic: '👤 PEOPLE = Prove Everyone Opens Private/Locked Environments\nThink of showing ID at a nightclub.',
      visual: `
    👤 "I am John Smith"
    │
    ▼
    🔐 AUTHENTICATION CHECK:
    ├── Username: john.smith ✓
    ├── Password: ******** ✓  
    ├── Fingerprint: 👆 ✓
    └── Phone code: 123456 ✓
    │
    ▼
    🎯 IDENTITY VERIFIED`
    },
    'authenticating-systems': {
      title: 'Authenticating Systems',
      explanation: 'Verifying the identity of computer systems, devices, or services before allowing communication or data exchange.',
      examples: 'Digital certificates, system keys, machine authentication, device certificates',
      mnemonic: '💻 SYSTEMS = Servers Yield Secure Trust Each Machine Shows\nThink of computers showing passports to each other.',
      visual: `
    💻 SERVER A: "I am secure server"
    │
    ▼
    🔐 SYSTEM VERIFICATION:
    ├── Digital certificate ✓
    ├── Cryptographic key ✓
    ├── Domain validation ✓  
    └── Trust chain ✓
    │
    ▼
    🤝 SYSTEMS CONNECTED`
    },
    'zero-trust': {
      title: 'Zero Trust',
      explanation: 'Security model that requires verification for every user and device, regardless of location. "Never trust, always verify" - no implicit trust zones.',
      examples: 'Continuous authentication, micro-segmentation, least privilege access, device verification',
      mnemonic: '🏰 CASTLE = Check All Subjects Through Locked Entries\nThink of a castle where EVERYONE shows papers at EVERY gate, even the king.',
      visual: `
    🌐 ZERO TRUST NETWORK
    
    👤 → 🚪 → 🔍 → 🚪 → 🔍 → 🚪 → 💼 RESOURCE
         │      │      │      │      │
      VERIFY  CHECK  VERIFY CHECK  VERIFY
      
    No shortcuts! Verify at every step!`
    },
    'bollards': {
      title: 'Bollards',
      explanation: 'Short vertical posts used to control or limit vehicle access to an area. Prevents vehicle-based attacks while allowing pedestrian access.',
      examples: 'Steel posts in front of buildings, retractable barriers, decorative posts with security function',
      mnemonic: '🚧 BOLLARD = Blocks Only Large Lethal Attack Ramming Devices\nThink of fence posts that stop cars but not people.',
      visual: `
    🚗 VEHICLE ATTACK
    │
    ▼
    🚧 🚧 🚧 BOLLARDS 🚧 🚧 🚧
    │
    ▼
    🚫 VEHICLE STOPPED
    
    👤 ← Pedestrians can still walk around`
    },
    'honeypot': {
      title: 'Honeypot',
      explanation: 'A decoy system designed to attract and detect attackers. It looks like a real target but is actually a trap to study attack methods.',
      examples: 'Fake servers, dummy databases, vulnerable-looking systems that are actually monitored',
      mnemonic: '🍯 HONEY = Hide Obviously Nasty Enemy Traps\nThink of Winnie the Pooh getting stuck in a honey jar - attackers get trapped!',
      visual: `
    🎭 FAKE SYSTEM (Honeypot)
    │  "Come attack me!"
    ▼
    👹 ATTACKER: "Easy target!"
    │  *attacks fake system*
    ▼
    📹 SECURITY TEAM: "Gotcha!"
    │  *studying attack methods*
    ▼
    🔒 REAL SYSTEMS PROTECTED`
    },
    'phishing': {
      title: 'Phishing',
      explanation: 'Fraudulent attempt to obtain sensitive information by disguising as a trustworthy entity in electronic communications.',
      examples: 'Fake emails from banks, bogus login pages, impersonated company communications',
      mnemonic: '🎣 PHISH = Pretend Hook Intended to Steal Hearts\nThink of fishing - using fake bait to catch real fish (victims).',
      visual: `
    📧 FAKE EMAIL:
    "From: YourBank@secure-bank.com"
    "Your account is locked! Click here!"
    │
    ▼
    👤 VICTIM: "Oh no! Better click..."
    │
    ▼
    🎣 HOOK SET!
    │
    ▼
    💰 CREDENTIALS STOLEN`
    },
    'ransomware': {
      title: 'Ransomware',
      explanation: 'Malicious software that encrypts victims files and demands payment for the decryption key. It holds data hostage for money.',
      examples: 'WannaCry, Locky, CryptoLocker - malware that locks files and demands Bitcoin payment',
      mnemonic: '🔐 RANSOM = Really Angry Nasty Software Obstructs Money\nThink of a kidnapper holding your files hostage for money.',
      visual: `
    💻 YOUR FILES:
    ├── document.txt ✓
    ├── photos.jpg ✓  
    └── work.xlsx ✓
    │
    ▼ 💀 RANSOMWARE ATTACK
    │
    ▼
    🔒 YOUR FILES:
    ├── document.txt.LOCKED 🔐
    ├── photos.jpg.LOCKED 🔐
    └── work.xlsx.LOCKED 🔐
    │
    💰 "Pay $500 to unlock!"`
    },

    // DOMAIN 2.3: VARIOUS TYPES OF VULNERABILITIES
    'application-vulnerabilities': {
      title: 'Application Vulnerabilities',
      explanation: 'Application vulnerabilities are weaknesses in software applications that attackers can exploit to gain unauthorized access, manipulate data, or disrupt functionality. These often arise from coding errors, lack of input validation, or improper configuration.',
      examples: 'A web application vulnerable to SQL injection, an unpatched application exposing sensitive data.',
      mnemonic: '📱 APP VULN = Application Problems Permit Unauthorized Network Leverage\nThink of a poorly built app as a house with weak locks - easy for attackers to break in.',
      visual: `
  📱 APPLICATION
  │
  ▼
  ⚠️ VULNERABILITY
  │ Code Errors | No Validation
  │
  ▼
  🕵️ ATTACKER EXPLOITS
  ✅ DATA BREACH OR DISRUPTION`
    },
    'memory-injection': {
      title: 'Memory Injection',
      explanation: 'Memory injection vulnerabilities allow attackers to insert malicious code into an application\'s memory, altering its behavior or gaining unauthorized access, often due to improper memory management.',
      examples: 'SQL injection in a web form, code injection via a vulnerable API.',
      mnemonic: '💉 MEM INJ = Memory Exploited, Malicious Injection\nThink of a syringe injecting bad code into the app\'s memory bloodstream.',
      visual: `
  🖥️ APP MEMORY
  │
  ▼
  💉 INJECT MALICIOUS CODE
  │ Exploit Weak Management
  │
  ▼
  🕵️ CONTROL GAINED
  ✅ ATTACKER RUNS CODE`
    },
    'race-conditions': {
      title: 'Race Conditions',
      explanation: 'Race conditions occur when multiple processes access shared resources simultaneously, leading to unpredictable outcomes or vulnerabilities if the timing of operations is exploited.',
      examples: 'Two processes updating a database simultaneously, causing data corruption; a privilege escalation due to timing issues.',
      mnemonic: '🏃 RACE CON = Racing Access Creates Exploitable Chaos\nThink of two runners (processes) tripping over each other to reach a resource first.',
      visual: `
  🖥️ SHARED RESOURCE
  │
  ▼
  🏃 PROCESSES RACE
  │ Timing Mismatch
  │
  ▼
  ⚠️ UNPREDICTABLE OUTCOME
  ✅ EXPLOITABLE VULNERABILITY`
    },
    'time-of-check-toc': {
      title: 'Time-of-Check (TOC)',
      explanation: 'Time-of-Check vulnerabilities occur when a system checks a condition (e.g., file permissions) but the condition changes before the action is performed, allowing exploitation.',
      examples: 'A program checks file access but the file is swapped before use, leading to unauthorized access.',
      mnemonic: '⏰ TOC = Time Of Check Tricked\nThink of checking a ticket but someone swaps it before you board the train.',
      visual: `
  🖥️ CHECK CONDITION
  │ File OK?
  │
  ▼
  ⏰ DELAY → CONDITION CHANGES
  │ File Swapped
  │
  ▼
  🕵️ EXPLOIT
  ✅ UNAUTHORIZED ACCESS`
    },
    'time-of-use-tou': {
      title: 'Time-of-Use (TOU)',
      explanation: 'Time-of-Use vulnerabilities occur when a system acts on a resource after a check, but the resource\'s state changes in between, allowing attackers to manipulate the outcome.',
      examples: 'A process uses a file after verifying it, but an attacker modifies it during the gap, leading to malicious execution.',
      mnemonic: '⏳ TOU = Time Of Use Undermined\nThink of using a key after checking it fits, but someone swaps the lock.',
      visual: `
  🖥️ USE RESOURCE
  │ After Check
  │
  ▼
  ⏳ CHANGE IN STATE
  │ Resource Altered
  │
  ▼
  🕵️ EXPLOIT
  ✅ MALICIOUS ACTION`
    },
    'malicious-update': {
      title: 'Malicious Update',
      explanation: 'Malicious update vulnerabilities occur when attackers tamper with software updates or patches, injecting malicious code into trusted update processes.',
      examples: 'A compromised software update server delivering malware, a fake update prompt tricking users.',
      mnemonic: '🦠 MAL UPD = Malicious Alteration Lurks in Update Process\nThink of a poisoned software patch - looks legitimate but harms the system.',
      visual: `
  🖥️ SOFTWARE
  │
  ▼
  📥 UPDATE PROCESS
  │ Compromised Source
  │
  ▼
  🦠 MALICIOUS CODE
  ✅ SYSTEM INFECTED`
    },
    'os-based-vulnerabilities': {
      title: 'Operating System (OS)-Based Vulnerabilities',
      explanation: 'OS-based vulnerabilities are weaknesses in the operating system\'s code, configuration, or services that attackers can exploit to gain access, escalate privileges, or disrupt operations.',
      examples: 'Unpatched Windows vulnerabilities like EternalBlue, misconfigured Linux permissions allowing privilege escalation.',
      mnemonic: '🖥️ OS VULN = Operating System Vulnerabilities Unlock Nefarious Leverage\nThink of an OS as a fortress with hidden weak gates attackers can breach.',
      visual: `
  🖥️ OPERATING SYSTEM
  │
  ▼
  ⚠️ VULNERABILITY
  │ Code Flaw | Misconfig
  │
  ▼
  🕵️ ATTACKER EXPLOITS
  ✅ SYSTEM COMPROMISE`
    },
    'web-based-vulnerabilities': {
      title: 'Web-Based Vulnerabilities',
      explanation: 'Web-based vulnerabilities exist in web applications or servers, often due to poor coding practices or misconfigurations, allowing attackers to manipulate or steal data.',
      examples: 'Cross-site scripting (XSS) on a website, SQL injection in a web form.',
      mnemonic: '🌐 WEB VULN = Web Exploits Breach Vulnerable Links\nThink of a website as a storefront with weak locks, inviting break-ins.',
      visual: `
  🌐 WEB SERVER
  │
  ▼
  ⚠️ VULNERABILITY
  │ Code Flaw | No Validation
  │
  ▼
  🕵️ ATTACKER
  ✅ DATA STOLEN OR ALTERED`
    },
    'sql-injection': {
      title: 'Structured Query Language Injection (SQLi)',
      explanation: 'SQL injection vulnerabilities allow attackers to inject malicious SQL queries into input fields, manipulating databases to access, modify, or delete data.',
      examples: 'Entering `\' OR \'1\'=\'1` in a login form to bypass authentication, extracting sensitive data from a database.',
      mnemonic: '💉 SQLI = SQL Queried, Lethal Injection\nThink of a poisoned query sneaking into a database like a spy.',
      visual: `
  🌐 WEB FORM
  │
  ▼
  💉 MALICIOUS SQL
  │ ' OR '1'='1
  │
  ▼
  🗄️ DATABASE
  ✅ DATA EXPOSED`
    },
    'cross-site-scripting': {
      title: 'Cross-Site Scripting (XSS)',
      explanation: 'XSS vulnerabilities allow attackers to inject malicious scripts into web pages viewed by users, stealing data or performing actions on their behalf.',
      examples: 'Injecting a script into a comment field to steal cookies, redirecting users to malicious sites.',
      mnemonic: '📜 XSS = Cross-Site Scripts Sabotage\nThink of a script as a hidden trap in a webpage, harming unsuspecting visitors.',
      visual: `
  🌐 WEB PAGE
  │
  ▼
  📜 MALICIOUS SCRIPT
  │ Steal Cookies | Redirect
  │
  ▼
  👤 USER VICTIM
  ✅ ATTACKER GAINS ACCESS`
    },
    'hardware-vulnerabilities': {
      title: 'Hardware Vulnerabilities',
      explanation: 'Hardware vulnerabilities are weaknesses in physical devices or their firmware, allowing attackers to bypass security controls or gain unauthorized access.',
      examples: 'Exploiting a CPU\'s Spectre/Meltdown flaws, tampering with IoT device hardware.',
      mnemonic: '🖥️ HW VULN = Hardware Weaknesses Vulnerable to Unauthorized Leverage\nThink of a device as a car with faulty brakes - a hidden flaw attackers exploit.',
      visual: `
  🖥️ HARDWARE DEVICE
  │
  ▼
  ⚠️ VULNERABILITY
  │ Firmware | Chip Flaw
  │
  ▼
  🕵️ ATTACKER
  ✅ DEVICE COMPROMISE`
    },
    'firmware-vulnerabilities': {
      title: 'Firmware Vulnerabilities',
      explanation: 'Firmware vulnerabilities exist in the low-level software controlling hardware, which can be exploited to gain persistent access or disrupt device functionality.',
      examples: 'Exploiting outdated router firmware, injecting malware into a BIOS.',
      mnemonic: '📀 FIRM VULN = Firmware Imperfections Risk Malware\nThink of firmware as a device\'s DNA - flaws allow attackers to rewrite its behavior.',
      visual: `
  🖥️ DEVICE FIRMWARE
  │
  ▼
  ⚠️ VULNERABILITY
  │ Outdated | Code Flaw
  │
  ▼
  🕵️ EXPLOIT
  ✅ PERSISTENT CONTROL`
    },
    'end-of-life-vulnerabilities': {
      title: 'End-of-Life Vulnerabilities',
      explanation: 'End-of-life vulnerabilities occur when hardware or software no longer receives updates or support, leaving known issues unpatched and exploitable.',
      examples: 'Using Windows XP post-support, running unsupported IoT devices.',
      mnemonic: '🗑️ EOL = End Of Life, Exploits Outlive Support\nThink of an abandoned house - no maintenance, easy for intruders to exploit.',
      visual: `
  🖥️ OLD DEVICE/SOFTWARE
  │
  ▼
  🗑️ END-OF-LIFE
  │ No Updates | Unpatched
  │
  ▼
  🕵️ EXPLOIT
  ✅ EASY COMPROMISE`
    },
    'legacy-vulnerabilities': {
      title: 'Legacy Vulnerabilities',
      explanation: 'Legacy vulnerabilities arise from outdated systems or software still in use, often incompatible with modern security standards and unpatched.',
      examples: 'Running old SCADA systems with known flaws, using deprecated protocols like SMBv1.',
      mnemonic: '🕰️ LEG VULN = Legacy Equipment Gives Vulnerabilities New Life\nThink of an old car with no airbags - outdated and risky to operate.',
      visual: `
  🕰️ LEGACY SYSTEM
  │
  ▼
  ⚠️ VULNERABILITY
  │ Old Code | No Updates
  │
  ▼
  🕵️ EXPLOIT
  ✅ SYSTEM BREACH`
    },
    'virtualization-vulnerabilities': {
      title: 'Virtualization Vulnerabilities',
      explanation: 'Virtualization vulnerabilities occur in virtualized environments, such as hypervisors or virtual machines, allowing attackers to compromise virtual systems or the host.',
      examples: 'Escaping a virtual machine to attack the host, exploiting hypervisor misconfigurations.',
      mnemonic: '🖥️ VIRT VULN = Virtual Infrastructure Risks Threats\nThink of a virtual city - weaknesses in its structure let attackers break out or in.',
      visual: `
  🖥️ VIRTUAL ENVIRONMENT
  │
  ▼
  ⚠️ VULNERABILITY
  │ Hypervisor | VM Config
  │
  ▼
  🕵️ ATTACKER
  ✅ HOST OR VM COMPROMISE`
    },
    'vm-escape': {
      title: 'Virtual Machine (VM) Escape',
      explanation: 'VM escape vulnerabilities allow attackers to break out of a virtual machine and access or control the underlying host system, compromising the entire virtual environment.',
      examples: 'Exploiting a hypervisor bug to run code on the host, escaping a VM in a cloud environment.',
      mnemonic: '🚪 VM ESC = Virtual Machine Exit, Sneaky Compromise\nThink of escaping a virtual jail - breaking out to control the prison (host).',
      visual: `
  🖥️ VM
  │
  ▼
  🚪 ESCAPE EXPLOIT
  │ Hypervisor Flaw
  │
  ▼
  🖥️ HOST SYSTEM
  ✅ FULL CONTROL`
    },
    'resource-reuse': {
      title: 'Resource Reuse',
      explanation: 'Resource reuse vulnerabilities occur when virtual resources (e.g., disk space, memory) are not properly cleared between VM instances, allowing attackers to access residual data.',
      examples: 'Accessing deleted files from a reused VM disk, recovering sensitive data from a prior tenant in a cloud VM.',
      mnemonic: '♻️ RES REUSE = Residual Exposed Secrets Remain, Exploitable Uncleaned Systems\nThink of a recycled laptop - old data left behind for the next user to find.',
      visual: `
  🖥️ VM RESOURCE
  │
  ▼
  ♻️ REUSED WITHOUT CLEARING
  │ Residual Data
  │
  ▼
  🕵️ ATTACKER RECOVERS
  ✅ SENSITIVE DATA LEAK`
    },
    'cloud-specific-vulnerabilities': {
      title: 'Cloud-Specific Vulnerabilities',
      explanation: 'Cloud-specific vulnerabilities are weaknesses unique to cloud environments, such as misconfigured services, shared resource risks, or insecure APIs, enabling unauthorized access or data breaches.',
      examples: 'Misconfigured S3 buckets exposing data, insecure cloud APIs allowing account hijacking.',
      mnemonic: '☁️ CLOUD VULN = Cloud Oversights Undermine Data Security\nThink of a cloud as a shared building - misconfigured doors let intruders roam freely.',
      visual: `
  ☁️ CLOUD ENVIRONMENT
  │
  ▼
  ⚠️ VULNERABILITY
  │ Misconfig | API Flaw
  │
  ▼
  🕵️ ATTACKER
  ✅ DATA OR ACCOUNT BREACH`
    },
    'supply-chain-vulnerabilities': {
      title: 'Supply Chain Vulnerabilities',
      explanation: 'Supply chain vulnerabilities occur when weaknesses in third-party vendors, suppliers, or service providers compromise the security of an organization\'s systems or data.',
      examples: 'A compromised software vendor delivering malware, a hardware supplier embedding backdoors.',
      mnemonic: '🚚 SUP CHAIN = Supply Undermines Protection, Compromised Hardware/Apps In Network\nThink of a tainted supply line - bad goods (software/hardware) harm the whole chain.',
      visual: `
  🚚 SUPPLY CHAIN
  │
  ▼
  ⚠️ VULNERABILITY
  │ Compromised Vendor
  │
  ▼
  🖥️ ORGANIZATION
  ✅ SYSTEM OR DATA BREACH`
    },
    'service-provider-vulnerabilities': {
      title: 'Service Provider Vulnerabilities',
      explanation: 'Service provider vulnerabilities arise when third-party services (e.g., cloud or managed services) have weaknesses that attackers exploit to access customer systems or data.',
      examples: 'A cloud provider\'s weak authentication allowing data access, a managed service provider breach exposing clients.',
      mnemonic: '🛠️ SERV PRO = Service Provider Risks Open Breaches\nThink of a hired contractor leaving a backdoor open in your house.',
      visual: `
  🛠️ SERVICE PROVIDER
  │
  ▼
  ⚠️ VULNERABILITY
  │ Weak Security
  │
  ▼
  🖥️ CUSTOMER SYSTEM
  ✅ DATA EXPOSED`
    },
    'hardware-provider-vulnerabilities': {
      title: 'Hardware Provider Vulnerabilities',
      explanation: 'Hardware provider vulnerabilities involve compromised hardware components or firmware introduced during manufacturing or distribution, enabling attacks.',
      examples: 'Pre-installed malware in IoT devices, tampered chips in servers.',
      mnemonic: '🔧 HW PRO = Hardware Provider Risks Open Weaknesses\nThink of a sabotaged tool - looks normal but built to fail or spy.',
      visual: `
  🔧 HARDWARE PROVIDER
  │
  ▼
  ⚠️ COMPROMISED HARDWARE
  │ Malware | Backdoor
  │
  ▼
  🖥️ ORGANIZATION
  ✅ SYSTEM COMPROMISE`
    },
    'software-provider-vulnerabilities': {
      title: 'Software Provider Vulnerabilities',
      explanation: 'Software provider vulnerabilities occur when third-party software contains weaknesses or malicious code, compromising systems that use it.',
      examples: 'A compromised library in a software update, a vendor\'s app with a backdoor.',
      mnemonic: '💾 SW PRO = Software Provider Risks Open Exploits\nThink of a tainted app store - apps look safe but hide malicious code.',
      visual: `
  💾 SOFTWARE PROVIDER
  │
  ▼
  ⚠️ VULNERABLE CODE
  │ Backdoor | Flaw
  │
  ▼
  🖥️ USER SYSTEM
  ✅ MALWARE OR BREACH`
    },
    'cryptographic-vulnerabilities': {
      title: 'Cryptographic Vulnerabilities',
      explanation: 'Cryptographic vulnerabilities arise from weaknesses in encryption algorithms, key management, or implementation, allowing attackers to decrypt data or bypass security.',
      examples: 'Using outdated MD5 hashing, weak key lengths in SSL certificates.',
      mnemonic: '🔐 CRYPTO VULN = Cryptographic Risks Yield Potential Threats, Overcome Unsecure Layers\nThink of a rusty lock - looks secure but easily picked by attackers.',
      visual: `
  🔐 ENCRYPTED DATA
  │
  ▼
  ⚠️ CRYPTO WEAKNESS
  │ Weak Algorithm | Poor Keys
  │
  ▼
  🕵️ ATTACKER DECRYPTS
  ✅ DATA EXPOSED`
    },
    'misconfiguration-vulnerabilities': {
      title: 'Misconfiguration Vulnerabilities',
      explanation: 'Misconfiguration vulnerabilities occur when systems, applications, or networks are improperly configured, exposing them to unauthorized access or attacks.',
      examples: 'Open cloud storage buckets, default firewall rules allowing all traffic.',
      mnemonic: '⚙️ MISCONF = Misconfigured Items Spawn Compromises, Overlooked Flaws\nThink of a miswired alarm system - leaves the house vulnerable.',
      visual: `
  🖥️ SYSTEM/NETWORK
  │
  ▼
  ⚙️ MISCONFIGURATION
  │ Open Ports | Weak Settings
  │
  ▼
  🕵️ ATTACKER
  ✅ EASY ACCESS`
    },
    'mobile-device-vulnerabilities': {
      title: 'Mobile Device Vulnerabilities',
      explanation: 'Mobile device vulnerabilities are weaknesses in mobile operating systems, apps, or configurations that attackers exploit to steal data or gain control.',
      examples: 'Unpatched Android vulnerabilities, insecure app permissions exposing data.',
      mnemonic: '📱 MOB VULN = Mobile Openings Breach Vulnerable, Unprotected Nodes\nThink of a smartphone as a pocket vault - flaws let thieves in easily.',
      visual: `
  📱 MOBILE DEVICE
  │
  ▼
  ⚠️ VULNERABILITY
  │ OS Flaw | App Weakness
  │
  ▼
  🕵️ ATTACKER
  ✅ DATA OR DEVICE COMPROMISE`
    },
    'side-loading': {
      title: 'Side Loading',
      explanation: 'Side loading vulnerabilities occur when users install apps from unverified sources, bypassing official app stores and potentially introducing malware.',
      examples: 'Installing a malicious APK on Android, downloading apps from unofficial websites.',
      mnemonic: '📥 SIDE LOAD = Sneaky Installation Delivers Exploits, Avoid Official Defenses\nThink of sneaking a package through a side door - bypasses security checks.',
      visual: `
  📱 MOBILE DEVICE
  │
  ▼
  📥 SIDE LOADED APP
  │ Unverified Source
  │
  ▼
  🦠 MALWARE
  ✅ DEVICE INFECTED`
    },
    'jailbreaking': {
      title: 'Jailbreaking',
      explanation: 'Jailbreaking vulnerabilities arise when users remove manufacturer restrictions on a device, enabling unauthorized apps but also exposing the device to exploits.',
      examples: 'Jailbreaking an iPhone to install unapproved apps, rooting an Android device leading to malware.',
      mnemonic: '🔓 JAILBREAK = Just Alter, Insecure Liberties Break Restrictions, Expose Attacks\nThink of breaking out of a secure cell - freedom comes with new risks.',
      visual: `
  📱 MOBILE DEVICE
  │
  ▼
  🔓 JAILBREAK
  │ Remove Restrictions
  │
  ▼
  ⚠️ VULNERABLE SYSTEM
  ✅ MALWARE OR EXPLOIT`
    },
    'zero-day-vulnerabilities': {
      title: 'Zero-Day Vulnerabilities',
      explanation: 'Zero-day vulnerabilities are previously unknown flaws in software or hardware that attackers exploit before patches are available, making them highly dangerous.',
      examples: 'A new exploit in a browser discovered by attackers, a zero-day in a popular OS before vendor awareness.',
      mnemonic: '🚨 ZERO DAY = Zealous Exploits Ravage Openings, Defenses Absent Yet\nThink of a surprise attack - no defenses ready, maximum impact.',
      visual: `
  🖥️ SYSTEM/SOFTWARE
  │
  ▼
  🚨 ZERO-DAY FLAW
  │ Unknown to Vendor
  │
  ▼
  🕵️ ATTACKER EXPLOITS
  ✅ NO PATCH AVAILABLE`
    },

    // DOMAIN 2.5: MITIGATION TECHNIQUES FOR ENTERPRISE SECURITY
    'segmentation-mitigation': {
      title: 'Segmentation',
      explanation: 'Segmentation divides a network into smaller, isolated segments to limit the spread of threats, reduce attack surfaces, and control access between different parts of the network. It enhances security by containing incidents and restricting lateral movement by attackers.',
      examples: 'Using VLANs to separate employee and guest Wi-Fi networks, implementing DMZ for public-facing servers.',
      mnemonic: '🔲 SEG = Separate Everything, Guard against Movement\nThink of a ship with watertight compartments - if one section floods (breach), the others stay secure.',
      visual: `
  🌐 ENTERPRISE NETWORK
  │
  ▼
  🔲 SEGMENTATION
  │ VLAN1: Employees
  │ VLAN2: Guests
  │ DMZ: Public Servers
  │
  ▼
  🚨 ATTACK IN ONE SEGMENT
  ✅ OTHER SEGMENTS PROTECTED`
    },
    'access-control-mitigation': {
      title: 'Access Control',
      explanation: 'Access control restricts who or what can access resources in a system, ensuring only authorized entities gain entry. It uses policies, authentication, and authorization to enforce security boundaries.',
      examples: 'Role-based access control (RBAC) for employees, mandatory access control (MAC) for sensitive government systems.',
      mnemonic: '🔑 ACC CON = Access Carefully Controlled, Only Need-to-Know\nThink of a locked vault - only those with the right key (credentials) can enter.',
      visual: `
  🖥️ SYSTEM RESOURCE
  │
  ▼
  🔑 ACCESS CONTROL
  │ Authenticate → Authorize
  │
  ├── ALLOW: Valid User ✓
  ├── DENY: Unauthorized ✗
  │
  ▼
  ✅ SECURE RESOURCE ACCESS`
    },
    'access-control-list-mitigation': {
      title: 'Access Control List (ACL)',
      explanation: 'An ACL is a set of rules defining permissions for users or systems to access network resources, such as allowing or denying traffic based on IP, port, or protocol.',
      examples: 'Firewall ACL allowing HTTP traffic to a web server, router ACL blocking specific IP ranges.',
      mnemonic: '📜 ACL = Access Control List Locks Resources\nThink of a guest list - only listed IPs or users get through the gate.',
      visual: `
  🌐 NETWORK TRAFFIC
  │
  ▼
  📜 ACL CHECK
  │ Rule: Allow/Deny | IP | Port
  │
  ├── PERMIT: Log ✓
  ├── DENY: Block ✗
  │
  ▼
  ✅ CONTROLLED TRAFFIC FLOW`
    },
    'permissions-mitigation': {
      title: 'Permissions',
      explanation: 'Permissions specify what actions authorized users or systems can perform on resources, such as read, write, or execute, ensuring least privilege principles are applied.',
      examples: 'Granting read-only access to a shared folder, restricting database users to specific tables.',
      mnemonic: '🔒 PERM = Permissions Ensure Restricted Manipulation\nThink of a library card - limits what books (resources) you can borrow or edit.',
      visual: `
  📁 RESOURCE
  │
  ▼
  🔒 PERMISSIONS CHECK
  │ User: Read | Write | Execute
  │
  ├── ALLOW: Specific Actions ✓
  ├── DENY: Restricted Actions ✗
  │
  ▼
  ✅ LEAST PRIVILEGE ENFORCED`
    },
    'application-allow-list': {
      title: 'Application Allow List',
      explanation: 'An application allow list (whitelisting) permits only approved applications to run on a system, blocking unauthorized or malicious software from executing.',
      examples: 'Windows AppLocker allowing only signed apps, endpoint security software restricting unapproved executables.',
      mnemonic: '✅ APP ALLOW = Approved Programs Permitted, All Others Locked Out\nThink of a VIP list - only approved apps get to \'party\' (run) on the system.',
      visual: `
  🖥️ SYSTEM
  │
  ▼
  ✅ ALLOW LIST
  │ Check: Approved App?
  │
  ├── RUN: Signed App ✓
  ├── BLOCK: Unknown App ✗
  │
  ▼
  ✅ MALWARE EXECUTION PREVENTED`
    },
    'isolation-mitigation': {
      title: 'Isolation',
      explanation: 'Isolation separates systems, applications, or processes to prevent unauthorized interactions, reducing the risk of compromise spreading across the environment.',
      examples: 'Sandboxing a suspicious file for analysis, isolating a compromised server from the network.',
      mnemonic: '🏝️ ISO = Isolate Systems, Obstruct Spread\nThink of quarantine wards - infected systems are isolated to stop the spread of harm.',
      visual: `
  🌐 NETWORK
  │
  ▼
  🏝️ ISOLATION
  │ Separate: System/App
  │
  ├── SAFE: Contained ✓
  ├── COMPROMISED: Isolated ✗
  │
  ▼
  ✅ THREAT CONTAINMENT`
    },
    'patching-mitigation': {
      title: 'Patching',
      explanation: 'Patching involves applying updates to software, firmware, or operating systems to fix vulnerabilities, enhance functionality, or improve security, reducing the risk of exploitation.',
      examples: 'Applying Windows security updates, patching a web server for a known Apache vulnerability.',
      mnemonic: '🩹 PATCH = Protect Against Threats, Close Holes\nThink of patching a leaky roof - fixes holes to keep the system safe from \'rain\' (attacks).',
      visual: `
  🖥️ SYSTEM WITH VULNERABILITY
  │
  ▼
  🩹 APPLY PATCH
  │ Update: Fix Bugs/Security
  │
  ▼
  ✅ SYSTEM SECURED`
    },
    'encryption-mitigation': {
      title: 'Encryption',
      explanation: 'Encryption converts data into a secure format to prevent unauthorized access, ensuring confidentiality and integrity during storage or transmission.',
      examples: 'Encrypting sensitive files with AES-256, using TLS for secure web communications.',
      mnemonic: '🔐 ENCRYPT = Encode, Neutralize Curious, Retain Privacy Totally\nThink of a locked diary - only those with the key (decryption) can read the secrets.',
      visual: `
  📄 PLAIN DATA
  │
  ▼
  🔐 ENCRYPTION
  │ Algorithm: AES/TLS
  │
  ▼
  🔒 CIPHER DATA
  ✅ UNAUTHORIZED ACCESS BLOCKED`
    },
    'monitoring-mitigation': {
      title: 'Monitoring',
      explanation: 'Monitoring continuously observes systems, networks, and applications to detect suspicious activities, anomalies, or policy violations, enabling timely incident response.',
      examples: 'SIEM monitoring for real-time alerts, network traffic analysis for unusual patterns.',
      mnemonic: '👁️ MON = Monitor Operations Nonstop\nThink of a security guard - always watching for suspicious activity in the environment.',
      visual: `
  🌐 ENTERPRISE ENVIRONMENT
  │
  ▼
  👁️ MONITORING SYSTEM
  │ Logs | Alerts | Dashboards
  │
  ▼
  🚨 DETECT ANOMALY
  ✅ INCIDENT RESPONSE TRIGGERED`
    },
    'least-privilege-mitigation': {
      title: 'Least Privilege',
      explanation: 'Least privilege ensures users and systems have only the minimum permissions needed to perform their tasks, reducing the risk of unauthorized access or damage.',
      examples: 'Granting a user read-only access to a database, restricting admin rights to specific tasks.',
      mnemonic: '🔍 LEAST PRIV = Limit Every Access, Strictly Task-based Rights In View\nThink of a movie ticket - grants access only to your assigned seat, not the whole theater.',
      visual: `
  👤 USER/SYSTEM
  │
  ▼
  🔍 LEAST PRIVILEGE
  │ Permissions: Minimal Required
  │
  ├── ALLOW: Task-Specific ✓
  ├── DENY: Extra Privileges ✗
  │
  ▼
  ✅ REDUCED RISK OF ABUSE`
    },
    'configuration-enforcement': {
      title: 'Configuration Enforcement',
      explanation: 'Configuration enforcement ensures systems adhere to predefined security settings and policies, preventing misconfigurations that could introduce vulnerabilities.',
      examples: 'Using Group Policy to enforce password complexity, ensuring firewalls maintain consistent rules.',
      mnemonic: '⚙️ CONF ENF = Configure Once, Never Falter Enforcement\nThink of a blueprint - systems must follow the secure design or face correction.',
      visual: `
  🖥️ SYSTEM CONFIG
  │
  ▼
  ⚙️ ENFORCEMENT CHECK
  │ Policy: Secure Settings
  │
  ├── COMPLIANT: Keep ✓
  ├── NON-COMPLIANT: Fix ✗
  │
  ▼
  ✅ SECURE CONFIG MAINTAINED`
    },
    'decommissioning-mitigation': {
      title: 'Decommissioning',
      explanation: 'Decommissioning involves securely retiring systems, applications, or data to prevent unauthorized access or data leakage after their lifecycle ends.',
      examples: 'Wiping hard drives before disposing of old servers, removing unused cloud instances.',
      mnemonic: '🗑️ DECOM = Delete, Erase Completely, Omit Residuals\nThink of shredding old documents - ensures nothing sensitive remains for others to find.',
      visual: `
  🖥️ OLD SYSTEM/DATA
  │
  ▼
  🗑️ DECOMMISSIONING
  │ Wipe | Destroy | Remove
  │
  ▼
  ✅ NO DATA LEAKAGE`
    },
    'hardening-techniques-mitigation': {
      title: 'Hardening Techniques',
      explanation: 'Hardening techniques strengthen systems by reducing attack surfaces, disabling unnecessary features, and applying security best practices to minimize vulnerabilities.',
      examples: 'Disabling unused ports, removing default accounts, enabling secure boot.',
      mnemonic: '🛡️ HARD TECH = Harden Against Risks, Defend Thoroughly Every Component\nThink of fortifying a castle - close all unnecessary gates and reinforce defenses.',
      visual: `
  🖥️ SYSTEM
  │
  ▼
  🛡️ HARDENING
  │ Disable Ports | Remove Accounts | Secure Settings
  │
  ▼
  ✅ REDUCED ATTACK SURFACE`
    },
    'encryption-hardening': {
      title: 'Encryption (Hardening)',
      explanation: 'Encryption within hardening secures data at rest or in transit, ensuring confidentiality and integrity even if systems are compromised.',
      examples: 'Enabling BitLocker for disk encryption, using HTTPS for web applications.',
      mnemonic: '🔐 ENC HARD = Encrypt Now, Conceal Hidden Assets, Restrict Disclosure\nThink of a safe within a fortress - adds an extra layer of protection for sensitive data.',
      visual: `
  📁 DATA ON SYSTEM
  │
  ▼
  🔐 ENCRYPTION (Hardening)
  │ Disk | Network | Files
  │
  ▼
  🔒 SECURE DATA
  ✅ PROTECTED EVEN IF BREACHED`
    },
    'endpoint-protection-installation': {
      title: 'Installation of Endpoint Protection',
      explanation: 'Endpoint protection involves deploying antivirus, anti-malware, or endpoint detection and response (EDR) tools to detect and mitigate threats on devices.',
      examples: 'Installing CrowdStrike for real-time threat detection, using Microsoft Defender for antivirus.',
      mnemonic: '🛡️ END PRO = Endpoint Defense Protects, Repels Threats Often\nThink of a personal bodyguard - protects each device from harm.',
      visual: `
  🖥️ ENDPOINT
  │
  ▼
  🛡️ ENDPOINT PROTECTION
  │ Antivirus | EDR | Updates
  │
  ▼
  ✅ MALWARE BLOCKED`
    },
    'host-based-firewall': {
      title: 'Host-Based Firewall',
      explanation: 'A host-based firewall controls network traffic to and from a single device, providing granular protection against unauthorized access or attacks.',
      examples: 'Windows Firewall blocking incoming connections, iptables on Linux restricting ports.',
      mnemonic: '🔥 HOST FIRE = Host-Only Shield, Traffic Inspected, Restrict Entry\nThink of a personal gatekeeper - checks all traffic at the device\'s doorstep.',
      visual: `
  🖥️ HOST DEVICE
  │
  ▼
  🔥 HOST-BASED FIREWALL
  │ Allow/Deny Rules
  │
  ├── ALLOW: Safe Traffic ✓
  ├── DENY: Suspicious ✗
  │
  ▼
  ✅ DEVICE-LEVEL PROTECTION`
    },
    'host-based-ips': {
      title: 'Host-Based Intrusion Prevention System (HIPS)',
      explanation: 'A HIPS monitors and blocks suspicious activities on a device, such as unauthorized processes or file changes, preventing intrusions at the host level.',
      examples: 'HIPS stopping a malicious script, alerting on unauthorized registry edits.',
      mnemonic: '🚨 HIPS = Host Intrusion Protection Stops Threats\nThink of a home alarm system - detects and stops intruders at the device level.',
      visual: `
  🖥️ HOST ACTIVITY
  │
  ▼
  🚨 HIPS MONITORING
  │ Processes | Files | Registry
  │
  ├── ALLOW: Normal ✓
  ├── BLOCK: Malicious ✗
  │
  ▼
  ✅ INTRUSION PREVENTED`
    },
    'disabling-ports-protocols': {
      title: 'Disabling Ports/Protocols',
      explanation: 'Disabling unused ports and protocols reduces the attack surface by preventing potential entry points for attackers.',
      examples: 'Disabling FTP on a server, closing port 23 (Telnet) on a router.',
      mnemonic: '🚫 DIS PORT = Disable Idle Services, Prevent Open Route Threats\nThink of locking unused doors - fewer entry points for intruders.',
      visual: `
  🖥️ SYSTEM WITH OPEN PORTS
  │
  ▼
  🚫 DISABLE PORTS/PROTOCOLS
  │ Close: FTP, Telnet, etc.
  │
  ▼
  ✅ REDUCED ENTRY POINTS`
    },
    'default-password-changes': {
      title: 'Default Password Changes',
      explanation: 'Changing default passwords on devices and software eliminates easily exploitable credentials, a common attack vector.',
      examples: 'Changing the default admin password on a router, updating default database credentials.',
      mnemonic: '🔑 DEF PASS CHG = Default Eliminated, Fortify Passwords Against Simple Compromise Hacks\nThink of changing a default house key - stops anyone with a master key from entering.',
      visual: `
  🔑 DEFAULT CREDENTIALS
  │
  ▼
  🔄 CHANGE PASSWORD
  │ Unique | Strong
  │
  ▼
  ✅ SECURE ACCESS`
    },
    'removal-unnecessary-software': {
      title: 'Removal of Unnecessary Software',
      explanation: 'Removing unneeded software reduces the attack surface by eliminating potential vulnerabilities associated with unused applications.',
      examples: 'Uninstalling unused FTP clients, removing deprecated software from servers.',
      mnemonic: '🗑️ REM SOFT = Remove Excess, Minimize Software Openings for Threats\nThink of decluttering a house - fewer items (software) mean fewer places for threats to hide.',
      visual: `
  🖥️ SYSTEM WITH SOFTWARE
  │
  ▼
  🗑️ REMOVE UNNECESSARY
  │ Uninstall: Unused Apps
  │
  ▼
  ✅ SMALLER ATTACK SURFACE`
    },

    // Domain 3: Security Architecture
    'cloud-responsibility': {
      title: 'Cloud Responsibility Matrix',
      explanation: 'Defines security responsibilities between cloud provider and customer. Provider secures "of" the cloud, customer secures "in" the cloud.',
      examples: 'AWS handles data center security, you handle data encryption and access controls',
      mnemonic: '☁️ CLOUD = Customer Looks Over User Data\nThink of renting apartment - landlord handles building, you handle your stuff.',
      visual: `
    CLOUD PROVIDER     |    CUSTOMER
    ==================|================
    🏢 Physical        |   📁 Data
    ⚡ Infrastructure  |   👤 Identity/Access
    🌐 Network         |   🔐 Encryption
    💻 Hypervisor      |   🛠️ Configuration`
    },
    'microservices': {
      title: 'Microservices Architecture',
      explanation: 'Application design where software is composed of small, independent services that communicate via APIs.',
      examples: 'Netflix uses hundreds of microservices for streaming, recommendations, billing',
      mnemonic: '🧩 MICRO = Many Independent Components Require Operations\nThink of LEGO blocks - small pieces working together.',
      visual: `
    MONOLITH          MICROSERVICES
    ┌─────────┐      ┌───┐ ┌───┐ ┌───┐
    │ ALL-IN  │  →   │ A │ │ B │ │ C │
    │   ONE   │      └───┘ └───┘ └───┘
    └─────────┘        ↕     ↕     ↕
                      API  API  API`
    },
    'zero-trust-architecture': {
      title: 'Zero Trust Architecture',
      explanation: 'Security model requiring verification for every user and device, regardless of location. Never trust, always verify.',
      examples: 'Google BeyondCorp, Microsoft Zero Trust, continuous authentication',
      mnemonic: '🔐 ZERO = Zone Entry Requires Official verification\nThink of fortress where everyone shows ID at every gate.',
      visual: `
    🌐 ZERO TRUST NETWORK
    
    👤 → 🚪 → 🔍 → 🚪 → 🔍 → 🚪 → 💼 RESOURCE
         │      │      │      │      │
      VERIFY  CHECK  VERIFY CHECK  VERIFY
      
    No shortcuts! Verify at every step!`
    },
    'network-segmentation': {
      title: 'Network Segmentation',
      explanation: 'Dividing network into smaller segments to improve security and performance by limiting lateral movement.',
      examples: 'VLANs, DMZ zones, subnet isolation, air-gapped networks',
      mnemonic: '🚧 SEGMENT = Separate Everyone, Guard Multiple Entry Network Tunnels\nThink of ship watertight compartments.',
      visual: `
    ┌─────────┐  ┌─────────┐  ┌─────────┐
    │ SEGMENT │  │ SEGMENT │  │ SEGMENT │
    │    A    │  │    B    │  │    C    │
    └─────────┘  └─────────┘  └─────────┘
         │            │            │
         └────────────┼────────────┘
                 CONTROLLED
                  TRAFFIC`
    },
    'high-availability': {
      title: 'High Availability (HA)',
      explanation: 'System design ensuring operational continuity through redundancy and fault tolerance.',
      examples: 'Load balancers, clustered servers, redundant power supplies, failover systems',
      mnemonic: '⚡ HA = Highly Available\nThink of backup pilots - if one fails, another takes over.',
      visual: `
    ┌─────────┐    ┌─────────┐
    │PRIMARY  │    │SECONDARY│
    │SERVER   │←→  │SERVER   │
    └─────────┘    └─────────┘
         │              │
         └──── 🔄 ──────┘
           FAILOVER`
    },
    // Domain 4: Security Operations
    'mdm': {
      title: 'Mobile Device Management (MDM)',
      explanation: 'Centralized administration of mobile devices in enterprise environment, enforcing security policies.',
      examples: 'Microsoft Intune, VMware Workspace ONE, remote wipe, app distribution',
      mnemonic: '📱 MDM = Manage Devices Centrally\nThink of TV remote controlling all channels.',
      visual: `
    📱📱📱 MOBILE DEVICES
         ↕
    🎮 MDM CONSOLE
         ↕
    👨‍💼 IT ADMINISTRATOR
    
    Controls: Apps, Policies, Encryption`
    },
    'siem': {
      title: 'Security Information and Event Management (SIEM)',
      explanation: 'Technology collecting, analyzing, and correlating security events from multiple sources to detect threats.',
      examples: 'Splunk, IBM QRadar, LogRhythm - aggregating firewall, server, and application logs',
      mnemonic: '👁️ SIEM = Security Intelligence, Events Monitored\nThink of security guard watching multiple camera feeds.',
      visual: `
    📊 LOG SOURCES
    ├── Firewall
    ├── Server
    ├── Applications
         ↓
    🔍 SIEM ANALYSIS
         ↓
    🚨 CORRELATION & ALERTS`
    },
    'incident-response': {
      title: 'Incident Response',
      explanation: 'Structured approach to handling security incidents from detection through recovery and lessons learned.',
      examples: 'Preparation, identification, containment, eradication, recovery, lessons learned (PICERL)',
      mnemonic: '🚨 PICERL = Prepare, Identify, Contain, Eradicate, Recover, Learn\nThink of emergency response team.',
      visual: `
    1. 🛡️ PREPARATION
    2. 🔍 IDENTIFICATION
    3. 🚧 CONTAINMENT
    4. 🧹 ERADICATION
    5. 🔧 RECOVERY
    6. 📚 LESSONS LEARNED`
    },
    'vulnerability-scanning': {
      title: 'Vulnerability Scanning',
      explanation: 'Automated process identifying security weaknesses in systems, applications, and networks.',
      examples: 'Nessus, OpenVAS, Qualys - scanning for missing patches, misconfigurations',
      mnemonic: '🔍 SCAN = Security Check All Networks\nThink of medical checkup finding health issues.',
      visual: `
    🖥️ TARGET SYSTEMS
         ↓
    🔍 VULNERABILITY SCANNER
         ↓
    📋 SECURITY REPORT
    ├── Critical: 5
    ├── High: 12
    ├── Medium: 25
    └── Low: 38`
    },
    'penetration-testing': {
      title: 'Penetration Testing',
      explanation: 'Authorized simulated cyber attack against systems to evaluate security effectiveness.',
      examples: 'White box (known), black box (unknown), gray box (partial knowledge) testing',
      mnemonic: '🎯 PENTEST = Professional Ethical Network Testing Exercises Security Thoroughly\nThink of friendly burglar testing locks.',
      visual: `
    🕵️ ETHICAL HACKER
         ↓
    🎯 TARGET SYSTEM
         ↓
    💥 SIMULATED ATTACK
         ↓
    📋 VULNERABILITY REPORT`
    },
    // Domain 5: Security Program Management
    'risk-management': {
      title: 'Risk Management',
      explanation: 'Process of identifying, assessing, and controlling risks to organizational assets and operations.',
      examples: 'Risk assessments, risk registers, mitigation strategies (accept, avoid, transfer, mitigate)',
      mnemonic: '⚖️ RISK = Recognize Issues, Strategize Countermeasures\nThink of weather forecast planning outdoor event.',
      visual: `
    🎯 RISK IDENTIFICATION
         ↓
    📊 RISK ASSESSMENT
         ↓
    ⚖️ RISK ANALYSIS
         ↓
    🛡️ RISK MITIGATION`
    },
    'compliance': {
      title: 'Compliance',
      explanation: 'Adherence to laws, regulations, standards, and policies governing organizational operations.',
      examples: 'GDPR compliance, HIPAA regulations, SOX requirements, PCI DSS standards',
      mnemonic: '📋 COMPLY = Check Official Laws, Policies, Leave no Violations\nThink of following traffic rules to avoid tickets.',
      visual: `
    📋 REGULATIONS
    ├── GDPR (Privacy)
    ├── HIPAA (Health)
    ├── SOX (Financial)  
    └── PCI DSS (Payment)
         ↓
    ✅ COMPLIANCE CHECK`
    },
    'security-awareness': {
      title: 'Security Awareness Training',
      explanation: 'Educational programs designed to help users recognize and respond appropriately to security threats.',
      examples: 'Phishing simulations, password training, social engineering awareness, incident reporting procedures',
      mnemonic: '🧠 AWARE = Always Watch And Recognize Everything\nThink of fire drill - practice makes perfect response.',
      visual: `
    👥 EMPLOYEES
         ↓
    🎓 SECURITY TRAINING
    ├── Phishing awareness
    ├── Password hygiene
    ├── Social engineering
    └── Incident reporting
         ↓
    🛡️ SECURITY-CONSCIOUS WORKFORCE`
    },
    // NEW COMPREHENSIVE CONCEPTS
    'vishing': {
      title: 'Voice Phishing (Vishing)',
      explanation: 'Phone-based social engineering attack where attackers impersonate trusted entities to steal information.',
      examples: 'Fake bank calls requesting account details, tech support scams, IRS impersonation calls',
      mnemonic: '📞 VOICE = Villains Often Impersonate Caller-ID Entities\nThink of wolf in sheeps clothing, but over the phone.',
      visual: `
    📞 UNKNOWN CALLER:
    "This is your bank. We need to verify..."
         ↓
    👤 VICTIM: "Oh, let me give you my SSN"
         ↓
    💰 IDENTITY STOLEN`
    },
    'smishing': {
      title: 'SMS Phishing (Smishing)',
      explanation: 'Text message-based phishing attacks that trick users into clicking malicious links or revealing information.',
      examples: 'Fake delivery notifications, bank security alerts, prize winner notifications',
      mnemonic: '📱 SMS = Scammers Message Smartphones\nThink of spam texts with suspicious links.',
      visual: `
    📱 TEXT MESSAGE:
    "URGENT: Click link to verify account"
    "bit.ly/fake-bank-login"
         ↓
    👆 USER CLICKS
         ↓
    🎣 CREDENTIALS PHISHED`
    },
    'misinformation': {
      title: 'Misinformation/Disinformation',
      explanation: 'False or inaccurate information spread unintentionally (misinformation) or deliberately (disinformation) to deceive.',
      examples: 'Fake news about security breaches, false software updates, bogus security warnings',
      mnemonic: '🗞️ MISINFO = Misleading Information Spreads, Information Not Factual Obviously\nThink of telephone game - message gets corrupted.',
      visual: `
    📰 ORIGINAL TRUTH
         ↓
    🔄 DISTORTION PROCESS
         ↓
    📢 FALSE INFORMATION
         ↓
    😵 CONFUSED USERS`
    },
    'impersonation': {
      title: 'Impersonation',
      explanation: 'Pretending to be someone else to gain unauthorized access or information, often targeting authority figures.',
      examples: 'Fake CEO emails, IT support impersonation, vendor representative calls',
      mnemonic: '🎭 FAKE = Fraudsters Act as Known Entities\nThink of actor playing different roles to fool audience.',
      visual: `
    🎭 ATTACKER
    "Hi, this is the CEO..."
         ↓
    👤 EMPLOYEE: "Oh, hello sir!"
         ↓
    💰 UNAUTHORIZED ACCESS GRANTED`
    },
    'business-email-compromise': {
      title: 'Business Email Compromise (BEC)',
      explanation: 'Sophisticated email fraud targeting businesses to transfer money or sensitive data through impersonation.',
      examples: 'Fake wire transfer requests from executives, vendor invoice fraud, payroll redirection',
      mnemonic: '💼 BEC = Boss Email Compromised\nThink of puppet master controlling executive email.',
      visual: `
    📧 FAKE EMAIL FROM "CEO":
    "Please wire $50,000 urgently"
         ↓
    💰 FINANCE TEAM TRANSFERS MONEY
         ↓
    🏃‍♂️ ATTACKER DISAPPEARS WITH FUNDS`
    },
    'pretexting': {
      title: 'Pretexting',
      explanation: 'Creating fabricated scenario to engage victim and steal information, often involving detailed backstory.',
      examples: 'Fake IT audits, emergency situations requiring immediate action, research surveys',
      mnemonic: '📖 PRETEXT = Prepared Reasoning Explains Their Excuse eXpertly Thoroughly\nThink of elaborate lie with supporting details.',
      visual: `
    📋 ELABORATE STORY:
    "Im from IT doing security audit..."
    "Your account may be compromised..."
    "I need to verify your credentials..."
         ↓
    👤 VICTIM BELIEVES STORY
         ↓
    🔑 CREDENTIALS REVEALED`
    },
    'watering-hole': {
      title: 'Watering Hole Attack',
      explanation: 'Compromising websites commonly visited by target organization to infect visitors with malware.',
      examples: 'Compromised industry websites, infected news sites, poisoned software download sites',
      mnemonic: '🕳️ WATERHOLE = Wait At Target Entry Regularly, Hunt Over Long Effort\nThink of predator waiting by watering hole for prey.',
      visual: `
    🌐 POPULAR WEBSITE
    (Compromised with malware)
         ↑
    👥 REGULAR VISITORS
         ↓
    💀 INFECTED WITH MALWARE`
    },
    'brand-impersonation': {
      title: 'Brand Impersonation',
      explanation: 'Creating fake websites, emails, or communications that mimic legitimate brands to steal credentials.',
      examples: 'Fake Microsoft login pages, bogus Amazon notifications, counterfeit Apple support emails',
      mnemonic: '🏷️ BRAND = Bad Replicas Are Not Distinguishable\nThink of counterfeit designer bags - looks real but fake.',
      visual: `
    🏢 REAL BRAND
         ↓
    🎭 FAKE COPY
    "microsoft-security.com"
    (Note the extra dash)
         ↓
    👤 USER FOOLED`
    },
    'typosquatting': {
      title: 'Typosquatting',
      explanation: 'Registering domain names similar to legitimate sites to catch users who make typing errors.',
      examples: 'gooogle.com instead of google.com, amazom.com instead of amazon.com',
      mnemonic: '⌨️ TYPO = Tricky Yours Purposely Override\nThink of setting up shop next to McDonalds called McKonalds.',
      visual: `
    ✅ REAL: amazon.com
    ❌ FAKE: amazom.com
              ↑
         MISSING LETTER
         ↓
    👤 USER TYPES WRONG
         ↓
    🎣 PHISHING SITE`
    },
    'old-compliance': {
      title: 'Compliance (Legacy)',
      examples: 'GDPR for data protection, HIPAA for healthcare, SOX for financial reporting',
      mnemonic: '📜 COMPLY = Companies Obviously Must Properly Legally Yield\nThink of following traffic laws.',
      visual: `
    📜 REGULATIONS
         ↓
    📋 POLICIES
         ↓
    ✅ AUDIT/CHECK
         ↓
    🏆 COMPLIANCE STATUS`
    },
    'user-guidance-training': {
      title: 'User Guidance and Training',
      explanation: 'Educational program teaching employees to recognize and respond to security threats.',
      examples: 'Phishing simulations, security policies training, social engineering awareness',
      mnemonic: '🎓 GUIDE = Giving Users Information Develops Everyone\nThink of training wheels helping you learn to ride.',
      visual: `
    👥 EMPLOYEES
         ↓
    🎓 TRAINING PROGRAM
    ├── Phishing awareness
    ├── Password security
    ├── Social engineering
         ↓
    🛡️ SECURITY-CONSCIOUS WORKFORCE`
    },
    // MALWARE ATTACKS
    'trojan': {
      title: 'Trojan Horse',
      explanation: 'Malicious software disguised as legitimate programs that performs unauthorized actions when executed.',
      examples: 'Fake antivirus software, game downloads with hidden malware, email attachments',
      mnemonic: '🐴 TROJAN = Tricks Recipients Over Just About Nothing\nThink of Greek wooden horse hiding soldiers inside.',
      visual: `
    🎁 APPEARS HARMLESS
    "Free Game Download!"
         ↓
    👤 USER INSTALLS
         ↓
    💀 HIDDEN MALWARE ACTIVATES
         ↓
    🔓 BACKDOOR CREATED`
    },
    'worm': {
      title: 'Computer Worm',
      explanation: 'Self-replicating malware that spreads across networks without user intervention or host file.',
      examples: 'WannaCry ransomware worm, Conficker worm, Morris worm',
      mnemonic: '🪱 WORM = Wiggles Over Replicating Maliciously\nThink of earthworm creating copies of itself automatically.',
      visual: `
    💻 INFECTED COMPUTER
         ↓
    🌐 SPREADS VIA NETWORK
         ↓
    💻💻💻 MULTIPLE INFECTIONS
         ↓
    🪱 WORM REPLICATES EVERYWHERE`
    },
    'spyware': {
      title: 'Spyware',
      explanation: 'Malicious software that secretly monitors and collects information about users activities.',
      examples: 'Keyloggers capturing passwords, screen capture tools, browser activity monitors',
      mnemonic: '👁️ SPY = Secretly Peeks at Your activity\nThink of hidden camera watching everything you do.',
      visual: `
    ⌨️ USER TYPES PASSWORD
         ↓
    👁️ SPYWARE WATCHES
         ↓
    📊 DATA COLLECTED
         ↓
    📤 SENT TO ATTACKER`
    },
    'bloatware': {
      title: 'Bloatware',
      explanation: 'Unwanted software that consumes system resources and may contain security vulnerabilities.',
      examples: 'Pre-installed manufacturer software, bundled toolbars, unnecessary system utilities',
      mnemonic: '🎈 BLOAT = Burdens Load, Obviously Annoying Tasks\nThink of balloon taking up space with just hot air.',
      visual: `
    💻 NEW COMPUTER
         ↓
    📦📦📦 UNWANTED SOFTWARE
         ↓
    🐌 SYSTEM SLOWS DOWN
         ↓
    🔧 USER MUST REMOVE`
    },
    'virus': {
      title: 'Computer Virus',
      explanation: 'Malicious code that attaches to other programs and requires user action to spread and execute.',
      examples: 'File infector viruses, boot sector viruses, macro viruses in documents',
      mnemonic: '🦠 VIRUS = Vicious Infection Replicates Using Stolen programs\nThink of biological virus infecting healthy cells.',
      visual: `
    📄 CLEAN FILE
         ↓
    🦠 VIRUS ATTACHES
         ↓
    📄💀 INFECTED FILE
         ↓
    👤 USER EXECUTES
         ↓
    🦠 VIRUS SPREADS`
    },
    'keylogger': {
      title: 'Keylogger',
      explanation: 'Software or hardware that records keystrokes to capture passwords, messages, and sensitive information.',
      examples: 'Hardware keyloggers on keyboards, software that monitors typing, banking trojans',
      mnemonic: '⌨️ KEYLOG = Keys Everyone Yearns Logged Over time\nThink of diary recording every key you press.',
      visual: `
    ⌨️ USER TYPES:
    "username: john"
    "password: secret123"
         ↓
    📝 KEYLOGGER RECORDS:
    "username: john"
    "password: secret123"
         ↓
    📤 SENT TO ATTACKER`
    },
    'logic-bomb': {
      title: 'Logic Bomb',
      explanation: 'Malicious code that executes when specific conditions are met, often time-based or event-triggered.',
      examples: 'Code that deletes files on a specific date, malware triggered by employee termination',
      mnemonic: '💣 LOGIC = Lies Over Gracefully, Ignites Conditionally\nThink of time bomb waiting for specific moment.',
      visual: `
    💻 DORMANT CODE
    "IF date == December 25"
    "THEN delete_all_files()"
         ↓
    📅 DECEMBER 25 ARRIVES
         ↓
    💥 LOGIC BOMB EXPLODES
         ↓
    🗑️ FILES DELETED`
    },
    'rootkit': {
      title: 'Rootkit',
      explanation: 'Malicious software designed to hide its presence and maintain persistent access to a system.',
      examples: 'Kernel-level rootkits, firmware rootkits, bootkit rootkits',
      mnemonic: '🌳 ROOT = Really Obviously Obviously Totally hidden\nThink of tree roots - hidden underground but controlling the tree.',
      visual: `
    👤 USER SEARCHES FOR MALWARE
         ↓
    🔍 "No threats found"
         ↓
    🌳 ROOTKIT HIDING DEEP IN SYSTEM
         ↓
    💀 MALWARE RUNS INVISIBLY`
    },
    // NETWORK ATTACKS
    'dns-attacks': {
      title: 'DNS Attacks',
      explanation: 'Attacks targeting Domain Name System to redirect traffic, poison cache, or perform reconnaissance.',
      examples: 'DNS poisoning, DNS hijacking, DNS tunneling, DNS amplification attacks',
      mnemonic: '🌐 DNS = Dangerous Name System attacks\nThink of corrupted phone book giving wrong addresses.',
      visual: `
    👤 USER: "Take me to bank.com"
         ↓
    💀 POISONED DNS: "Go to evil-bank.com"
         ↓
    🎣 USER GOES TO FAKE SITE`
    },
    'distributed-denial-service': {
      title: 'Distributed Denial of Service (DDoS)',
      explanation: 'Coordinated attack using multiple systems to overwhelm target with traffic, making it unavailable.',
      examples: 'Botnet attacks, volumetric attacks, protocol attacks, application layer attacks',
      mnemonic: '⚔️ DDOS = Distributed Destruction Overwhelms Servers\nThink of army attacking castle from all sides.',
      visual: `
    🏰 TARGET SERVER
         ↑ ↑ ↑
    💻🔫💻🔫💻 BOTNET ARMY
    "Attack! Attack! Attack!"
         ↓
    💥 SERVER OVERWHELMED`
    },
    'malicious-usb': {
      title: 'Malicious USB',
      explanation: 'USB devices containing malware or hardware designed to compromise systems when connected.',
      examples: 'USB drops, rubber ducky attacks, USB killers, infected thumb drives',
      mnemonic: '🔌 USB = Usually Suspicious Badware\nThink of Trojan horse but smaller - evil gift.',
      visual: `
    🎁 "FREE USB FOUND!"
         ↓
    👤 USER PLUGS IN
         ↓
    💀 MALWARE EXECUTES
         ↓
    🔓 SYSTEM COMPROMISED`
    },
    // PHYSICAL SECURITY
    'access-control-vestibule': {
      title: 'Access Control Vestibule (Mantrap)',
      explanation: 'Double-door system allowing only one person at a time, preventing tailgating and unauthorized access.',
      examples: 'Bank vault entrances, secure facility entry points, data center access',
      mnemonic: '🚪🚪 MANTRAP = Multiple Authentication Needed To Reach Access Point\nThink of airlock - one door closes before next opens.',
      visual: `
    🚪 OUTER DOOR
    │  [Person enters]
    │  🔒 Door locks
    ▼
    📦 VESTIBULE
    │  [Identity verified]
    ▼
    🚪 INNER DOOR
       [Access granted]`
    },
    'fencing': {
      title: 'Security Fencing',
      explanation: 'Physical barriers designed to prevent unauthorized access to facilities and detect intrusion attempts.',
      examples: 'Chain-link fencing, razor wire, electric fencing, anti-climb barriers',
      mnemonic: '🚧 FENCE = Facility Entry Not Cleared Easily\nThink of castle walls keeping enemies out.',
      visual: `
    🏢 SECURE FACILITY
    ║               ║
    ║  Protected     ║
    ║    Area        ║
    ║               ║
    ╚═══════════════╝
         FENCING`
    },
    'video-surveillance': {
      title: 'Video Surveillance',
      explanation: 'Camera systems for monitoring, recording, and deterring unauthorized activities in secured areas.',
      examples: 'CCTV cameras, IP cameras, PTZ cameras, facial recognition systems',
      mnemonic: '📹 VIDEO = Vigilant Intelligence Detects Every Offensive action\nThink of all-seeing eye watching everything.',
      visual: `
    📹 CAMERA NETWORK
    ├─ 📹 Front entrance
    ├─ 📹 Parking lot
    ├─ 📹 Server room
    └─ 📹 Emergency exits
         ↓
    🎮 SECURITY CONTROL ROOM`
    },
    'security-guard': {
      title: 'Security Guards',
      explanation: 'Human security personnel providing physical protection, access control, and incident response.',
      examples: 'Armed guards, unarmed guards, patrol officers, reception security',
      mnemonic: '👮 GUARD = Generally Understands And Responds to Danger\nThink of castle guards protecting the kingdom.',
      visual: `
    🏢 FACILITY
         ↑
    👮 SECURITY GUARD
    "Who goes there?"
         ↑
    👤 VISITOR
    [Shows ID badge]`
    },
    // CRYPTOGRAPHY & PKI
    'digital-signatures': {
      title: 'Digital Signatures',
      explanation: 'Cryptographic technique providing authentication, integrity, and non-repudiation for digital documents.',
      examples: 'Document signing, code signing, email signatures, PDF signatures',
      mnemonic: '✍️ SIGNATURE = Secure Identity Generated, No Alteration Truly Understood, Receiver Expects\nThink of magic ink that proves who wrote it.',
      visual: `
    📄 DOCUMENT
         ↓
    🔐 PRIVATE KEY SIGNS
         ↓
    ✍️ DIGITAL SIGNATURE
         ↓
    🔑 PUBLIC KEY VERIFIES
         ↓
    ✅ AUTHENTIC & UNMODIFIED`
    },
    // ZERO TRUST ARCHITECTURE - CONTROL PLANE
    'control-plane': {
      title: 'Zero Trust Control Plane',
      explanation: 'Management layer responsible for making policy decisions and orchestrating security controls in zero trust architecture.',
      examples: 'Policy management systems, identity providers, security orchestration platforms',
      mnemonic: '🎮 CONTROL = Centrally Operates Network Trust Realtime Operations Logically\nThink of air traffic control tower managing all flights.',
      visual: `
    🎮 CONTROL PLANE
    ├── 👨‍💼 Policy Administrator
    ├── ⚙️ Policy Engine  
    ├── 🔍 Risk Assessment
    └── 📊 Decision Making
         ↓
    ✈️ DATA PLANE ENFORCEMENT`
    },
    'adaptive-identity': {
      title: 'Adaptive Identity',
      explanation: 'Dynamic identity verification that adjusts authentication requirements based on risk factors, user behavior, and context.',
      examples: 'Requiring additional verification for unusual locations, step-up authentication for sensitive operations, behavioral analysis',
      mnemonic: '🎭 ADAPT = Always Decide Authentication Policies Thoughtfully\nThink of chameleon changing colors based on environment.',
      visual: `
    👤 USER LOGIN ATTEMPT
         ↓
    🔍 RISK ANALYSIS
    ├── Location: High risk
    ├── Device: Unknown
    ├── Time: Unusual
         ↓
    🔐 ADAPTIVE RESPONSE
    "Additional verification required"`
    },
    'threat-scope-reduction': {
      title: 'Threat Scope Reduction',
      explanation: 'Minimizing the potential attack surface by limiting access, reducing permissions, and containing threats to smallest possible scope.',
      examples: 'Micro-segmentation, least privilege access, network isolation, containerization',
      mnemonic: '🎯 SCOPE = Secure Carefully, Only Permit Essential access\nThink of sniper scope - focus on specific target only.',
      visual: `
    🌐 FULL NETWORK (Large attack surface)
         ↓
    🚧 SEGMENTATION
         ↓
    🎯 REDUCED SCOPE
    ├── Isolated systems
    ├── Limited access
    └── Contained threats`
    },
    'policy-driven-access': {
      title: 'Policy-Driven Access Control',
      explanation: 'Access decisions made automatically based on predefined security policies rather than static permissions.',
      examples: 'Conditional access policies, dynamic authorization, attribute-based access control (ABAC)',
      mnemonic: '📜 POLICY = People Only Login If Correctly Identified\nThink of bouncer at club checking guest list rules.',
      visual: `
    👤 ACCESS REQUEST
         ↓
    📜 POLICY ENGINE
    "IF user=john AND device=trusted 
     AND location=office THEN allow"
         ↓
    ✅ ACCESS GRANTED/DENIED`
    },
    'policy-administrator': {
      title: 'Policy Administrator (PA)',
      explanation: 'Component that manages and configures access control policies in zero trust architecture.',
      examples: 'Microsoft Conditional Access, Okta policies, AWS IAM policies, Google Cloud IAM',
      mnemonic: '👨‍💼 ADMIN = Always Decides Management Instructions Naturally\nThink of office manager setting building access rules.',
      visual: `
    👨‍💼 POLICY ADMINISTRATOR
         ↓
    📋 CREATES/MANAGES POLICIES
    ├── User access rules
    ├── Device requirements
    ├── Location restrictions
         ↓
    📤 SENDS TO POLICY ENGINE`
    },
    'policy-engine': {
      title: 'Policy Engine (PE)',
      explanation: 'Decision-making component that evaluates access requests against policies and makes allow/deny decisions.',
      examples: 'Authorization engines, decision points in ABAC systems, rule evaluation engines',
      mnemonic: '⚙️ ENGINE = Evaluates, Never Guesses, Issues Network Decisions\nThink of judge weighing evidence before verdict.',
      visual: `
    📥 ACCESS REQUEST
         ↓
    ⚙️ POLICY ENGINE
    ├── Check user attributes
    ├── Evaluate policies
    ├── Consider context
         ↓
    ⚖️ DECISION: ALLOW/DENY`
    },
    'data-plane': {
      title: 'Data Plane',
      explanation: 'The operational layer where actual data flows and policies are enforced in zero trust architecture.',
      examples: 'Network traffic enforcement, application proxies, security gateways, firewalls',
      mnemonic: '✈️ PLANE = Processes Live Access, Never Estimates\nThink of airplane runway where planes actually take off.',
      visual: `
    📊 DATA PLANE
    ├── 🚛 DATA TRAFFIC
    ├── 🛡️ ENFORCEMENT POINTS
    ├── 🔍 INSPECTION/FILTERING
    └── 📈 MONITORING/LOGGING`
    },
    'implicit-trust-zones': {
      title: 'Implicit Trust Zones',
      explanation: 'Traditional network areas where devices are trusted by default based on location, eliminated in zero trust.',
      examples: 'Internal corporate networks, VPN networks, DMZ zones (traditional model)',
      mnemonic: '🚫 IMPLICIT = In Modern Policies, Location Is Completely Invalid Trust\nThink of VIP area with no bouncer - anyone inside trusted.',
      visual: `
    🏢 TRADITIONAL NETWORK
    ├── "Inside = Trusted" ❌
    ├── "Outside = Untrusted" ❌
         ↓
    🔐 ZERO TRUST
    ├── "Verify Everything" ✅
    └── "Trust Nothing" ✅`
    },
    'subject-system': {
      title: 'Subject/System',
      explanation: 'Entities (users, devices, applications) requesting access to resources in zero trust architecture.',
      examples: 'End users, mobile devices, IoT devices, service accounts, applications',
      mnemonic: '👤 SUBJECT = Someone Using Behalf Just Expects Controlled Trust\nThink of student raising hand to ask question.',
      visual: `
    SUBJECTS REQUESTING ACCESS:
    👤 Users
    📱 Devices  
    🤖 Applications
    🔧 Services
         ↓
    🎯 RESOURCE ACCESS REQUEST`
    },
    'policy-enforcement-point': {
      title: 'Policy Enforcement Point (PEP)',
      explanation: 'Component that enforces access control decisions made by the policy engine at the point of access.',
      examples: 'Network gateways, application proxies, API gateways, security appliances',
      mnemonic: '👮 ENFORCE = Everyone Never Forces Orders, Requires Correct Evidence\nThink of security checkpoint enforcing airport rules.',
      visual: `
    ⚖️ POLICY ENGINE DECISION
         ↓
    👮 ENFORCEMENT POINT
    ├── Allow traffic ✅
    ├── Block access ❌  
    ├── Log activity 📝
    └── Monitor compliance 📊`
    },
    // PHYSICAL SECURITY CONCEPTS
    'mantrap': {
      title: 'Access Control Vestibule (Mantrap)',
      explanation: 'Double-door system allowing only one person at a time, preventing tailgating and unauthorized access.',
      examples: 'Bank vault entrances, secure facility entry points, data center access',
      mnemonic: '🚪🚪 MANTRAP = Multiple Authentication Needed To Reach Access Point\nThink of airlock - one door closes before next opens.',
      visual: `
    🚪 OUTER DOOR
    │  [Person enters]
    │  🔒 Door locks
    ▼
    📦 VESTIBULE
    │  [Identity verified]
    ▼
    🚪 INNER DOOR
       [Access granted]`
    },
    'access-badge': {
      title: 'Access Badge',
      explanation: 'Physical credential card used for building access control, often containing RFID, magnetic stripe, or smart card technology.',
      examples: 'Employee ID cards, proximity cards, smart cards, contactless badges',
      mnemonic: '🏷️ BADGE = Building Access Depends on Genuine Employee card\nThink of name tag that opens doors.',
      visual: `
    🏷️ ACCESS BADGE
    ├── 📡 RFID chip
    ├── 🔢 Employee ID
    ├── 📸 Photo ID
         ↓
    🚪 CARD READER
         ↓
    ✅ ACCESS GRANTED`
    },
    'lighting': {
      title: 'Security Lighting',
      explanation: 'Illumination systems designed to deter intruders, aid surveillance, and improve overall facility security.',
      examples: 'Motion-activated lights, perimeter lighting, emergency lighting, infrared illumination',
      mnemonic: '💡 LIGHT = Lamps Illuminate Genuine Harmful Threats\nThink of porch light scaring away burglars.',
      visual: `
    🌙 DARKNESS (Security risk)
         ↓
    💡 SECURITY LIGHTING
    ├── Deters intruders
    ├── Aids cameras
    ├── Improves visibility
         ↓
    👁️ ENHANCED SECURITY`
    },
    'infrared-sensor': {
      title: 'Infrared Sensors',
      explanation: 'Motion detection devices that sense heat signatures and thermal changes to detect intruders.',
      examples: 'PIR (Passive Infrared) sensors, thermal cameras, heat-based motion detectors',
      mnemonic: '🌡️ INFRARED = Invisible Frequencies Reveal All Red-hot Entities Detected\nThink of predator vision seeing heat signatures.',
      visual: `
    👤 INTRUDER (Body heat)
         ↓
    🌡️ INFRARED SENSOR
    "Temperature change detected!"
         ↓
    🚨 ALARM TRIGGERED`
    },
    'pressure-sensor': {
      title: 'Pressure Sensors',
      explanation: 'Detection devices that trigger when weight or pressure is applied to floors, mats, or surfaces.',
      examples: 'Pressure-sensitive floor mats, weight-triggered alarms, step detection systems',
      mnemonic: '⚖️ PRESSURE = People Really Expect Sensors to Sense Unwanted Requested Entry\nThink of doormat that knows when stepped on.',
      visual: `
    🚶‍♂️ PERSON STEPS
         ↓
    ⚖️ PRESSURE MAT
    "Weight detected: 150 lbs"
         ↓
    🔔 NOTIFICATION SENT`
    },
    'microwave-sensor': {
      title: 'Microwave Sensors',
      explanation: 'Motion detection devices that emit microwave signals and detect changes in reflected signals caused by movement.',
      examples: 'Doppler radar sensors, microwave motion detectors, perimeter intrusion detection',
      mnemonic: '📡 MICROWAVE = Motion Immediately Changes Reflected Operative Waves And Vibrates Electronics\nThink of radar detecting moving objects.',
      visual: `
    📡 MICROWAVE TRANSMITTER
    ~~~~~~~~~~~ waves out
    👤 MOVING PERSON
    ~~~~~~~~### waves back (changed)
         ↓
    🚨 MOTION DETECTED`
    },
    'ultrasonic-sensor': {
      title: 'Ultrasonic Sensors',
      explanation: 'Motion detection devices using high-frequency sound waves to detect movement and changes in environment.',
      examples: 'Ultrasonic motion detectors, sound-based intrusion detection, acoustic sensors',
      mnemonic: '🔊 ULTRASONIC = Ultra Sound Tremendously Reveals All Suspicious Objects Never Invisible Completely\nThink of bat echolocation detecting obstacles.',
      visual: `
    🔊 ULTRASONIC TRANSMITTER
    ♪♪♪♪♪♪ sound waves
    👤 PERSON MOVES
    ♪♪#♪♪♪ disrupted pattern
         ↓
    ⚠️ INTRUSION ALERT`
    },
    // DECEPTION AND DISRUPTION TECHNOLOGY
    'honeynet': {
      title: 'Honeynet',
      explanation: 'Network of multiple interconnected honeypots that simulate a realistic network environment to attract and study attackers.',
      examples: 'Distributed honeypot networks, virtual honeynet infrastructures, research honeynets',
      mnemonic: '🕸️ HONEYNET = Hundreds Of Networked Elaborate Yields Network Evidence Together\nThink of spider web - larger trap catches bigger prey.',
      visual: `
    🕸️ HONEYNET TOPOLOGY
    ├── 🍯 Honeypot A (Web server)
    ├── 🍯 Honeypot B (Database)  
    ├── 🍯 Honeypot C (Router)
    └── 🍯 Honeypot D (Workstation)
         ↓
    📊 CENTRALIZED ANALYSIS`
    },
    'honeyfile': {
      title: 'Honeyfile',
      explanation: 'Decoy files designed to detect unauthorized access by alerting when opened, copied, or modified.',
      examples: 'Fake financial documents, decoy customer databases, trap configuration files',
      mnemonic: '📄 HONEYFILE = Hidden Obvious Notices, Everyone Yields Files Immediately Logged Everywhere\nThink of fake treasure map that alerts when touched.',
      visual: `
    📁 FILE SYSTEM
    ├── real_data.xlsx
    ├── 🍯 FAKE_financials.xlsx (Honeyfile)
    ├── config.txt
         ↓
    👤 ATTACKER OPENS FAKE FILE
         ↓
    🚨 "Honeyfile accessed!" ALERT`
    },
    'honeytoken': {
      title: 'Honeytoken',
      explanation: 'Fake digital breadcrumbs like credentials, API keys, or database records designed to detect unauthorized access.',
      examples: 'Fake AWS keys, bogus API tokens, decoy database records, canary tokens',
      mnemonic: '🎫 HONEYTOKEN = Hidden Objects Never Expected, Yield Tracking Over Key Entry Networks\nThink of fake concert ticket that alerts when scanned.',
      visual: `
    🔐 FAKE CREDENTIALS
    "aws_secret_key=FAKE123TOKEN"
         ↓
    👤 ATTACKER USES TOKEN
         ↓
    📞 ALERT: "Honeytoken used from IP: 1.2.3.4"`
    },
    // CHANGE MANAGEMENT CONCEPTS
    'approval-process': {
      title: 'Change Approval Process',
      explanation: 'Formal procedure requiring authorization before implementing changes to systems, ensuring security and stability.',
      examples: 'Change Advisory Board (CAB), multi-level approvals, emergency change procedures',
      mnemonic: '✅ APPROVE = Always Properly Prove Requirements Or Violate Everything\nThink of getting parents permission before going out.',
      visual: `
    📝 CHANGE REQUEST
         ↓
    👥 REVIEW COMMITTEE
    ├── Risk assessment
    ├── Impact analysis  
    ├── Resource approval
         ↓
    ✅ APPROVED/REJECTED`
    },
    'ownership': {
      title: 'Change Ownership',
      explanation: 'Clear assignment of responsibility for changes, including who initiates, approves, implements, and maintains changes.',
      examples: 'Change owners, system owners, process owners, business owners',
      mnemonic: '👤 OWNER = Obviously Who Never Expects Requests\nThink of house owner responsible for all repairs.',
      visual: `
    🏠 SYSTEM/PROCESS
         ↓
    👤 DESIGNATED OWNER
    ├── Accountable for changes
    ├── Authorizes modifications
    ├── Maintains documentation
    └── Handles issues`
    },
    'stakeholders': {
      title: 'Change Stakeholders',
      explanation: 'Individuals or groups affected by or involved in changes, whose input and approval may be required.',
      examples: 'End users, IT operations, security teams, business units, management',
      mnemonic: '👥 STAKEHOLDER = Several Teams Always Keep Everyone Holding Ownership, Learning Decisions Evolve Responsibly\nThink of town hall meeting discussing new park.',
      visual: `
    📊 PROPOSED CHANGE
         ↓
    👥 STAKEHOLDER ANALYSIS
    ├── 👨‍💻 IT Team (Technical impact)
    ├── 👩‍💼 Business (Process impact)
    ├── 🛡️ Security (Risk impact)
    └── 👥 End Users (Usage impact)`
    },
    'impact-analysis': {
      title: 'Change Impact Analysis',
      explanation: 'Assessment of potential effects a change may have on systems, processes, security, and business operations.',
      examples: 'Risk assessments, dependency analysis, security impact reviews, business continuity analysis',
      mnemonic: '🎯 IMPACT = Identify Mainly Potential Areas Causing Trouble\nThink of dropping stone in pond - ripples spread everywhere.',
      visual: `
    🔄 PROPOSED CHANGE
         ↓
    📊 IMPACT ANALYSIS
    ├── 🔒 Security risks
    ├── ⚙️ System dependencies  
    ├── 👥 User experience
    ├── 💰 Cost implications
    └── ⏰ Timeline effects`
    },
    'test-results': {
      title: 'Change Test Results',
      explanation: 'Documented outcomes from testing changes in controlled environments before production deployment.',
      examples: 'Unit test results, integration testing, security testing, user acceptance testing',
      mnemonic: '🧪 TEST = Thoroughly Examine Solutions Today\nThink of science experiment proving hypothesis before sharing.',
      visual: `
    🧪 TESTING PHASE
    ├── Unit tests: ✅ PASS
    ├── Integration: ✅ PASS
    ├── Security scan: ⚠️ 2 warnings
    ├── Performance: ✅ PASS
         ↓
    📋 DOCUMENTED RESULTS`
    },
    'backout-plan': {
      title: 'Change Backout Plan',
      explanation: 'Predetermined procedure to reverse or rollback changes if implementation fails or causes problems.',
      examples: 'Rollback scripts, restore procedures, failback mechanisms, recovery plans',
      mnemonic: '⬅️ BACKOUT = Before Acting, Create Contingency Keeping Original Unchanged Territory\nThink of exit strategy before entering maze.',
      visual: `
    🔄 CHANGE IMPLEMENTATION
         ↓
    ❌ PROBLEM OCCURS
         ↓
    ⬅️ BACKOUT PLAN ACTIVATED
    ├── Stop new change
    ├── Restore backup
    ├── Verify system health
    └── Notify stakeholders`
    },
    'maintenance-window': {
      title: 'Maintenance Window',
      explanation: 'Scheduled time period designated for system changes, updates, or maintenance with minimal business impact.',
      examples: 'Weekend updates, after-hours maintenance, planned downtime windows, service windows',
      mnemonic: '🕐 WINDOW = When Information Networks Downtime Operates Wisely\nThink of store closing hours for cleaning and restocking.',
      visual: `
    📅 SCHEDULED MAINTENANCE
    🕐 2:00 AM - 6:00 AM Sunday
         ↓
    ⚙️ SYSTEM UPDATES
    ├── Minimal user impact
    ├── Support team available
    ├── Rollback ready
         ↓
    ✅ SERVICE RESTORED`
    },
    'standard-operating-procedure': {
      title: 'Standard Operating Procedure (SOP)',
      explanation: 'Documented step-by-step instructions for routine operations and change management activities.',
      examples: 'Change implementation procedures, incident response SOPs, security protocols, operational runbooks',
      mnemonic: '📋 SOP = Step-by-step Operations Procedure\nThink of recipe book - follow exact steps for consistent results.',
      visual: `
    📋 SOP DOCUMENT
    ├── 1. Pre-change checklist
    ├── 2. Implementation steps
    ├── 3. Verification procedures
    ├── 4. Rollback process
    └── 5. Documentation updates`
    },
    // COMPREHENSIVE MISSING CONCEPTS - DOMAIN 1
    'authorization-models': {
      title: 'Authorization Models',
      explanation: 'Frameworks that determine what actions authenticated users can perform, controlling access to resources based on permissions and roles.',
      examples: 'Role-based access control (RBAC), Attribute-based access control (ABAC), Discretionary access control (DAC), Mandatory access control (MAC)',
      mnemonic: '🔑 AUTH = Always Understand Trust Hierarchy\nThink of hotel key cards - different levels unlock different floors.',
      visual: `
    👤 USER (Authenticated)
         ↓
    🔑 AUTHORIZATION CHECK
    ├── Role: Manager
    ├── Permissions: Read/Write
    ├── Resources: Financial data
         ↓
    ✅ ACCESS GRANTED/DENIED`
    },
    'gap-analysis': {
      title: 'Gap Analysis',
      explanation: 'Process of comparing current security posture against desired security objectives to identify deficiencies and improvement areas.',
      examples: 'Security assessments, compliance audits, risk analysis, policy reviews',
      mnemonic: '🕳️ GAP = Generally Assess Present vs Planned\nThink of measuring distance between where you are and where you want to be.',
      visual: `
    🎯 DESIRED STATE
         ↑
    📏 SECURITY GAP
         ↓
    📊 CURRENT STATE
         ↓
    📋 IMPROVEMENT PLAN`
    },
    // TECHNICAL IMPLICATIONS
    'allow-deny-lists': {
      title: 'Allow Lists/Deny Lists',
      explanation: 'Security controls that explicitly permit (allow list) or block (deny list) specific entities, applications, or activities.',
      examples: 'Firewall rules, application whitelisting, IP blocking, email filtering',
      mnemonic: '🚦 LISTS = Let In Safe Traffic, Stop harmful entries\nThink of VIP guest list - only approved names get in.',
      visual: `
    📋 ALLOW LIST (Whitelist)
    ✅ approved_app.exe
    ✅ trusted_domain.com
    ✅ 192.168.1.100
    
    📋 DENY LIST (Blacklist)  
    ❌ malware.exe
    ❌ bad_domain.com
    ❌ 10.0.0.50`
    },
    'restricted-activities': {
      title: 'Restricted Activities',
      explanation: 'Specific actions or operations that are limited or prohibited during certain periods, typically during system changes or maintenance.',
      examples: 'No database updates during backup, restricted user access during patches, limited network changes during peak hours',
      mnemonic: '🚫 RESTRICT = Really Ensure System Totally Restricts Identified Critical Tasks\nThink of construction zone - some activities not allowed.',
      visual: `
    ⚠️ MAINTENANCE WINDOW
    🚫 RESTRICTED ACTIVITIES:
    ├── No user modifications
    ├── No system reboots
    ├── No configuration changes
    ├── No new deployments
         ↓
    ✅ ACTIVITIES RESUME POST-MAINTENANCE`
    },
    'downtime': {
      title: 'System Downtime',
      explanation: 'Period when systems or services are unavailable, either planned for maintenance or unplanned due to failures.',
      examples: 'Scheduled maintenance windows, system failures, network outages, planned upgrades',
      mnemonic: '⏰ DOWN = Definitely Offline, Waiting Network restart\nThink of elevator being "Out of Order" - temporarily unavailable.',
      visual: `
    🟢 SYSTEM ONLINE
         ↓
    ⚠️ PLANNED DOWNTIME
    📅 2:00 AM - 6:00 AM
         ↓
    🔧 MAINTENANCE/UPDATES
         ↓
    🟢 SYSTEM RESTORED`
    },
    'service-restart': {
      title: 'Service Restart',
      explanation: 'Process of stopping and starting system services, often required after configuration changes or updates.',
      examples: 'Web server restart, database service restart, network service restart, application pool restart',
      mnemonic: '🔄 RESTART = Really Ensure Service Totally Activates Right Tomorrow\nThink of turning computer off and on again to fix problems.',
      visual: `
    🟢 SERVICE RUNNING
         ↓
    🛑 STOP SERVICE
         ↓
    ⚙️ APPLY CHANGES
         ↓
    ▶️ START SERVICE
         ↓
    🟢 SERVICE RUNNING (Updated)`
    },
    'application-restart': {
      title: 'Application Restart',
      explanation: 'Process of stopping and restarting applications, typically needed to apply configuration changes or resolve issues.',
      examples: 'Web application restart, mobile app restart, desktop application restart, container restart',
      mnemonic: '📱 APP = Always Properly Process restart\nThink of closing and reopening an app when it freezes.',
      visual: `
    📱 APPLICATION ACTIVE
         ↓
    🔄 RESTART INITIATED
         ↓
    💾 SAVE STATE (if possible)
         ↓
    🚪 CLOSE APPLICATION
         ↓
    ▶️ LAUNCH APPLICATION
         ↓
    📱 APPLICATION READY`
    },
    'legacy-applications': {
      title: 'Legacy Applications',
      explanation: 'Older software systems that remain in use despite newer alternatives being available, often due to business dependencies.',
      examples: 'Mainframe systems, older ERP systems, custom-built applications, outdated operating systems',
      mnemonic: '🏛️ LEGACY = Long-Existing Generally Acknowledged Critical Years\nThink of old building still in use - outdated but functional.',
      visual: `
    🏛️ LEGACY APPLICATION
    ├── ⚠️ Security risks
    ├── 💰 High maintenance cost
    ├── 🔗 Business dependencies
    ├── 👥 Limited expertise
         ↓
    🛡️ COMPENSATING CONTROLS NEEDED`
    },
    'dependencies': {
      title: 'System Dependencies',
      explanation: 'Interconnections between systems, applications, or services where one component relies on another to function properly.',
      examples: 'Database dependencies, network dependencies, service dependencies, library dependencies',
      mnemonic: '🔗 DEPEND = Definitely Ensure Proper Enterprise Network Dependencies\nThink of domino effect - one falls, others affected.',
      visual: `
    🌐 WEB APPLICATION
         ↓ depends on
    🗄️ DATABASE SERVER
         ↓ depends on
    🔌 NETWORK CONNECTION
         ↓ depends on
    ⚡ POWER SUPPLY
    
    ❌ Any failure = Chain reaction`
    },
    // CRYPTOGRAPHIC CONCEPTS - PKI AND ENCRYPTION
    'public-key': {
      title: 'Public Key',
      explanation: 'Cryptographic key that can be freely shared and used to encrypt data or verify digital signatures in asymmetric encryption.',
      examples: 'SSL certificate public keys, SSH public keys, email encryption public keys',
      mnemonic: '🗝️ PUBLIC = People Usually Broadcast Large Identity Keys\nThink of mailbox - everyone knows the address (public) but only owner has key (private).',
      visual: `
    👤 ALICE
    🗝️ Public Key (Shareable)
         ↓
    🌐 PUBLISHED OPENLY
         ↓
    👤 BOB encrypts with Alice's public key
         ↓
    🔒 Only Alice can decrypt with private key`
    },
    'private-key': {
      title: 'Private Key',
      explanation: 'Secret cryptographic key that must be kept confidential and is used to decrypt data or create digital signatures.',
      examples: 'SSL certificate private keys, personal encryption keys, code signing private keys',
      mnemonic: '🔐 PRIVATE = Personal Really Important Vault And Total Encryption\nThink of house key - never share, only you should have it.',
      visual: `
    👤 ALICE
    🔐 Private Key (Secret!)
    ├── 🚫 Never shared
    ├── 🔓 Decrypts messages
    ├── ✍️ Signs documents
    └── 🛡️ Proves identity`
    },
    'key-escrow': {
      title: 'Key Escrow',
      explanation: 'Practice of storing cryptographic keys with a trusted third party for recovery purposes or legal compliance.',
      examples: 'Corporate key recovery systems, government access requirements, backup encryption keys',
      mnemonic: '🏦 ESCROW = Emergency Storage Crypto Recovery Operations Waiting\nThink of safety deposit box - trusted keeper holds spare key.',
      visual: `
    🔐 ENCRYPTION KEY
         ↓
    🏦 TRUSTED ESCROW AGENT
    ├── Secure storage
    ├── Legal compliance
    ├── Recovery procedures
         ↓
    🚨 EMERGENCY: Key retrieved`
    },
    'full-disk-encryption': {
      title: 'Full-disk Encryption',
      explanation: 'Encryption of entire storage device including operating system, applications, and data files.',
      examples: 'BitLocker (Windows), FileVault (macOS), LUKS (Linux), hardware-based encryption',
      mnemonic: '💽 FULLDISK = Fully Unified Logical Layer Disk Integrated Security Key\nThink of vault door - entire room protected, not just individual safes.',
      visual: `
    💻 COMPUTER
    💽 HARD DRIVE (Encrypted)
    ├── 🔒 Operating System
    ├── 🔒 Applications  
    ├── 🔒 User Data
    ├── 🔒 System Files
         ↓
    🔑 Boot password required`
    },
    'partition-encryption': {
      title: 'Partition Encryption',
      explanation: 'Encryption applied to specific disk partitions rather than the entire drive, allowing selective protection.',
      examples: 'Encrypted data partitions, separate encrypted volumes, dual-boot scenarios',
      mnemonic: '🗂️ PARTITION = Particularly Applied Regional Territory Information Organized Naturally\nThink of apartment building - encrypt specific apartments, not whole building.',
      visual: `
    💽 HARD DRIVE
    ├── 🔓 System Partition
    ├── 🔒 Data Partition (Encrypted)
    ├── 🔓 Backup Partition
    └── 🔒 Documents Partition (Encrypted)`
    },
    'file-encryption': {
      title: 'File Encryption',
      explanation: 'Encryption applied to individual files or documents, providing granular protection for specific data.',
      examples: 'PGP encrypted files, password-protected documents, encrypted email attachments',
      mnemonic: '📄 FILE = Focused Individual Level Encryption\nThink of secret documents in locked briefcase - specific files protected.',
      visual: `
    📁 FOLDER
    ├── 📄 public_document.txt
    ├── 🔒 secret_file.txt.enc
    ├── 📊 report.xlsx
    └── 🔒 confidential.pdf.enc`
    },
    'volume-encryption': {
      title: 'Volume Encryption',
      explanation: 'Encryption applied to logical storage volumes, which can span multiple physical devices or portions of devices.',
      examples: 'Encrypted network volumes, RAID volume encryption, virtual disk encryption',
      mnemonic: '📚 VOLUME = Various Organized Logical Units Made Encrypted\nThink of library section - all books in that area protected together.',
      visual: `
    📚 LOGICAL VOLUME
    ├── 💽 Physical Disk 1 (part)
    ├── 💽 Physical Disk 2 (part)  
    ├── 💽 Physical Disk 3 (part)
         ↓
    🔒 ENTIRE VOLUME ENCRYPTED`
    },
    'database-encryption': {
      title: 'Database Encryption',
      explanation: 'Protection of database contents through encryption at various levels - field, table, or entire database.',
      examples: 'Transparent Data Encryption (TDE), column-level encryption, encrypted database files',
      mnemonic: '🗄️ DATABASE = Data Always Totally And Basically Always Stored Encrypted\nThink of filing cabinet with locked drawers - data protected at rest.',
      visual: `
    🗄️ DATABASE
    ├── 🔒 Customer_Table (Encrypted)
    │   ├── ID: 123
    │   ├── Name: John (🔒 encrypted)
    │   └── SSN: *** (🔒 encrypted)
    └── 📊 Analytics_Table (Plain)`
    },
    'record-encryption': {
      title: 'Record Encryption',
      explanation: 'Encryption applied to individual database records or rows, providing fine-grained data protection.',
      examples: 'Patient records encryption, financial transaction encryption, individual customer data encryption',
      mnemonic: '📝 RECORD = Really Encrypted Confidential Operations Requiring Defense\nThink of medical chart - each patient record individually sealed.',
      visual: `
    📋 DATABASE TABLE
    ├── 🔒 Record 1: Patient A (Encrypted)
    ├── 🔓 Record 2: Public Info (Plain)
    ├── 🔒 Record 3: Patient B (Encrypted)
    └── 🔓 Record 4: Reference Data (Plain)`
    },
    // MORE CRYPTOGRAPHIC AND SECURITY CONCEPTS
    'transport-communication-encryption': {
      title: 'Transport/Communication Encryption',
      explanation: 'Protection of data while it travels across networks, ensuring confidentiality and integrity during transmission.',
      examples: 'HTTPS/TLS, SSH, VPN encryption, email encryption (S/MIME), secure messaging',
      mnemonic: '🚛 TRANSPORT = Totally Reliable And Necessary Security Prevents Observation, Requires Tunneling\nThink of armored truck - protects valuables during transport.',
      visual: `
    📱 DEVICE A
         ↓
    🔒 ENCRYPTED TUNNEL
    ~~~🛡️~~~🛡️~~~🛡️~~~
         ↓
    💻 DEVICE B
    
    🕵️ EAVESDROPPER: "Can't read this!"`
    },
    'asymmetric-encryption': {
      title: 'Asymmetric Encryption',
      explanation: 'Encryption method using two mathematically related keys - public and private - for secure communication without sharing secrets.',
      examples: 'RSA, ECC (Elliptic Curve), Diffie-Hellman, digital signatures, SSL/TLS',
      mnemonic: '🔑 ASYMMETRIC = Always Separate Your Math Methods, Each Totally Reliable Identity Check\nThink of mailbox - public address, private key to open.',
      visual: `
    👤 ALICE                👤 BOB
    🗝️ Public Key         🗝️ Public Key
    🔐 Private Key        🔐 Private Key
         ↓                     ↑
    📧 Encrypt with Bob's public key
         ↓
    🔒 ENCRYPTED MESSAGE
         ↓
    🔓 Bob decrypts with his private key`
    },
    'symmetric-encryption': {
      title: 'Symmetric Encryption',
      explanation: 'Encryption method using the same key for both encryption and decryption, requiring secure key sharing.',
      examples: 'AES, DES, 3DES, ChaCha20, RC4 (deprecated), bulk data encryption',
      mnemonic: '🔄 SYMMETRIC = Same Your Method Made, Everyone Together Requires Identical Key\nThink of house key - same key locks and unlocks door.',
      visual: `
    🔑 SHARED SECRET KEY
    ├── 👤 Alice uses to encrypt
    └── 👤 Bob uses to decrypt
    
    📄 DATA → 🔒 ENCRYPTED → 📄 DATA
         ↑           ↓
    Same Key    Same Key`
    },
    'key-exchange': {
      title: 'Key Exchange',
      explanation: 'Process of securely sharing cryptographic keys between parties to establish secure communications.',
      examples: 'Diffie-Hellman, ECDH (Elliptic Curve), RSA key exchange, TLS handshake',
      mnemonic: '🤝 EXCHANGE = Every eXpert Changes Handshakes And Never Gets Exposed\nThink of secret handshake - share method without revealing secret.',
      visual: `
    👤 ALICE                👤 BOB
    🔢 Private: a          🔢 Private: b
    📢 Public: A           📢 Public: B
         ↓ ↔ ↓
    🔑 SHARED SECRET = f(a,B) = f(b,A)
         ↓
    🔒 SECURE COMMUNICATION`
    },
    'algorithms': {
      title: 'Cryptographic Algorithms',
      explanation: 'Mathematical procedures and rules used for encrypting, decrypting, and securing data.',
      examples: 'AES, RSA, SHA-256, ECDSA, ChaCha20, HMAC, Argon2',
      mnemonic: '🧮 ALGORITHM = Always Learn Great Operations, Reliable Intelligence Through Heavy Math\nThink of recipe - specific steps to create secure result.',
      visual: `
    📄 PLAINTEXT
         ↓
    🧮 ALGORITHM + 🔑 KEY
         ↓
    🔒 CIPHERTEXT
         ↓
    🧮 ALGORITHM + 🔑 KEY
         ↓
    📄 PLAINTEXT`
    },
    'key-length': {
      title: 'Cryptographic Key Length',
      explanation: 'Size of cryptographic keys measured in bits, directly affecting the security strength and computational requirements.',
      examples: 'AES-128/192/256, RSA-2048/3072/4096, ECC-256/384/521',
      mnemonic: '📏 LENGTH = Longer Ensures Not Getting Through Hackers\nThink of password length - longer = harder to guess.',
      visual: `
    🔑 KEY LENGTH COMPARISON:
    ├── 🔒 128-bit: Good security
    ├── 🔒🔒 256-bit: Strong security  
    ├── 🔒🔒🔒 2048-bit: Very strong
    └── 🔒🔒🔒🔒 4096-bit: Maximum strength
    
    ⏱️ More bits = More security + More time`
    },
    'tpm': {
      title: 'Trusted Platform Module (TPM)',
      explanation: 'Hardware security chip providing secure storage for cryptographic keys, passwords, and certificates.',
      examples: 'BitLocker key storage, secure boot verification, hardware-based attestation, device encryption',
      mnemonic: '💎 TPM = Trusted Processor Module\nThink of hardware safe built into computer - tamper-resistant key storage.',
      visual: `
    💻 COMPUTER MOTHERBOARD
         ↓
    💎 TPM CHIP (Hardware)
    ├── 🔐 Secure key storage
    ├── 🛡️ Tamper resistance
    ├── 🔍 Attestation
    └── 🚫 Hardware-based security`
    },
    'hsm': {
      title: 'Hardware Security Module (HSM)',
      explanation: 'Dedicated, tamper-resistant hardware device designed to securely store and manage cryptographic keys.',
      examples: 'Network-attached HSMs, PCIe card HSMs, USB token HSMs, cloud HSM services',
      mnemonic: '🏰 HSM = Hardware Security Mansion\nThink of bank vault - specialized, hardened, physical protection for most valuable assets.',
      visual: `
    🏰 HSM DEVICE
    ├── 🔐 Key generation
    ├── 🔑 Key storage  
    ├── 🧮 Crypto operations
    ├── 🛡️ Tamper detection
    └── 🚨 Self-destruct on breach`
    },
    'key-management-system': {
      title: 'Key Management System (KMS)',
      explanation: 'Centralized system for creating, distributing, storing, and managing cryptographic keys throughout their lifecycle.',
      examples: 'AWS KMS, Azure Key Vault, HashiCorp Vault, enterprise key management appliances',
      mnemonic: '🗝️ KMS = Keys Managed Systematically\nThink of hotel key management - central system tracks all room keys.',
      visual: `
    🏢 KEY MANAGEMENT SYSTEM
    ├── 🎲 Key Generation
    ├── 📤 Key Distribution
    ├── 🗄️ Key Storage
    ├── 🔄 Key Rotation
    ├── 📊 Key Auditing
    └── 🗑️ Key Destruction`
    },
    'secure-enclave': {
      title: 'Secure Enclave',
      explanation: 'Isolated, secure area within a processor that provides hardware-level protection for sensitive operations and data.',
      examples: 'Apple Secure Enclave, Intel SGX, ARM TrustZone, confidential computing',
      mnemonic: '🏛️ ENCLAVE = Encrypted Never Seen, Controls Locked And Very Encrypted\nThink of embassy - secure territory within foreign land.',
      visual: `
    💻 MAIN PROCESSOR
    ┌─────────────────┐
    │  Regular Apps   │
    │ ┌─────────────┐ │
    │ │🏛️ SECURE    │ │
    │ │  ENCLAVE    │ │
    │ │ 🔐 Protected│ │
    │ └─────────────┘ │
    └─────────────────┘`
    },
    // ADDITIONAL CRYPTOGRAPHIC CONCEPTS
    'steganography': {
      title: 'Steganography',
      explanation: 'Art of hiding information within other non-secret data or physical objects, concealing the existence of the message.',
      examples: 'Hidden messages in images, audio files, videos, or documents; watermarking',
      mnemonic: '🖼️ STEGO = Secret Text Embedded, Generally Obviously hidden\nThink of invisible ink - message hidden in plain sight.',
      visual: `
    🖼️ INNOCENT IMAGE
    ├── 👁️ Visible: Sunset photo
    ├── 🔍 Hidden: Secret message
    └── 📱 Viewer sees: Just a photo
    
    🔍 SPECIAL TOOL REVEALS:
    "Meeting at midnight"`
    },
    'tokenization': {
      title: 'Data Tokenization',
      explanation: 'Process of replacing sensitive data with non-sensitive placeholder tokens that have no exploitable value.',
      examples: 'Credit card tokenization, PII tokenization, database field tokenization, payment processing',
      mnemonic: '🎫 TOKEN = Take Original, Keep Everything Nonsensitive\nThink of coat check - get ticket, not your actual coat.',
      visual: `
    💳 REAL CREDIT CARD
    "4532-1234-5678-9012"
         ↓
    🎫 TOKEN
    "TOK_789ABC123XYZ"
         ↓
    💾 STORED SAFELY (Tokenized)
    🔐 REAL DATA (Secure vault)`
    },
    'data-masking': {
      title: 'Data Masking',
      explanation: 'Process of obscuring specific data within a database to protect sensitive information while maintaining data usability.',
      examples: 'Partial masking (***-**-1234), random substitution, format-preserving encryption',
      mnemonic: '🎭 MASK = Make All Sensitive Knowledge hidden\nThink of masquerade ball - identity hidden but still functional.',
      visual: `
    👤 ORIGINAL DATA:
    Name: John Smith
    SSN: 123-45-6789
    Email: john@email.com
         ↓
    🎭 MASKED DATA:
    Name: J*** S****
    SSN: ***-**-6789
    Email: j***@email.com`
    },
    'hashing': {
      title: 'Cryptographic Hashing',
      explanation: 'One-way mathematical function that converts data into a fixed-size string of characters, used for integrity verification.',
      examples: 'SHA-256, SHA-3, MD5 (deprecated), password hashing, file integrity checks',
      mnemonic: '🔨 HASH = Hardened Algorithm Secures Hash\nThink of meat grinder - goes in whole, comes out unrecognizable but consistent.',
      visual: `
    📄 INPUT DATA
    "Hello World"
         ↓
    🔨 HASH FUNCTION (SHA-256)
         ↓
    🔤 HASH OUTPUT
    "a591a6d40bf420404..."
    
    ✅ Always same input = same hash
    ❌ Cannot reverse to get original`
    },
    'salting': {
      title: 'Cryptographic Salting',
      explanation: 'Adding random data to input before hashing to prevent rainbow table attacks and ensure unique hashes.',
      examples: 'Password salting, unique salts per user, bcrypt, scrypt, Argon2',
      mnemonic: '🧂 SALT = Secure Addition Limits Table attacks\nThink of cooking - salt makes every dish unique even with same recipe.',
      visual: `
    🔑 PASSWORD: "password123"
    +
    🧂 RANDOM SALT: "xy7#mK9$"
         ↓
    🔨 HASH FUNCTION
         ↓
    🔤 SALTED HASH
    "9f86d081884c7d659a..."
    
    🛡️ PREVENTS RAINBOW TABLES`
    },
    'key-stretching': {
      title: 'Key Stretching',
      explanation: 'Process of making cryptographic keys more secure by increasing the time required to test possible keys.',
      examples: 'PBKDF2, bcrypt, scrypt, Argon2, iterative hashing',
      mnemonic: '🏃‍♂️ STRETCH = Slow Time Required, Each Trial Consumes Hours\nThink of marathon - make attackers run long distance to crack.',
      visual: `
    🔑 WEAK KEY
    "password"
         ↓
    🔄 STRETCHING (1000s iterations)
    ├── Hash 1
    ├── Hash 2  
    ├── Hash 3
    ├── ... 
    └── Hash 10000
         ↓
    💪 STRONG KEY`
    },
    'blockchain': {
      title: 'Blockchain Technology',
      explanation: 'Distributed ledger technology using cryptographic hashes to create immutable, transparent record chains.',
      examples: 'Bitcoin, Ethereum, supply chain tracking, smart contracts, digital identity',
      mnemonic: '⛓️ BLOCKCHAIN = Blocks Link Obviously Creating Keeping Chained Hashes And Information Naturally\nThink of unbreakable chain - each link depends on previous.',
      visual: `
    📦 BLOCK 1        📦 BLOCK 2        📦 BLOCK 3
    ├── Data          ├── Data          ├── Data
    ├── Hash          ├── Hash          ├── Hash
    └── Prev: NULL    └── Prev: Hash1   └── Prev: Hash2
         ↓                 ↓                 ↓
    ⛓️ IMMUTABLE CHAIN`
    },
    'open-public-ledger': {
      title: 'Open Public Ledger',
      explanation: 'Publicly accessible distributed database where all transactions are transparently recorded and verifiable.',
      examples: 'Bitcoin blockchain, Ethereum network, public cryptocurrency transactions',
      mnemonic: '📖 LEDGER = Logical Entry Database, Generally Everyone Reads\nThink of town square bulletin board - everyone can read, verify.',
      visual: `
    🌐 PUBLIC LEDGER
    ├── 📝 Transaction 1 (Public)
    ├── 📝 Transaction 2 (Public)  
    ├── 📝 Transaction 3 (Public)
    └── 👁️ Anyone can verify
    
    ✅ Transparent
    ✅ Immutable
    ✅ Decentralized`
    },
    // DOMAIN 2: THREATS, VULNERABILITIES, AND MITIGATIONS
    // THREAT ACTORS
    'nation-state': {
      title: 'Nation-state Threat Actors',
      explanation: 'Government-sponsored cyber attackers with substantial resources, advanced capabilities, and strategic objectives.',
      examples: 'APT groups (Advanced Persistent Threats), foreign intelligence services, military cyber units, state-sponsored hackers',
      mnemonic: '🏛️ NATION = Never Attack Teams Individual Operations Neutrally\nThink of countries cyber army - well-funded, organized, strategic.',
      visual: `
    🏛️ GOVERNMENT BACKING
    ├── 💰 Unlimited resources
    ├── 🎯 Strategic objectives
    ├── ⏰ Long-term persistence  
    ├── 🛠️ Advanced tools/exploits
    └── 🕵️ Intelligence gathering`
    },
    'unskilled-attacker': {
      title: 'Unskilled Attacker (Script Kiddie)',
      explanation: 'Inexperienced attackers who use pre-made tools and scripts created by others, lacking technical expertise.',
      examples: 'Script kiddies, wannabe hackers using automated tools, social media attackers, basic DoS attacks',
      mnemonic: '👶 SCRIPT = Simple Copy-paste Really Inexperienced Perpetrator Takes\nThink of kid using parents tools without understanding how they work.',
      visual: `
    👶 UNSKILLED ATTACKER
    ├── 🔽 Downloads existing tools
    ├── 📋 Follows tutorials  
    ├── ❌ Limited understanding
    ├── 🎯 Opportunistic attacks
    └── 📢 Often noisy/detectable`
    },
    'hacktivist': {
      title: 'Hacktivist',
      explanation: 'Activists who use hacking techniques to promote political or social causes, seeking publicity for their message.',
      examples: 'Anonymous, defacing websites, DDoS against corporations, leaking sensitive information for causes',
      mnemonic: '✊ HACKTIVIST = Hackers Acting Collectively, Kinetic Technology In Vigilant Internet Social Transformation\nThink of digital protesters with computers instead of signs.',
      visual: `
    ✊ POLITICAL/SOCIAL CAUSE
         ↓
    💻 HACKING TECHNIQUES
    ├── Website defacement
    ├── Data leaks
    ├── DDoS attacks
         ↓
    📢 PUBLIC MESSAGE/AWARENESS`
    },
    'insider-threat': {
      title: 'Insider Threat',
      explanation: 'Security risk posed by people within the organization who have authorized access but may misuse it.',
      examples: 'Malicious employees, compromised accounts, negligent users, contractors with access',
      mnemonic: '🏠 INSIDER = Internal Network Stays Intimate, Damage Easily Reached\nThink of house keys - most dangerous when given to wrong person.',
      visual: `
    🏢 TRUSTED ORGANIZATION
         ↓
    🆔 AUTHORIZED ACCESS
         ↓
    😈 MALICIOUS INTENT
    or
    😴 NEGLIGENT BEHAVIOR
         ↓
    💥 INTERNAL DAMAGE`
    },
    'organized-crime': {
      title: 'Organized Crime',
      explanation: 'Criminal organizations that use cyber attacks for financial gain, operating sophisticated business-like structures.',
      examples: 'Ransomware gangs, credit card fraud rings, cryptocurrency theft, online banking fraud',
      mnemonic: '🏴‍☠️ CRIME = Cash Rules In Money Enterprises\nThink of digital mafia - organized, profit-driven, systematic.',
      visual: `
    🏴‍☠️ CRIMINAL ORGANIZATION
    ├── 💰 Profit motive
    ├── 🎯 Systematic operations
    ├── 🤝 Organized hierarchy
    ├── 🛠️ Professional tools
    └── 🌐 Global reach`
    },
    'shadow-it': {
      title: 'Shadow IT',
      explanation: 'Unauthorized IT systems, devices, software, applications, and services used without explicit IT department approval.',
      examples: 'Personal cloud storage, unauthorized applications, BYOD violations, unapproved software installations',
      mnemonic: '👻 SHADOW = Systems Hidden And Deployed Outside Without approval\nThink of secret parallel IT infrastructure - invisible to official oversight.',
      visual: `
    🏢 OFFICIAL IT INFRASTRUCTURE
         │
    👻 SHADOW IT (Hidden)
    ├── 📱 Personal devices
    ├── ☁️ Unauthorized cloud  
    ├── 📲 Unapproved apps
    └── ❌ No security oversight`
    },
    // THREAT ACTOR ATTRIBUTES
    'internal-external': {
      title: 'Internal/External Threat Classification',
      explanation: 'Categorization of threat actors based on their relationship to the target organization - inside or outside.',
      examples: 'Internal: employees, contractors, vendors; External: hackers, nation-states, competitors',
      mnemonic: '🔄 INTERNAL = Inside Network, Trusted Employment Rights, Network Access Levels\nThink of castle - threats from inside walls vs outside siege.',
      visual: `
    🏰 ORGANIZATION PERIMETER
    
    🔵 INTERNAL THREATS:
    ├── 👥 Employees
    ├── 🤝 Contractors  
    └── 🔑 Authorized access
    
    🔴 EXTERNAL THREATS:
    ├── 🏴‍☠️ Hackers
    ├── 🏛️ Nation-states
    └── 🚫 No authorized access`
    },
    'resources-funding': {
      title: 'Threat Actor Resources/Funding',
      explanation: 'Available financial resources, tools, and capabilities that determine the sophistication and persistence of attacks.',
      examples: 'Nation-state: unlimited; Organized crime: high; Hacktivists: moderate; Script kiddies: low',
      mnemonic: '💰 RESOURCES = Really Expensive Sophisticated Operations Usually Require Capital Effectively Spent\nThink of military budget - more money = better equipment.',
      visual: `
    💰 FUNDING LEVELS:
    
    🏛️ NATION-STATE: $$$$$
    ├── Custom exploits
    ├── Advanced persistent threats
    └── Long-term operations
    
    🏴‍☠️ ORGANIZED CRIME: $$$$
    ├── Professional tools
    ├── Ransomware-as-a-Service
    
    👶 SCRIPT KIDDIE: $
    └── Free/stolen tools`
    },
    'sophistication-capability': {
      title: 'Level of Sophistication/Capability',
      explanation: 'Technical skill level and advanced capabilities of threat actors, determining attack complexity and evasion techniques.',
      examples: 'High: APT, zero-days, custom malware; Medium: modified tools; Low: off-the-shelf exploits',
      mnemonic: '🎯 SOPHISTICATED = Smart Operations Perfectly Hide Intelligence, Stealth Technology, International Capabilities And Technical Expertise Displayed\nThink of chess grandmaster vs beginner - skill determines strategy complexity.',
      visual: `
    🎯 SOPHISTICATION LEVELS:
    
    🥇 ADVANCED (Nation-state)
    ├── Zero-day exploits
    ├── Custom malware
    ├── Advanced evasion
    
    🥈 INTERMEDIATE (Organized crime)  
    ├── Modified existing tools
    ├── Some evasion techniques
    
    🥉 BASIC (Script kiddie)
    └── Off-the-shelf tools`
    },
    // THREAT ACTOR MOTIVATIONS
    'data-exfiltration': {
      title: 'Data Exfiltration',
      explanation: 'Unauthorized extraction and removal of sensitive data from systems, often for espionage or financial gain.',
      examples: 'Stealing customer databases, intellectual property theft, medical records, financial data extraction',
      mnemonic: '📤 EXFILTRATION = Extract Files, Information Leaves Through Remote Access, Transfer Initiates Obvious Networks\nThink of smuggling - secretly moving valuable items out.',
      visual: `
    🏢 TARGET ORGANIZATION
    ├── 💳 Customer data
    ├── 🔬 Trade secrets  
    ├── 📊 Financial records
         ↓
    🕵️ ATTACKER INFILTRATES
         ↓
    📤 DATA EXTRACTED SECRETLY
         ↓
    💰 SOLD/USED MALICIOUSLY`
    },
    'espionage': {
      title: 'Cyber Espionage',
      explanation: 'Covert intelligence gathering through cyber means to obtain classified or confidential information.',
      examples: 'State-sponsored hacking, industrial espionage, political intelligence gathering, military secrets',
      mnemonic: '🕵️ ESPIONAGE = Everyone Spies, Purpose Is Obviously Network And Government Espionage\nThink of digital spy - gathering secrets for strategic advantage.',
      visual: `
    🎯 TARGET (Government/Corporation)
         ↓
    🕵️ COVERT INFILTRATION
    ├── 🔓 Access classified info
    ├── 👁️ Monitor communications
    ├── 📱 Steal technology
         ↓
    🏛️ FOREIGN INTELLIGENCE SERVICE`
    },
    'service-disruption': {
      title: 'Service Disruption',
      explanation: 'Attacks aimed at making services, systems, or networks unavailable to legitimate users.',
      examples: 'DDoS attacks, system takedowns, infrastructure attacks, website defacement',
      mnemonic: '🚫 DISRUPTION = Deny Internet Service, Ruin User Performance, Technology Infrastructure Obviously Nullified\nThink of blocking traffic - prevent normal operations.',
      visual: `
    🌐 NORMAL SERVICE
    ├── ✅ Users connected
    ├── ✅ Systems running
         ↓
    💥 DISRUPTION ATTACK
         ↓
    🚫 SERVICE UNAVAILABLE
    ├── ❌ Users blocked
    ├── ❌ Systems down`
    },
    'blackmail': {
      title: 'Blackmail/Extortion',
      explanation: 'Threatening to release sensitive information or cause harm unless payment or demands are met.',
      examples: 'Ransomware, threatening to leak data, extortion emails, sextortion scams',
      mnemonic: '📧 BLACKMAIL = Bad Letters Actually Compromise, Keep Money And Information Locked\nThink of ransom note - pay or consequences.',
      visual: `
    🔒 ENCRYPTED/STOLEN DATA
         ↓
    📧 EXTORTION MESSAGE:
    "Pay $50,000 Bitcoin or 
     we release your data"
         ↓
    💰 VICTIM PAYS
    or
    📰 DATA PUBLISHED`
    },
    'financial-gain': {
      title: 'Financial Gain',
      explanation: 'Cyber attacks motivated by direct monetary profit through theft, fraud, or monetizing stolen data.',
      examples: 'Credit card fraud, banking theft, cryptocurrency mining, selling stolen data',
      mnemonic: '💰 FINANCIAL = Fraudsters In Network Always Need Cash, Income And Lucrative\nThink of digital bank robbery - stealing money online.',
      visual: `
    🎯 FINANCIAL TARGETS
    ├── 💳 Credit cards
    ├── 🏦 Bank accounts
    ├── ₿ Cryptocurrency
         ↓
    🏴‍☠️ CYBER THEFT
         ↓
    💰 DIRECT PROFIT`
    },
    'philosophical-political-beliefs': {
      title: 'Philosophical/Political Beliefs',
      explanation: 'Cyber attacks driven by ideological motivations to promote or oppose political/social causes.',
      examples: 'Hacktivism, politically motivated defacements, attacks on opposing governments/organizations',
      mnemonic: '✊ POLITICAL = People Obviously Love Ideas, Technology Implements Change And Liberation\nThink of digital protest - using hacking to make political statements.',
      visual: `
    ✊ STRONG BELIEFS
    ├── 🏛️ Against government
    ├── 🏢 Against corporations
    ├── 🌍 Environmental causes
         ↓
    💻 CYBER ACTIVISM
         ↓
    📢 PUBLIC MESSAGE`
    },
    'ethical': {
      title: 'Ethical Motivations',
      explanation: 'Hacking activities driven by moral principles to expose wrongdoing or improve security.',
      examples: 'White hat hacking, responsible disclosure, whistleblowing, security research',
      mnemonic: '⚖️ ETHICAL = Everyone Thinks Helping Is Correct And Legitimate\nThink of digital good samaritan - helping make things more secure.',
      visual: `
    🔍 SECURITY RESEARCHER
         ↓
    🔓 FINDS VULNERABILITY
         ↓
    📧 RESPONSIBLE DISCLOSURE
         ↓
    🛡️ SECURITY IMPROVED`
    },
    'revenge': {
      title: 'Revenge Motivation',
      explanation: 'Cyber attacks motivated by personal vendettas, desire for retaliation against perceived wrongs.',
      examples: 'Disgruntled employees, ex-partners, former business associates, personal disputes',
      mnemonic: '😡 REVENGE = Really Enraged, Vindictive Enemy Never Gets Even\nThink of digital vendetta - settling scores online.',
      visual: `
    💼 WORKPLACE CONFLICT
    or
    💔 PERSONAL DISPUTE
         ↓
    😡 DESIRE FOR REVENGE
         ↓
    💻 CYBER RETALIATION
         ↓
    💥 TARGETED DAMAGE`
    },
    'disruption-chaos': {
      title: 'Disruption/Chaos',
      explanation: 'Attacks motivated by desire to cause maximum disorder, confusion, and instability.',
      examples: 'Random attacks, trolling, chaos-seeking hackers, destructive malware',
      mnemonic: '🌪️ CHAOS = Causing Havoc And Obviously Spreading\nThink of digital vandalism - destroying for the sake of destruction.',
      visual: `
    😈 CHAOS MOTIVATION
         ↓
    🎯 RANDOM TARGETS
         ↓
    💥 MAXIMUM DISRUPTION
    ├── 🔥 System destruction
    ├── 📊 Data corruption
    └── 🌪️ General mayhem`
    },
    'war': {
      title: 'Cyber Warfare',
      explanation: 'State-sponsored cyber attacks as part of military operations or international conflicts.',
      examples: 'Infrastructure attacks, military system targeting, election interference, critical service disruption',
      mnemonic: '⚔️ WAR = Weaponized Attacks, Really destructive\nThink of digital battlefield - nations fighting with code instead of bombs.',
      visual: `
    🏛️ NATION A vs NATION B
         ↓
    ⚔️ CYBER WARFARE
    ├── ⚡ Power grid attacks
    ├── 💧 Water system targeting  
    ├── 🏥 Hospital disruption
    ├── 🗳️ Election interference
         ↓
    🌍 GEOPOLITICAL IMPACT`
    },
    // DOMAIN 2.2: THREAT VECTORS AND ATTACK SURFACES
    // MESSAGE-BASED THREATS
    'email': {
      title: 'Email-based Attacks',
      explanation: 'Malicious activities delivered through email systems, exploiting trust and communication channels.',
      examples: 'Phishing emails, malware attachments, business email compromise, spam, email spoofing',
      mnemonic: '📧 EMAIL = Everyone Mails And Immediately Loses security\nThink of postal mail - can contain anything, good or bad.',
      visual: `
    📧 MALICIOUS EMAIL
    ├── 👤 Spoofed sender
    ├── 🎣 Phishing content
    ├── 📎 Malware attachment
    ├── 🔗 Malicious links
         ↓
    😵 VICTIM INTERACTION
         ↓
    💥 COMPROMISE`
    },
    'sms': {
      title: 'SMS-based Attacks (Smishing)',
      explanation: 'Text message-based attacks that trick users into clicking malicious links or revealing information.',
      examples: 'Phishing text messages, fake delivery notifications, prize scams, banking alerts',
      mnemonic: '📱 SMS = Scam Messages Sent\nThink of spam texts trying to trick you.',
      visual: `
    📱 TEXT MESSAGE:
    "Your package delivery failed.
     Click here to reschedule:
     suspicious-link.com"
         ↓
    👤 USER CLICKS
         ↓
    🎣 CREDENTIALS STOLEN`
    },
    'instant-messaging': {
      title: 'Instant Messaging Attacks',
      explanation: 'Attacks delivered through chat platforms and instant messaging applications.',
      examples: 'Malicious links in chat, file transfers with malware, social engineering in DMs',
      mnemonic: '💬 INSTANT = Instead of Normal Security, Trust Allows Network Threats\nThink of chat messages - informal, less scrutinized.',
      visual: `
    💬 CHAT MESSAGE:
    "Hey, check out this cool
     photo I found: malware.exe"
         ↓
    👤 TRUSTED CONTACT SENDS
         ↓
    🔽 USER DOWNLOADS
         ↓
    💀 MALWARE INFECTION`
    },
    // IMAGE-BASED THREATS  
    'embedded-malware': {
      title: 'Embedded Malware in Images',
      explanation: 'Malicious code hidden within image files that executes when the image is processed or viewed.',
      examples: 'Steganography, malicious EXIF data, image exploits, infected media files',
      mnemonic: '🖼️ EMBEDDED = Every Media Basically Enables Data, Delivers Evil Deliberately\nThink of trojan horse - looks innocent, contains danger.',
      visual: `
    🖼️ INNOCENT IMAGE
    "vacation_photo.jpg"
    ├── 👁️ Visible: Beach scene
    ├── 💀 Hidden: Malware code
         ↓
    📱 USER OPENS IMAGE
         ↓
    💥 MALWARE EXECUTES`
    },
    'steganography-attacks': {
      title: 'Steganographic Attacks',
      explanation: 'Using steganography to hide malicious payloads or communications within innocent-looking files.',
      examples: 'Hidden messages in images, covert channels, data exfiltration, command and control',
      mnemonic: '🔍 STEGANOGRAPHY = Secret Text Embedded, Generally Always Not Obviously Graphical, Really Applicable Payload Hides You\nThink of invisible ink - message hidden in plain sight.',
      visual: `
    📸 NORMAL PHOTO
         ↓
    🔍 STEGANOGRAPHY TOOL
         ↓
    💀 HIDDEN MALICIOUS CODE
         ↓
    📤 SENT UNDETECTED`
    },
    // FILE-BASED THREATS
    'portable-executable': {
      title: 'Portable Executable Attacks',
      explanation: 'Malicious executable files designed to run on target systems and compromise them.',
      examples: 'Malware .exe files, trojans, virus-infected executables, backdoor programs',
      mnemonic: '💾 EXECUTABLE = Every eXecutable Eventually Causes Unwanted Tasks And Brings Large Exploits\nThink of running unknown programs - risky execution.',
      visual: `
    💾 MALICIOUS .EXE
    "game_installer.exe"
         ↓
    👤 USER EXECUTES
         ↓
    💻 SYSTEM COMPROMISE
    ├── 🔒 Backdoor installed
    ├── 📤 Data stolen
    └── 💀 Malware deployed`
    },
    'office-documents': {
      title: 'Office Document Attacks',
      explanation: 'Malicious code embedded in office documents that exploits document processing vulnerabilities.',
      examples: 'Macro viruses, malicious Word docs, infected Excel spreadsheets, PowerPoint malware',
      mnemonic: '📄 OFFICE = Obviously Files From Internet Can Endanger\nThink of documents with hidden dangers - macros, exploits.',
      visual: `
    📄 OFFICE DOCUMENT
    "invoice.docx"
    ├── 📝 Normal content
    ├── 💀 Hidden macro
         ↓
    👤 USER OPENS DOCUMENT
         ↓
    ⚠️ "Enable macros?"
         ↓
    💥 MALWARE EXECUTES`
    },
    'pdf-attacks': {
      title: 'PDF-based Attacks',
      explanation: 'Malicious portable document format files that exploit PDF reader vulnerabilities.',
      examples: 'Malicious JavaScript in PDFs, embedded executables, PDF exploits, form-based attacks',
      mnemonic: '📑 PDF = Portable Document Format attacks\nThink of documents that look safe but contain hidden scripts.',
      visual: `
    📑 MALICIOUS PDF
    "report.pdf"
    ├── 📄 Normal content
    ├── ⚙️ Embedded JavaScript
         ↓
    👤 USER OPENS PDF
         ↓
    💀 EXPLOIT ACTIVATES
         ↓
    💻 SYSTEM COMPROMISED`
    },
    'voice-call-threats': {
      title: 'Voice Call Threats',
      explanation: 'Phone-based attacks using voice communication to manipulate victims or exploit telecommunications.',
      examples: 'Vishing (voice phishing), phone impersonation, caller ID spoofing, SIM swapping attacks',
      mnemonic: '📞 VOICE = Verbal Operations to Infiltrate, Control, Extract\nThink of scammers pretending to be your bank.',
      visual: `
    📞 MALICIOUS CALL
    "This is your bank..."
    ├── 🎭 IMPERSONATION
    ├── 🔊 URGENT TONE
    └── 📋 REQUEST INFO
         ↓
    👤 VICTIM SHARES DATA
         ↓
    💰 IDENTITY THEFT`
    },
    'removable-device-attacks': {
      title: 'Removable Device Attacks',
      explanation: 'Malicious attacks delivered through USB drives, CDs, or other portable storage media.',
      examples: 'USB drops, infected flash drives, malicious CDs/DVDs, rubber ducky attacks, BadUSB',
      mnemonic: '🔌 USB = Untrusted Storage Brings malware\nThink of Trojan horse but smaller and pluggable.',
      visual: `
    🔌 INFECTED USB DRIVE
    "Found in parking lot"
    ├── 💾 AUTORUN.EXE
    ├── 🦠 MALWARE PAYLOAD
    └── 📂 FAKE DOCUMENTS
         ↓
    💻 PLUGGED INTO PC
         ↓
    🚨 SYSTEM INFECTED`
    },
    'vulnerable-software': {
      title: 'Vulnerable Software Components',
      explanation: 'Software with security flaws, outdated versions, or unpatched vulnerabilities that can be exploited.',
      examples: 'Unpatched operating systems, outdated web browsers, vulnerable third-party libraries, legacy applications',
      mnemonic: '🕳️ HOLE = How Old, Lacks Everything (patches)\nThink of Swiss cheese software.',
      visual: `
    📦 VULNERABLE SOFTWARE
    ├── 🕳️ KNOWN CVE
    ├── ⏰ OUTDATED VERSION
    └── 🚫 NO PATCHES
         ↓
    🎯 ATTACKER TARGETS
         ↓
    💥 EXPLOITATION`
    },
    'unsupported-systems': {
      title: 'Unsupported Systems and Applications',
      explanation: 'End-of-life systems that no longer receive security updates, creating permanent security risks.',
      examples: 'Windows XP/7, Internet Explorer, legacy databases, discontinued security products',
      mnemonic: '⚰️ EOL = End Of Life, security holes\nThink of zombie systems - dead but still moving.',
      visual: `
    ⚰️ END-OF-LIFE SYSTEM
    ├── 🚫 NO MORE UPDATES
    ├── 🕳️ KNOWN VULNS
    └── 💀 PERMANENT RISK
         ↓
    🎯 EASY TARGET
         ↓
    🔓 COMPROMISE`
    },
    'unsecure-networks': {
      title: 'Unsecure Networks',
      explanation: 'Network connections without proper encryption or authentication that expose data to interception.',
      examples: 'Open WiFi, unencrypted HTTP, weak WEP encryption, rogue access points',
      mnemonic: '📡 OPEN = Obviously Provides Everyone Network access\nThink of leaving your door wide open.',
      visual: `
    📡 OPEN WIFI NETWORK
    "Free_WiFi"
    ├── 🚫 NO ENCRYPTION
    ├── 👥 PUBLIC ACCESS
    └── 👂 EASY SNIFFING
         ↓
    🕵️ ATTACKER LISTENS
         ↓
    🔓 DATA CAPTURED`
    },
    'open-service-ports': {
      title: 'Open Service Ports',
      explanation: 'Network ports left open unnecessarily, providing potential entry points for attackers.',
      examples: 'Unused FTP ports (21), Telnet (23), unnecessary web services (80/443), database ports (3306/1433)',
      mnemonic: '🚪 PORT = Potential Open Route for Threats\nThink of leaving all house doors unlocked.',
      visual: `
    🖥️ SERVER
    ├── 🚪 Port 21 (FTP) OPEN
    ├── 🚪 Port 23 (Telnet) OPEN
    ├── 🚪 Port 80 (HTTP) OPEN
    └── 🚪 Port 3306 (MySQL) OPEN
         ↓
    🔍 PORT SCAN REVEALS ALL
         ↓
    🎯 ATTACK VECTORS`
    },
    'default-credentials': {
      title: 'Default Credentials',
      explanation: 'Factory-set usernames and passwords that are never changed, providing easy access to systems.',
      examples: 'admin/admin, admin/password, root/toor, default router passwords (admin/1234)',
      mnemonic: '🔑 DEFAULT = Device Easily Found, Always Uses Lazy Tactics\nThink of keys under the doormat.',
      visual: `
    📱 DEVICE SETUP
    Username: admin
    Password: admin
    ├── 📋 NEVER CHANGED
    ├── 🌐 PUBLICLY KNOWN
    └── 📖 IN MANUAL
         ↓
    🕵️ ATTACKER GOOGLES
         ↓
    🔓 INSTANT ACCESS`
    },
    'supply-chain-attacks': {
      title: 'Supply Chain Attacks',
      explanation: 'Attacks targeting the software or hardware supply chain to compromise end users through trusted sources.',
      examples: 'SolarWinds hack, malicious npm packages, hardware tampering, compromised software updates',
      mnemonic: '🏭 CHAIN = Compromised Hardware/Applications Infiltrate Networks\nThink of poisoning the factory.',
      visual: `
    🏭 SOFTWARE VENDOR
    ├── 💻 DEVELOPMENT
    ├── 🦠 MALWARE INSERTED
    └── 📦 PACKAGE RELEASED
         ↓
    🏢 CUSTOMERS INSTALL
         ↓
    🌐 WIDESPREAD COMPROMISE`
    },
    'botnet': {
      title: 'Botnet',
      explanation: 'Network of compromised computers controlled remotely to perform coordinated malicious activities.',
      examples: 'Zeus botnet, Mirai IoT botnet, distributed denial of service attacks, cryptocurrency mining',
      mnemonic: '🤖 BOT = Bunch Of Things controlled remotely\nThink of robot army following commands.',
      visual: `
    🤖 BOTNET
    Command & Control
         ↓
    🌐 INFECTED MACHINES
    ├── 💻 Bot 1
    ├── 💻 Bot 2
    ├── 💻 Bot 3
    └── 📱 Bot n
         ↓
    ⚡ COORDINATED ATTACKS`
    },
    'adware': {
      title: 'Adware',
      explanation: 'Software that displays unwanted advertisements and may track user behavior for targeted ads.',
      examples: 'Browser toolbar ads, pop-up generators, tracking cookies, bundled advertising software',
      mnemonic: '📢 AD = Annoying Display of advertisements\nThink of billboard spam on your screen.',
      visual: `
    📢 ADWARE
    ├── 🎯 POP-UP ADS
    ├── 🍪 TRACKING COOKIES
    ├── 📊 BEHAVIOR ANALYSIS
    └── 💰 AD REVENUE
         ↓
    😤 USER FRUSTRATION
         ↓
    🛒 UNWANTED PURCHASES`
    },
    'brute-force-physical': {
      title: 'Physical Brute Force Attacks',
      explanation: 'Physical attacks using repetitive force or attempts to bypass security measures.',
      examples: 'Lock picking, breaking down doors, tailgating attempts, badge cloning trials',
      mnemonic: '💪 BRUTE = Breaking Repeatedly Until Target Entry\nThink of caveman smashing rocks.',
      visual: `
    💪 PHYSICAL BRUTE FORCE
    ├── 🔐 LOCK PICKING
    ├── 🚪 DOOR RAMMING
    ├── 🏃 TAILGATING
    └── 🔄 REPEATED ATTEMPTS
         ↓
    🚨 SECURITY BREACH
         ↓
    🏢 FACILITY ACCESS`
    },
    'rfid-cloning': {
      title: 'RFID Cloning',
      explanation: 'Copying radio frequency identification tags to gain unauthorized access or impersonate legitimate users.',
      examples: 'Badge cloning, key card duplication, access card skimming, proximity card copying',
      mnemonic: '📻 RFID = Radio Frequency Identity Duplication\nThink of copying house keys.',
      visual: `
    📻 RFID CLONING
    Original card 💳
         ↓
    📡 RFID READER/WRITER
    ├── 📊 EXTRACTS DATA
    ├── 📝 COPIES TO BLANK
    └── 🎭 CREATES DUPLICATE
         ↓
    💳 CLONED CARD
         ↓
    🔓 UNAUTHORIZED ACCESS`
    },
    'environmental-attacks': {
      title: 'Environmental Attacks',
      explanation: 'Physical attacks targeting environmental systems to disrupt operations or gain access.',
      examples: 'HVAC manipulation, power disruption, fire suppression tampering, water damage',
      mnemonic: '🌡️ ENV = Everyone Needs Ventilation (to attack)\nThink of controlling building atmosphere.',
      visual: `
    🌡️ ENVIRONMENTAL ATTACK
    ├── ❄️ HVAC MANIPULATION
    ├── ⚡ POWER DISRUPTION
    ├── 🔥 FIRE SYSTEMS
    └── 💧 WATER DAMAGE
         ↓
    🏢 FACILITY DISRUPTION
         ↓
    🚨 OPERATIONS DOWN`
    },
    'ddos-attacks': {
      title: 'Distributed Denial of Service (DDoS)',
      explanation: 'Coordinated attacks from multiple sources overwhelming a target system to deny legitimate access.',
      examples: 'Botnet attacks, volumetric attacks, application layer attacks, protocol attacks',
      mnemonic: '🌊 DDOS = Distributed Denial Of Service\nThink of traffic jam blocking highway.',
      visual: `
    🌊 DDOS ATTACK
    🤖 Botnet 1 ─┐
    🤖 Botnet 2 ─┤
    🤖 Botnet 3 ─┼─→ 🎯 TARGET SERVER
    🤖 Botnet 4 ─┤      ↓
    🤖 Botnet n ─┘   💀 OVERWHELMED
                       ↓
                  🚫 SERVICE DOWN`
    },
    'amplified-ddos': {
      title: 'Amplified DDoS Attacks',
      explanation: 'DDoS attacks using amplification servers to multiply attack traffic volume significantly.',
      examples: 'DNS amplification, NTP amplification, SNMP amplification, Memcached amplification',
      mnemonic: '📢 AMP = Attack Multiplied Powerfully\nThink of megaphone making voice louder.',
      visual: `
    📢 AMPLIFIED DDOS
    🤖 Small request (64 bytes)
         ↓
    🔊 DNS/NTP SERVER
    ├── 📊 PROCESSES REQUEST
    └── 📤 LARGE RESPONSE (4KB)
         ↓
    🎯 TARGET × 60 AMPLIFICATION
         ↓
    💀 MASSIVE OVERLOAD`
    },
    'reflected-ddos': {
      title: 'Reflected DDoS Attacks',
      explanation: 'DDoS attacks where attackers spoof victim IP addresses to redirect response traffic to the target.',
      examples: 'DNS reflection, NTP reflection, SSDP reflection, UDP reflection attacks',
      mnemonic: '🪞 REFLECT = Really Effective, Forwards Large Excessive, Concentrated Traffic\nThink of mirror bouncing light.',
      visual: `
    🪞 REFLECTED DDOS
    🤖 ATTACKER
    (spoofs victim IP)
         ↓
    💻 REFLECTOR SERVER
    ├── 📨 SEES VICTIM IP
    └── 📤 SENDS TO "VICTIM"
         ↓
    🎯 VICTIM FLOODED
         ↓
    🚫 SERVICE DENIED`
    },
    'injection-attacks': {
      title: 'Injection Attacks',
      explanation: 'Code injection attacks where malicious input is inserted into application queries or commands.',
      examples: 'SQL injection, command injection, LDAP injection, NoSQL injection, XML injection',
      mnemonic: '💉 INJECT = Insert Nasty JavaScript/Executable Code Today\nThink of giving system a poison shot.',
      visual: `
    💉 INJECTION ATTACK
    User input: "'; DROP TABLE users;--"
    ├── 📝 MALICIOUS PAYLOAD
    ├── 🗃️ DATABASE QUERY
    └── 💀 COMMAND EXECUTED
         ↓
    🗑️ DATA DESTROYED
         ↓
    🚨 SYSTEM COMPROMISED`
    },
    'buffer-overflow': {
      title: 'Buffer Overflow',
      explanation: 'Attack that overwrites memory buffers to execute malicious code or crash systems.',
      examples: 'Stack overflow, heap overflow, integer overflow, format string attacks',
      mnemonic: '🪣 OVERFLOW = Overwriting Verified Established Runtime, Floods Legitimate Operating Workspace\nThink of water bucket spilling over.',
      visual: `
    🪣 BUFFER OVERFLOW
    Buffer: [A][A][A][A][A]
    Input:  AAAAAAAAAAAAAAAA💀
    ├── 📊 BUFFER EXCEEDED
    ├── 💾 MEMORY OVERWRITTEN
    └── 🎯 EXECUTION HIJACKED
         ↓
    💀 MALICIOUS CODE RUNS
         ↓
    🔓 SYSTEM COMPROMISE`
    },
    'replay-attacks': {
      title: 'Replay Attacks',
      explanation: 'Attacks that intercept and retransmit valid data transmissions to gain unauthorized access.',
      examples: 'Session replay, authentication replay, transaction replay, wireless signal replay',
      mnemonic: '🔄 REPLAY = Record, Examine, Play, Listen, And Yield access\nThink of recording movie and playing it back.',
      visual: `
    🔄 REPLAY ATTACK
    👤 USER → 🔐 AUTH TOKEN → 🏦 SERVER
              ↓
         📹 ATTACKER CAPTURES
              ↓
    🎭 ATTACKER → 🔐 SAME TOKEN → 🏦 SERVER
              ↓
         ✅ AUTHENTICATION SUCCESS
              ↓
    🔓 UNAUTHORIZED ACCESS`
    },
    'privilege-escalation': {
      title: 'Privilege Escalation',
      explanation: 'Gaining higher-level permissions than originally granted, often exploiting system vulnerabilities.',
      examples: 'Vertical escalation (user to admin), horizontal escalation (user to user), kernel exploits',
      mnemonic: '⬆️ ESCALATE = Everyone Should Control All Limited Access Through Exploitation\nThink of climbing corporate ladder illegally.',
      visual: `
    ⬆️ PRIVILEGE ESCALATION
    👤 LIMITED USER
         ↓
    🕳️ EXPLOIT VULNERABILITY
    ├── 🐛 SYSTEM BUG
    ├── 🔧 MISCONFIG
    └── 📂 WEAK PERMISSIONS
         ↓
    👑 ADMIN PRIVILEGES
         ↓
    🔓 FULL SYSTEM CONTROL`
    },
    'forgery-attacks': {
      title: 'Forgery Attacks',
      explanation: 'Attacks that create fake requests or credentials to impersonate legitimate users or systems.',
      examples: 'Cross-site request forgery (CSRF), request forgery, credential forgery, token forgery',
      mnemonic: '🎭 FORGE = Fake Operations Really Generate Excessive damage\nThink of counterfeiting money.',
      visual: `
    🎭 FORGERY ATTACK
    🌐 MALICIOUS WEBSITE
    ├── 📝 CRAFTS FAKE REQUEST
    ├── 👤 USER VISITS SITE
    └── 📤 SENDS TO BANK
         ↓
    🏦 BANK SEES "USER" REQUEST
         ↓
    💰 TRANSFERS MONEY`
    },
    'directory-traversal': {
      title: 'Directory Traversal',
      explanation: 'Attack that accesses files and directories outside the web application root directory.',
      examples: 'Path traversal, dot-dot-slash attacks, file inclusion vulnerabilities, directory climbing',
      mnemonic: '📁 TRAVERSE = Travel Across Restricted Areas, Violate Established Resource System Entirely\nThink of climbing over fences to access forbidden areas.',
      visual: `
    📁 DIRECTORY TRAVERSAL
    Normal: /app/files/document.pdf
    Attack: /app/../../../etc/passwd
    ├── 📂 ESCAPES WEB ROOT
    ├── 🔍 ACCESSES SYSTEM FILES
    └── 👁️ READS SENSITIVE DATA
         ↓
    🔓 PASSWORD FILE EXPOSED
         ↓
    💀 SYSTEM COMPROMISED`
    },
    'account-lockout': {
      title: 'Account Lockout Indicators',
      explanation: 'Security indicators when user accounts are locked due to suspicious activity or policy violations.',
      examples: 'Multiple failed login attempts, brute force detection, concurrent login attempts, policy violations',
      mnemonic: '🔒 LOCKOUT = Login Obviously Compromised, Keep Out Users Temporarily\nThink of bouncer blocking suspicious person.',
      visual: `
    🔒 ACCOUNT LOCKOUT
    👤 USER LOGIN ATTEMPTS
    ❌ Wrong password (1)
    ❌ Wrong password (2)
    ❌ Wrong password (3)
         ↓
    🚨 THRESHOLD EXCEEDED
         ↓
    🔐 ACCOUNT LOCKED`
    },
    'concurrent-sessions': {
      title: 'Concurrent Session Usage',
      explanation: 'Detecting when user accounts are being accessed simultaneously from multiple locations or devices.',
      examples: 'Same user logged in from different countries, multiple device logins, impossible travel patterns',
      mnemonic: '👥 CONCURRENT = Can Obviously Not Currently Use Respective, Remote Environments Normally Together\nThink of being in two places at once.',
      visual: `
    👥 CONCURRENT SESSIONS
    User: john.doe
    ├── 📍 Login: New York (2:30 PM)
    └── 📍 Login: Tokyo (3:30 AM)
         ↓
    🚨 IMPOSSIBLE TRAVEL
         ↓
    🔔 SECURITY ALERT`
    },
    'blocked-content': {
      title: 'Blocked Content Indicators',
      explanation: 'Security events indicating attempts to access malicious or restricted content.',
      examples: 'Malware downloads blocked, phishing sites blocked, restricted website access, policy violations',
      mnemonic: '🚫 BLOCKED = Be Logical, Obviously Compromised, Keep Everyone Defensively\nThink of firewall stopping bad traffic.',
      visual: `
    🚫 BLOCKED CONTENT
    👤 USER REQUEST
    "malicious-site.com"
         ↓
    🛡️ SECURITY FILTER
    ├── 🔍 CHECKS REPUTATION
    ├── ⚠️ MALICIOUS DETECTED
    └── 🚫 ACCESS DENIED
         ↓
    📝 SECURITY LOG ENTRY`
    },
    'impossible-travel': {
      title: 'Impossible Travel',
      explanation: 'Detection of user login patterns that are physically impossible based on geographic locations and time.',
      examples: 'Logins from different continents within hours, rapid geographic movement, simultaneous distant logins',
      mnemonic: '✈️ TRAVEL = Time Reveals All, Verify Every Location\nThink of teleportation being impossible.',
      visual: `
    ✈️ IMPOSSIBLE TRAVEL
    User: alice@company.com
    📍 9:00 AM: London, UK
    📍 9:30 AM: Sydney, Australia
         ↓
    🧮 PHYSICS CHECK
    Distance: 17,000 km in 30 min
         ↓
    🚨 IMPOSSIBLE = COMPROMISE`
    },
    'resource-consumption': {
      title: 'Resource Consumption Anomalies',
      explanation: 'Unusual patterns of system resource usage that may indicate malicious activity or compromise.',
      examples: 'CPU spikes, memory exhaustion, network bandwidth abuse, disk space consumption, crypto mining',
      mnemonic: '📊 RESOURCE = Really Excessive System Operations Under Rogue Control Execution\nThink of computer working overtime.',
      visual: `
    📊 RESOURCE CONSUMPTION
    Normal: CPU 10% | Memory 40%
    Alert:  CPU 95% | Memory 90%
    ├── 🔥 CRYPTO MINING
    ├── 🦠 MALWARE ACTIVITY
    └── 🌊 DDOS TRAFFIC
         ↓
    🚨 PERFORMANCE IMPACT`
    },
    'cloud-architecture': {
      title: 'Cloud Architecture',
      explanation: 'Computing model where services and resources are delivered over the internet through third-party providers.',
      examples: 'AWS, Azure, Google Cloud, IaaS, PaaS, SaaS, public/private/hybrid clouds',
      mnemonic: '☁️ CLOUD = Computing Lives On Unlimited Distributed infrastructure\nThink of data floating in digital sky.',
      visual: `
    ☁️ CLOUD ARCHITECTURE
    🏢 ON-PREMISES → ☁️ CLOUD PROVIDER
    ├── 💾 IaaS (Infrastructure)
    ├── 🔧 PaaS (Platform)
    └── 📱 SaaS (Software)
         ↓
    🌐 INTERNET ACCESS
         ↓
    📈 SCALABILITY & FLEXIBILITY`
    },
    'hybrid-considerations': {
      title: 'Hybrid Cloud Considerations',
      explanation: 'Combining on-premises infrastructure with cloud services, requiring careful integration and security planning.',
      examples: 'Data residency requirements, network connectivity, identity federation, cost optimization',
      mnemonic: '🔗 HYBRID = Half Your Business Resources In Distributed systems\nThink of bridge connecting two lands.',
      visual: `
    🔗 HYBRID CLOUD
    🏢 ON-PREMISES ↔️ ☁️ PUBLIC CLOUD
    ├── 🔐 SECURE CONNECTION
    ├── 🆔 IDENTITY SYNC
    ├── 📊 DATA FLOW
    └── 🔄 WORKLOAD BALANCING
         ↓
    🎯 BEST OF BOTH WORLDS`
    },
    'third-party-vendors': {
      title: 'Third-party Cloud Vendors',
      explanation: 'External organizations providing cloud infrastructure, platforms, or software services with specific security implications.',
      examples: 'AWS, Microsoft Azure, Google Cloud, Salesforce, vendor risk assessment, SLA management',
      mnemonic: '🤝 VENDOR = Very External, Never Directly Owned Resources\nThink of renting instead of owning.',
      visual: `
    🤝 THIRD-PARTY VENDOR
    🏢 YOUR COMPANY
         ↓
    📋 CONTRACT/SLA
         ↓
    🏭 VENDOR SERVICES
    ├── 🔒 VENDOR SECURITY
    ├── 📊 VENDOR COMPLIANCE
    └── 🎯 VENDOR RISKS
         ↓
    ⚖️ SHARED RESPONSIBILITY`
    },
    'infrastructure-as-code': {
      title: 'Infrastructure as Code (IaC)',
      explanation: 'Managing and provisioning computing infrastructure through machine-readable definition files.',
      examples: 'Terraform, CloudFormation, Ansible, Puppet, version-controlled infrastructure templates',
      mnemonic: '📝 IAC = Infrastructure Automatically Created\nThink of building with blueprints instead of manual work.',
      visual: `
    📝 INFRASTRUCTURE AS CODE
    💻 DEVELOPER WRITES
    ├── 🏗️ infrastructure.tf
    ├── 🔧 config.yaml
    └── 📋 deploy.json
         ↓
    🤖 AUTOMATION TOOL
         ↓
    🏢 INFRASTRUCTURE DEPLOYED`
    },
    'serverless-architecture': {
      title: 'Serverless Architecture',
      explanation: 'Cloud computing model where the provider manages server infrastructure and automatically allocates resources.',
      examples: 'AWS Lambda, Azure Functions, Google Cloud Functions, event-driven computing, pay-per-execution',
      mnemonic: '⚡ SERVERLESS = Server Execution Really Virtualized, Extremely Rapid, Less Everything Server-side\nThink of code running without worrying about servers.',
      visual: `
    ⚡ SERVERLESS
    📝 CODE FUNCTION
         ↓
    📤 UPLOAD TO CLOUD
         ↓
    🎯 EVENT TRIGGERS
         ↓
    🏃 FUNCTION EXECUTES
         ↓
    💰 PAY PER EXECUTION`
    },
    'air-gapped-isolation': {
      title: 'Air-gapped Physical Isolation',
      explanation: 'Complete physical separation of systems from networks, providing the highest level of security isolation.',
      examples: 'Classified systems, industrial control systems, secure enclaves, offline backup systems',
      mnemonic: '🌬️ AIRGAP = Absolutely Isolated, Removes Gateway Access Points\nThink of island with no bridges.',
      visual: `
    🌬️ AIR-GAPPED SYSTEM
    🏝️ ISOLATED NETWORK
    ├── 🚫 NO INTERNET
    ├── 🚫 NO WIRELESS
    ├── 🚫 NO USB PORTS
    └── 🔐 PHYSICAL SECURITY
         ↓
    💯 MAXIMUM ISOLATION
         ↓
    🛡️ HIGHEST SECURITY`
    },
    'logical-segmentation': {
      title: 'Logical Network Segmentation',
      explanation: 'Software-based division of networks to isolate resources and control traffic flow without physical separation.',
      examples: 'VLANs, subnets, virtual firewalls, software-defined perimeters, network zones',
      mnemonic: '🧠 LOGICAL = Layers Obviously Group, Isolate, Control All Links\nThink of software creating invisible walls.',
      visual: `
    🧠 LOGICAL SEGMENTATION
    🌐 PHYSICAL NETWORK
    ├── 🟦 VLAN 10 (Sales)
    ├── 🟨 VLAN 20 (Finance)
    ├── 🟩 VLAN 30 (IT)
    └── 🟥 VLAN 99 (DMZ)
         ↓
    🛡️ TRAFFIC CONTROL
         ↓
    🔒 ISOLATED DOMAINS`
    },
    'software-defined-networking': {
      title: 'Software-Defined Networking (SDN)',
      explanation: 'Network architecture approach that enables centralized control of network behavior through software applications.',
      examples: 'OpenFlow, network virtualization, centralized controllers, programmable networks',
      mnemonic: '🎛️ SDN = Software Defines Networks\nThink of remote control for entire network.',
      visual: `
    🎛️ SDN ARCHITECTURE
    👨‍💻 NETWORK APPS
         ↓
    🧠 SDN CONTROLLER
    ├── 📋 NETWORK POLICIES
    ├── 🗺️ TOPOLOGY VIEW
    └── 🎯 TRAFFIC RULES
         ↓
    🌐 NETWORK DEVICES
         ↓
    🔄 DYNAMIC CONFIGURATION`
    },
    'on-premises-architecture': {
      title: 'On-premises Architecture',
      explanation: 'Computing infrastructure owned and operated within an organization\'s own facilities and under their direct control.',
      examples: 'Private data centers, company-owned servers, internal networks, local storage systems',
      mnemonic: '🏢 ONPREM = Our Network, Private Resources, Entirely Managed internally\nThink of owning vs renting your house.',
      visual: `
    🏢 ON-PREMISES
    🏭 COMPANY DATA CENTER
    ├── 🔒 FULL CONTROL
    ├── 💰 CAPITAL INVESTMENT
    ├── 👨‍🔧 INTERNAL STAFF
    └── 📊 PREDICTABLE COSTS
         ↓
    🛡️ COMPLETE OWNERSHIP
         ↓
    ⚖️ FULL RESPONSIBILITY`
    },
    'centralized-decentralized': {
      title: 'Centralized vs Decentralized Architecture',
      explanation: 'Architectural approaches differing in how control, decision-making, and resources are distributed.',
      examples: 'Centralized: single data center, unified management. Decentralized: edge computing, distributed systems',
      mnemonic: '🎯 CENTER = Central Entity, Network Totally Executed Remotely vs 🕸️ DECENT = Distributed, Everyone Controls, Entirely Network Topology\nThink of monarchy vs democracy.',
      visual: `
    🎯 CENTRALIZED        🕸️ DECENTRALIZED
    👑 SINGLE CONTROL     👥 MULTIPLE NODES
    ├── 🏢 ONE LOCATION   ├── 🌐 MANY LOCATIONS
    ├── 📊 UNIFIED MGMT   ├── 🔗 PEER-TO-PEER
    └── 🎮 SINGLE POINT   └── 🛡️ FAULT TOLERANT
         ↓                     ↓
    ⚡ FAST DECISIONS     🔄 RESILIENT SYSTEM`
    },
    'containerization': {
      title: 'Containerization',
      explanation: 'Lightweight virtualization method that packages applications with their dependencies into portable containers.',
      examples: 'Docker containers, Kubernetes orchestration, microservices deployment, container registries',
      mnemonic: '📦 CONTAINER = Code Obviously Needs Total Application, Isolation, Network, Environment Resources\nThink of shipping containers for software.',
      visual: `
    📦 CONTAINERIZATION
    💻 APPLICATION + DEPENDENCIES
         ↓
    📦 CONTAINER IMAGE
    ├── 🔧 RUNTIME
    ├── 📚 LIBRARIES
    └── ⚙️ CONFIG FILES
         ↓
    🚀 DEPLOY ANYWHERE
         ↓
    ⚡ CONSISTENT EXECUTION`
    },
    'virtualization': {
      title: 'Virtualization',
      explanation: 'Technology that creates virtual versions of computing resources like servers, storage, and networks.',
      examples: 'VMware, Hyper-V, virtual machines, hypervisors, virtual networks, resource pooling',
      mnemonic: '🎭 VIRTUAL = Very Important Resources, Technology Utilizes All Layers\nThink of one computer pretending to be many.',
      visual: `
    🎭 VIRTUALIZATION
    🖥️ PHYSICAL SERVER
         ↓
    🔧 HYPERVISOR
    ├── 💻 VM 1 (Windows)
    ├── 🐧 VM 2 (Linux)
    ├── 🍎 VM 3 (macOS)
    └── 📊 RESOURCE SHARING
         ↓
    📈 MAXIMUM EFFICIENCY`
    },
    'iot-architecture': {
      title: 'Internet of Things (IoT)',
      explanation: 'Network of interconnected physical devices embedded with sensors and software to collect and exchange data.',
      examples: 'Smart home devices, industrial sensors, wearables, connected vehicles, smart city infrastructure',
      mnemonic: '🌐 IOT = Internet Of Things connected\nThink of everything talking to everything else.',
      visual: `
    🌐 IOT ECOSYSTEM
    🏠 SMART HOME
    ├── 🌡️ TEMPERATURE SENSORS
    ├── 📱 SMART PHONES
    ├── 🚗 CONNECTED CARS
    └── ⌚ WEARABLES
         ↓
    ☁️ CLOUD PROCESSING
         ↓
    📊 DATA INSIGHTS`
    },
    'ics-scada': {
      title: 'Industrial Control Systems (ICS/SCADA)',
      explanation: 'Computer systems used to monitor and control industrial processes and critical infrastructure.',
      examples: 'Power grid control, water treatment systems, manufacturing automation, oil refineries',
      mnemonic: '🏭 SCADA = Supervisory Control And Data Acquisition\nThink of mission control for industrial processes.',
      visual: `
    🏭 ICS/SCADA SYSTEM
    🎛️ CONTROL CENTER
         ↓
    📡 COMMUNICATION NETWORK
         ↓
    🏗️ INDUSTRIAL PROCESSES
    ├── ⚡ POWER GENERATION
    ├── 💧 WATER TREATMENT
    └── 🛢️ OIL REFINING
         ↓
    🔍 REAL-TIME MONITORING`
    },
    'embedded-systems': {
      title: 'Embedded Systems',
      explanation: 'Specialized computer systems designed to perform dedicated functions within larger mechanical or electrical systems.',
      examples: 'Medical devices, automotive systems, home appliances, industrial controllers, smart cards',
      mnemonic: '⚙️ EMBEDDED = Electronic Machines Built Into Electronic Devices, Dedicated, Efficient, Defined\nThink of computer brain inside everyday objects.',
      visual: `
    ⚙️ EMBEDDED SYSTEM
    📱 DEVICE (e.g., Smart TV)
    ├── 🧠 MICROPROCESSOR
    ├── 💾 MEMORY
    ├── 🔌 I/O INTERFACES
    └── 💻 FIRMWARE
         ↓
    🎯 SPECIFIC FUNCTION
         ↓
    ⚡ OPTIMIZED PERFORMANCE`
    },
    'security-zones': {
      title: 'Security Zones',
      explanation: 'Logical or physical network segments with defined security requirements and access controls.',
      examples: 'DMZ, internal network, guest network, management network, production vs development zones',
      mnemonic: '🏰 ZONES = Zero-trust Operations, Network Environments Segmented\nThink of castle with different security areas.',
      visual: `
    🏰 SECURITY ZONES
    🌐 INTERNET
         ↓
    🔥 DMZ (Web Servers)
         ↓
    🛡️ FIREWALL
         ↓
    🏢 INTERNAL NETWORK
    ├── 💼 CORPORATE LAN
    ├── 🔧 MANAGEMENT ZONE
    └── 📊 DATABASE ZONE`
    },
    'fail-open-closed': {
      title: 'Fail-open vs Fail-closed',
      explanation: 'Security design philosophy determining system behavior when security controls encounter failures.',
      examples: 'Fail-open: door unlocks on power loss. Fail-closed: door stays locked on power loss',
      mnemonic: '🚪 FAIL = Failure Actions, Immediately Lock vs 🔓 OPEN = Operations Proceed, Emergency Needs\nThink of emergency exit vs bank vault.',
      visual: `
    🚪 FAIL-CLOSED          🔓 FAIL-OPEN
    ⚡ POWER FAILURE        ⚡ POWER FAILURE
         ↓                       ↓
    🔒 SYSTEM LOCKS         🚪 SYSTEM OPENS
    🛡️ SECURE (DENY)       ⚠️ ACCESSIBLE (ALLOW)
    📉 LOW AVAILABILITY     📈 HIGH AVAILABILITY
    🔐 HIGH SECURITY        ⚠️ SECURITY RISK`
    },
    'active-passive-monitoring': {
      title: 'Active vs Passive Monitoring',
      explanation: 'Different approaches to network monitoring based on how systems interact with network traffic.',
      examples: 'Active: network probes, synthetic transactions. Passive: traffic analysis, packet capture',
      mnemonic: '🎯 ACTIVE = Always Checking, Tests Infrastructure, Verifies Everything vs 👁️ PASSIVE = Purely Analyzes, Silently Sees, Invisibly Validates Events\nThink of detective asking questions vs watching silently.',
      visual: `
    🎯 ACTIVE MONITORING     👁️ PASSIVE MONITORING
    🔍 SENDS TEST PACKETS    📡 CAPTURES TRAFFIC
         ↓                        ↓
    ⚡ GENERATES TRAFFIC     🕵️ ANALYZES PATTERNS
    🎯 PROACTIVE TESTING     📊 REACTIVE ANALYSIS
    ⚠️ NETWORK IMPACT       ✅ NO NETWORK IMPACT`
    },
    'inline-tap-monitoring': {
      title: 'Inline vs Tap/Monitor Deployment',
      explanation: 'Different methods of deploying security devices in network infrastructure.',
      examples: 'Inline: firewall blocking traffic. Tap: IDS monitoring copies of traffic',
      mnemonic: '🛑 INLINE = In Line, Network Experiences device vs 🕊️ TAP = Traffic Analysis Point, copies only\nThink of toll booth vs surveillance camera.',
      visual: `
    🛑 INLINE DEPLOYMENT     🕊️ TAP DEPLOYMENT
    📤 TRAFFIC → 🛡️ DEVICE   📤 TRAFFIC → 🌐 DESTINATION
         ↓                        ↗️
    ✅ SECURITY CHECK        📋 COPY TO TAP
         ↓                        ↓
    📥 ALLOWED TRAFFIC       🔍 SECURITY ANALYSIS
    🛡️ ACTIVE PROTECTION    📊 PASSIVE MONITORING`
    },
    'jump-server': {
      title: 'Jump Server',
      explanation: 'Intermediary server that provides secure access to devices in different security zones.',
      examples: 'Bastion host, secure gateway, privileged access workstation, administrative jump box',
      mnemonic: '🦘 JUMP = Just Unified Management Point\nThink of stepping stone to cross river safely.',
      visual: `
    🦘 JUMP SERVER
    👨‍💻 ADMIN → 🏰 JUMP SERVER
                    ↓
               🔐 AUTHENTICATION
                    ↓
               🌐 SECURE TUNNEL
                    ↓
               🎯 TARGET SYSTEMS
    ├── 💻 Server 1
    ├── 💻 Server 2  
    └── 💻 Server 3`
    },
    'proxy-server': {
      title: 'Proxy Server',
      explanation: 'Intermediary server that forwards requests between clients and other servers, providing security and caching.',
      examples: 'Web proxy, reverse proxy, SOCKS proxy, content filtering proxy, load balancing proxy',
      mnemonic: '🎭 PROXY = Processes Requests On eXternal, Yields responses\nThink of secretary handling all your calls.',
      visual: `
    🎭 PROXY SERVER
    💻 CLIENT → 🎭 PROXY → 🌐 WEB SERVER
    ├── 🔍 CONTENT FILTERING
    ├── 💾 CACHING
    ├── 🚫 ACCESS CONTROL
    └── 📊 LOGGING
         ↓
    🛡️ SECURITY & PERFORMANCE`
    },
    'ips-ids-systems': {
      title: 'IPS/IDS Systems',
      explanation: 'Intrusion Prevention/Detection Systems that monitor and respond to suspicious network activity.',
      examples: 'Network IDS/IPS, host-based IDS/IPS, signature detection, anomaly detection',
      mnemonic: '🚨 IDS = Intrusion Detection System (watches) vs 🛡️ IPS = Intrusion Prevention System (blocks)\nThink of security camera vs security guard.',
      visual: `
    🚨 IDS (DETECTION)       🛡️ IPS (PREVENTION)
    👁️ MONITORS TRAFFIC     🛑 BLOCKS ATTACKS
         ↓                        ↓
    📝 LOGS ALERTS          ⚡ IMMEDIATE ACTION
    👨‍💻 ADMIN RESPONSE       🤖 AUTOMATED RESPONSE
    📊 FORENSIC ANALYSIS     🔒 REAL-TIME PROTECTION`
    },
    'sensors-physical': {
      title: 'Physical Sensors',
      explanation: 'Devices that detect and respond to physical changes in the environment for security monitoring.',
      examples: 'Motion detectors, door sensors, glass break detectors, vibration sensors, temperature sensors',
      mnemonic: '👁️ SENSORS = Security Equipment, Never Sleeps, Observes Risky Situations\nThink of electronic guard dogs that never sleep.',
      visual: `
    👁️ PHYSICAL SENSORS
    🏢 SECURED FACILITY
    ├── 🚶 MOTION DETECTION
    ├── 🚪 DOOR MONITORING
    ├── 🔊 SOUND ANALYSIS
    └── 🌡️ ENVIRONMENTAL
         ↓
    🚨 ALERT GENERATION
         ↓
    👮 SECURITY RESPONSE`
    },
    'updating-diagrams': {
      title: 'Updating Diagrams',
      explanation: 'Maintaining current and accurate visual representations of systems, networks, and processes after changes.',
      examples: 'Network topology diagrams, system architecture diagrams, data flow diagrams, security zone maps',
      mnemonic: '📊 DIAGRAM = Documents Infrastructure, Always Graphically Reflects All Modifications\nThink of keeping blueprints current.',
      visual: `
    📊 DIAGRAM UPDATE PROCESS
    🔧 SYSTEM CHANGE
         ↓
    📝 CHANGE REQUEST
         ↓
    🎨 UPDATE DIAGRAM
    ├── 📐 NETWORK TOPOLOGY
    ├── 🏗️ SYSTEM ARCHITECTURE
    └── 🔄 DATA FLOWS
         ↓
    ✅ DOCUMENTATION CURRENT`
    },
    'updating-policies-procedures': {
      title: 'Updating Policies and Procedures',
      explanation: 'Revising organizational policies and procedures to reflect system changes and maintain compliance.',
      examples: 'Security policies, operational procedures, compliance documentation, user guides',
      mnemonic: '📋 POLICY = Procedures Obviously Listed, Indicate Current Yearly practices\nThink of updating employee handbook.',
      visual: `
    📋 POLICY UPDATE
    🔄 SYSTEM CHANGE
         ↓
    📖 REVIEW POLICIES
    ├── 🛡️ SECURITY POLICIES
    ├── ⚙️ OPERATIONAL PROCEDURES
    └── 📜 COMPLIANCE DOCS
         ↓
    ✏️ REVISE & APPROVE
         ↓
    📤 DISTRIBUTE UPDATES`
    },
    'version-control': {
      title: 'Version Control',
      explanation: 'System for tracking and managing changes to code, documents, and configurations over time.',
      examples: 'Git repositories, document versioning, configuration management, change tracking',
      mnemonic: '🔢 VERSION = Verify Every Revision, Store Information, Organize, Navigate changes\nThink of time machine for files.',
      visual: `
    🔢 VERSION CONTROL
    📝 DOCUMENT v1.0
         ↓
    ✏️ CHANGES MADE
         ↓
    📝 DOCUMENT v1.1
    ├── 📅 TIMESTAMP
    ├── 👤 AUTHOR INFO
    ├── 📝 CHANGE LOG
    └── 🔄 ROLLBACK OPTION
         ↓
    📚 VERSION HISTORY`
    },
    'certificate-authorities': {
      title: 'Certificate Authorities (CAs)',
      explanation: 'Trusted entities that issue, manage, and validate digital certificates in public key infrastructure.',
      examples: 'VeriSign, DigiCert, Let\'s Encrypt, internal enterprise CAs, government CAs',
      mnemonic: '🏛️ CA = Certificate Authority, validates identities\nThink of government issuing official IDs.',
      visual: `
    🏛️ CERTIFICATE AUTHORITY
    📝 CERTIFICATE REQUEST
         ↓
    🔍 IDENTITY VERIFICATION
         ↓
    🔐 CERTIFICATE ISSUANCE
    ├── 📜 DIGITAL CERTIFICATE
    ├── 🔑 PUBLIC KEY
    ├── 📅 EXPIRATION DATE
    └── ✍️ CA SIGNATURE
         ↓
    🌐 TRUSTED COMMUNICATION`
    },
    'certificate-revocation-lists': {
      title: 'Certificate Revocation Lists (CRLs)',
      explanation: 'Lists of digital certificates that have been revoked before their expiration date.',
      examples: 'Compromised certificates, employee terminations, certificate misuse, key compromise',
      mnemonic: '📋 CRL = Certificate Revocation List, blocked certificates\nThink of blacklist for bad certificates.',
      visual: `
    📋 CERTIFICATE REVOCATION LIST
    🔐 VALID CERTIFICATE
         ↓
    ⚠️ COMPROMISE DETECTED
         ↓
    🚫 ADD TO CRL
    ├── 📜 CERTIFICATE SERIAL
    ├── 📅 REVOCATION DATE
    └── 🎯 REVOCATION REASON
         ↓
    🌐 CRL DISTRIBUTION
         ↓
    🛑 CERTIFICATE BLOCKED`
    },
    'ocsp': {
      title: 'Online Certificate Status Protocol (OCSP)',
      explanation: 'Real-time protocol for checking the revocation status of digital certificates.',
      examples: 'OCSP responders, certificate validation, real-time status checks, OCSP stapling',
      mnemonic: '🌐 OCSP = Online Certificate Status Protocol, real-time checking\nThink of calling to verify ID is still valid.',
      visual: `
    🌐 OCSP PROTOCOL
    💻 CLIENT REQUEST
    "Is cert ABC123 valid?"
         ↓
    🏛️ OCSP RESPONDER
    ├── 🔍 CHECK CERTIFICATE
    ├── 📋 VERIFY STATUS
    └── 📅 CHECK EXPIRATION
         ↓
    ✅ RESPONSE: GOOD/REVOKED/UNKNOWN
         ↓
    🔐 SECURE CONNECTION`
    },
    'self-signed-certificates': {
      title: 'Self-signed Certificates',
      explanation: 'Digital certificates signed by their own creators rather than trusted certificate authorities.',
      examples: 'Internal development certificates, test environments, small organization certificates',
      mnemonic: '✍️ SELFSIGN = Self-Signed, Everyone Lacks Faith, Suspicious Identity, Gets No trust\nThink of writing your own recommendation letter.',
      visual: `
    ✍️ SELF-SIGNED CERTIFICATE
    👤 CERTIFICATE CREATOR
         ↓
    📝 CREATE CERTIFICATE
    ├── 🔑 GENERATE KEY PAIR
    ├── 📜 CREATE CERTIFICATE
    └── ✍️ SIGN WITH OWN KEY
         ↓
    ⚠️ BROWSER WARNING
    "Untrusted Certificate"
         ↓
    🤔 USER DECISION`
    },
    'third-party-certificates': {
      title: 'Third-party Certificates',
      explanation: 'Digital certificates issued by external, trusted certificate authorities rather than internal systems.',
      examples: 'Commercial SSL certificates, public CAs like DigiCert, cross-signed certificates',
      mnemonic: '🤝 THIRD = Trusted, Highly Regarded, Independent, Reliable Distributor\nThink of official license from government.',
      visual: `
    🤝 THIRD-PARTY CERTIFICATE
    🏢 YOUR ORGANIZATION
         ↓
    📝 CERTIFICATE REQUEST
         ↓
    🏛️ EXTERNAL CA (DigiCert)
    ├── 🔍 IDENTITY VERIFICATION
    ├── 💰 PAYMENT PROCESSING
    └── ✅ VALIDATION COMPLETE
         ↓
    📜 TRUSTED CERTIFICATE
         ↓
    🌐 BROWSER ACCEPTANCE`
    },
    'root-of-trust': {
      title: 'Root of Trust',
      explanation: 'Foundational security component that forms the basis for all other security functions in a system.',
      examples: 'Hardware security modules, trusted platform modules, secure boot, hardware root of trust',
      mnemonic: '🌳 ROOT = Really Original, Operates Trustworthy foundations\nThink of tree roots supporting everything above.',
      visual: `
    🌳 ROOT OF TRUST
    🔐 HARDWARE SECURITY
         ↓
    🛡️ TRUSTED FOUNDATION
    ├── 🔑 CRYPTOGRAPHIC KEYS
    ├── 🔒 SECURE STORAGE
    └── 🎯 IDENTITY ANCHOR
         ↓
    🏗️ SECURITY ARCHITECTURE
         ↓
    🌐 TRUSTED SYSTEM`
    },
    'csr-generation': {
      title: 'Certificate Signing Request (CSR) Generation',
      explanation: 'Process of creating a request for a digital certificate that includes public key and identifying information.',
      examples: 'OpenSSL CSR creation, web server certificate requests, code signing CSRs',
      mnemonic: '📝 CSR = Certificate Signing Request, application for certificate\nThink of application form for official document.',
      visual: `
    📝 CSR GENERATION
    🔑 GENERATE KEY PAIR
         ↓
    📋 CREATE CSR
    ├── 🏢 ORGANIZATION INFO
    ├── 🌐 DOMAIN NAME
    ├── 🔑 PUBLIC KEY
    └── 📧 CONTACT INFO
         ↓
    📤 SUBMIT TO CA
         ↓
    📜 RECEIVE CERTIFICATE`
    },
    'wildcard-certificates': {
      title: 'Wildcard Certificates',
      explanation: 'SSL certificates that secure a domain and all its subdomains using a wildcard character (*).',
      examples: '*.example.com covers www.example.com, mail.example.com, ftp.example.com',
      mnemonic: '🌟 WILDCARD = Widely Includes Large Domains, Covers All Related Domains\nThink of master key for all rooms.',
      visual: `
    🌟 WILDCARD CERTIFICATE
    📜 *.example.com
         ↓
    🌐 COVERS ALL SUBDOMAINS
    ├── ✅ www.example.com
    ├── ✅ mail.example.com
    ├── ✅ shop.example.com
    └── ✅ api.example.com
         ↓
    💰 COST EFFECTIVE
         ↓
    🛡️ COMPREHENSIVE PROTECTION`
    },
    'client-based-agentless': {
      title: 'Client-based vs Agentless',
      explanation: 'Two approaches for deploying security solutions: installing software agents or using remote scanning methods.',
      examples: 'Client-based: EDR agents, antivirus software. Agentless: network scanning, cloud-based assessment',
      mnemonic: '👨‍💻 CLIENT = Computer Loads Infrastructure, Executes Network Tasks vs 🌐 AGENTLESS = Always Goes Everywhere, No Tough Local Execution Setup\nThink of hiring employee vs outsourcing.',
      visual: `
    👨‍💻 CLIENT-BASED        🌐 AGENTLESS
    📥 INSTALL AGENT        📡 REMOTE SCANNING
    ├── 💾 LOCAL STORAGE    ├── ☁️ CLOUD-BASED
    ├── 🔄 REAL-TIME        ├── 📊 SCHEDULED SCANS
    └── 🎯 DEEP INSIGHT     └── 🌍 BROAD COVERAGE
         ↓                       ↓
    🛡️ COMPREHENSIVE        ⚡ EASY DEPLOYMENT`
    },
    'wireless-networks': {
      title: 'Wireless Network Threats',
      explanation: 'Security vulnerabilities specific to wireless communication networks and protocols.',
      examples: 'WiFi eavesdropping, rogue access points, evil twin attacks, WEP/WPA vulnerabilities',
      mnemonic: '📶 WIRELESS = Waves Inherently Risk Exposure, Lacks Enhanced Security Standards\nThink of radio waves anyone can catch.',
      visual: `
    📶 WIRELESS THREATS
    📡 WIRELESS SIGNALS
    ├── 👂 EAVESDROPPING
    ├── 🎭 ROGUE AP
    ├── 👥 EVIL TWIN
    └── 🔓 WEAK ENCRYPTION
         ↓
    🕵️ ATTACKER INTERCEPTS
         ↓
    💀 DATA COMPROMISE`
    },
    'wired-networks': {
      title: 'Wired Network Threats',
      explanation: 'Security risks associated with physical network connections and infrastructure.',
      examples: 'Network tapping, physical access to switches, cable interception, unauthorized connections',
      mnemonic: '🔌 WIRED = Wires Invite Risk, Especially Direct access\nThink of someone tapping phone lines.',
      visual: `
    🔌 WIRED NETWORK THREATS
    🌐 NETWORK CABLE
    ├── 🔍 PHYSICAL TAPPING
    ├── 🔌 UNAUTHORIZED ACCESS
    ├── 📡 SIGNAL INTERCEPTION
    └── 🏢 SWITCH COMPROMISE
         ↓
    👁️ DATA MONITORING
         ↓
    📊 TRAFFIC ANALYSIS`
    },
    'bluetooth-threats': {
      title: 'Bluetooth Threats',
      explanation: 'Security vulnerabilities in Bluetooth wireless communication protocol.',
      examples: 'Bluejacking, bluesnarfing, bluetooth sniffing, unauthorized pairing, proximity attacks',
      mnemonic: '🔵 BLUETOOTH = Blue Lets Users Exchange, Takes Over Other Handheld devices\nThink of short-range wireless hijacking.',
      visual: `
    🔵 BLUETOOTH THREATS
    📱 BLUETOOTH DEVICE
    ├── 🎯 BLUEJACKING (spam)
    ├── 📂 BLUESNARFING (data theft)
    ├── 🔗 UNAUTHORIZED PAIRING
    └── 📡 PROXIMITY ATTACKS
         ↓
    👤 VICTIM IN RANGE
         ↓
    💀 DEVICE COMPROMISE`
    },
    'msps-vendors-suppliers': {
      title: 'Managed Service Providers, Vendors, and Suppliers',
      explanation: 'Third-party organizations in the supply chain that can introduce security risks through their services or products.',
      examples: 'Cloud service providers, software vendors, hardware suppliers, IT outsourcing companies',
      mnemonic: '🤝 MSP = Many Security Partners, potential risks\nThink of trusting others with your keys.',
      visual: `
    🤝 SUPPLY CHAIN PARTNERS
    🏢 YOUR ORGANIZATION
         ↓
    🌐 MSPs (IT Services)
    🏭 VENDORS (Software)
    📦 SUPPLIERS (Hardware)
         ↓
    ⚠️ THIRD-PARTY RISKS
    ├── 🔓 WEAK SECURITY
    ├── 🕵️ DATA ACCESS
    └── 🦠 COMPROMISED SUPPLY
         ↓
    🎯 SUPPLY CHAIN ATTACK`
    },
    'resource-inaccessibility': {
      title: 'Resource Inaccessibility',
      explanation: 'Security indicator when legitimate users cannot access systems or resources they should have access to.',
      examples: 'Website downtime, service outages, denied access to files, application unavailability',
      mnemonic: '🚫 INACCESSIBLE = Infrastructure Not Available, Can\'t Connect, Everything Stops, System Isolated, Blocked, Locked, Entirely\nThink of locked out of your own house.',
      visual: `
    🚫 RESOURCE INACCESSIBILITY
    👤 LEGITIMATE USER
         ↓
    🔒 ACCESS ATTEMPT
         ↓
    ❌ ACCESS DENIED
    ├── 🌐 SERVICE DOWN
    ├── 🦠 MALWARE IMPACT
    ├── 🌊 DDOS ATTACK
    └── 🔧 SYSTEM FAILURE
         ↓
    😤 BUSINESS DISRUPTION`
    },
    'out-of-cycle-logging': {
      title: 'Out-of-cycle Logging',
      explanation: 'Unexpected or unusual log entries that occur outside normal operational patterns.',
      examples: 'After-hours access attempts, weekend system changes, holiday activities, unusual timestamps',
      mnemonic: '⏰ CYCLE = Can You Check, Logs Entered oddly?\nThink of someone working when office should be empty.',
      visual: `
    ⏰ OUT-OF-CYCLE LOGGING
    📅 NORMAL BUSINESS HOURS
    9 AM - 5 PM
         ↓
    🚨 UNUSUAL LOG ENTRY
    "Login at 2:30 AM"
         ↓
    🔍 SECURITY INVESTIGATION
    ├── 🕵️ WHO WAS IT?
    ├── 🎯 WHY THEN?
    └── ⚠️ LEGITIMATE?
         ↓
    🚨 POTENTIAL THREAT`
    },
    'published-documented-vulnerabilities': {
      title: 'Published/Documented Vulnerabilities',
      explanation: 'Publicly disclosed security flaws that are documented in vulnerability databases.',
      examples: 'CVE entries, security advisories, vendor bulletins, public exploit databases',
      mnemonic: '📖 PUBLISHED = Public Understanding, Broadcast, Listed, Information Shared, Hackers Exploit Data\nThink of recipe for attack published online.',
      visual: `
    📖 PUBLISHED VULNERABILITY
    🔍 SECURITY RESEARCHER
         ↓
    📋 CVE-2024-XXXX
    ├── 🎯 AFFECTED SYSTEMS
    ├── 📊 CVSS SCORE: 9.8
    ├── 🛠️ EXPLOIT AVAILABLE
    └── 🩹 PATCH RELEASED
         ↓
    🌐 PUBLIC KNOWLEDGE
         ↓
    ⚡ RACE TO PATCH`
    },
    'missing-logs': {
      title: 'Missing Logs',
      explanation: 'Absence of expected log entries that should normally be generated by systems, indicating potential tampering or evasion.',
      examples: 'Deleted audit trails, log rotation issues, disabled logging, anti-forensics techniques',
      mnemonic: '🕳️ MISSING = Many Important Security System Information, Never Generated\nThink of security camera with no recording.',
      visual: `
    🕳️ MISSING LOGS
    📊 EXPECTED LOG PATTERN
    ├── ✅ 09:00 - Login
    ├── ❌ 09:30 - MISSING
    ├── ❌ 10:00 - MISSING
    └── ✅ 10:30 - Activity
         ↓
    🚨 SUSPICIOUS GAP
    ├── 🦠 MALWARE CLEANUP
    ├── 👤 INSIDER THREAT
    └── 🛠️ SYSTEM MALFUNCTION
         ↓
    🔍 FORENSIC INVESTIGATION`
    },
    'rtos': {
      title: 'Real-time Operating System (RTOS)',
      explanation: 'Operating system designed to handle real-time applications with strict timing requirements and deterministic responses.',
      examples: 'Industrial control systems, medical devices, automotive systems, aerospace applications',
      mnemonic: '⏱️ RTOS = Real-Time Operating System, immediate responses\nThink of surgeon needing instant response from heart monitor.',
      visual: `
    ⏱️ REAL-TIME OS
    🚗 AUTOMOTIVE SYSTEM
    ├── ⚡ IMMEDIATE RESPONSE
    ├── 🎯 DETERMINISTIC
    ├── 📊 LOW LATENCY
    └── ⏰ DEADLINE CRITICAL
         ↓
    🛡️ SAFETY CRITICAL
         ↓
    💀 NO DELAYS ALLOWED`
    },
    'availability-consideration': {
      title: 'Availability Consideration',
      explanation: 'Architectural factor ensuring systems and services remain accessible and operational when needed.',
      examples: 'High availability designs, redundancy planning, failover mechanisms, uptime requirements',
      mnemonic: '🌟 AVAILABLE = Always Visible, Always Instant, Logical Access, Broadly Live, Everything accessible\nThink of 24/7 convenience store.',
      visual: `
    🌟 AVAILABILITY
    👤 USER REQUEST
         ↓
    🔄 REDUNDANT SYSTEMS
    ├── 💻 PRIMARY SERVER
    ├── 🔄 BACKUP SERVER
    └── ⚡ LOAD BALANCER
         ↓
    ✅ SERVICE ACCESSIBLE
         ↓
    📈 99.9% UPTIME`
    },
    'resilience-consideration': {
      title: 'Resilience Consideration',
      explanation: 'System capability to maintain functionality and recover quickly from failures or attacks.',
      examples: 'Fault tolerance, graceful degradation, disaster recovery, auto-healing systems',
      mnemonic: '💪 RESILIENT = Really Excellent Systems, Infrastructure Learns, Immediately Endures, Never Totally fails\nThink of rubber ball bouncing back.',
      visual: `
    💪 RESILIENCE
    ⚡ SYSTEM FAILURE
         ↓
    🔄 AUTO-RECOVERY
    ├── 🛠️ SELF-HEALING
    ├── 📊 GRACEFUL DEGRADATION
    └── 🔄 FAILOVER
         ↓
    🏃 QUICK RECOVERY
         ↓
    🌟 CONTINUED OPERATION`
    },
    'cost-consideration': {
      title: 'Cost Consideration',
      explanation: 'Financial factors in architectural decisions including initial investment, operational costs, and maintenance expenses.',
      examples: 'CAPEX vs OPEX, cloud vs on-premises costs, licensing fees, maintenance contracts',
      mnemonic: '💰 COST = Calculate Operations, Systems, Total expenses\nThink of balancing security budget.',
      visual: `
    💰 COST ANALYSIS
    📊 ARCHITECTURE OPTIONS
    ├── 🏢 ON-PREMISES ($$$)
    ├── ☁️ CLOUD ($ per use)
    └── 🔄 HYBRID ($$ mixed)
         ↓
    ⚖️ COST VS BENEFIT
         ↓
    🎯 OPTIMAL CHOICE`
    },
    'responsiveness-consideration': {
      title: 'Responsiveness Consideration',
      explanation: 'System ability to react quickly to user requests and changing conditions.',
      examples: 'Low latency networks, fast processing, real-time monitoring, quick incident response',
      mnemonic: '⚡ RESPONSIVE = Really Excellent Speed, Provides Outstanding Network, Speedy, Instant, Very Efficient\nThink of race car responding to steering.',
      visual: `
    ⚡ RESPONSIVENESS
    👤 USER ACTION
         ↓
    📈 FAST PROCESSING
    ├── 🚀 LOW LATENCY
    ├── 📊 REAL-TIME DATA
    └── ⏰ QUICK RESPONSE
         ↓
    😊 USER SATISFACTION
         ↓
    📈 BUSINESS SUCCESS`
    },
    'scalability-consideration': {
      title: 'Scalability Consideration',
      explanation: 'System capability to handle growing workloads by adding resources horizontally or vertically.',
      examples: 'Auto-scaling cloud resources, load balancing, microservices architecture, elastic infrastructure',
      mnemonic: '📈 SCALABLE = System Can Always Learn, Adapts, Builds, Larger, Expands\nThink of accordion expanding and contracting.',
      visual: `
    📈 SCALABILITY
    📊 INCREASING DEMAND
         ↓
    🔄 AUTO-SCALING
    ├── ➡️ HORIZONTAL (more servers)
    ├── ⬆️ VERTICAL (more power)
    └── ⚡ ELASTIC RESOURCES
         ↓
    🌍 HANDLES GROWTH
         ↓
    💪 MAINTAINS PERFORMANCE`
    },
    'ease-of-deployment': {
      title: 'Ease of Deployment',
      explanation: 'How simple and efficient it is to install, configure, and launch systems in production.',
      examples: 'Automated deployment, containerization, infrastructure as code, CI/CD pipelines',
      mnemonic: '🚀 DEPLOY = Don\'t Ever Press, Laboriously Over Years\nThink of one-click installation.',
      visual: `
    🚀 EASY DEPLOYMENT
    💻 DEVELOPER CODE
         ↓
    🤖 AUTOMATED PIPELINE
    ├── 🏗️ BUILD PROCESS
    ├── 🧪 AUTOMATED TESTING
    └── 📦 CONTAINERIZATION
         ↓
    🎯 ONE-CLICK DEPLOY
         ↓
    🌐 PRODUCTION READY`
    },
    'risk-transference': {
      title: 'Risk Transference',
      explanation: 'Shifting security and operational risks to third parties through contracts, insurance, or cloud services.',
      examples: 'Cloud service providers, cyber insurance, managed security services, SLA agreements',
      mnemonic: '🤝 TRANSFER = Third-parties Responsible, Accept, Nobody Should Face, Everyone Reduces risk\nThink of insurance transferring risk to company.',
      visual: `
    🤝 RISK TRANSFERENCE
    🏢 YOUR ORGANIZATION
    ├── ⚠️ ORIGINAL RISK
    ├── 📋 CONTRACT/SLA
    └── 💰 PAYMENT
         ↓
    🏛️ THIRD PARTY
    ├── 🛡️ ACCEPTS RISK
    ├── 📋 PROVIDES SERVICE
    └── 🔒 SECURITY RESPONSIBILITY
         ↓
    📉 REDUCED RISK EXPOSURE`
    },
    'ease-of-recovery': {
      title: 'Ease of Recovery',
      explanation: 'How quickly and simply systems can be restored after failures, disasters, or security incidents.',
      examples: 'Backup restoration, disaster recovery procedures, automated recovery, rollback mechanisms',
      mnemonic: '🔄 RECOVERY = Really Easy, Can Operate, Verify, Everything Returns, Yields normal operation\nThink of phoenix rising from ashes.',
      visual: `
    🔄 RECOVERY PROCESS
    💥 SYSTEM FAILURE
         ↓
    📋 RECOVERY PLAN
    ├── 💾 RESTORE BACKUPS
    ├── 🔄 FAILOVER SYSTEMS
    └── 🧪 VERIFY OPERATIONS
         ↓
    ✅ SYSTEM RESTORED
         ↓
    📈 BUSINESS CONTINUITY`
    },
    'patch-availability': {
      title: 'Patch Availability',
      explanation: 'The accessibility and timeliness of security updates and fixes from vendors.',
      examples: 'Regular vendor updates, automated patching, patch management systems, security bulletins',
      mnemonic: '🩹 PATCH = Provides Additional Technology, Closes Holes\nThink of bandage covering wound.',
      visual: `
    🩹 PATCH AVAILABILITY
    🔍 VULNERABILITY DISCOVERED
         ↓
    🏭 VENDOR RESPONSE
    ├── 🛠️ DEVELOP FIX
    ├── 🧪 TEST PATCH
    └── 📤 RELEASE UPDATE
         ↓
    📥 AUTOMATIC DOWNLOAD
         ↓
    🛡️ SYSTEM PROTECTED`
    },
    'inability-to-patch': {
      title: 'Inability to Patch',
      explanation: 'Situations where systems cannot be updated due to technical, operational, or business constraints.',
      examples: 'Legacy systems, embedded devices, critical operations, vendor discontinuation, compatibility issues',
      mnemonic: '🚫 NOPATCH = No Options, Patches Always Totally Compromise Hardware\nThink of antique car needing special parts.',
      visual: `
    🚫 INABILITY TO PATCH
    🕰️ LEGACY SYSTEM
    ├── 📜 OUTDATED OS
    ├── 🏭 VENDOR DISCONTINUED
    ├── ⚙️ CUSTOM HARDWARE
    └── 💰 CRITICAL OPERATION
         ↓
    ⚠️ PERMANENT VULNERABILITY
         ↓
    🛡️ COMPENSATING CONTROLS`
    },
    'power-consideration': {
      title: 'Power Consideration',
      explanation: 'Electrical power requirements, availability, and backup systems for maintaining operations.',
      examples: 'UPS systems, backup generators, power redundancy, energy efficiency, power monitoring',
      mnemonic: '⚡ POWER = Permanent Operations, Wattage, Energy, Resources\nThink of keeping lights on during storm.',
      visual: `
    ⚡ POWER SYSTEMS
    🏢 DATA CENTER
    ├── 🔌 PRIMARY POWER
    ├── 🔋 UPS BACKUP
    ├── 🏭 GENERATOR
    └── 📊 POWER MONITORING
         ↓
    🌟 CONTINUOUS OPERATION
         ↓
    📈 BUSINESS CONTINUITY`
    },
    'compute-consideration': {
      title: 'Compute Consideration',
      explanation: 'Processing power requirements including CPU, memory, and computational resources needed for operations.',
      examples: 'CPU capacity planning, memory requirements, processing workloads, performance optimization',
      mnemonic: '💻 COMPUTE = Central Processing Unit, Memory, Processing, Utilizes, Technology, Everything\nThink of brain needing enough neurons.',
      visual: `
    💻 COMPUTE RESOURCES
    📊 WORKLOAD DEMANDS
    ├── 🧠 CPU PROCESSING
    ├── 💾 MEMORY USAGE
    ├── 💽 STORAGE I/O
    └── 📡 NETWORK BANDWIDTH
         ↓
    ⚖️ CAPACITY PLANNING
         ↓
    🎯 OPTIMAL PERFORMANCE`
    },
    'device-placement': {
      title: 'Device Placement',
      explanation: 'Strategic positioning of security devices to maximize protection and minimize security gaps.',
      examples: 'DMZ placement, network chokepoints, perimeter positioning, internal segmentation points',
      mnemonic: '📍 PLACEMENT = Position Logically, Always Consider, Every Meaningful, Entry, Network, Target\nThink of chess pieces positioned strategically.',
      visual: `
    📍 DEVICE PLACEMENT
    🌐 NETWORK TOPOLOGY
    ├── 🔥 FIREWALL (perimeter)
    ├── 🛡️ IDS (internal)
    ├── 🔒 WAF (web tier)
    └── 📊 SIEM (SOC)
         ↓
    🎯 STRATEGIC COVERAGE
         ↓
    🛡️ MAXIMUM PROTECTION`
    },
    'attack-surface': {
      title: 'Attack Surface',
      explanation: 'Total exposure points where an unauthorized user can try to enter or extract data from a system.',
      examples: 'Open ports, web interfaces, API endpoints, network services, user access points',
      mnemonic: '🎯 SURFACE = System Usually Reveals, Faces, Access, Creates, Exposure\nThink of fortress with multiple entry points.',
      visual: `
    🎯 ATTACK SURFACE
    🏰 SYSTEM PERIMETER
    ├── 🌐 WEB INTERFACES
    ├── 📡 API ENDPOINTS
    ├── 🔌 NETWORK PORTS
    ├── 👤 USER ACCESS
    └── 📱 MOBILE APPS
         ↓
    ⚠️ POTENTIAL ENTRY POINTS
         ↓
    🛡️ SURFACE REDUCTION NEEDED`
    },
    'connectivity': {
      title: 'Connectivity',
      explanation: 'Network connections and communication paths between systems, affecting security architecture.',
      examples: 'Network topology, inter-system communication, external connections, internal routing',
      mnemonic: '🔗 CONNECT = Communication Obviously, Networks Need, Everything, Connected, Together\nThink of roads connecting cities.',
      visual: `
    🔗 CONNECTIVITY
    🏢 SYSTEMS NETWORK
    ├── 🌐 INTERNET LINKS
    ├── 🏢 INTERNAL ROUTING
    ├── ☁️ CLOUD CONNECTIONS
    └── 📱 REMOTE ACCESS
         ↓
    🛡️ SECURE CHANNELS
         ↓
    📊 MONITORED TRAFFIC`
    },
    'load-balancer': {
      title: 'Load Balancer',
      explanation: 'Network device that distributes incoming requests across multiple servers to optimize performance and availability.',
      examples: 'Application load balancers, network load balancers, DNS load balancing, global load balancing',
      mnemonic: '⚖️ BALANCE = Balances All Loads, Always Network, Controls, Everything\nThink of traffic director managing flow.',
      visual: `
    ⚖️ LOAD BALANCER
    👥 INCOMING REQUESTS
         ↓
    🎛️ LOAD BALANCER
    ├── 📊 TRAFFIC ANALYSIS
    ├── 🎯 SERVER SELECTION
    └── 🔄 DISTRIBUTION
         ↓
    💻 SERVER FARM
    ├── 💻 Server 1 (30%)
    ├── 💻 Server 2 (35%)
    └── 💻 Server 3 (35%)
         ↓
    📈 OPTIMAL PERFORMANCE`
    },
    'network-sensors': {
      title: 'Network Sensors',
      explanation: 'Monitoring devices that collect and analyze network traffic for security threats and performance issues.',
      examples: 'Network TAPs, packet analyzers, flow collectors, intrusion detection sensors',
      mnemonic: '👁️ SENSORS = See Everything, Network, Suspicious, Observes, Really, Suspicious\nThink of security cameras for network traffic.',
      visual: `
    👁️ NETWORK SENSORS
    🌐 NETWORK TRAFFIC
         ↓
    📡 SENSOR DEPLOYMENT
    ├── 📊 PACKET CAPTURE
    ├── 🔍 TRAFFIC ANALYSIS
    ├── 🚨 THREAT DETECTION
    └── 📈 PERFORMANCE METRICS
         ↓
    🖥️ SOC MONITORING
         ↓
    🚨 SECURITY ALERTS`
    },
    '802.1x': {
      title: '802.1X Port Security',
      explanation: 'IEEE standard for network access control providing authentication for devices connecting to network ports.',
      examples: 'Wired port authentication, wireless network access, certificate-based auth, RADIUS integration',
      mnemonic: '🔐 802.1X = 8 Zero 2, 1 eXcellent authentication\nThink of ID check before entering building.',
      visual: `
    🔐 802.1X AUTHENTICATION
    💻 CLIENT DEVICE
         ↓
    🔌 NETWORK PORT
         ↓
    🏛️ AUTHENTICATION SERVER
    ├── 🔍 CREDENTIAL CHECK
    ├── 📋 POLICY LOOKUP
    └── ✅ ACCESS DECISION
         ↓
    🌐 NETWORK ACCESS GRANTED
         ↓
    🛡️ AUTHORIZED COMMUNICATION`
    },
    'eap': {
      title: 'Extensible Authentication Protocol (EAP)',
      explanation: 'Authentication framework used in wireless and point-to-point connections supporting multiple authentication methods.',
      examples: 'EAP-TLS, EAP-PEAP, EAP-TTLS, EAP-MD5, certificate authentication, token-based auth',
      mnemonic: '🔑 EAP = Extensible Authentication Protocol, many methods\nThink of multi-tool with different authentication options.',
      visual: `
    🔑 EAP FRAMEWORK
    👤 USER AUTHENTICATION
         ↓
    🔧 EAP METHOD SELECTION
    ├── 📜 EAP-TLS (certificates)
    ├── 🔒 EAP-PEAP (tunneled)
    ├── 🛡️ EAP-TTLS (secure tunnel)
    └── 🎫 EAP-TOKEN (tokens)
         ↓
    ✅ AUTHENTICATION SUCCESS
         ↓
    🌐 NETWORK ACCESS`
    },
    'waf': {
      title: 'Web Application Firewall (WAF)',
      explanation: 'Security device that monitors, filters, and blocks HTTP traffic to protect web applications.',
      examples: 'SQL injection protection, XSS filtering, DDoS mitigation, API protection, rate limiting',
      mnemonic: '🛡️ WAF = Web Application Firewall, protects websites\nThink of bouncer checking IDs at web app entrance.',
      visual: `
    🛡️ WEB APPLICATION FIREWALL
    🌐 WEB TRAFFIC
         ↓
    🔍 WAF INSPECTION
    ├── 🚫 SQL INJECTION BLOCK
    ├── 🛑 XSS FILTER
    ├── 📊 RATE LIMITING
    └── 🔒 MALICIOUS CONTENT
         ↓
    ✅ CLEAN TRAFFIC
         ↓
    🖥️ WEB APPLICATION`
    },
    'utm': {
      title: 'Unified Threat Management (UTM)',
      explanation: 'Comprehensive security appliance combining multiple security functions in a single device.',
      examples: 'Firewall + antivirus + IPS + content filtering, all-in-one security, SOHO protection',
      mnemonic: '🏢 UTM = Unified Threat Management, everything in one box\nThink of Swiss Army knife for security.',
      visual: `
    🏢 UTM APPLIANCE
    🌐 NETWORK TRAFFIC
         ↓
    🛡️ MULTIPLE PROTECTION
    ├── 🔥 FIREWALL
    ├── 🦠 ANTIVIRUS
    ├── 🚨 IPS/IDS
    ├── 🔍 CONTENT FILTER
    └── 📊 VPN
         ↓
    🎯 SINGLE POINT SECURITY
         ↓
    🏢 PROTECTED NETWORK`
    },
    'ngfw': {
      title: 'Next-Generation Firewall (NGFW)',
      explanation: 'Advanced firewall with deep packet inspection, application awareness, and integrated threat prevention.',
      examples: 'Application control, user identification, SSL inspection, threat intelligence integration',
      mnemonic: '🚀 NGFW = Next Generation Firewall, smarter protection\nThink of AI-powered security guard.',
      visual: `
    🚀 NEXT-GEN FIREWALL
    📦 NETWORK PACKET
         ↓
    🧠 DEEP INSPECTION
    ├── 📱 APPLICATION ID
    ├── 👤 USER AWARENESS
    ├── 🔒 SSL DECRYPT
    ├── 🛡️ THREAT INTEL
    └── 📊 BEHAVIOR ANALYSIS
         ↓
    🎯 INTELLIGENT BLOCKING
         ↓
    🛡️ ADVANCED PROTECTION`
    },
    'layer4-layer7': {
      title: 'Layer 4/Layer 7 Firewalls',
      explanation: 'Firewalls operating at transport layer (L4) for port-based filtering or application layer (L7) for content inspection.',
      examples: 'L4: TCP/UDP port filtering. L7: HTTP content inspection, application protocols',
      mnemonic: '🏗️ LAYER = L4 Addresses Your, Ethernet Rules vs L7 Looks At Your, Everything Really\nThink of security checking bags (L4) vs reading contents (L7).',
      visual: `
    🏗️ FIREWALL LAYERS
    📦 NETWORK PACKET
         ↓
    🔢 LAYER 4 (TRANSPORT)     📱 LAYER 7 (APPLICATION)
    ├── 🔌 PORT NUMBERS       ├── 📝 CONTENT ANALYSIS
    ├── 🌐 IP ADDRESSES       ├── 🔍 PROTOCOL INSPECTION
    └── 🚫 BASIC BLOCKING     └── 🎯 APP-AWARE RULES
         ↓                         ↓
    ⚡ FAST FILTERING         🧠 INTELLIGENT CONTROL`
    },
    'vpn': {
      title: 'Virtual Private Network (VPN)',
      explanation: 'Secure tunnel that encrypts data transmission over public networks, creating private network connections.',
      examples: 'Site-to-site VPN, remote access VPN, SSL VPN, IPSec VPN, client VPN software',
      mnemonic: '🔐 VPN = Virtual Private Network, encrypted tunnel\nThink of secure underground tunnel between buildings.',
      visual: `
    🔐 VPN CONNECTION
    💻 REMOTE USER
         ↓
    🌐 INTERNET (unsecure)
         ↓
    🔒 VPN TUNNEL
    ├── 🛡️ ENCRYPTION
    ├── 🔑 AUTHENTICATION
    └── 📊 DATA INTEGRITY
         ↓
    🏢 CORPORATE NETWORK
         ↓
    💻 SECURE ACCESS`
    },
    'remote-access': {
      title: 'Remote Access',
      explanation: 'Methods and technologies that allow users to connect to networks and systems from distant locations.',
      examples: 'VPN connections, RDP, SSH, web-based access, mobile device management, zero trust access',
      mnemonic: '📱 REMOTE = Really Excellent, Makes Operations, Totally, Everywhere\nThink of working from home securely.',
      visual: `
    📱 REMOTE ACCESS
    🏠 REMOTE LOCATION
         ↓
    🔐 AUTHENTICATION
    ├── 🔑 CREDENTIALS
    ├── 🎫 MFA TOKEN
    └── 📜 CERTIFICATES
         ↓
    🌐 SECURE CONNECTION
         ↓
    🏢 CORPORATE RESOURCES
         ↓
    💼 PRODUCTIVE WORK`
    },
    'tunneling': {
      title: 'Tunneling',
      explanation: 'Encapsulating one network protocol within another to create secure communication channels.',
      examples: 'VPN tunnels, SSH tunneling, GRE tunnels, MPLS tunnels, encrypted data channels',
      mnemonic: '🚇 TUNNEL = Transport Using Network, Networks, Encapsulated, Layered\nThink of subway tunnel protecting passengers from weather.',
      visual: `
    🚇 NETWORK TUNNELING
    📦 ORIGINAL PACKET
         ↓
    📋 TUNNEL HEADER ADDED
    ├── 🔒 ENCRYPTION
    ├── 🏷️ ROUTING INFO
    └── 🛡️ PROTECTION
         ↓
    🌐 TRANSPORT NETWORK
         ↓
    📦 ORIGINAL PACKET RESTORED`
    },
    'tls': {
      title: 'Transport Layer Security (TLS)',
      explanation: 'Cryptographic protocol providing secure communication over networks, successor to SSL.',
      examples: 'HTTPS websites, email encryption, VPN protocols, API security, certificate-based authentication',
      mnemonic: '🔒 TLS = Transport Layer Security, encrypts communication\nThink of armored truck transporting valuables.',
      visual: `
    🔒 TLS PROTOCOL
    🤝 HANDSHAKE PROCESS
    ├── 📜 CERTIFICATE EXCHANGE
    ├── 🔑 KEY AGREEMENT
    └── 🛡️ CIPHER SELECTION
         ↓
    🔐 ENCRYPTED CHANNEL
         ↓
    📊 SECURE DATA TRANSFER
         ↓
    ✅ VERIFIED INTEGRITY`
    },
    'ipsec': {
      title: 'Internet Protocol Security (IPSec)',
      explanation: 'Suite of protocols for securing Internet Protocol communications through authentication and encryption.',
      examples: 'Site-to-site VPNs, network layer encryption, ESP protocol, AH protocol, tunnel mode',
      mnemonic: '🛡️ IPSEC = Internet Protocol Security, network layer protection\nThink of armoring entire network packets.',
      visual: `
    🛡️ IPSEC PROTECTION
    📦 IP PACKET
         ↓
    🔐 IPSEC PROCESSING
    ├── 🔑 AUTHENTICATION
    ├── 🛡️ ENCRYPTION
    ├── 📊 INTEGRITY CHECK
    └── 🚫 REPLAY PROTECTION
         ↓
    📦 SECURED PACKET
         ↓
    🌐 NETWORK TRANSPORT`
    },
    'sd-wan': {
      title: 'Software-Defined Wide Area Network (SD-WAN)',
      explanation: 'Technology that uses software-defined networking to manage and optimize WAN connections across distributed locations.',
      examples: 'Branch office connectivity, cloud access optimization, traffic steering, centralized policy management',
      mnemonic: '🌐 SDWAN = Software Defined, Wide Area Network\nThink of smart traffic management for corporate networks.',
      visual: `
    🌐 SD-WAN ARCHITECTURE
    🏢 HEADQUARTERS
         ↓
    🧠 SD-WAN CONTROLLER
    ├── 📋 CENTRALIZED POLICIES
    ├── 🎯 TRAFFIC OPTIMIZATION
    └── 📊 PERFORMANCE MONITORING
         ↓
    🌍 DISTRIBUTED BRANCHES
    ├── 🏢 Branch 1
    ├── 🏢 Branch 2
    └── ☁️ CLOUD SERVICES
         ↓
    ⚡ OPTIMIZED CONNECTIVITY`
    },
    'sase': {
      title: 'Secure Access Service Edge (SASE)',
      explanation: 'Cloud-based architecture combining network and security functions to provide secure access from anywhere.',
      examples: 'Zero trust network access, cloud security, SD-WAN integration, unified security policies',
      mnemonic: '☁️ SASE = Secure Access Service Edge, cloud-based security\nThink of security checkpoint in the cloud.',
      visual: `
    ☁️ SASE ARCHITECTURE
    👤 REMOTE USERS
    📱 MOBILE DEVICES
    🏢 BRANCH OFFICES
         ↓
    🌩️ SASE CLOUD
    ├── 🛡️ SECURITY SERVICES
    ├── 🌐 NETWORK SERVICES
    ├── 🔐 ZERO TRUST ACCESS
    └── 📊 POLICY ENGINE
         ↓
    🎯 APPLICATIONS & DATA
         ↓
    🛡️ SECURE CONNECTIVITY`
    },
    'secure-baselines-establish': {
      title: 'Establish Secure Baselines',
      explanation: 'Creating standardized security configurations that serve as the foundation for all systems.',
      examples: 'CIS benchmarks, NIST frameworks, security configuration guides, hardening standards',
      mnemonic: '📏 BASELINE = Basic Architecture, Security, Expectations, Level, Infrastructure, Never, Exceeds\nThink of minimum security requirements.',
      visual: `
    📏 SECURE BASELINE
    📋 SECURITY STANDARDS
    ├── 🔐 PASSWORD POLICIES
    ├── 🛡️ FIREWALL RULES
    ├── 🔄 UPDATE SCHEDULES
    └── 📊 MONITORING CONFIG
         ↓
    🎯 CONSISTENT SECURITY
         ↓
    📈 COMPLIANCE READY`
    },
    'secure-baselines-deploy': {
      title: 'Deploy Secure Baselines',
      explanation: 'Implementing and rolling out standardized security configurations across all systems.',
      examples: 'Automated deployment tools, configuration management, group policy, infrastructure as code',
      mnemonic: '🚀 DEPLOY = Distribute Every Policy, Logically, Operationally, Yearlong\nThink of rolling out security updates.',
      visual: `
    🚀 BASELINE DEPLOYMENT
    📋 APPROVED BASELINE
         ↓
    🤖 AUTOMATION TOOLS
    ├── 📦 CONFIG PACKAGES
    ├── 🔄 MASS DEPLOYMENT
    └── ✅ VERIFICATION
         ↓
    🌐 ORGANIZATION-WIDE
         ↓
    🛡️ UNIFORM SECURITY`
    },
    'secure-baselines-maintain': {
      title: 'Maintain Secure Baselines',
      explanation: 'Ongoing management and updates of security baselines to address new threats and requirements.',
      examples: 'Regular reviews, threat intelligence updates, compliance changes, security patches',
      mnemonic: '🔧 MAINTAIN = Monitor Always, Inspect, Never Trust, All Is Normal\nThink of regular car maintenance.',
      visual: `
    🔧 BASELINE MAINTENANCE
    ⏰ SCHEDULED REVIEW
         ↓
    🔍 ASSESS CHANGES
    ├── 🆕 NEW THREATS
    ├── 📜 COMPLIANCE UPDATES
    └── 🔧 TECH CHANGES
         ↓
    📝 UPDATE BASELINES
         ↓
    🔄 REDEPLOY CHANGES`
    },
    'mobile-device-hardening': {
      title: 'Mobile Device Hardening',
      explanation: 'Security measures applied to smartphones and tablets to protect against threats.',
      examples: 'Device encryption, app restrictions, remote wipe, screen locks, VPN enforcement',
      mnemonic: '📱 MOBILE = Many Options, Bring, Individual, Laptop, Everywhere\nThink of securing phones like miniature computers.',
      visual: `
    📱 MOBILE HARDENING
    📲 MOBILE DEVICE
    ├── 🔐 STRONG PASSCODE
    ├── 🔒 DEVICE ENCRYPTION
    ├── 🚫 APP RESTRICTIONS
    ├── 📍 LOCATION TRACKING
    └── 🗑️ REMOTE WIPE
         ↓
    🛡️ SECURE MOBILE
         ↓
    💼 BUSINESS READY`
    },
    'workstation-hardening': {
      title: 'Workstation Hardening',
      explanation: 'Security configurations applied to desktop and laptop computers to reduce attack surface.',
      examples: 'Disable unused services, software restrictions, user account control, endpoint protection',
      mnemonic: '💻 WORKSTATION = Working Optimally, Really Keeps, System, Total, Access, Totally, Isolated, Operationally, Normalized\nThink of fortress desktop.',
      visual: `
    💻 WORKSTATION HARDENING
    🖥️ DESKTOP COMPUTER
    ├── 🚫 DISABLE UNUSED SERVICES
    ├── 🛡️ ANTIVIRUS/EDR
    ├── 🔐 UAC ENABLED
    ├── 🔄 AUTO-UPDATES
    └── 🔒 USER RESTRICTIONS
         ↓
    🛡️ HARDENED ENDPOINT
         ↓
    💼 SECURE PRODUCTIVITY`
    },
    'network-device-hardening': {
      title: 'Network Device Hardening (Switches/Routers)',
      explanation: 'Security configurations for network infrastructure devices to prevent unauthorized access.',
      examples: 'Default password changes, SNMP security, unused port shutdown, firmware updates',
      mnemonic: '🌐 NETWORK = Never Ever, Trust, Wireless, Obviously, Routers, Keep\nThink of securing network backbone.',
      visual: `
    🌐 NETWORK HARDENING
    🔧 NETWORK DEVICE
    ├── 🔑 CHANGE DEFAULTS
    ├── 🚫 DISABLE UNUSED PORTS
    ├── 🔒 ENABLE ENCRYPTION
    ├── 📊 SECURE MANAGEMENT
    └── 🔄 FIRMWARE UPDATES
         ↓
    🛡️ SECURED INFRASTRUCTURE
         ↓
    🌐 RELIABLE NETWORK`
    },
    'byod': {
      title: 'Bring Your Own Device (BYOD)',
      explanation: 'Policy allowing employees to use personal devices for work purposes with appropriate security controls.',
      examples: 'Personal smartphones, tablets, laptops, mobile device management, containerization',
      mnemonic: '📱 BYOD = Bring Your Own Device, personal meets professional\nThink of using personal phone for work email.',
      visual: `
    📱 BYOD DEPLOYMENT
    👤 EMPLOYEE
    "I want to use my phone"
         ↓
    📋 BYOD POLICY
    ├── 📱 DEVICE ENROLLMENT
    ├── 🛡️ SECURITY CONTROLS
    ├── 🔐 CONTAINERIZATION
    └── 📊 MONITORING
         ↓
    💼 SECURE WORK ACCESS
         ↓
    😊 EMPLOYEE SATISFACTION`
    },
    'cope': {
      title: 'Corporate-Owned, Personally Enabled (COPE)',
      explanation: 'Corporate devices that employees can use for both business and limited personal activities.',
      examples: 'Company phones with personal apps allowed, managed corporate devices, dual-use tablets',
      mnemonic: '🏢 COPE = Corporate Owned, Personally Enabled, company device for dual use\nThink of company car you can drive home.',
      visual: `
    🏢 COPE MODEL
    🏢 CORPORATE IT
    "Here's your work phone"
         ↓
    📱 COMPANY DEVICE
    ├── 💼 BUSINESS APPS
    ├── 🎮 APPROVED PERSONAL APPS
    ├── 🛡️ SECURITY POLICIES
    └── 📊 CORPORATE CONTROL
         ↓
    ⚖️ BALANCED USAGE
         ↓
    🎯 EMPLOYEE CONVENIENCE`
    },
    'cyod': {
      title: 'Choose Your Own Device (CYOD)',
      explanation: 'Corporate program allowing employees to select devices from a pre-approved list.',
      examples: 'Device catalog selection, approved models list, standardized configurations, bulk purchasing',
      mnemonic: '🎯 CYOD = Choose Your Own Device, limited options\nThink of restaurant menu with set choices.',
      visual: `
    🎯 CYOD PROGRAM
    📋 APPROVED DEVICE LIST
    ├── 📱 iPhone 13/14/15
    ├── 📱 Samsung Galaxy S23
    └── 💻 Dell Laptop Models
         ↓
    👤 EMPLOYEE CHOOSES
         ↓
    🏢 CORPORATE PURCHASE
         ↓
    🛡️ STANDARDIZED SECURITY`
    },
    'data-types-regulated': {
      title: 'Regulated Data',
      explanation: 'Information subject to specific legal or regulatory requirements for handling and protection.',
      examples: 'HIPAA health records, PCI credit card data, FERPA education records, GDPR personal data',
      mnemonic: '📜 REGULATED = Rules, Everyone, Governs, Uses, Laws, All, Totally, Enforced, Data\nThink of data with legal handcuffs.',
      visual: `
    📜 REGULATED DATA
    🏥 HEALTHCARE (HIPAA)
    💳 FINANCIAL (PCI-DSS)
    🎓 EDUCATION (FERPA)
    🌍 PERSONAL (GDPR)
         ↓
    ⚖️ LEGAL REQUIREMENTS
         ↓
    🛡️ MANDATORY PROTECTION`
    },
    'data-types-trade-secret': {
      title: 'Trade Secret Data',
      explanation: 'Confidential business information that provides competitive advantage and is not publicly known.',
      examples: 'Secret formulas, proprietary algorithms, customer lists, manufacturing processes, business strategies',
      mnemonic: '🤐 TRADE = Totally Restricted, Always Discrete, Everyone forbidden\nThink of Coca-Cola secret recipe.',
      visual: `
    🤐 TRADE SECRET
    🏭 COCA-COLA FORMULA
    ├── 🔒 VAULT STORAGE
    ├── 👥 LIMITED ACCESS
    ├── 📝 NDAs REQUIRED
    └── 🚫 NEVER PUBLIC
         ↓
    💰 COMPETITIVE ADVANTAGE
         ↓
    🏆 BUSINESS SUCCESS`
    },
    'data-types-intellectual-property': {
      title: 'Intellectual Property Data',
      explanation: 'Creative works and innovations protected by patents, trademarks, copyrights, or trade secrets.',
      examples: 'Patents, copyrights, trademarks, design documents, software source code, research data',
      mnemonic: '🧠 IP = Intellectual Property, creative ownership\nThink of protecting your brilliant ideas.',
      visual: `
    🧠 INTELLECTUAL PROPERTY
    📋 PATENTS
    ©️ COPYRIGHTS
    ™️ TRADEMARKS
    💻 SOURCE CODE
         ↓
    ⚖️ LEGAL PROTECTION
         ↓
    💰 MONETARY VALUE`
    },
    'data-types-legal': {
      title: 'Legal Information Data',
      explanation: 'Data related to legal proceedings, contracts, compliance, and attorney-client communications.',
      examples: 'Legal contracts, court documents, attorney communications, compliance records, litigation holds',
      mnemonic: '⚖️ LEGAL = Lawyers, Everyone, Governs, All, Lawsuits\nThink of lawyer briefcase contents.',
      visual: `
    ⚖️ LEGAL INFORMATION
    📋 CONTRACTS
    🏛️ COURT DOCUMENTS
    👨‍💼 ATTORNEY COMMUNICATIONS
    📊 COMPLIANCE RECORDS
         ↓
    🔒 ATTORNEY-CLIENT PRIVILEGE
         ↓
    ⚖️ LEGAL PROTECTION`
    },
    'data-types-financial': {
      title: 'Financial Information Data',
      explanation: 'Monetary and financial data requiring special protection due to fraud and privacy risks.',
      examples: 'Bank account numbers, credit card data, financial statements, tax records, payroll information',
      mnemonic: '💰 FINANCIAL = Funds, Income, Numbers, Always, Need, Critical, Important, All, Locked\nThink of bank vault contents.',
      visual: `
    💰 FINANCIAL INFORMATION
    💳 CREDIT CARD NUMBERS
    🏦 BANK ACCOUNTS
    📊 FINANCIAL STATEMENTS
    💸 TAX RECORDS
         ↓
    🔒 HIGH-VALUE TARGET
         ↓
    🛡️ STRICT PROTECTION`
    },
    'data-types-human-readable': {
      title: 'Human-readable vs Non-human-readable Data',
      explanation: 'Distinction between data formats that humans can directly interpret versus those requiring processing.',
      examples: 'Human: text documents, images. Non-human: encrypted files, binary data, database records',
      mnemonic: '👁️ HUMAN = Humans Usually, Make, All, Naturally readable vs 🤖 MACHINE = Many Applications, Computers, Handle, Information, Never, Everyone\nThink of reading book vs barcode.',
      visual: `
    👁️ HUMAN-READABLE      🤖 NON-HUMAN-READABLE
    📖 TEXT DOCUMENTS      📊 BINARY DATA
    🖼️ IMAGES/VIDEOS       🔢 ENCRYPTED FILES
    📧 EMAIL TEXT          💾 DATABASE RECORDS
    📝 REPORTS             🔒 HASHED PASSWORDS
         ↓                      ↓
    👤 DIRECT READING      🖥️ PROCESSING REQUIRED`
    },
    'data-classifications-sensitive': {
      title: 'Sensitive Data Classification',
      explanation: 'Information that could cause harm to individuals or organizations if disclosed inappropriately.',
      examples: 'Employee personal information, customer data, internal communications, project details',
      mnemonic: '⚠️ SENSITIVE = Security, Everyone, Needs, Strong, Important, Trust, Information, Vital, Everyone\nThink of handling with care labels.',
      visual: `
    ⚠️ SENSITIVE DATA
    👤 EMPLOYEE SSNs
    🏥 MEDICAL RECORDS
    📧 INTERNAL EMAILS
    📊 BUSINESS PLANS
         ↓
    🔒 CONTROLLED ACCESS
         ↓
    ⚖️ PRIVACY PROTECTION`
    },
    'data-classifications-confidential': {
      title: 'Confidential Data Classification',
      explanation: 'Highly sensitive information requiring strict access controls and causing serious damage if disclosed.',
      examples: 'Executive communications, merger plans, security procedures, proprietary research',
      mnemonic: '🤐 CONFIDENTIAL = Can Only, Not, Freely, Information, Disclosed, Everyone, Never, Talks, Internally, All, Locked\nThink of top secret documents.',
      visual: `
    🤐 CONFIDENTIAL DATA
    🎯 MERGER PLANS
    🔐 SECURITY PROCEDURES
    💼 EXECUTIVE COMMUNICATIONS
    🧪 RESEARCH DATA
         ↓
    🚫 NEED-TO-KNOW BASIS
         ↓
    🛡️ MAXIMUM PROTECTION`
    },
    'data-classifications-public': {
      title: 'Public Data Classification',
      explanation: 'Information that can be freely shared without risk or harm to the organization.',
      examples: 'Marketing materials, press releases, public websites, published research, contact information',
      mnemonic: '📢 PUBLIC = Publicly, Usually, Broadcast, Lots, Information, Clearly\nThink of newspaper articles.',
      visual: `
    📢 PUBLIC DATA
    📰 PRESS RELEASES
    🌐 COMPANY WEBSITE
    📈 MARKETING MATERIALS
    📞 CONTACT INFO
         ↓
    ✅ NO ACCESS RESTRICTIONS
         ↓
    🌍 FREELY SHAREABLE`
    },
    'data-classifications-restricted': {
      title: 'Restricted Data Classification',
      explanation: 'Information with the highest level of protection, causing exceptional damage if disclosed.',
      examples: 'National security data, trade secrets, personal identification, critical infrastructure details',
      mnemonic: '🔒 RESTRICTED = Really, Everyone, Seriously, Takes, Rigorously, Information, Controls, Totally, Everyone, Denied\nThink of classified military documents.',
      visual: `
    🔒 RESTRICTED DATA
    🏛️ NATIONAL SECURITY
    🤐 TRADE SECRETS
    🆔 PERSONAL ID DATA
    🏭 CRITICAL INFRASTRUCTURE
         ↓
    🚫 MINIMAL ACCESS
         ↓
    🛡️ HIGHEST PROTECTION`
    },
    'data-classifications-private': {
      title: 'Private Data Classification',
      explanation: 'Personal information belonging to individuals requiring protection under privacy laws.',
      examples: 'Personal health information, social security numbers, addresses, phone numbers, private communications',
      mnemonic: '🏠 PRIVATE = Personal, Restricted, Individual, Very, All, Totally, Everyone\nThink of personal diary entries.',
      visual: `
    🏠 PRIVATE DATA
    🏥 HEALTH RECORDS
    🆔 SOCIAL SECURITY
    📧 PERSONAL EMAILS
    📞 PHONE NUMBERS
         ↓
    ⚖️ PRIVACY LAWS
         ↓
    🛡️ INDIVIDUAL PROTECTION`
    },
    'data-classifications-critical': {
      title: 'Critical Data Classification',
      explanation: 'Information essential for business operations that could cause severe disruption if unavailable.',
      examples: 'Core business systems, customer databases, financial records, operational procedures',
      mnemonic: '🎯 CRITICAL = Can Really, Impact, Totally, Infrastructure, Can, All, Lose\nThink of life support systems.',
      visual: `
    🎯 CRITICAL DATA
    💾 CORE DATABASES
    💰 FINANCIAL SYSTEMS
    📊 OPERATIONAL DATA
    🏭 PRODUCTION SYSTEMS
         ↓
    ⚡ BUSINESS ESSENTIAL
         ↓
    🔄 HIGH AVAILABILITY REQUIRED`
    },
    'data-at-rest': {
      title: 'Data at Rest',
      explanation: 'Information stored on persistent media like hard drives, databases, or backup systems.',
      examples: 'Database files, encrypted hard drives, backup tapes, stored documents, archived emails',
      mnemonic: '💾 REST = Really Everything, Stored, Totally stationary\nThink of books on shelf.',
      visual: `
    💾 DATA AT REST
    🏗️ STORAGE SYSTEMS
    ├── 💽 HARD DRIVES
    ├── 🗄️ DATABASES
    ├── 📼 BACKUP TAPES
    └── ☁️ CLOUD STORAGE
         ↓
    🔒 ENCRYPTION NEEDED
         ↓
    🛡️ PERSISTENT PROTECTION`
    },
    'data-in-transit': {
      title: 'Data in Transit',
      explanation: 'Information actively moving across networks or between systems.',
      examples: 'Email transmission, file transfers, web browsing, API calls, network communications',
      mnemonic: '🚛 TRANSIT = Traveling, Really, All, Networks, Systems, Information, Travels\nThink of packages being shipped.',
      visual: `
    🚛 DATA IN TRANSIT
    💻 SOURCE SYSTEM
         ↓
    🌐 NETWORK TRANSMISSION
    ├── 📧 EMAIL SEND
    ├── 📁 FILE TRANSFER
    ├── 🌍 WEB TRAFFIC
    └── 📡 API CALLS
         ↓
    🎯 DESTINATION SYSTEM
         ↓
    🔒 ENCRYPTION REQUIRED`
    },
    'data-in-use': {
      title: 'Data in Use',
      explanation: 'Information actively being processed by applications, in memory, or being manipulated by users.',
      examples: 'RAM contents, CPU registers, active documents, running applications, user interactions',
      mnemonic: '⚡ USE = User Systems, Everything processing\nThink of chef cooking with ingredients.',
      visual: `
    ⚡ DATA IN USE
    🧠 SYSTEM MEMORY
    ├── 💻 CPU PROCESSING
    ├── 📝 ACTIVE DOCUMENTS
    ├── 🖥️ USER INTERACTION
    └── 🔄 RUNNING APPS
         ↓
    ⚠️ MOST VULNERABLE STATE
         ↓
    🛡️ RUNTIME PROTECTION`
    },
    'data-sovereignty': {
      title: 'Data Sovereignty',
      explanation: 'Legal concept that data is subject to the laws and governance of the country where it is collected or stored.',
      examples: 'EU GDPR requirements, Russian data localization, Chinese cybersecurity law, national data residency',
      mnemonic: '🏛️ SOVEREIGNTY = State Obviously, Virtually, Everything, Regionally, Everyone, Information, Governs, Never, Travels, Yearly\nThink of digital borders.',
      visual: `
    🏛️ DATA SOVEREIGNTY
    🌍 DATA COLLECTION (Country A)
         ↓
    🏢 DATA STORAGE (Country B)
         ↓
    ⚖️ LEGAL CONFLICT
    ├── 🇪🇺 EU LAWS APPLY?
    ├── 🇺🇸 US LAWS APPLY?
    └── 🇨🇳 CHINESE LAWS APPLY?
         ↓
    📍 LOCATION DETERMINES LAW`
    },
    'geolocation-data': {
      title: 'Geolocation Data Considerations',
      explanation: 'Physical location factors affecting data protection, legal compliance, and access controls.',
      examples: 'Data residency laws, cross-border transfers, regional compliance, latency considerations',
      mnemonic: '🗺️ GEO = Geographic, Everyone, Operates differently\nThink of GPS coordinates for data.',
      visual: `
    🗺️ GEOLOCATION FACTORS
    📍 DATA LOCATION
    ├── 🏛️ LOCAL LAWS
    ├── 🌐 TRANSFER RESTRICTIONS
    ├── ⚡ LATENCY IMPACT
    └── 🛡️ REGIONAL COMPLIANCE
         ↓
    🎯 LOCATION-BASED DECISIONS
         ↓
    ⚖️ LEGAL COMPLIANCE`
    },
    'geographic-restrictions': {
      title: 'Geographic Restrictions',
      explanation: 'Access controls based on physical location to comply with laws or business requirements.',
      examples: 'IP geoblocking, regional content restrictions, export controls, data localization requirements',
      mnemonic: '🌍 GEOGRAPHIC = Geographically, Everyone, Obviously, Governs, Regionally, All, People, Have, Individual, Countries\nThink of country-specific Netflix content.',
      visual: `
    🌍 GEOGRAPHIC RESTRICTIONS
    👤 USER FROM COUNTRY X
         ↓
    🔍 LOCATION CHECK
         ↓
    📋 POLICY EVALUATION
    ├── ✅ ALLOWED COUNTRIES
    ├── 🚫 RESTRICTED REGIONS
    └── ⚖️ LEGAL COMPLIANCE
         ↓
    🎯 ACCESS DECISION`
    },
    'data-encryption': {
      title: 'Data Encryption Methods',
      explanation: 'Cryptographic techniques to protect data confidentiality by converting it into unreadable format.',
      examples: 'AES encryption, RSA encryption, database encryption, file encryption, full disk encryption',
      mnemonic: '🔐 ENCRYPT = Everyone, Never, Can, Read, Your, Personal, Things\nThink of secret code only you understand.',
      visual: `
    🔐 DATA ENCRYPTION
    📄 PLAINTEXT DATA
    "Sensitive Information"
         ↓
    🔑 ENCRYPTION KEY
         ↓
    🔒 ENCRYPTED DATA
    "AkD#9$mF@2x!"
         ↓
    🛡️ PROTECTED FROM THREATS`
    },
    'data-hashing': {
      title: 'Data Hashing Methods',
      explanation: 'One-way cryptographic functions that create fixed-size fingerprints for data integrity verification.',
      examples: 'SHA-256, MD5 checksums, password hashing, file integrity checks, digital signatures',
      mnemonic: '#️⃣ HASH = Hash Always, Shows, Honestly data fingerprint\nThink of unique fingerprint for files.',
      visual: `
    #️⃣ DATA HASHING
    📄 ORIGINAL DATA
    "Any size input"
         ↓
    🔄 HASH FUNCTION (SHA-256)
         ↓
    #️⃣ HASH VALUE
    "Fixed size output: a1b2c3..."
         ↓
    ✅ INTEGRITY VERIFICATION`
    },
    'data-tokenization': {
      title: 'Data Tokenization Methods',
      explanation: 'Replacing sensitive data with non-sensitive tokens that have no exploitable meaning.',
      examples: 'Payment card tokenization, database tokenization, format-preserving tokens, vault-based systems',
      mnemonic: '🎫 TOKEN = Take Original, Keep, Everything, Numbered differently\nThink of coat check ticket instead of coat.',
      visual: `
    🎫 DATA TOKENIZATION
    💳 SENSITIVE DATA
    "4532-1234-5678-9012"
         ↓
    🏦 TOKEN VAULT
         ↓
    🎫 TOKEN
    "TOK_98765432"
         ↓
    🔒 ORIGINAL SAFELY STORED`
    },
    'data-obfuscation': {
      title: 'Data Obfuscation Methods',
      explanation: 'Techniques to make data deliberately unclear or confusing while preserving its functionality.',
      examples: 'Code obfuscation, data scrambling, noise injection, format transformation',
      mnemonic: '🌫️ OBFUSCATE = Obviously, Blur, Files, Usually, Scrambled, Confusing, All, Totally, Everything\nThink of making data foggy.',
      visual: `
    🌫️ DATA OBFUSCATION
    📝 CLEAR DATA
    "function getUserData()"
         ↓
    🔄 OBFUSCATION PROCESS
         ↓
    🌫️ OBSCURED DATA
    "var a=function b(){...}"
         ↓
    🎯 FUNCTIONAL BUT UNCLEAR`
    },
    'permission-restrictions': {
      title: 'Permission Restrictions',
      explanation: 'Access controls that limit who can view, modify, or use specific data based on roles and needs.',
      examples: 'Role-based access control, least privilege, need-to-know basis, file permissions, database ACLs',
      mnemonic: '🔐 PERMISSION = People, Everyone, Restricted, Must, Include, Strict, Security, Information, Only, Needed\nThink of VIP access only.',
      visual: `
    🔐 PERMISSION RESTRICTIONS
    👤 USER REQUEST
    "Access financial data"
         ↓
    🔍 PERMISSION CHECK
    ├── 👥 USER ROLE
    ├── 📊 DATA CLASSIFICATION  
    ├── 🎯 BUSINESS NEED
    └── ⏰ TIME RESTRICTIONS
         ↓
    ✅ GRANT/DENY ACCESS`
    },
    'load-balancing': {
      title: 'Load Balancing',
      explanation: 'Distribution of network traffic or workload across multiple servers to prevent overload and ensure optimal performance.',
      examples: 'Round-robin distribution, weighted distribution, health checks, auto-scaling groups',
      mnemonic: '⚖️ BALANCE = Bandwidth Allocation Lets All Network Computing Execute\nThink of traffic cop directing cars to different lanes.',
      visual: `
    ⚖️ LOAD BALANCER
    🌐 INCOMING TRAFFIC
         ↓
    📊 DISTRIBUTION LOGIC
    ├── 🖥️ SERVER 1 (25%)
    ├── 🖥️ SERVER 2 (25%)
    ├── 🖥️ SERVER 3 (25%)
    └── 🖥️ SERVER 4 (25%)
         ↓
    📈 OPTIMAL PERFORMANCE`
    },
    'clustering': {
      title: 'Clustering',
      explanation: 'Group of interconnected servers working together as a single system to provide high availability and fault tolerance.',
      examples: 'Active/passive clusters, active/active clusters, shared storage, heartbeat monitoring',
      mnemonic: '🤝 CLUSTER = Connected Linked Units Sharing Together Excellent Reliability\nThink of team huddle - stronger together.',
      visual: `
    🤝 SERVER CLUSTER
    🖥️ NODE 1 ←→ 🖥️ NODE 2
         ↕️           ↕️
    💾 SHARED STORAGE
         ↕️           ↕️
    🖥️ NODE 3 ←→ 🖥️ NODE 4
         ↓
    ❤️ HEARTBEAT MONITORING
         ↓
    🛡️ FAULT TOLERANCE`
    },
    'hot-site': {
      title: 'Hot Site',
      explanation: 'Fully operational backup facility with current data, systems, and connectivity ready for immediate use during disasters.',
      examples: 'Real-time data replication, identical hardware, immediate switchover, minimal RTO/RPO',
      mnemonic: '🔥 HOT = Hardware Operating Today, ready immediately\nThink of hot coffee - instantly ready to drink.',
      visual: `
    🔥 HOT SITE
    🏢 PRIMARY SITE ←→ 🏢 BACKUP SITE
         ↓                ↓
    💾 LIVE DATA ←→ 💾 REPLICATED DATA
         ↓                ↓
    ⚡ DISASTER ───→ 🔀 INSTANT FAILOVER
         ↓                ↓
    🛑 DOWNTIME    ✅ IMMEDIATE OPERATION`
    },
    'cold-site': {
      title: 'Cold Site',
      explanation: 'Basic backup facility with space, power, and connectivity but no pre-installed systems or current data.',
      examples: 'Empty server room, basic infrastructure, requires hardware installation and data restoration',
      mnemonic: '❄️ COLD = Can Only Load Data, needs time to warm up\nThink of cold car - needs time to start.',
      visual: `
    ❄️ COLD SITE
    🏢 PRIMARY SITE    🏢 EMPTY FACILITY
         ↓                ↓
    💾 LIVE DATA       🔌 BASIC POWER
         ↓                ↓
    ⚡ DISASTER ───→ 📦 INSTALL HARDWARE
         ↓                ↓
    🛑 DOWNTIME ───→ 💾 RESTORE DATA
         ↓                ↓
    ⏱️ DAYS/WEEKS ───→ ✅ OPERATION`
    },
    'warm-site': {
      title: 'Warm Site',
      explanation: 'Partially configured backup facility with some systems and infrastructure but requires data restoration and configuration.',
      examples: 'Pre-installed servers, network equipment ready, requires data sync and application setup',
      mnemonic: '🌡️ WARM = Working And Ready, Minimally configured\nThink of warm house - mostly ready, needs finishing touches.',
      visual: `
    🌡️ WARM SITE
    🏢 PRIMARY SITE    🏢 PARTIAL SETUP
         ↓                ↓
    💾 LIVE DATA       🖥️ PRE-INSTALLED
         ↓                ↓
    ⚡ DISASTER ───→ 📥 DATA RESTORATION
         ↓                ↓
    🛑 HOURS/DAYS ───→ ⚙️ CONFIGURATION
         ↓                ↓
    ⏱️ MODERATE TIME ──→ ✅ OPERATION`
    },
    'geographic-dispersion': {
      title: 'Geographic Dispersion',
      explanation: 'Distribution of IT resources across multiple geographical locations to mitigate regional disasters and ensure continuity.',
      examples: 'Multiple data centers, different time zones, natural disaster protection, regulatory compliance',
      mnemonic: '🌍 GEO = Geographically Everyone Operates, spread worldwide\nThink of global franchise - locations everywhere.',
      visual: `
    🌍 GEOGRAPHIC DISPERSION
    🏢 SITE A (EAST)
         ↕️
    🌐 GLOBAL NETWORK
         ↕️
    🏢 SITE B (WEST) ←→ 🏢 SITE C (INTL)
         ↓
    🌪️ REGIONAL DISASTER
         ↓
    ✅ OTHER SITES CONTINUE
         ↓
    🛡️ BUSINESS CONTINUITY`
    },
    'platform-diversity': {
      title: 'Platform Diversity',
      explanation: 'Using different operating systems, hardware, and software platforms to reduce single points of failure and security risks.',
      examples: 'Windows + Linux servers, different vendors, varied architectures, multi-cloud providers',
      mnemonic: '🌈 DIVERSE = Different Infrastructure Varies Everyone Risk, Spread Everything\nThink of investment portfolio - diversify to reduce risk.',
      visual: `
    🌈 PLATFORM DIVERSITY
    🖥️ PLATFORM A (WINDOWS)
         ↕️
    🔀 LOAD BALANCER
         ↕️
    🐧 PLATFORM B (LINUX) ←→ ☁️ PLATFORM C (CLOUD)
         ↓
    ⚡ SINGLE PLATFORM FAILURE
         ↓
    ✅ OTHER PLATFORMS CONTINUE
         ↓
    🛡️ REDUCED RISK`
    },
    'multi-cloud-systems': {
      title: 'Multi-Cloud Systems',
      explanation: 'Using multiple cloud service providers to avoid vendor lock-in and improve availability and disaster recovery.',
      examples: 'AWS + Azure + GCP, hybrid cloud, cloud bursting, data portability, cost optimization',
      mnemonic: '☁️ MULTI = Multiple Users Need Total Independence\nThink of multiple ISPs - if one fails, use another.',
      visual: `
    ☁️ MULTI-CLOUD ARCHITECTURE
    🏢 ORGANIZATION
         ↓
    🔀 CLOUD ORCHESTRATION
    ├── ☁️ AWS (30%)
    ├── ☁️ AZURE (40%)
    └── ☁️ GCP (30%)
         ↓
    ⚡ SINGLE CLOUD OUTAGE
         ↓
    ✅ OTHER CLOUDS AVAILABLE
         ↓
    🛡️ VENDOR INDEPENDENCE`
    },
    'continuity-of-operations': {
      title: 'Continuity of Operations',
      explanation: 'Maintaining essential business functions during and after disruptive events through planning and preparation.',
      examples: 'Business continuity plan, emergency procedures, alternative work sites, communication plans',
      mnemonic: '🔄 COOP = Continuity Operations Optimize Performance\nThink of relay race - seamless handoff keeps going.',
      visual: `
    🔄 CONTINUITY OF OPERATIONS
    🏢 NORMAL OPERATIONS
         ↓
    ⚡ DISRUPTIVE EVENT
         ↓
    📋 ACTIVATION OF PLAN
    ├── 🏠 REMOTE WORK
    ├── 📞 COMMUNICATION
    └── 🖥️ ALTERNATE SITES
         ↓
    ✅ ESSENTIAL FUNCTIONS
         ↓
    🔄 BUSINESS CONTINUES`
    },
    'capacity-planning': {
      title: 'Capacity Planning',
      explanation: 'Process of determining resource requirements to meet future demand and maintain performance levels.',
      examples: 'CPU utilization forecasting, storage growth planning, bandwidth requirements, user growth projections',
      mnemonic: '📊 CAPACITY = Can Anyone Plan And Calculate Infrastructure Totally Yearly\nThink of restaurant planning - need enough tables for busy nights.',
      visual: `
    📊 CAPACITY PLANNING
    📈 CURRENT USAGE
         ↓
    🔮 FUTURE PROJECTIONS
    ├── 👥 USER GROWTH
    ├── 💾 DATA EXPANSION
    └── 🖥️ PROCESSING NEEDS
         ↓
    📋 RESOURCE PLANNING
         ↓
    🛡️ ADEQUATE CAPACITY`
    },
    'people-capacity': {
      title: 'People Capacity Planning',
      explanation: 'Planning for adequate staffing levels, skills, and training to maintain operations during normal and emergency situations.',
      examples: 'Staff cross-training, succession planning, 24/7 coverage, emergency response teams',
      mnemonic: '👥 PEOPLE = Personnel Everyone Opens Performance, Learning Expectations\nThink of sports team - need bench players ready.',
      visual: `
    👥 PEOPLE CAPACITY
    👨‍💼 CURRENT STAFF
         ↓
    📚 SKILLS ASSESSMENT
    ├── 🎓 TRAINING NEEDS
    ├── 🔄 CROSS-TRAINING
    └── 👨‍🏫 SUCCESSION PLAN
         ↓
    ⚡ EMERGENCY SITUATION
         ↓
    ✅ ADEQUATE COVERAGE`
    },
    'technology-capacity': {
      title: 'Technology Capacity Planning',
      explanation: 'Ensuring adequate computing resources, storage, and network capacity to handle current and future technology demands.',
      examples: 'Server CPU/RAM planning, storage expansion, network bandwidth, database scaling',
      mnemonic: '💻 TECH = Technology Everyone Can Handle\nThink of highway planning - need enough lanes for traffic.',
      visual: `
    💻 TECHNOLOGY CAPACITY
    🖥️ CURRENT RESOURCES
         ↓
    📊 UTILIZATION METRICS
    ├── 🧠 CPU USAGE
    ├── 💾 MEMORY
    └── 💽 STORAGE
         ↓
    📈 GROWTH PROJECTIONS
         ↓
    🔧 RESOURCE SCALING`
    },
    'infrastructure-capacity': {
      title: 'Infrastructure Capacity Planning',
      explanation: 'Planning for physical infrastructure needs including power, cooling, space, and network connectivity.',
      examples: 'Data center space, power consumption, cooling requirements, fiber capacity, rack space',
      mnemonic: '🏗️ INFRA = Infrastructure Needs Future Ready Architecture\nThink of city planning - need infrastructure for growth.',
      visual: `
    🏗️ INFRASTRUCTURE CAPACITY
    🏢 CURRENT FACILITY
         ↓
    ⚡ POWER ASSESSMENT
    ├── 🔌 ELECTRICAL LOAD
    ├── ❄️ COOLING NEEDS
    └── 📦 PHYSICAL SPACE
         ↓
    📈 EXPANSION PLANNING
         ↓
    🔧 INFRASTRUCTURE SCALING`
    },
    'tabletop-exercises': {
      title: 'Tabletop Exercises',
      explanation: 'Discussion-based sessions where team members walk through simulated emergency scenarios to test response procedures.',
      examples: 'Incident response drills, business continuity scenarios, security breach simulations, decision-making practice',
      mnemonic: '🎲 TABLETOP = Talk About Business Like Everyone Tests Operations Procedures\nThink of war games - strategy without real battle.',
      visual: `
    🎲 TABLETOP EXERCISE
    👥 RESPONSE TEAM
         ↓
    📋 SCENARIO PRESENTATION
    ├── 🚨 SIMULATED INCIDENT
    ├── 💬 DISCUSSION
    └── 🤔 DECISION POINTS
         ↓
    📝 LESSONS LEARNED
         ↓
    🔧 PLAN IMPROVEMENTS`
    },
    'failover-testing': {
      title: 'Failover Testing',
      explanation: 'Testing the automatic switching from primary to backup systems to ensure seamless continuity during failures.',
      examples: 'Database failover, server switching, network rerouting, application migration, recovery time testing',
      mnemonic: '🔀 FAILOVER = Failure Activates Infrastructure, Learning Operations Verify Everything Runs\nThink of understudy taking over - seamless performance.',
      visual: `
    🔀 FAILOVER TEST
    🖥️ PRIMARY SYSTEM
         ↓
    ⚡ SIMULATED FAILURE
         ↓
    🔄 AUTOMATIC SWITCH
         ↓
    🖥️ BACKUP SYSTEM
         ↓
    ✅ CONTINUITY VERIFIED
         ↓
    📊 PERFORMANCE METRICS`
    },
    'simulation-testing': {
      title: 'Simulation Testing',
      explanation: 'Realistic testing environments that mimic production conditions to validate system behavior under various scenarios.',
      examples: 'Load testing, stress testing, chaos engineering, disaster simulation, user behavior modeling',
      mnemonic: '🎮 SIMULATE = System In Mock Uses Live And Tests Everything\nThink of flight simulator - realistic practice without risk.',
      visual: `
    🎮 SIMULATION TESTING
    🏭 PRODUCTION MIRROR
         ↓
    🔬 CONTROLLED CONDITIONS
    ├── 📈 LOAD TESTING
    ├── ⚡ STRESS TESTING
    └── 🌪️ CHAOS TESTING
         ↓
    📊 BEHAVIOR ANALYSIS
         ↓
    🔧 SYSTEM OPTIMIZATION`
    },
    'parallel-processing': {
      title: 'Parallel Processing',
      explanation: 'Running backup systems simultaneously with production to ensure immediate availability and validate functionality.',
      examples: 'Dual active systems, synchronized databases, parallel computations, redundant processing',
      mnemonic: '⏸️ PARALLEL = Processing All Resources And Links, Like Everyone Lives\nThink of backup dancer - moving in sync with lead.',
      visual: `
    ⏸️ PARALLEL PROCESSING
    🖥️ SYSTEM A ←→ 🖥️ SYSTEM B
         ↓           ↓
    💾 DATA SYNC ←→ 💾 DATA SYNC
         ↓           ↓
    ✅ BOTH ACTIVE  ✅ BOTH READY
         ↓
    ⚡ FAILURE A ───→ 🔄 SYSTEM B CONTINUES`
    },
    'backup-onsite-offsite': {
      title: 'Onsite/Offsite Backups',
      explanation: 'Storage of backup data both locally for quick recovery and remotely for disaster protection.',
      examples: 'Local tape storage, cloud backups, remote data centers, hybrid backup strategies',
      mnemonic: '💾 BACKUP = Both Uniformly Collected, Keep Up Procedures\nThink of photo album - keep one at home, one at relatives.',
      visual: `
    💾 BACKUP STRATEGY
    🏢 PRIMARY DATA
         ↓
    🔄 BACKUP PROCESS
    ├── 💾 ONSITE BACKUP
    │   └── ⚡ FAST RECOVERY
    └── ☁️ OFFSITE BACKUP
        └── 🌪️ DISASTER PROTECTION
         ↓
    🛡️ DUAL PROTECTION`
    },
    'backup-frequency': {
      title: 'Backup Frequency',
      explanation: 'How often backup operations are performed based on data criticality and acceptable data loss tolerance.',
      examples: 'Continuous backup, daily backups, weekly full backups, incremental backups, real-time sync',
      mnemonic: '🕐 FREQUENCY = Fast Recovery Ensures Quality, Usually Everyone Needs Constant Yielding\nThink of saving documents - more important, save more often.',
      visual: `
    🕐 BACKUP FREQUENCY
    📊 DATA CRITICALITY
         ↓
    ⏰ BACKUP SCHEDULE
    ├── 🔄 CONTINUOUS (CRITICAL)
    ├── 📅 DAILY (IMPORTANT)
    └── 📆 WEEKLY (STANDARD)
         ↓
    📈 RPO OPTIMIZATION
         ↓
    🛡️ DATA PROTECTION`
    },
    'backup-encryption': {
      title: 'Backup Encryption',
      explanation: 'Protecting backup data by encrypting it both in transit and at rest to prevent unauthorized access.',
      examples: 'AES-256 encryption, encrypted tapes, secure cloud storage, key management, encrypted databases',
      mnemonic: '🔐 ENCRYPT = Everyone Needs Careful Restoration, Your Personal Tapes\nThink of diary with lock - private even when stored.',
      visual: `
    🔐 BACKUP ENCRYPTION
    💾 ORIGINAL DATA
         ↓
    🔒 ENCRYPTION PROCESS
    ├── 🔑 KEY GENERATION
    └── 🛡️ AES-256 CIPHER
         ↓
    💾 ENCRYPTED BACKUP
         ↓
    📦 SECURE STORAGE
         ↓
    🔓 DECRYPTION FOR RESTORE`
    },
    'backup-snapshots': {
      title: 'Backup Snapshots',
      explanation: 'Point-in-time copies of data or system state that can be quickly restored to previous conditions.',
      examples: 'VM snapshots, database snapshots, filesystem snapshots, incremental changes, rollback points',
      mnemonic: '📸 SNAPSHOT = Save Notable And Professional, System Has One Time\nThink of camera - capture moment in time.',
      visual: `
    📸 BACKUP SNAPSHOTS
    💾 SYSTEM STATE T1
         ↓
    📸 SNAPSHOT CREATION
         ↓
    💾 SYSTEM STATE T2
         ↓
    📸 SNAPSHOT CREATION
         ↓
    ⚡ SYSTEM FAILURE
         ↓
    🔄 RESTORE TO T1/T2`
    },
    'backup-recovery': {
      title: 'Backup Recovery',
      explanation: 'Process of restoring data and systems from backup copies to return to operational state after failure.',
      examples: 'Full restore, partial restore, file-level recovery, bare metal recovery, database restoration',
      mnemonic: '🔄 RECOVERY = Restore Everyone Carefully, Operations Verify Everything Restored Yielding\nThink of phoenix - rising from ashes.',
      visual: `
    🔄 BACKUP RECOVERY
    ⚡ DATA LOSS EVENT
         ↓
    🔍 ASSESS DAMAGE
         ↓
    💾 SELECT BACKUP
         ↓
    🔄 RESTORATION PROCESS
         ↓
    ✅ VALIDATE INTEGRITY
         ↓
    🏃 RESUME OPERATIONS`
    },
    'backup-replication': {
      title: 'Backup Replication',
      explanation: 'Creating and maintaining multiple copies of data across different locations or systems for redundancy.',
      examples: 'Real-time replication, asynchronous replication, geographic replication, cross-site backups',
      mnemonic: '🔁 REPLICATE = Regularly Everyone Provides Live Information, Creating All Together Everything\nThink of copy machine - multiple identical copies.',
      visual: `
    🔁 BACKUP REPLICATION
    💾 PRIMARY DATA
         ↓
    🔄 REPLICATION ENGINE
    ├── 💾 REPLICA 1 (LOCAL)
    ├── 💾 REPLICA 2 (REMOTE)
    └── 💾 REPLICA 3 (CLOUD)
         ↓
    ⚡ PRIMARY FAILURE
         ↓
    ✅ REPLICAS AVAILABLE`
    },
    'backup-journaling': {
      title: 'Backup Journaling',
      explanation: 'Recording all changes to data in a log file to enable point-in-time recovery and transaction rollback.',
      examples: 'Database transaction logs, filesystem journals, change tracking, audit trails, incremental backups',
      mnemonic: '📝 JOURNAL = Just One User, Record Now And Later\nThink of ship log - record every event chronologically.',
      visual: `
    📝 BACKUP JOURNALING
    💾 DATA OPERATIONS
         ↓
    📋 TRANSACTION LOG
    ├── ✏️ INSERT RECORD
    ├── 🔄 UPDATE RECORD
    └── 🗑️ DELETE RECORD
         ↓
    ⚡ SYSTEM FAILURE
         ↓
    🔄 REPLAY JOURNAL
         ↓
    ✅ DATA CONSISTENCY`
    },
    'power-generators': {
      title: 'Power Generators',
      explanation: 'Backup power systems that automatically provide electricity during utility power outages to maintain operations.',
      examples: 'Diesel generators, natural gas generators, automatic transfer switches, fuel management, load testing',
      mnemonic: '⚡ GENERATOR = Generated Electricity Never Ends, Reliable And Totally Outstanding Reliability\nThink of backup singer - steps in when main fails.',
      visual: `
    ⚡ POWER GENERATOR
    🔌 UTILITY POWER
         ↓
    ⚡ POWER OUTAGE
         ↓
    🔄 AUTOMATIC SWITCH
         ↓
    🏭 GENERATOR START
         ↓
    ⚡ BACKUP POWER
         ↓
    🏢 OPERATIONS CONTINUE`
    },
    'ups-power': {
      title: 'Uninterruptible Power Supply (UPS)',
      explanation: 'Battery backup system providing immediate power during outages to prevent data loss and allow graceful shutdown.',
      examples: 'Online UPS, line-interactive UPS, standby UPS, battery backup, surge protection, power conditioning',
      mnemonic: '🔋 UPS = Uninterrupted Power Supply, always ready\nThink of spare battery - instant power when needed.',
      visual: `
    🔋 UPS SYSTEM
    🔌 UTILITY POWER
         ↓
    🔋 UPS CHARGING
         ↓
    ⚡ POWER OUTAGE
         ↓
    🔋 BATTERY POWER
         ↓
    🖥️ SYSTEMS PROTECTED
         ↓
    💾 GRACEFUL SHUTDOWN`
    },
    'hardening-mobile-devices': {
      title: 'Mobile Device Hardening',
      explanation: 'Securing smartphones and tablets by implementing security controls and removing unnecessary features.',
      examples: 'Screen locks, encryption, app restrictions, VPN requirements, remote wipe, MDM enrollment',
      mnemonic: '📱 MOBILE = Monitor, Operations, Block, Inappropriate, Lock, Everything securely\nThink of armor for knight - protection while mobile.',
      visual: `
    📱 MOBILE HARDENING
    📱 DEVICE DEPLOYMENT
         ↓
    🔒 SECURITY MEASURES
    ├── 🔐 SCREEN LOCK
    ├── 🔒 ENCRYPTION
    ├── 🚫 APP RESTRICTIONS
    └── 🌐 VPN REQUIRED
         ↓
    📊 MDM ENROLLMENT
         ↓
    🛡️ SECURED DEVICE`
    },
    'hardening-workstations': {
      title: 'Workstation Hardening',
      explanation: 'Securing desktop and laptop computers by removing unnecessary services and implementing security controls.',
      examples: 'Disable unused services, remove unnecessary software, enable firewalls, patch management, antivirus',
      mnemonic: '💻 WORKSTATION = Working, Operations, Require, Keeping, Security, Totally, Applied, Through, Implementation, Operations, Never\nThink of fortress walls - multiple layers of protection.',
      visual: `
    💻 WORKSTATION HARDENING
    🖥️ BASE SYSTEM
         ↓
    🔧 HARDENING PROCESS
    ├── 🚫 REMOVE BLOATWARE
    ├── 🔒 DISABLE SERVICES
    ├── 🔥 ENABLE FIREWALL
    └── 🛡️ INSTALL ANTIVIRUS
         ↓
    ✅ HARDENED WORKSTATION
         ↓
    🛡️ REDUCED ATTACK SURFACE`
    },
    'hardening-switches': {
      title: 'Network Switch Hardening',
      explanation: 'Securing network switches by disabling unused ports, implementing access controls, and configuring security features.',
      examples: 'Disable unused ports, VLAN segmentation, port security, DHCP snooping, spanning tree protection',
      mnemonic: '🔀 SWITCH = Secure, Workstations, Including, Trunks, Carefully, Hardening networks\nThink of traffic control - manage every connection.',
      visual: `
    🔀 SWITCH HARDENING
    🔌 NETWORK SWITCH
         ↓
    🔧 HARDENING STEPS
    ├── 🚫 DISABLE UNUSED PORTS
    ├── 🏷️ VLAN SEGMENTATION
    ├── 🔒 PORT SECURITY
    └── 🛡️ DHCP SNOOPING
         ↓
    ✅ SECURED NETWORK
         ↓
    🛡️ PROTECTED INFRASTRUCTURE`
    },
    'hardening-routers': {
      title: 'Router Hardening',
      explanation: 'Securing network routers by configuring access controls, disabling unnecessary services, and implementing security protocols.',
      examples: 'Change default passwords, disable unused services, enable logging, configure ACLs, secure protocols',
      mnemonic: '🌐 ROUTER = Route, Operations, Using, Thoughtful, Enhanced, Reliability\nThink of security checkpoint - control all traffic.',
      visual: `
    🌐 ROUTER HARDENING
    📡 NETWORK ROUTER
         ↓
    🔧 SECURITY CONFIG
    ├── 🔑 CHANGE PASSWORDS
    ├── 🚫 DISABLE SERVICES
    ├── 📊 ENABLE LOGGING
    └── 🔒 CONFIGURE ACLS
         ↓
    ✅ HARDENED ROUTER
         ↓
    🛡️ SECURE ROUTING`
    },
    'hardening-cloud-infrastructure': {
      title: 'Cloud Infrastructure Hardening',
      explanation: 'Securing cloud resources by implementing proper access controls, encryption, and monitoring.',
      examples: 'IAM policies, encryption at rest, network security groups, monitoring, logging, backup strategies',
      mnemonic: '☁️ CLOUD = Configure, Like, Operations, Users, Demand security\nThink of digital fortress - secured in the cloud.',
      visual: `
    ☁️ CLOUD HARDENING
    ☁️ CLOUD RESOURCES
         ↓
    🔧 SECURITY MEASURES
    ├── 🔑 IAM POLICIES
    ├── 🔒 ENCRYPTION
    ├── 🛡️ SECURITY GROUPS
    └── 📊 MONITORING
         ↓
    ✅ SECURED CLOUD
         ↓
    🛡️ PROTECTED INFRASTRUCTURE`
    },
    'hardening-servers': {
      title: 'Server Hardening',
      explanation: 'Securing servers by removing unnecessary services, implementing access controls, and following security best practices.',
      examples: 'Disable unused services, patch management, secure configurations, access controls, monitoring',
      mnemonic: '🖥️ SERVER = Secure, Everything, Reduce, Vulnerabilities, Enable, Reliability\nThink of bank vault - multiple security layers.',
      visual: `
    🖥️ SERVER HARDENING
    🖥️ BASE SERVER
         ↓
    🔧 HARDENING PROCESS
    ├── 🚫 REMOVE SERVICES
    ├── 🔄 PATCH SYSTEM
    ├── 🔒 SECURE CONFIG
    └── 🛡️ ACCESS CONTROLS
         ↓
    ✅ HARDENED SERVER
         ↓
    🛡️ PRODUCTION READY`
    },
    'hardening-ics-scada': {
      title: 'ICS/SCADA Hardening',
      explanation: 'Securing Industrial Control Systems and SCADA by implementing network segmentation and specialized security controls.',
      examples: 'Network segmentation, air-gapped networks, specialized firewalls, access controls, monitoring',
      mnemonic: '🏭 ICS = Industrial, Control, Systems need specialized security\nThink of nuclear plant - critical infrastructure protection.',
      visual: `
    🏭 ICS/SCADA HARDENING
    🏭 INDUSTRIAL SYSTEM
         ↓
    🔧 SPECIALIZED SECURITY
    ├── 🌐 NETWORK SEGMENTATION
    ├── 🔒 AIR-GAPPED NETWORKS
    ├── 🛡️ INDUSTRIAL FIREWALL
    └── 📊 SPECIALIZED MONITORING
         ↓
    ✅ SECURED INFRASTRUCTURE
         ↓
    🛡️ CRITICAL PROTECTION`
    },
    'hardening-embedded-systems': {
      title: 'Embedded Systems Hardening',
      explanation: 'Securing embedded devices by removing unnecessary code, implementing secure boot, and controlling access.',
      examples: 'Secure boot, code signing, minimal OS, hardware security modules, secure update mechanisms',
      mnemonic: '🔧 EMBEDDED = Everyone, Must, Build, Everything, Deliberately, Designed, Extremely, Defensively\nThink of pacemaker - life-critical security.',
      visual: `
    🔧 EMBEDDED HARDENING
    🔧 EMBEDDED DEVICE
         ↓
    🔒 SECURITY MEASURES
    ├── 🔐 SECURE BOOT
    ├── 📝 CODE SIGNING
    ├── 🏗️ MINIMAL OS
    └── 🔒 HSM PROTECTION
         ↓
    ✅ HARDENED DEVICE
         ↓
    🛡️ SECURE OPERATION`
    },
    'hardening-rtos': {
      title: 'RTOS Hardening',
      explanation: 'Securing Real-Time Operating Systems by implementing timing controls, memory protection, and deterministic security.',
      examples: 'Memory protection, timing analysis, secure scheduling, interrupt handling, deterministic behavior',
      mnemonic: '⏱️ RTOS = Real-Time, Operations, Secure timing\nThink of aircraft system - timing is critical for safety.',
      visual: `
    ⏱️ RTOS HARDENING
    ⏱️ REAL-TIME SYSTEM
         ↓
    🔒 SECURITY CONTROLS
    ├── 🛡️ MEMORY PROTECTION
    ├── ⏱️ TIMING ANALYSIS
    ├── 📋 SECURE SCHEDULING
    └── ⚡ INTERRUPT HANDLING
         ↓
    ✅ SECURED RTOS
         ↓
    🛡️ DETERMINISTIC SECURITY`
    },
    'hardening-iot-devices': {
      title: 'IoT Device Hardening',
      explanation: 'Securing Internet of Things devices by implementing authentication, encryption, and secure communication protocols.',
      examples: 'Strong authentication, encryption, secure protocols, firmware updates, network segmentation',
      mnemonic: '🌐 IOT = Internet, Of, Things needs security everywhere\nThink of smart home - every device is a potential entry point.',
      visual: `
    🌐 IOT HARDENING
    🌐 IOT DEVICE
         ↓
    🔒 SECURITY MEASURES
    ├── 🔑 AUTHENTICATION
    ├── 🔒 ENCRYPTION
    ├── 🛡️ SECURE PROTOCOLS
    └── 🔄 SECURE UPDATES
         ↓
    ✅ SECURED IOT
         ↓
    🛡️ PROTECTED NETWORK`
    },
    'wireless-installation-considerations': {
      title: 'Wireless Installation Considerations',
      explanation: 'Planning factors for optimal wireless network deployment including coverage, security, and performance.',
      examples: 'RF planning, interference analysis, power considerations, antenna placement, environmental factors',
      mnemonic: '📡 WIRELESS = Working, Installation, Requires, Expert, Location, Equipment, Site, Setup\nThink of radio tower - location determines coverage.',
      visual: `
    📡 WIRELESS INSTALLATION
    🏢 FACILITY LAYOUT
         ↓
    📊 SITE SURVEY
    ├── 📡 RF ANALYSIS
    ├── 🏗️ PHYSICAL LAYOUT
    └── 🚫 INTERFERENCE CHECK
         ↓
    📍 OPTIMAL PLACEMENT
         ↓
    📶 COVERAGE ACHIEVED`
    },
    'site-surveys': {
      title: 'Wireless Site Surveys',
      explanation: 'Comprehensive analysis of a location to determine optimal wireless network design and access point placement.',
      examples: 'RF site surveys, predictive surveys, post-installation validation, coverage analysis, interference detection',
      mnemonic: '🔍 SURVEY = Site, Understanding, Reveals, Valuable, Excellent, Yielding data\nThink of land survey - measure before building.',
      visual: `
    🔍 SITE SURVEY
    📋 SURVEY PLANNING
         ↓
    📡 RF MEASUREMENTS
    ├── 📶 SIGNAL STRENGTH
    ├── 🚫 INTERFERENCE
    └── 📊 COVERAGE GAPS
         ↓
    📈 ANALYSIS REPORT
         ↓
    📍 AP PLACEMENT PLAN`
    },
    'heat-maps': {
      title: 'Wireless Heat Maps',
      explanation: 'Visual representation of wireless signal strength and coverage areas using color-coded maps.',
      examples: 'Signal strength maps, coverage visualization, dead zone identification, capacity planning maps',
      mnemonic: '🗺️ HEATMAP = Helpful, Easy, Analysis, Tool, Mapping, All, Performance data\nThink of weather map - colors show temperature zones.',
      visual: `
    🗺️ WIRELESS HEAT MAP
    📡 SIGNAL DATA
         ↓
    🎨 COLOR MAPPING
    ├── 🔴 STRONG SIGNAL
    ├── 🟡 MEDIUM SIGNAL
    └── 🔵 WEAK SIGNAL
         ↓
    📊 VISUAL ANALYSIS
         ↓
    🎯 OPTIMIZATION PLAN`
    },
    'cellular-connection': {
      title: 'Cellular Connection Methods',
      explanation: 'Mobile device connectivity through cellular networks for data and voice communications.',
      examples: '4G LTE, 5G networks, mobile hotspots, carrier management, data plans, roaming policies',
      mnemonic: '📶 CELLULAR = Can, Everyone, Link, Locations, Using, Long, And, Reliable networks\nThink of cell tower - wide area coverage.',
      visual: `
    📶 CELLULAR CONNECTION
    📱 MOBILE DEVICE
         ↓
    📡 CELLULAR TOWER
         ↓
    🌐 CARRIER NETWORK
         ↓
    🏢 CORPORATE SYSTEMS
         ↓
    ✅ MOBILE CONNECTIVITY`
    },
    'wifi-connection': {
      title: 'Wi-Fi Connection Methods',
      explanation: 'Wireless local area network connectivity for mobile devices in office and public locations.',
      examples: 'Corporate Wi-Fi, guest networks, WPA3 encryption, certificate authentication, captive portals',
      mnemonic: '📶 WIFI = Wireless, Fidelity, Internet access\nThink of office hotspot - local area connectivity.',
      visual: `
    📶 WI-FI CONNECTION
    📱 MOBILE DEVICE
         ↓
    📡 ACCESS POINT
         ↓
    🔒 AUTHENTICATION
         ↓
    🌐 NETWORK ACCESS
         ↓
    ✅ LOCAL CONNECTIVITY`
    },
    'bluetooth-connection': {
      title: 'Bluetooth Connection Methods',
      explanation: 'Short-range wireless connectivity for peripheral devices and data transfer between mobile devices.',
      examples: 'Keyboard/mouse pairing, file transfers, audio devices, beacon technology, proximity authentication',
      mnemonic: '🔵 BLUETOOTH = Blue, Link, Using, Equipment, Through, Optimized, Open, Transmission, Hardware\nThink of short leash - close range connection.',
      visual: `
    🔵 BLUETOOTH CONNECTION
    📱 PRIMARY DEVICE
         ↓
    🔗 PAIRING PROCESS
         ↓
    ⌨️ PERIPHERAL DEVICE
         ↓
    📡 SHORT-RANGE LINK
         ↓
    ✅ SECURE CONNECTION`
    },
    'wpa3': {
      title: 'Wi-Fi Protected Access 3 (WPA3)',
      explanation: 'Latest wireless security protocol providing enhanced encryption and protection against attacks.',
      examples: 'SAE authentication, enhanced encryption, forward secrecy, protection against brute force attacks',
      mnemonic: '🔐 WPA3 = Wi-Fi, Protected, Access, version 3\nThink of upgraded lock - stronger security than before.',
      visual: `
    🔐 WPA3 SECURITY
    📡 WIRELESS NETWORK
         ↓
    🔒 SAE AUTHENTICATION
         ↓
    🛡️ ENHANCED ENCRYPTION
         ↓
    🔑 FORWARD SECRECY
         ↓
    ✅ SECURE CONNECTION`
    },
    'aaa-radius': {
      title: 'AAA/RADIUS Authentication',
      explanation: 'Authentication, Authorization, and Accounting using Remote Authentication Dial-In User Service protocol.',
      examples: 'Centralized authentication, network access control, user authorization, session accounting, policy enforcement',
      mnemonic: '🏢 AAA = Authentication, Authorization, Accounting\n🔄 RADIUS = Remote, Authentication, Dial-In, User, Service\nThink of hotel front desk - check in, authorize, track stay.',
      visual: `
    🏢 AAA/RADIUS
    👤 USER REQUEST
         ↓
    🔍 AUTHENTICATION
         ↓
    🔑 AUTHORIZATION
         ↓
    📊 ACCOUNTING
         ↓
    ✅ NETWORK ACCESS`
    },
    'cryptographic-protocols': {
      title: 'Cryptographic Protocols',
      explanation: 'Standardized methods for securing communications through encryption, key exchange, and digital signatures.',
      examples: 'TLS/SSL, IPSec, SSH, PGP, HTTPS, certificate-based encryption, key exchange protocols',
      mnemonic: '🔐 CRYPTO = Cryptographic, Reliable, Years, Protecting, Through, Operations\nThink of secret code - mathematical protection.',
      visual: `
    🔐 CRYPTOGRAPHIC PROTOCOLS
    📝 PLAINTEXT MESSAGE
         ↓
    🔑 ENCRYPTION KEY
         ↓
    🔒 ALGORITHM PROCESS
         ↓
    📦 ENCRYPTED MESSAGE
         ↓
    ✅ SECURE TRANSMISSION`
    },
    'authentication-protocols': {
      title: 'Authentication Protocols',
      explanation: 'Standardized methods for verifying user identity and granting appropriate access to systems.',
      examples: 'Kerberos, LDAP, SAML, OAuth, OpenID Connect, multi-factor authentication protocols',
      mnemonic: '🔑 AUTH = Authentication, Using, Trusted, Hardened protocols\nThink of ID verification - multiple ways to prove identity.',
      visual: `
    🔑 AUTHENTICATION PROTOCOLS
    👤 USER IDENTITY
         ↓
    📋 CREDENTIAL CHECK
         ↓
    🔍 PROTOCOL VALIDATION
         ↓
    ✅ IDENTITY VERIFIED
         ↓
    🚪 ACCESS GRANTED`
    },
    'input-validation': {
      title: 'Input Validation',
      explanation: 'Security practice of checking and sanitizing all data inputs to prevent injection attacks and data corruption.',
      examples: 'SQL injection prevention, XSS protection, parameter validation, data type checking, length limits',
      mnemonic: '✅ INPUT = Inspect, Never, Put, Unsafe, Things in applications\nThink of airport security - check everything entering.',
      visual: `
    ✅ INPUT VALIDATION
    📝 USER INPUT
         ↓
    🔍 VALIDATION CHECKS
    ├── 📏 LENGTH CHECK
    ├── 🔤 FORMAT CHECK
    └── 🚫 MALICIOUS CHECK
         ↓
    ✅ SANITIZED INPUT
         ↓
    🛡️ SECURE PROCESSING`
    },
    'secure-cookies': {
      title: 'Secure Cookies',
      explanation: 'Web cookies configured with security attributes to prevent theft and unauthorized access.',
      examples: 'HttpOnly flag, Secure flag, SameSite attribute, encrypted cookie values, session management',
      mnemonic: '🍪 SECURE = Safe, Encrypted, Cookies, Using, Restrictive, Extras\nThink of locked cookie jar - protected treats.',
      visual: `
    🍪 SECURE COOKIES
    🌐 WEB APPLICATION
         ↓
    🔒 SECURITY FLAGS
    ├── 🔒 HTTPONLY
    ├── 🛡️ SECURE
    └── 🏠 SAMESITE
         ↓
    📦 PROTECTED COOKIE
         ↓
    🛡️ SECURE SESSION`
    },
    'static-code-analysis': {
      title: 'Static Code Analysis',
      explanation: 'Automated analysis of source code to identify security vulnerabilities and coding issues without executing the program.',
      examples: 'SAST tools, vulnerability scanning, code quality checks, security rule enforcement, compliance verification',
      mnemonic: '🔍 STATIC = Scan, The, Application, To, Identify, Concerns\nThink of proofreading - check before publishing.',
      visual: `
    🔍 STATIC CODE ANALYSIS
    💻 SOURCE CODE
         ↓
    🤖 AUTOMATED SCAN
    ├── 🚫 VULNERABILITIES
    ├── 📋 CODING ISSUES
    └── 📊 QUALITY METRICS
         ↓
    📝 ANALYSIS REPORT
         ↓
    🔧 CODE IMPROVEMENTS`
    },
    'code-signing': {
      title: 'Code Signing',
      explanation: 'Digital signature process that verifies the authenticity and integrity of software code.',
      examples: 'Digital certificates, software publishers, integrity verification, tamper detection, trust validation',
      mnemonic: '✍️ SIGNING = Software, Identity, Guarantees, Never, Inspected, Never, Generated falsely\nThink of autograph - proves authenticity.',
      visual: `
    ✍️ CODE SIGNING
    💻 SOFTWARE CODE
         ↓
    🔑 PRIVATE KEY
         ↓
    ✍️ DIGITAL SIGNATURE
         ↓
    📦 SIGNED SOFTWARE
         ↓
    ✅ VERIFIED AUTHENTICITY`
    },
    'sandboxing': {
      title: 'Sandboxing',
      explanation: 'Security mechanism that isolates applications in restricted environments to prevent malicious code from affecting the system.',
      examples: 'Application isolation, virtual environments, restricted permissions, malware analysis, browser sandboxes',
      mnemonic: '🏖️ SANDBOX = Safe, Applications, Never, Damage, Box, Operations, eXecute safely\nThink of playground sandbox - contained play area.',
      visual: `
    🏖️ SANDBOXING
    📱 APPLICATION
         ↓
    📦 ISOLATED ENVIRONMENT
    ├── 🚫 LIMITED PERMISSIONS
    ├── 🔒 RESTRICTED ACCESS
    └── 🛡️ MONITORED ACTIVITY
         ↓
    ✅ CONTAINED EXECUTION
         ↓
    🛡️ SYSTEM PROTECTION`
    },
    'security-monitoring': {
      title: 'Security Monitoring',
      explanation: 'Continuous observation and analysis of systems and networks to detect security threats and incidents.',
      examples: 'SIEM systems, log analysis, real-time alerts, behavioral monitoring, threat detection, incident response',
      mnemonic: '👁️ MONITORING = Multiple, Operations, Never, Ignore, Threats, Operations, Require, Inspection, Never, Going unnoticed\nThink of security guard - always watching.',
      visual: `
    👁️ SECURITY MONITORING
    🖥️ SYSTEMS & NETWORKS
         ↓
    📊 DATA COLLECTION
    ├── 📋 LOG ANALYSIS
    ├── 🚨 ALERT GENERATION
    └── 🔍 THREAT DETECTION
         ↓
    📈 ANALYSIS & RESPONSE
         ↓
    🛡️ SECURITY POSTURE`
    },
    'acquisition-procurement': {
      title: 'Acquisition/Procurement Process',
      explanation: 'Systematic approach to acquiring hardware, software, and services with security considerations throughout the procurement lifecycle.',
      examples: 'Vendor evaluation, security requirements, contract negotiations, supply chain security, compliance verification',
      mnemonic: '📋 PROCURE = Process, Requirements, Operations, Contracts, Under, Rigorous, Evaluation\nThink of careful shopping - evaluate before buying.',
      visual: `
    📋 ACQUISITION PROCESS
    📝 REQUIREMENTS DEFINED
         ↓
    🔍 VENDOR EVALUATION
    ├── 🛡️ SECURITY ASSESSMENT
    ├── 📊 COMPLIANCE CHECK
    └── 💰 COST ANALYSIS
         ↓
    📄 CONTRACT NEGOTIATION
         ↓
    ✅ SECURE PROCUREMENT`
    },
    'asset-ownership': {
      title: 'Asset Ownership',
      explanation: 'Clear designation of who is responsible for each asset throughout its lifecycle, including security and maintenance.',
      examples: 'Asset custodians, data owners, system administrators, business units, accountability frameworks',
      mnemonic: '👤 OWNER = Operations, With, Named, Entities, Responsible\nThink of car ownership - someone is always responsible.',
      visual: `
    👤 ASSET OWNERSHIP
    🖥️ ASSET IDENTIFIED
         ↓
    👨‍💼 OWNER ASSIGNED
    ├── 📋 RESPONSIBILITIES
    ├── 🔒 SECURITY DUTIES
    └── 📊 ACCOUNTABILITY
         ↓
    ✅ CLEAR OWNERSHIP
         ↓
    🛡️ MANAGED ASSET`
    },
    'asset-classification': {
      title: 'Asset Classification',
      explanation: 'Categorizing assets based on their importance, sensitivity, and criticality to business operations.',
      examples: 'Critical systems, sensitive data, public information, confidential assets, security levels',
      mnemonic: '🏷️ CLASSIFY = Critical, Level, And, Security, Sensitivity, Importance, For, You\nThink of library system - books sorted by category.',
      visual: `
    🏷️ ASSET CLASSIFICATION
    🖥️ ASSET INVENTORY
         ↓
    📊 EVALUATION CRITERIA
    ├── 🎯 CRITICALITY
    ├── 🔒 SENSITIVITY
    └── 💼 BUSINESS VALUE
         ↓
    🏷️ CLASSIFICATION LABEL
         ↓
    🛡️ APPROPRIATE PROTECTION`
    },
    'asset-inventory': {
      title: 'Asset Inventory',
      explanation: 'Comprehensive catalog of all organizational assets including hardware, software, and data with their attributes.',
      examples: 'Hardware inventory, software licenses, network devices, mobile devices, cloud resources, data repositories',
      mnemonic: '📋 INVENTORY = In, Numbers, Verify, Equipment, Never, Take, Operations, Risk, Yielding\nThink of warehouse count - know what you have.',
      visual: `
    📋 ASSET INVENTORY
    🏢 ORGANIZATION
         ↓
    🔍 ASSET DISCOVERY
    ├── 🖥️ HARDWARE
    ├── 💿 SOFTWARE
    └── 📊 DATA
         ↓
    📝 CATALOG CREATION
         ↓
    📊 INVENTORY DATABASE`
    },
    'asset-enumeration': {
      title: 'Asset Enumeration',
      explanation: 'Systematic identification and cataloging of assets to ensure complete visibility of organizational resources.',
      examples: 'Network scanning, automated discovery, manual audits, service identification, version detection',
      mnemonic: '🔢 ENUMERATE = Everyone, Needs, Unique, Methods, Evaluating, Resources, And, Their, Existence\nThink of taking attendance - count everyone present.',
      visual: `
    🔢 ASSET ENUMERATION
    🌐 NETWORK ENVIRONMENT
         ↓
    🔍 DISCOVERY METHODS
    ├── 📡 NETWORK SCANNING
    ├── 🤖 AUTOMATED TOOLS
    └── 👨‍💻 MANUAL AUDITS
         ↓
    📝 ENUMERATED LIST
         ↓
    📊 COMPLETE VISIBILITY`
    },
    'asset-sanitization': {
      title: 'Asset Sanitization',
      explanation: 'Process of securely removing or destroying data from storage devices before disposal or reuse.',
      examples: 'Data wiping, disk formatting, degaussing, overwriting, cryptographic erasure, secure deletion',
      mnemonic: '🧹 SANITIZE = Securely, And, Never, Include, Traces, Information, Zapped, Entirely\nThink of hospital cleaning - thorough decontamination.',
      visual: `
    🧹 ASSET SANITIZATION
    💾 STORAGE DEVICE
         ↓
    🔍 DATA CLASSIFICATION
         ↓
    🧹 SANITIZATION METHOD
    ├── 🔄 OVERWRITING
    ├── 🧲 DEGAUSSING
    └── 🔥 CRYPTOGRAPHIC ERASE
         ↓
    ✅ DATA DESTROYED
         ↓
    🛡️ SECURE DISPOSAL`
    },
    'asset-destruction': {
      title: 'Asset Destruction',
      explanation: 'Physical destruction of assets and storage media to ensure complete data elimination when sanitization is insufficient.',
      examples: 'Hard drive shredding, media destruction, incineration, pulverization, certified destruction services',
      mnemonic: '💥 DESTROY = Data, Elimination, Securely, Through, Reduction, Operations, Yielding nothing\nThink of paper shredder - completely unusable.',
      visual: `
    💥 ASSET DESTRUCTION
    💾 SENSITIVE MEDIA
         ↓
    🔨 PHYSICAL DESTRUCTION
    ├── 🔨 SHREDDING
    ├── 🔥 INCINERATION
    └── 💥 PULVERIZATION
         ↓
    🗑️ DESTROYED REMAINS
         ↓
    ✅ DATA ELIMINATED`
    },
    'destruction-certification': {
      title: 'Destruction Certification',
      explanation: 'Formal documentation proving that assets and data have been properly destroyed according to security standards.',
      examples: 'Destruction certificates, chain of custody, witness statements, audit trails, compliance documentation',
      mnemonic: '📜 CERTIFY = Certificate, Ensures, Reliable, Tracking, Information, For, Years\nThink of diploma - official proof of completion.',
      visual: `
    📜 DESTRUCTION CERTIFICATION
    💥 DESTRUCTION PROCESS
         ↓
    👨‍⚖️ WITNESS VERIFICATION
         ↓
    📋 DOCUMENTATION
    ├── 📝 CERTIFICATE
    ├── 📊 AUDIT TRAIL
    └── 🔒 CHAIN OF CUSTODY
         ↓
    ✅ CERTIFIED DESTRUCTION
         ↓
    📚 COMPLIANCE RECORD`
    },
    'data-retention': {
      title: 'Data Retention',
      explanation: 'Policies and procedures for keeping data for specific periods based on legal, regulatory, and business requirements.',
      examples: 'Retention schedules, legal hold requirements, regulatory compliance, backup policies, archival systems',
      mnemonic: '🗃️ RETAIN = Requirements, Establish, Time, And, Information, Needed\nThink of library archives - keep important records.',
      visual: `
    🗃️ DATA RETENTION
    📊 DATA CLASSIFICATION
         ↓
    📋 RETENTION POLICY
    ├── ⏰ TIME PERIODS
    ├── 📚 LEGAL REQUIREMENTS
    └── 💼 BUSINESS NEEDS
         ↓
    🗄️ CONTROLLED STORAGE
         ↓
    🗑️ SCHEDULED DISPOSAL`
    },
    'security-guidelines': {
      title: 'Security Guidelines',
      explanation: 'High-level recommendations and best practices for implementing security measures across the organization.',
      examples: 'Security frameworks, implementation guides, best practice documents, advisory guidelines',
      mnemonic: '📖 GUIDELINES = Guide, Users, In, Developing, Enhanced, Logical, Information, Never, Excessive, Security\nThink of cookbook - general recipes for success.',
      visual: `
    📖 SECURITY GUIDELINES
    🎯 SECURITY OBJECTIVES
         ↓
    📋 BEST PRACTICES
    ├── 📚 FRAMEWORKS
    ├── 🛠️ IMPLEMENTATION GUIDES
    └── 💡 RECOMMENDATIONS
         ↓
    👥 ORGANIZATION ADOPTION
         ↓
    🛡️ IMPROVED SECURITY`
    },
    'aup': {
      title: 'Acceptable Use Policy (AUP)',
      explanation: 'Policy defining appropriate and inappropriate uses of organizational IT resources and systems.',
      examples: 'Internet usage, email guidelines, software installation, personal use limits, prohibited activities',
      mnemonic: '✅ AUP = Acceptable, Use, Policy defines proper behavior\nThink of library rules - what you can and cannot do.',
      visual: `
    ✅ ACCEPTABLE USE POLICY
    👤 EMPLOYEE BEHAVIOR
         ↓
    📋 USAGE GUIDELINES
    ├── ✅ PERMITTED ACTIVITIES
    ├── 🚫 PROHIBITED ACTIONS
    └── ⚖️ CONSEQUENCES
         ↓
    📝 ACKNOWLEDGMENT
         ↓
    🛡️ SECURE USAGE`
    },
    'information-security-policies': {
      title: 'Information Security Policies',
      explanation: 'Comprehensive policies governing the protection, handling, and management of organizational information assets.',
      examples: 'Data classification policies, access control policies, encryption policies, incident response policies',
      mnemonic: '🔒 INFO = Information, Never, Fails, Operations when properly secured\nThink of vault rules - protect valuable assets.',
      visual: `
    🔒 INFORMATION SECURITY POLICIES
    📊 INFORMATION ASSETS
         ↓
    📋 POLICY FRAMEWORK
    ├── 🏷️ CLASSIFICATION
    ├── 🔑 ACCESS CONTROL
    └── 🛡️ PROTECTION MEASURES
         ↓
    👥 IMPLEMENTATION
         ↓
    🔒 SECURED INFORMATION`
    },
    'business-continuity-policy': {
      title: 'Business Continuity Policy',
      explanation: 'Policy ensuring the organization can continue essential operations during and after disruptive events.',
      examples: 'Continuity planning, emergency procedures, recovery strategies, essential functions identification',
      mnemonic: '🔄 CONTINUITY = Continue, Operations, Never, Terminated, In, Normal, Until, It, Tries, Yielding\nThink of backup generator - power continues flowing.',
      visual: `
    🔄 BUSINESS CONTINUITY POLICY
    🏢 BUSINESS OPERATIONS
         ↓
    📋 CONTINUITY PLANNING
    ├── 🎯 ESSENTIAL FUNCTIONS
    ├── 🔄 RECOVERY STRATEGIES
    └── 📞 EMERGENCY PROCEDURES
         ↓
    ⚡ DISRUPTIVE EVENT
         ↓
    ✅ CONTINUED OPERATIONS`
    },
    'disaster-recovery-policy': {
      title: 'Disaster Recovery Policy',
      explanation: 'Policy outlining procedures for recovering IT systems and data after major disasters or disruptions.',
      examples: 'Recovery procedures, backup strategies, restoration priorities, communication plans, testing requirements',
      mnemonic: '🌪️ DISASTER = Detailed, Instructions, Save, And, Secure, Technology, Emergency, Recovery\nThink of emergency evacuation plan - know what to do when disaster strikes.',
      visual: `
    🌪️ DISASTER RECOVERY POLICY
    💥 DISASTER EVENT
         ↓
    📋 RECOVERY PROCEDURES
    ├── 🔄 BACKUP RESTORATION
    ├── 🎯 PRIORITY SYSTEMS
    └── 📞 COMMUNICATION PLAN
         ↓
    🛠️ RECOVERY EXECUTION
         ↓
    ✅ SYSTEMS RESTORED`
    },
    'incident-response-policy': {
      title: 'Incident Response Policy',
      explanation: 'Policy defining procedures for detecting, responding to, and recovering from security incidents.',
      examples: 'Incident classification, response procedures, communication protocols, evidence handling, post-incident review',
      mnemonic: '🚨 INCIDENT = Immediate, Never, Compromised, Information, Detected, Emergency, Never, Tolerated\nThink of fire drill - everyone knows their role.',
      visual: `
    🚨 INCIDENT RESPONSE POLICY
    ⚠️ SECURITY INCIDENT
         ↓
    📋 RESPONSE PROCEDURES
    ├── 🔍 DETECTION
    ├── 🛡️ CONTAINMENT
    └── 🔄 RECOVERY
         ↓
    📞 COMMUNICATION
         ↓
    📝 POST-INCIDENT REVIEW`
    },
    'sdlc-policy': {
      title: 'Software Development Lifecycle (SDLC) Policy',
      explanation: 'Policy governing secure software development practices throughout the development lifecycle.',
      examples: 'Secure coding standards, code review requirements, testing procedures, deployment guidelines',
      mnemonic: '💻 SDLC = Secure, Development, Lifecycle, Controls\nThink of assembly line - quality checks at every stage.',
      visual: `
    💻 SDLC POLICY
    📋 DEVELOPMENT REQUIREMENTS
         ↓
    🔒 SECURE DEVELOPMENT
    ├── 📝 SECURE CODING
    ├── 🔍 CODE REVIEW
    └── 🧪 SECURITY TESTING
         ↓
    🚀 SECURE DEPLOYMENT
         ↓
    🛡️ SECURE SOFTWARE`
    },
    'change-management-policy': {
      title: 'Change Management Policy',
      explanation: 'Policy governing how changes to systems, processes, and configurations are planned, approved, and implemented.',
      examples: 'Change approval process, documentation requirements, testing procedures, rollback plans, emergency changes',
      mnemonic: '🔄 CHANGE = Careful, Handling, And, Never, Going, Extreme\nThink of construction permits - approval before building.',
      visual: `
    🔄 CHANGE MANAGEMENT POLICY
    💡 CHANGE REQUEST
         ↓
    📋 APPROVAL PROCESS
    ├── 📝 DOCUMENTATION
    ├── 🧪 TESTING
    └── 👥 APPROVAL
         ↓
    🔄 IMPLEMENTATION
         ↓
    ✅ CHANGE COMPLETED`
    },
    'password-standards': {
      title: 'Password Standards',
      explanation: 'Technical standards defining password complexity, length, rotation, and management requirements.',
      examples: 'Minimum length requirements, complexity rules, password rotation policies, password managers',
      mnemonic: '🔐 PASSWORD = Protect, And, Secure, Systems, With, Official, Requirements, Detailed\nThink of combination lock - specific requirements for security.',
      visual: `
    🔐 PASSWORD STANDARDS
    👤 USER AUTHENTICATION
         ↓
    📋 STANDARD REQUIREMENTS
    ├── 📏 LENGTH (12+ chars)
    ├── 🔤 COMPLEXITY (mixed case)
    └── 🔄 ROTATION (90 days)
         ↓
    🔑 STRONG PASSWORDS
         ↓
    🛡️ SECURE ACCESS`
    },
    'access-control-standards': {
      title: 'Access Control Standards',
      explanation: 'Technical standards for implementing access controls including authentication, authorization, and accounting.',
      examples: 'RBAC implementation, multi-factor authentication, privilege escalation, access reviews',
      mnemonic: '🔑 ACCESS = Authorization, Clearly, Controls, Entry, Securely, Systematically\nThink of building security - multiple checkpoints.',
      visual: `
    🔑 ACCESS CONTROL STANDARDS
    👤 USER IDENTITY
         ↓
    🔍 AUTHENTICATION
         ↓
    📋 AUTHORIZATION CHECK
    ├── 🏷️ ROLE-BASED
    ├── 🔐 MULTI-FACTOR
    └── ⏰ TIME-BASED
         ↓
    🚪 ACCESS GRANTED
         ↓
    📊 ACCOUNTING & AUDIT`
    },
    'physical-security-standards': {
      title: 'Physical Security Standards',
      explanation: 'Standards for protecting physical facilities, equipment, and personnel from unauthorized access and threats.',
      examples: 'Badge access systems, surveillance cameras, environmental controls, visitor management',
      mnemonic: '🏢 PHYSICAL = Protecting, Hardware, Yields, Security, In, Controlled, Areas, Logically\nThink of bank vault - multiple physical barriers.',
      visual: `
    🏢 PHYSICAL SECURITY STANDARDS
    🏢 FACILITY PROTECTION
         ↓
    🔒 ACCESS CONTROLS
    ├── 🎫 BADGE SYSTEMS
    ├── 📹 SURVEILLANCE
    └── 👥 VISITOR MANAGEMENT
         ↓
    🛡️ SECURED PERIMETER
         ↓
    🔐 PROTECTED ASSETS`
    },
    'encryption-standards': {
      title: 'Encryption Standards',
      explanation: 'Technical standards specifying encryption algorithms, key lengths, and implementation requirements.',
      examples: 'AES-256 encryption, RSA key lengths, TLS versions, key management procedures',
      mnemonic: '🔐 ENCRYPT = Everyone, Never, Compromises, Robust, Yet, Proper, Technology\nThink of secret code - mathematical protection.',
      visual: `
    🔐 ENCRYPTION STANDARDS
    📄 SENSITIVE DATA
         ↓
    🔑 ENCRYPTION REQUIREMENTS
    ├── 🛡️ AES-256 ALGORITHM
    ├── 🔑 KEY LENGTH (256-bit)
    └── 🔒 TLS 1.3 TRANSPORT
         ↓
    📦 ENCRYPTED DATA
         ↓
    🛡️ PROTECTED INFORMATION`
    },
    'deception-disruption-technology': {
      title: 'Deception and Disruption Technology',
      explanation: 'Security technologies that mislead attackers and disrupt their activities through fake systems and false information.',
      examples: 'Honeypots, honeynets, decoy files, fake credentials, threat intelligence gathering, attacker misdirection',
      mnemonic: '🎭 DECEPTION = Deploy, Everything, Can, Eliminate, Persistent, Threats, In, Operations, Never\nThink of magician - misdirection and illusion.',
      visual: `
    🎭 DECEPTION TECHNOLOGY
    🏢 REAL NETWORK
         ↓
    🍯 DEPLOY HONEYPOTS
    ├── 🖥️ FAKE SERVERS
    ├── 📁 DECOY FILES
    └── 🔑 FAKE CREDENTIALS
         ↓
    👤 ATTACKER INTERACTION
         ↓
    📊 THREAT INTELLIGENCE`
    },
    'control-types': {
      title: 'Security Control Types',
      explanation: 'Categories of security measures based on their function: preventive, detective, corrective, deterrent, compensating, and directive.',
      examples: 'Preventive: firewalls, Detective: IDS, Corrective: patches, Deterrent: cameras, Compensating: manual processes',
      mnemonic: '🛡️ CONTROLS = Categories, Organize, Never, Take, Risks, Operations, Logical, Security\nThink of security layers - different types of protection.',
      visual: `
    🛡️ SECURITY CONTROL TYPES
    🏢 ORGANIZATION
         ↓
    🔧 CONTROL IMPLEMENTATION
    ├── 🚫 PREVENTIVE (block)
    ├── 🔍 DETECTIVE (monitor)
    ├── 🔧 CORRECTIVE (fix)
    ├── ⚠️ DETERRENT (discourage)
    ├── 🔄 COMPENSATING (alternative)
    └── 📋 DIRECTIVE (guide)
         ↓
    🛡️ LAYERED SECURITY`
    },
    'application-security-testing': {
      title: 'Application Security Testing',
      explanation: 'Methods for testing applications to identify security vulnerabilities and coding flaws.',
      examples: 'Static analysis, dynamic analysis, interactive testing, dependency scanning, code review',
      mnemonic: '🔍 APP = Application, Protection, Programs need security testing\nThink of quality control - test before release.',
      visual: `
    🔍 APPLICATION SECURITY TESTING
    💻 APPLICATION CODE
         ↓
    🧪 TESTING METHODS
    ├── 📊 STATIC ANALYSIS
    ├── 🏃 DYNAMIC ANALYSIS
    └── 📦 PACKAGE MONITORING
         ↓
    📋 VULNERABILITY REPORT
         ↓
    🔧 SECURITY IMPROVEMENTS`
    },
    'static-analysis': {
      title: 'Static Analysis',
      explanation: 'Security testing method that analyzes source code without executing the program to find vulnerabilities.',
      examples: 'Code scanning tools, SAST, security rule checking, compliance verification, automated code review',
      mnemonic: '📊 STATIC = Source, Testing, And, Thorough, Inspection, Code\nThink of reading blueprint - analyze without building.',
      visual: `
    📊 STATIC ANALYSIS
    💻 SOURCE CODE
         ↓
    🔍 CODE SCANNING
    ├── 🚫 SECURITY FLAWS
    ├── 📋 CODING ISSUES
    └── 📊 COMPLIANCE CHECK
         ↓
    📝 ANALYSIS REPORT
         ↓
    🔧 CODE IMPROVEMENTS`
    },
    'dynamic-analysis': {
      title: 'Dynamic Analysis',
      explanation: 'Security testing method that analyzes running applications to identify vulnerabilities during execution.',
      examples: 'DAST tools, runtime testing, behavioral analysis, input fuzzing, penetration testing',
      mnemonic: '🏃 DYNAMIC = During, Your, Normal, Application, Makes, Issues, Clear\nThink of crash test - test while running.',
      visual: `
    🏃 DYNAMIC ANALYSIS
    🚀 RUNNING APPLICATION
         ↓
    🧪 RUNTIME TESTING
    ├── 📥 INPUT FUZZING
    ├── 🔍 BEHAVIOR ANALYSIS
    └── 🎯 VULNERABILITY TESTING
         ↓
    📊 RUNTIME RESULTS
         ↓
    🛡️ SECURITY VALIDATION`
    },
    'package-monitoring': {
      title: 'Package Monitoring',
      explanation: 'Continuous monitoring of software dependencies and third-party packages for known vulnerabilities.',
      examples: 'Dependency scanning, SCA tools, license compliance, vulnerability databases, package updates',
      mnemonic: '📦 PACKAGE = Protect, All, Code, Keeping, Attacks, Gone, Efficiently\nThink of supply chain tracking - monitor all components.',
      visual: `
    📦 PACKAGE MONITORING
    📚 SOFTWARE DEPENDENCIES
         ↓
    🔍 VULNERABILITY SCANNING
    ├── 📊 CVE DATABASE
    ├── 🏷️ LICENSE CHECK
    └── 🔄 UPDATE TRACKING
         ↓
    🚨 SECURITY ALERTS
         ↓
    🔧 REMEDIATION ACTIONS`
    },
    'threat-feeds': {
      title: 'Threat Intelligence Feeds',
      explanation: 'Continuous streams of cybersecurity data providing information about current and emerging threats.',
      examples: 'IOC feeds, malware signatures, IP blacklists, domain reputation, attack patterns, TTPs',
      mnemonic: '📡 FEEDS = Fresh, Enhanced, Emerging, Data, Security intelligence\nThink of news feed - continuous threat updates.',
      visual: `
    📡 THREAT FEEDS
    🌐 THREAT SOURCES
         ↓
    📊 INTELLIGENCE COLLECTION
    ├── 🔍 OSINT
    ├── 💰 COMMERCIAL
    └── 🤝 SHARING ORGS
         ↓
    📈 THREAT ANALYSIS
         ↓
    🛡️ DEFENSIVE MEASURES`
    },
    'osint': {
      title: 'Open-Source Intelligence (OSINT)',
      explanation: 'Intelligence gathering from publicly available sources for threat detection and security analysis.',
      examples: 'Social media monitoring, public databases, forums, news articles, government reports, academic papers',
      mnemonic: '🔍 OSINT = Open, Source, Intelligence, Never, Terminated\nThink of detective work - clues from public sources.',
      visual: `
    🔍 OSINT COLLECTION
    🌐 PUBLIC SOURCES
         ↓
    📊 DATA GATHERING
    ├── 📱 SOCIAL MEDIA
    ├── 📰 NEWS SITES
    └── 📚 PUBLIC DATABASES
         ↓
    🧠 INTELLIGENCE ANALYSIS
         ↓
    📋 THREAT ASSESSMENT`
    },
    'proprietary-threat-intel': {
      title: 'Proprietary/Third-Party Intelligence',
      explanation: 'Commercial threat intelligence services providing exclusive and curated security information.',
      examples: 'Commercial feeds, vendor intelligence, subscription services, exclusive IOCs, premium threat data',
      mnemonic: '💰 PROPRIETARY = Private, Researchers, Offer, Premium, Reliable, Intelligence, Exclusively, Tailored, And, Relevant, Yielding\nThink of premium cable - pay for exclusive content.',
      visual: `
    💰 PROPRIETARY INTELLIGENCE
    🏢 COMMERCIAL VENDOR
         ↓
    💰 SUBSCRIPTION SERVICE
         ↓
    📊 EXCLUSIVE THREAT DATA
    ├── 🎯 TARGETED THREATS
    ├── 🔍 CURATED ANALYSIS
    └── ⚡ REAL-TIME UPDATES
         ↓
    🛡️ ENHANCED PROTECTION`
    },
    'information-sharing-org': {
      title: 'Information-Sharing Organizations',
      explanation: 'Collaborative groups that share cybersecurity threat intelligence and best practices among members.',
      examples: 'ISACs, CERTs, government agencies, industry consortiums, threat sharing platforms',
      mnemonic: '🤝 SHARING = Security, Helps, All, Recognize, Incidents, Never, Goes unnoticed\nThink of neighborhood watch - community protection.',
      visual: `
    🤝 INFORMATION SHARING
    🏢 MEMBER ORGANIZATIONS
         ↓
    📊 THREAT INTELLIGENCE
         ↓
    🔄 BIDIRECTIONAL SHARING
    ├── 📤 CONTRIBUTE DATA
    └── 📥 RECEIVE INTEL
         ↓
    🛡️ COLLECTIVE SECURITY
         ↓
    🌐 COMMUNITY PROTECTION`
    },
    'dark-web-monitoring': {
      title: 'Dark Web Monitoring',
      explanation: 'Monitoring hidden internet networks for stolen data, cybercriminal activities, and emerging threats.',
      examples: 'Credential monitoring, data breach detection, cybercriminal forums, malware markets, threat actor tracking',
      mnemonic: '🕳️ DARK = Dangerous, Activities, Require, Keen monitoring\nThink of undercover surveillance - watching criminal activities.',
      visual: `
    🕳️ DARK WEB MONITORING
    🌐 HIDDEN NETWORKS
         ↓
    🔍 MONITORING TOOLS
    ├── 💳 STOLEN CREDENTIALS
    ├── 📊 BREACH DATA
    └── 👤 THREAT ACTORS
         ↓
    🚨 SECURITY ALERTS
         ↓
    🛡️ PROACTIVE DEFENSE`
    },
    'responsible-disclosure': {
      title: 'Responsible Disclosure Program',
      explanation: 'Structured process for security researchers to report vulnerabilities to organizations in a coordinated manner.',
      examples: 'Disclosure timelines, coordinated disclosure, vulnerability reporting, researcher recognition, remediation coordination',
      mnemonic: '🤝 RESPONSIBLE = Researchers, Everyone, Security, Professionals, Organizations, Never, Suddenly, Inform, But, Legally, Ethically\nThink of diplomatic relations - proper channels and timing.',
      visual: `
    🤝 RESPONSIBLE DISCLOSURE
    🔍 SECURITY RESEARCHER
         ↓
    📝 VULNERABILITY REPORT
         ↓
    🏢 ORGANIZATION NOTIFICATION
         ↓
    🔧 REMEDIATION PERIOD
         ↓
    📢 PUBLIC DISCLOSURE
         ↓
    🏆 RESEARCHER RECOGNITION`
    },
    'bug-bounty-program': {
      title: 'Bug Bounty Program',
      explanation: 'Crowdsourced security testing program where organizations pay researchers for finding and reporting vulnerabilities.',
      examples: 'HackerOne, Bugcrowd, private programs, vulnerability rewards, responsible disclosure, security crowdsourcing',
      mnemonic: '🏆 BOUNTY = Bugs, Organization, Users, Never, Tolerate, Yielding rewards\nThink of wanted poster - reward for capturing criminals.',
      visual: `
    🏆 BUG BOUNTY PROGRAM
    🏢 ORGANIZATION SCOPE
         ↓
    🎯 SECURITY RESEARCHERS
         ↓
    🔍 VULNERABILITY HUNTING
         ↓
    📝 VALID BUG REPORT
         ↓
    💰 MONETARY REWARD
         ↓
    🛡️ IMPROVED SECURITY`
    },
    'false-positive': {
      title: 'False Positive',
      explanation: 'Security alert or detection that incorrectly identifies benign activity as malicious or threatening.',
      examples: 'Legitimate software flagged as malware, normal traffic triggering IDS alerts, benign files quarantined',
      mnemonic: '❌ FALSE = Fake, Alerts, Leave, Security, Everyone frustrated\nThink of car alarm going off for no reason - false alarm.',
      visual: `
    ❌ FALSE POSITIVE
    🔍 SECURITY SCANNING
         ↓
    📊 BENIGN ACTIVITY
         ↓
    🚨 INCORRECT ALERT
         ↓
    👨‍💻 ANALYST INVESTIGATION
         ↓
    ✅ CONFIRMED SAFE
         ↓
    🔧 TUNING REQUIRED`
    },
    'false-negative': {
      title: 'False Negative',
      explanation: 'Failure of security controls to detect actual malicious activity or threats that are present.',
      examples: 'Malware not detected by antivirus, real attacks missed by IDS, actual vulnerabilities not found',
      mnemonic: '😴 FALSE = Failed, Alert, Lets, Security, Enemies through\nThink of sleeping guard - real threat goes unnoticed.',
      visual: `
    😴 FALSE NEGATIVE
    🔍 SECURITY SCANNING
         ↓
    🦠 ACTUAL THREAT
         ↓
    😴 NO ALERT GENERATED
         ↓
    💥 THREAT SUCCEEDS
         ↓
    🚨 INCIDENT OCCURS
         ↓
    🔧 DETECTION IMPROVEMENT`
    },
    'cvss': {
      title: 'Common Vulnerability Scoring System (CVSS)',
      explanation: 'Standardized framework for rating the severity of security vulnerabilities with scores from 0-10.',
      examples: 'CVSS v3.1, base scores, temporal scores, environmental scores, severity ratings, vulnerability prioritization',
      mnemonic: '📊 CVSS = Common, Vulnerability, Scoring, System standardized\nThink of movie ratings - standard scale for severity.',
      visual: `
    📊 CVSS SCORING
    🔍 VULNERABILITY FOUND
         ↓
    📋 CVSS ANALYSIS
    ├── 🎯 BASE SCORE (0-10)
    ├── ⏰ TEMPORAL FACTORS
    └── 🏢 ENVIRONMENTAL IMPACT
         ↓
    📊 FINAL CVSS SCORE
         ↓
    🎯 PRIORITIZATION`
    },
    'cve': {
      title: 'Common Vulnerabilities and Exposures (CVE)',
      explanation: 'Standardized identifier system for publicly known cybersecurity vulnerabilities and exposures.',
      examples: 'CVE-2021-44228 (Log4j), CVE database, MITRE coordination, vulnerability tracking, patch management',
      mnemonic: '🏷️ CVE = Common, Vulnerabilities, Everyone tracks\nThink of barcode - unique identifier for each vulnerability.',
      visual: `
    🏷️ CVE SYSTEM
    🔍 VULNERABILITY DISCOVERED
         ↓
    📝 CVE REQUEST
         ↓
    🏛️ MITRE ASSIGNMENT
         ↓
    🏷️ CVE-YYYY-NNNNN
         ↓
    🌐 PUBLIC DATABASE
         ↓
    🔧 PATCH DEVELOPMENT`
    },
    // Domain 4.5 Digital Forensics Concepts
    'documentation-evidence': {
      title: 'Documentation/Evidence',
      explanation: 'Systematic recording and preservation of digital evidence throughout forensic investigations to maintain legal integrity.',
      examples: 'Chain of custody forms, hash values, timestamps, photographs of hardware, witness statements',
      mnemonic: '📋 DOCUMENT = Data Organized, Certified, Uniform, Methods, Evidence, Never Tampered\nThink of crime scene photographer documenting everything.',
      visual: `
    📋 DOCUMENTATION PROCESS
    🔍 EVIDENCE DISCOVERY
         ↓
    📸 PHOTOGRAPH SCENE
         ↓
    🔗 CHAIN OF CUSTODY
    ├── 👤 WHO HANDLED IT
    ├── ⏰ WHEN ACCESSED
    ├── 📍 WHERE STORED
    └── 🎯 WHY EXAMINED
         ↓
    ⚖️ COURT ADMISSIBLE`
    },
    'acquisition': {
      title: 'Acquisition',
      explanation: 'Process of creating forensically sound copies of digital evidence while maintaining data integrity.',
      examples: 'Bit-by-bit disk imaging, memory dumps, network packet captures, mobile device extraction',
      mnemonic: '💾 ACQUIRE = All Content, Quality Unchanged, Integrity, Replica Exact\nThink of making perfect photocopy of important document.',
      visual: `
    💾 FORENSIC ACQUISITION
    🖥️ SOURCE DEVICE
         ↓
    🔒 WRITE-PROTECT
         ↓
    📱 IMAGING TOOL
    ├── 🔍 BIT-BY-BIT COPY
    ├── 🏷️ HASH VERIFICATION
    └── ⏰ TIMESTAMP LOG
         ↓
    💿 FORENSIC IMAGE
         ↓
    🛡️ ORIGINAL PRESERVED`
    },
    'on-premises-cloud': {
      title: 'On-premises vs Cloud',
      explanation: 'Comparison of forensic challenges between local infrastructure and cloud-based environments.',
      examples: 'Physical server access vs API requests, direct hardware imaging vs virtual snapshots, local logs vs distributed data',
      mnemonic: '☁️ CLOUD = Complex Location, Ownership Unclear, Distributed\n🏢 ON-PREM = On-site, Nearby, Physical, Reachable, Easy, Mine',
      visual: `
    🏢 ON-PREMISES        ☁️ CLOUD
    ├── 🔧 DIRECT ACCESS    ├── 🌐 API CALLS
    ├── 🖥️ PHYSICAL MEDIA   ├── 💾 VIRTUAL DATA
    ├── 📍 SINGLE LOCATION  ├── 🌍 DISTRIBUTED
    ├── 🛡️ FULL CONTROL     ├── 🤝 SHARED CONTROL
    └── ⚡ IMMEDIATE        └── 📞 REQUEST-BASED
         ↓                      ↓
    🔍 TRADITIONAL         🆕 MODERN CHALLENGES`
    },
    'integrity': {
      title: 'Integrity',
      explanation: 'Ensuring digital evidence remains unaltered and authentic throughout the forensic process.',
      examples: 'Hash verification (MD5, SHA), digital signatures, checksums, immutable storage systems',
      mnemonic: '🔐 INTEGRITY = Information Never Tampered, Evidence Genuine, Reliable, Intact, True, Yours\nThink of sealed evidence bag in court.',
      visual: `
    🔐 EVIDENCE INTEGRITY
    📁 ORIGINAL DATA
         ↓
    🏷️ HASH CALCULATION
    MD5: a1b2c3d4...
         ↓
    🔒 SECURE STORAGE
         ↓
    🔍 RE-VERIFICATION
    MD5: a1b2c3d4... ✅
         ↓
    ⚖️ INTEGRITY PROVEN`
    },
    'preservation': {
      title: 'Preservation',
      explanation: 'Long-term protection of digital evidence from degradation, loss, or unauthorized modification.',
      examples: 'Cold storage systems, redundant backups, controlled environments, access logging, retention policies',
      mnemonic: '🗄️ PRESERVE = Protect, Retain, Environment Secure, Safe, Extended, Ready, Vault, Everything\nThink of museum preserving ancient artifacts.',
      visual: `
    🗄️ EVIDENCE PRESERVATION
    💾 DIGITAL EVIDENCE
         ↓
    🔄 MULTIPLE COPIES
    ├── 🏢 PRIMARY STORAGE
    ├── 🏛️ BACKUP SITE
    └── ☁️ CLOUD ARCHIVE
         ↓
    🌡️ CONTROLLED ENVIRONMENT
    ├── 🔒 ACCESS CONTROL
    ├── 📊 MONITORING
    └── 📝 AUDIT TRAIL
         ↓
    ⏳ LONG-TERM INTEGRITY`
    },
    'e-discovery': {
      title: 'E-Discovery',
      explanation: 'Electronic discovery process of identifying, collecting, and producing digital evidence for legal proceedings.',
      examples: 'Email searches, document review, metadata preservation, privilege screening, litigation holds',
      mnemonic: '📧 E-DISCOVERY = Electronic Documents, Investigation, Subpoena, Court, Orders, Vast, Electronic, Required, Yours\nThink of lawyer searching through digital filing cabinet.',
      visual: `
    📧 E-DISCOVERY PROCESS
    ⚖️ LEGAL MATTER
         ↓
    🔒 LITIGATION HOLD
         ↓
    🔍 DATA IDENTIFICATION
    ├── 📧 EMAILS
    ├── 📄 DOCUMENTS
    ├── 💬 MESSAGES
    └── 🗂️ DATABASES
         ↓
    📋 COLLECTION & REVIEW
         ↓
    🏛️ LEGAL PRODUCTION`
    },
    'data-recovery': {
      title: 'Data Recovery',
      explanation: 'Techniques to retrieve deleted, corrupted, or inaccessible digital data from storage devices.',
      examples: 'Undelete utilities, file carving, damaged drive repair, RAID reconstruction, mobile data extraction',
      mnemonic: '🔄 RECOVERY = Retrieve, Extract, Carve, Obtain, Verify, Everything, Restore, Yours\nThink of archaeologist carefully uncovering buried artifacts.',
      visual: `
    🔄 DATA RECOVERY
    💾 DAMAGED STORAGE
         ↓
    🔍 FORENSIC ANALYSIS
    ├── 🗑️ DELETED FILES
    ├── 📱 FILE CARVING
    ├── 🔧 SECTOR REPAIR
    └── 🧩 FRAGMENT ASSEMBLY
         ↓
    📁 RECOVERED DATA
         ↓
    ✅ VERIFICATION SUCCESS`
    },
    // Domain 4.6 Security Automation and Orchestration Concepts
    'automation-concepts': {
      title: 'Automation Concepts',
      explanation: 'Fundamental principles of using technology to perform security tasks without human intervention.',
      examples: 'Scripted responses, rule-based actions, scheduled scans, automated patching, threat intelligence feeds',
      mnemonic: '🤖 AUTOMATE = Always Useful, Tasks Operated, Mechanically, Autonomous, Technology, Everything\nThink of factory robot doing repetitive tasks perfectly.',
      visual: `
    🤖 SECURITY AUTOMATION
    🚨 SECURITY EVENT
         ↓
    🔍 AUTOMATED DETECTION
         ↓
    ⚡ INSTANT RESPONSE
    ├── 🔒 BLOCK THREAT
    ├── 📧 SEND ALERT
    ├── 📊 LOG EVENT
    └── 🛡️ APPLY COUNTERMEASURE
         ↓
    👤 HUMAN REVIEW (IF NEEDED)`
    },
    'use-cases': {
      title: 'Use Cases',
      explanation: 'Common scenarios and applications where security automation provides significant value and efficiency.',
      examples: 'Incident response, vulnerability scanning, compliance reporting, threat hunting, user provisioning',
      mnemonic: '🎯 USE-CASE = Useful Security, Every Computer, Automatically, Solving, Everything\nThink of Swiss Army knife with automatic tools.',
      visual: `
    🎯 AUTOMATION USE CASES
    📊 VULNERABILITY SCANNING
    ├── 🔍 DAILY SCANS
    ├── 📋 AUTO REPORTING
    └── 🚨 CRITICAL ALERTS
    📧 INCIDENT RESPONSE
    ├── 🔒 AUTO CONTAINMENT
    ├── 📞 STAKEHOLDER NOTIFY
    └── 📝 EVIDENCE COLLECTION
    🏛️ COMPLIANCE
    ├── 📊 AUTOMATED AUDITS
    └── 📄 REPORT GENERATION`
    },
    'benefits': {
      title: 'Benefits',
      explanation: 'Advantages gained from implementing security automation in organizational processes.',
      examples: 'Faster response times, reduced human error, 24/7 monitoring, cost efficiency, consistent processes',
      mnemonic: '✨ BENEFITS = Better, Efficiency, Never-ending, Error-free, Fast, Intelligent, Technology, Saves\nThink of superhero with powers of speed and accuracy.',
      visual: `
    ✨ AUTOMATION BENEFITS
    ⏰ SPEED
    ├── ⚡ INSTANT RESPONSE
    └── 🏃 FASTER THAN HUMAN
    🎯 ACCURACY
    ├── ❌ NO HUMAN ERROR
    └── 📋 CONSISTENT PROCESS
    💰 EFFICIENCY
    ├── 🌙 24/7 OPERATION
    ├── 💡 RESOURCE OPTIMIZATION
    └── 📈 SCALABILITY
         ↓
    🚀 ENHANCED SECURITY`
    },
    'considerations': {
      title: 'Considerations',
      explanation: 'Important factors and potential challenges to evaluate when implementing security automation.',
      examples: 'False positives, system complexity, human oversight needs, integration challenges, maintenance requirements',
      mnemonic: '⚠️ CONSIDER = Carefully Observe, Never Skip, Issues, Dangers, Everything, Risks\nThink of pilot checking all systems before takeoff.',
      visual: `
    ⚠️ AUTOMATION CONSIDERATIONS
    🤖 AUTOMATION SYSTEM
         ↓
    ❓ CHALLENGES
    ├── 🚨 FALSE POSITIVES
    ├── 🧩 COMPLEXITY
    ├── 👤 HUMAN OVERSIGHT
    ├── 🔗 INTEGRATION ISSUES
    └── 🔧 MAINTENANCE NEEDS
         ↓
    🎯 CAREFUL PLANNING
         ↓
    ✅ SUCCESSFUL DEPLOYMENT`
    },
    'integration-apis': {
      title: 'Integration and APIs',
      explanation: 'Methods for connecting security automation tools with existing systems and services through application programming interfaces.',
      examples: 'REST APIs, webhooks, SIEM connectors, cloud service APIs, third-party security tool integration',
      mnemonic: '🔗 API = Application Programming Interface, connects Everything\n🤝 INTEGRATE = Include, Numerous, Tools, Everything, Gets, Reliable, Automated, Together, Everything',
      visual: `
    🔗 API INTEGRATION
    🛡️ SECURITY TOOL A
         ↓
    📡 REST API CALL
         ↓
    🔀 ORCHESTRATION PLATFORM
         ↓
    📡 WEBHOOK TRIGGER
         ↓
    🛡️ SECURITY TOOL B
         ↓
    🔄 AUTOMATED WORKFLOW`
    },
    'protocols-standards': {
      title: 'Protocols and Standards',
      explanation: 'Established communication protocols and industry standards that enable interoperability in security automation.',
      examples: 'STIX/TAXII, OpenC2, SCAP, OVAL, REST, JSON, XML, SOAR playbook formats',
      mnemonic: '📋 STANDARDS = Structured, Tested, Always, Never-changing, Documented, Accepted, Reliable, Data, Systems\nThink of universal language for security tools.',
      visual: `
    📋 SECURITY STANDARDS
    🌐 STIX/TAXII
    ├── 🔍 THREAT INTELLIGENCE
    └── 📊 STRUCTURED DATA
    ⚡ OPENC2
    ├── 🤖 COMMAND & CONTROL
    └── 🔗 DEVICE INTEGRATION
    📊 SCAP/OVAL
    ├── 🔍 VULNERABILITY DATA
    └── 📋 COMPLIANCE CHECKS
         ↓
    🤝 INTEROPERABILITY`
    },
    // Domain 4.7 Data Sources for Investigation Concepts
    'log-data': {
      title: 'Log Data',
      explanation: 'Recorded information about system activities, events, and transactions used for security analysis.',
      examples: 'System logs, application logs, security logs, access logs, error logs, audit trails',
      mnemonic: '📝 LOGS = Life Of Generated Systems, everything recorded\nThink of ship captain\'s log recording every important event.',
      visual: `
    📝 LOG DATA SOURCES
    🖥️ SYSTEM LOGS
    ├── 🔐 AUTHENTICATION
    ├── 🔄 PROCESS EVENTS
    └── 🛡️ SECURITY EVENTS
    📱 APPLICATION LOGS
    ├── 🌐 WEB SERVER
    ├── 🗃️ DATABASE
    └── 📧 EMAIL SYSTEM
    🔍 AUDIT LOGS
    ├── 👤 USER ACTIONS
    └── 🔧 ADMIN CHANGES`
    },
    'data-sources': {
      title: 'Data Sources',
      explanation: 'Various origins of digital information used in security investigations and threat analysis.',
      examples: 'Network traffic, endpoint telemetry, cloud logs, IoT sensors, mobile devices, external feeds',
      mnemonic: '🌊 SOURCES = Systems Offering, Ultimate, Raw, Computer, Events, Systems\nThink of multiple rivers feeding into investigation lake.',
      visual: `
    🌊 INVESTIGATION DATA SOURCES
    🌐 NETWORK
    ├── 📦 PACKET CAPTURES
    ├── 🔥 FIREWALL LOGS
    └── 📡 DNS QUERIES
    💻 ENDPOINTS
    ├── 🖥️ HOST LOGS
    ├── 🦠 ANTIVIRUS ALERTS
    └── 📁 FILE ACTIVITIES
    ☁️ CLOUD PLATFORMS
    ├── 🔐 IDENTITY LOGS
    └── 📊 SERVICE METRICS`
    },
    'analysis-methods': {
      title: 'Analysis Methods',
      explanation: 'Techniques and approaches used to examine and interpret security data for threat detection and investigation.',
      examples: 'Statistical analysis, pattern recognition, correlation analysis, anomaly detection, timeline analysis',
      mnemonic: '🔬 ANALYSIS = Always, Never-ending, Assessment, Looking, Yielding, Security, Intelligence, Systems\nThink of detective examining clues with magnifying glass.',
      visual: `
    🔬 ANALYSIS METHODS
    📊 STATISTICAL ANALYSIS
    ├── 📈 TREND IDENTIFICATION
    └── 📉 ANOMALY DETECTION
    🧩 PATTERN RECOGNITION
    ├── 🔍 SIGNATURE MATCHING
    └── 🎯 BEHAVIORAL PATTERNS
    🔗 CORRELATION ANALYSIS
    ├── ⏰ TIME RELATIONSHIPS
    └── 📍 EVENT CONNECTIONS
         ↓
    🚨 THREAT IDENTIFICATION`
    },
    'siem-review': {
      title: 'SIEM Review',
      explanation: 'Process of examining Security Information and Event Management system data for security insights and incident investigation.',
      examples: 'Dashboard analysis, alert triage, log correlation, threat hunting queries, compliance reporting',
      mnemonic: '👁️ SIEM = Security Information, Event Management, see Everything\n🔍 REVIEW = Real-time, Event, Verification, Investigation, Everything, Watched',
      visual: `
    👁️ SIEM REVIEW PROCESS
    📊 SIEM DASHBOARD
         ↓
    🚨 SECURITY ALERTS
    ├── 🔴 HIGH PRIORITY
    ├── 🟡 MEDIUM PRIORITY
    └── 🟢 LOW PRIORITY
         ↓
    🔍 ALERT INVESTIGATION
    ├── 📋 LOG CORRELATION
    ├── 🎯 IOC MATCHING
    └── 📈 TREND ANALYSIS
         ↓
    🎯 THREAT VALIDATION`
    },
    'user-behavior-analytics': {
      title: 'User Behavior Analytics (UBA)',
      explanation: 'Technology that analyzes patterns of human behavior to detect potential security threats and anomalies.',
      examples: 'Baseline behavior modeling, anomalous login detection, privilege escalation alerts, data exfiltration patterns',
      mnemonic: '👤 UBA = User Behavior Analytics, understanding People\n📊 BEHAVIOR = Being, Everyone, Has, Automatic, Visible, Internal, Obvious, Routines',
      visual: `
    👤 USER BEHAVIOR ANALYTICS
    🏠 BASELINE BEHAVIOR
    ├── ⏰ NORMAL HOURS
    ├── 📍 USUAL LOCATIONS
    ├── 📁 TYPICAL FILES
    └── 💻 COMMON APPS
         ↓
    🚨 ANOMALY DETECTION
    ├── 🌙 UNUSUAL TIME
    ├── 🌍 NEW LOCATION
    ├── 💎 SENSITIVE DATA
    └── 🔓 PRIVILEGE CHANGES
         ↓
    🔍 INVESTIGATION TRIGGER`
    },
    'security-monitoring-47': {
      title: 'Security Monitoring (Investigation Data)',
      explanation: 'Continuous observation and analysis of security-related activities and events across IT infrastructure for investigation purposes.',
      examples: '24/7 SOC operations, real-time alerting, threat intelligence feeds, automated response systems',
      mnemonic: '👁️ MONITOR = Many, Online, Never-sleeping, Investigators, Tracking, Observing, Reality\nThink of security guard watching multiple camera feeds.',
      visual: `
    👁️ SECURITY MONITORING
    🖥️ SOC ANALYST
         ↓
    📺 MULTIPLE SCREENS
    ├── 🌐 NETWORK TRAFFIC
    ├── 💻 ENDPOINT STATUS
    ├── ☁️ CLOUD ACTIVITIES
    └── 👤 USER ACTIONS
         ↓
    🚨 REAL-TIME ALERTS
         ↓
    ⚡ IMMEDIATE RESPONSE
         ↓
    🛡️ THREAT MITIGATION`
    },
    // DOMAIN 4.4: SECURITY ALERTING AND MONITORING CONCEPTS
    'monitoring-computing-resources': {
      title: 'Monitoring Computing Resources',
      explanation: 'Continuous observation and analysis of systems, applications, and infrastructure to detect security issues and performance problems.',
      examples: 'CPU/memory monitoring, network traffic analysis, application performance monitoring, infrastructure health checks',
      mnemonic: '👁️ MONITORING = Many Operations, Network Intelligence, Tracking, Operations, Risk, Infrastructure, Network, Guards\nThink of security guard watching multiple camera feeds - constant vigilance.',
      visual: `
    👁️ COMPUTING RESOURCE MONITORING
    
    🖥️ SYSTEMS MONITORING:
    ├── 💾 Memory utilization
    ├── 🔄 CPU performance
    ├── 💽 Disk usage
    ├── 🌐 Network interfaces
    └── 🔧 System processes
    
    📱 APPLICATIONS MONITORING:
    ├── ⚡ Response times
    ├── 🚫 Error rates
    ├── 👥 User sessions
    ├── 📊 Transaction volumes
    └── 🔒 Security events
    
    🏗️ INFRASTRUCTURE MONITORING:
    ├── 🔌 Power systems
    ├── 🌡️ Temperature sensors
    ├── 🌐 Network devices
    ├── 🗄️ Storage systems
    └── ☁️ Cloud resources`
    },
    'monitoring-activities': {
      title: 'Security Monitoring Activities',
      explanation: 'Core activities involved in maintaining situational awareness of security posture through data collection, analysis, and response.',
      examples: 'Log aggregation from multiple sources, real-time alerting, vulnerability scanning, compliance reporting, data archiving',
      mnemonic: '🎯 ACTIVITIES = Always, Continuously, Track, Infrastructure, Vigilantly, Intelligence, Threats, Information, Everything, Security\nThink of air traffic control - coordinating multiple monitoring activities simultaneously.',
      visual: `
    🎯 SECURITY MONITORING ACTIVITIES
    
    📊 LOG AGGREGATION:
    ├── 🔗 Centralized collection
    ├── 📊 Data normalization
    ├── ⏰ Real-time processing
    └── 🗄️ Structured storage
    
    🚨 ALERTING:
    ├── 🎯 Threshold monitoring
    ├── ⚡ Real-time notifications
    ├── 📱 Multi-channel delivery
    └── 🔄 Escalation procedures
    
    🔍 SCANNING:
    ├── 📅 Scheduled assessments
    ├── 🎯 Targeted analysis
    ├── 🔄 Continuous monitoring
    └── 📋 Compliance checks
    
    📈 REPORTING & ARCHIVING:
    ├── 📊 Executive dashboards
    ├── 📋 Compliance reports
    ├── 🗄️ Long-term retention
    └── 📜 Audit trails`
    },
    'alert-response-remediation': {
      title: 'Alert Response and Remediation',
      explanation: 'Systematic process of investigating, validating, and responding to security alerts to minimize impact and prevent future incidents.',
      examples: 'Quarantining infected systems, tuning false positive alerts, incident escalation, automated response workflows',
      mnemonic: '🚨 RESPONSE = Rapid, Emergency, Security, Protocol, Operations, Network, Security, Everyone\nThink of emergency room triage - prioritize and treat based on severity.',
      visual: `
    🚨 ALERT RESPONSE PROCESS
    
    📢 ALERT GENERATED
         ↓
    🔍 INITIAL TRIAGE
    ├── 📊 Severity assessment
    ├── 🎯 Impact analysis
    ├── ✅ Alert validation
    └── 🏷️ Classification
         ↓
    🛡️ IMMEDIATE RESPONSE
    ├── 🔒 Quarantine systems
    ├── 🚫 Block threats
    ├── 📞 Notify stakeholders
    └── 📋 Document actions
         ↓
    🔧 REMEDIATION
    ├── 🩹 Apply fixes
    ├── 🔄 Restore services
    ├── ✅ Validate effectiveness
    └── 📊 Update procedures
         ↓
    🎯 ALERT TUNING
    ├── 🔧 Reduce false positives
    ├── 📈 Improve detection
    └── 📋 Update rules`
    },
    'quarantine': {
      title: 'Security Quarantine',
      explanation: 'Isolation of suspected malicious or compromised systems to prevent spread of threats while allowing for investigation and remediation.',
      examples: 'Network isolation of infected endpoint, sandbox analysis environment, restricted VLAN placement, air-gapped investigation',
      mnemonic: '🏥 QUARANTINE = Quick Action, Usually, Attracts, Rapid, Attention, Network, Threat, Isolated, Network, Emergency\nThink of medical quarantine - isolate to prevent spread while treating.',
      visual: `
    🏥 SECURITY QUARANTINE PROCESS
    
    🚨 THREAT DETECTED
         ↓
    🔒 IMMEDIATE ISOLATION
    ├── 🌐 Network disconnection
    ├── 🚫 Access restrictions
    ├── 🔧 Service suspension
    └── 📱 User notification
         ↓
    🔍 INVESTIGATION PHASE
    ├── 🧪 Forensic analysis
    ├── 🦠 Malware examination
    ├── 📊 Impact assessment
    └── 📋 Evidence collection
         ↓
    🔧 REMEDIATION
    ├── 🧹 System cleaning
    ├── 🩹 Patch application
    ├── 🔄 Configuration hardening
    └── ✅ Security validation
         ↓
    🔓 CONTROLLED RESTORATION
         ↓
    👁️ ENHANCED MONITORING`
    },
    'alert-tuning': {
      title: 'Alert Tuning',
      explanation: 'Process of adjusting security monitoring rules and thresholds to reduce false positives while maintaining detection effectiveness.',
      examples: 'Adjusting SIEM correlation rules, modifying threshold values, whitelisting known-good activities, customizing detection logic',
      mnemonic: '🎛️ TUNING = Threshold, Understanding, Network, Intelligence, Never, Generates false alerts\nThink of radio tuning - adjust frequency to get clear signal without noise.',
      visual: `
    🎛️ ALERT TUNING PROCESS
    
    📊 BASELINE ANALYSIS
    ├── 📈 Current alert volume
    ├── 🎯 False positive rate
    ├── 😴 False negative review
    └── ⏰ Response times
         ↓
    🔧 TUNING ADJUSTMENTS
    ├── 📏 Threshold modification
    ├── ⚪ Whitelist updates
    ├── 🔗 Correlation improvements
    ├── ⏰ Time-based rules
    └── 🎯 Context enrichment
         ↓
    🧪 TESTING PHASE
    ├── 🏗️ Staging environment
    ├── 📊 Impact assessment
    ├── 🎯 Accuracy validation
    └── 📋 Performance testing
         ↓
    🚀 PRODUCTION DEPLOYMENT
         ↓
    📈 CONTINUOUS MONITORING
    ├── ✅ Effectiveness metrics
    ├── 🔄 Ongoing optimization
    └── 📋 Regular reviews`
    },
    'security-monitoring-tools': {
      title: 'Security Monitoring Tools',
      explanation: 'Comprehensive suite of technologies and platforms used to detect, analyze, and respond to security threats across the enterprise.',
      examples: 'SIEM platforms, vulnerability scanners, antivirus solutions, DLP systems, SNMP monitoring, NetFlow analyzers',
      mnemonic: '🛠️ TOOLS = Technology, Operations, Operations, Logistics, Security\nThink of mechanic toolbox - right tool for each specific security job.',
      visual: `
    🛠️ SECURITY MONITORING TOOLS
    
    📊 SIEM PLATFORMS:
    ├── 📈 Real-time analysis
    ├── 🔗 Event correlation
    ├── 📋 Compliance reporting
    └── 🚨 Automated alerting
    
    🔍 SCANNING TOOLS:
    ├── 🦠 Vulnerability scanners
    ├── 🛡️ Antivirus engines
    ├── 🔒 Configuration assessments
    └── 🌐 Network discovery
    
    📡 NETWORK MONITORING:
    ├── 📊 SNMP monitoring
    ├── 🌊 NetFlow analysis
    ├── 📦 Packet capture
    └── 🔍 Traffic analysis
    
    🛡️ PROTECTION TOOLS:
    ├── 🛡️ Data Loss Prevention
    ├── 🚫 Endpoint protection
    ├── 🌐 Web filtering
    └── 📧 Email security`
    },
    'scap': {
      title: 'Security Content Automation Protocol (SCAP)',
      explanation: 'Framework of standards that enables automated vulnerability management, measurement, and compliance evaluation.',
      examples: 'OVAL definitions, XCCDF checklists, CVE references, CVSS scoring, configuration compliance checks',
      mnemonic: '📋 SCAP = Security, Content, Automation, Protocol standardized\nThink of assembly line automation - standardized process for security assessment.',
      visual: `
    📋 SCAP FRAMEWORK
    
    📊 SCAP COMPONENTS:
    ├── 🏷️ CVE (Common Vulnerabilities)
    ├── 📊 CVSS (Scoring System)
    ├── 🔍 OVAL (Assessment Language)
    ├── 📋 XCCDF (Configuration Checklist)
    ├── 🆔 CPE (Platform Enumeration)
    └── 📖 CCE (Configuration Enumeration)
    
    🔄 AUTOMATION BENEFITS:
    ├── 🎯 Standardized assessments
    ├── ⚡ Automated scanning
    ├── 📊 Consistent reporting
    ├── 📈 Compliance tracking
    └── 🔄 Continuous monitoring
    
    🏢 USE CASES:
    ├── ⚖️ Compliance validation
    ├── 🔍 Vulnerability assessment
    ├── 🔧 Configuration management
    └── 📊 Risk measurement`
    },
    'benchmarks': {
      title: 'Security Benchmarks',
      explanation: 'Standardized configuration guidelines and best practices for securing systems, applications, and infrastructure components.',
      examples: 'CIS Controls, NIST guidelines, DISA STIGs, vendor security guides, industry-specific standards',
      mnemonic: '📏 BENCHMARKS = Best, Established, Network, Configurations, Hardening, Methods, Applied, Really, Keep, Systems secure\nThink of blueprint - standardized plan for secure construction.',
      visual: `
    📏 SECURITY BENCHMARKS
    
    🏛️ STANDARD ORGANIZATIONS:
    ├── 🔒 CIS (Center for Internet Security)
    ├── 🏛️ NIST (National Institute)
    ├── 🎖️ DISA (Defense Information)
    ├── 🏢 Vendor guidelines
    └── 🏭 Industry standards
    
    🎯 BENCHMARK COVERAGE:
    ├── 🖥️ Operating systems
    ├── 🗃️ Database systems
    ├── 🌐 Web applications
    ├── 🌐 Network devices
    └── ☁️ Cloud platforms
    
    📊 IMPLEMENTATION PROCESS:
    ├── 📋 Baseline assessment
    ├── 🔧 Configuration hardening
    ├── ✅ Compliance validation
    ├── 📈 Continuous monitoring
    └── 📊 Regular auditing`
    },
    'agents-agentless': {
      title: 'Agent vs Agentless Monitoring',
      explanation: 'Different approaches to security monitoring - installing software agents on endpoints versus remote monitoring without local software.',
      examples: 'Agent-based: EDR software, antivirus clients; Agentless: Network scanning, SNMP monitoring, API-based cloud monitoring',
      mnemonic: '🤖 AGENTS = Always, Gathering, Everything, Network, Technology, Systems locally\n🌐 AGENTLESS = Always, Gathering, Everything, Network, Technology, Less, Everything, Scanned, Securely remotely\nThink of undercover agent vs surveillance camera - different ways to gather intelligence.',
      visual: `
    🤖 AGENT-BASED MONITORING
    ├── ✅ ADVANTAGES:
    │   ├── 📊 Detailed local data
    │   ├── ⚡ Real-time monitoring
    │   ├── 🔧 Local response capability
    │   └── 🔍 Deep system visibility
    └── ❌ DISADVANTAGES:
        ├── 💽 Resource consumption
        ├── 🔧 Deployment complexity
        ├── 📊 Management overhead
        └── 🎯 Attack surface increase
    
    🌐 AGENTLESS MONITORING
    ├── ✅ ADVANTAGES:
    │   ├── 🚀 Easy deployment
    │   ├── 💽 No resource impact
    │   ├── 🔧 Centralized management
    │   └── 🎯 Reduced attack surface
    └── ❌ DISADVANTAGES:
        ├── 📊 Limited visibility
        ├── 🌐 Network dependency
        ├── ⏰ Potential delays
        └── 🔒 Authentication requirements`
    },
    'siem-tools': {
      title: 'Security Information and Event Management (SIEM)',
      explanation: 'Centralized platform that aggregates, correlates, and analyzes security events from multiple sources to provide real-time threat detection.',
      examples: 'Splunk, IBM QRadar, ArcSight, LogRhythm, Microsoft Sentinel, Elastic Security',
      mnemonic: '📊 SIEM = Security, Information, Event, Management centralized\nThink of mission control center - all information flows to one place for analysis.',
      visual: `
    📊 SIEM ARCHITECTURE
    
    📡 DATA COLLECTION:
    ├── 🗄️ Log aggregation
    ├── 📡 Real-time feeds
    ├── 🌐 Network monitoring
    ├── 📱 Endpoint data
    └── ☁️ Cloud sources
         ↓
    🔍 PROCESSING ENGINE:
    ├── 📊 Data normalization
    ├── 🔗 Event correlation
    ├── 📈 Pattern analysis
    ├── 🎯 Threat detection
    └── 🤖 Machine learning
         ↓
    📈 ANALYSIS & RESPONSE:
    ├── 🚨 Real-time alerting
    ├── 📊 Dashboard visualization
    ├── 📋 Incident management
    ├── 🔍 Forensic investigation
    └── 📊 Compliance reporting
         ↓
    🎯 SECURITY OPERATIONS:
    ├── 👥 SOC team interface
    ├── 🔄 Automated responses
    ├── 📞 Notification systems
    └── 📈 Metrics tracking`
    },
    'antivirus-monitoring': {
      title: 'Antivirus Monitoring',
      explanation: 'Centralized management and monitoring of antivirus protection across enterprise endpoints to ensure consistent malware detection and response.',
      examples: 'Signature updates, quarantine management, scan scheduling, threat reporting, endpoint compliance monitoring',
      mnemonic: '🛡️ ANTIVIRUS = Always, Never, Tolerates, Infections, Vigilant, Immediate, Response, Updates, Security\nThink of immune system - constantly vigilant against infections.',
      visual: `
    🛡️ ANTIVIRUS MONITORING SYSTEM
    
    🎯 DETECTION METHODS:
    ├── 🔍 Signature-based detection
    ├── 🧠 Heuristic analysis
    ├── 🤖 Behavioral monitoring
    ├── ☁️ Cloud reputation
    └── 🔬 Machine learning
    
    📊 CENTRAL MANAGEMENT:
    ├── 📋 Policy deployment
    ├── 🔄 Signature updates
    ├── 📅 Scan scheduling
    ├── 🏥 Quarantine management
    └── 📊 Status monitoring
    
    🚨 THREAT RESPONSE:
    ├── 🔒 Automatic quarantine
    ├── 🧹 Malware removal
    ├── 📞 Alert notifications
    ├── 📋 Incident reporting
    └── 🔧 Remediation tracking
    
    📈 MONITORING METRICS:
    ├── 🎯 Detection rates
    ├── 🚨 Threat incidents
    ├── ✅ Compliance status
    ├── 🔄 Update success
    └── 📊 Performance impact`
    },
    'dlp-monitoring': {
      title: 'Data Loss Prevention (DLP) Monitoring',
      explanation: 'Continuous monitoring and protection of sensitive data to prevent unauthorized access, use, or transmission both at rest and in motion.',
      examples: 'Email attachment scanning, USB device monitoring, cloud upload detection, database access tracking, print job monitoring',
      mnemonic: '🛡️ DLP = Data, Loss, Prevention monitoring\n🔒 PROTECTION = Preventing, Risky, Operations, Through, Enhanced, Continuous, Technology, Intelligence, Operations, Networks\nThink of security guard checking bags - inspect everything leaving the building.',
      visual: `
    🛡️ DLP MONITORING ARCHITECTURE
    
    📊 DATA CLASSIFICATION:
    ├── 🏷️ Sensitive data identification
    ├── 📋 Content categorization
    ├── 🔍 Pattern recognition
    ├── 🤖 Machine learning classification
    └── 📊 Risk scoring
    
    👁️ MONITORING POINTS:
    ├── 📧 Email gateways
    ├── 🌐 Web traffic
    ├── 📱 Endpoint devices
    ├── 🌐 Network traffic
    ├── ☁️ Cloud applications
    └── 🗄️ Data repositories
    
    🚨 DETECTION & RESPONSE:
    ├── 🎯 Policy violation detection
    ├── 🚫 Automatic blocking
    ├── 🔒 Encryption enforcement
    ├── 📞 Alert generation
    └── 📋 Incident logging
    
    📈 COMPLIANCE REPORTING:
    ├── 📊 Policy effectiveness
    ├── 🎯 Violation trends
    ├── 📋 Audit trails
    └── ⚖️ Regulatory compliance`
    },
    'snmp-traps': {
      title: 'SNMP Traps Monitoring',
      explanation: 'Network management protocol that enables devices to send unsolicited alerts (traps) to monitoring systems when specific events occur.',
      examples: 'Device failure notifications, threshold breach alerts, configuration changes, link up/down events, security violations',
      mnemonic: '📡 SNMP = Simple, Network, Management, Protocol automated\n🪤 TRAPS = Triggered, Rapidly, Alerts, Problems, Spotted\nThink of mouse trap - automatically triggers when something happens.',
      visual: `
    📡 SNMP TRAP MONITORING
    
    🏗️ NETWORK INFRASTRUCTURE:
    ├── 🌐 Routers and switches
    ├── 🛡️ Firewalls
    ├── 📡 Wireless access points
    ├── 🖥️ Servers
    └── 🖨️ Network printers
         ↓
    🪤 TRAP GENERATION:
    ├── 🚨 Critical events
    ├── ⚠️ Warning conditions
    ├── 📊 Threshold breaches
    ├── 🔧 Configuration changes
    └── 🔒 Security events
         ↓
    🎯 SNMP MANAGER:
    ├── 📡 Trap collection
    ├── 📊 Event correlation
    ├── 🔍 Data parsing
    ├── 🚨 Alert generation
    └── 📋 Incident creation
         ↓
    👥 OPERATIONS TEAM:
    ├── 📱 Real-time notifications
    ├── 📊 Dashboard monitoring
    ├── 🔧 Automated responses
    └── 📋 Incident management`
    },
    'netflow-monitoring': {
      title: 'NetFlow Monitoring',
      explanation: 'Network monitoring technology that collects and analyzes network traffic flow data to provide visibility into network behavior and security threats.',
      examples: 'Bandwidth utilization analysis, traffic pattern detection, DDoS attack identification, insider threat detection, compliance monitoring',
      mnemonic: '🌊 NETFLOW = Network, Everything, Traffic, Flows, Logged, Operations, Watched\nThink of river flow meter - measuring volume, direction, and characteristics of data streams.',
      visual: `
    🌊 NETFLOW MONITORING SYSTEM
    
    📊 FLOW DATA COLLECTION:
    ├── 🔄 Source/destination IPs
    ├── 📡 Protocol information
    ├── 🚪 Port numbers
    ├── 📊 Byte/packet counts
    ├── ⏰ Timestamps
    └── 🏷️ Quality of Service
    
    🔍 TRAFFIC ANALYSIS:
    ├── 📈 Bandwidth utilization
    ├── 🎯 Top talkers identification
    ├── 🔗 Communication patterns
    ├── 🕐 Time-based trends
    └── 🚨 Anomaly detection
    
    🛡️ SECURITY APPLICATIONS:
    ├── 🌊 DDoS attack detection
    ├── 🔍 Insider threat hunting
    ├── 🦠 Malware communication
    ├── 📤 Data exfiltration detection
    └── 🎯 Lateral movement tracking
    
    📋 COMPLIANCE & REPORTING:
    ├── 📊 Traffic accounting
    ├── 🎯 Policy enforcement
    ├── 📈 Capacity planning
    └── ⚖️ Regulatory compliance`
    },
    'vulnerability-scanner-monitoring': {
      title: 'Vulnerability Scanner Monitoring',
      explanation: 'Automated systems that continuously identify, assess, and track security vulnerabilities across networks, systems, and applications.',
      examples: 'Nessus, OpenVAS, Qualys, Rapid7, network discovery scans, authenticated scans, compliance assessments',
      mnemonic: '🔍 VULNERABILITY = Very, Useful, Logging, Network, Everything, Risky, Assets, Become, Intelligence, Logged, Intelligence, Technology, Yields\nThink of medical checkup - regular examination to find health problems early.',
      visual: `
    🔍 VULNERABILITY SCANNER MONITORING
    
    🎯 SCAN TYPES:
    ├── 🌐 Network discovery
    ├── 🔓 Unauthenticated scans
    ├── 🔑 Authenticated scans
    ├── 📱 Web application scans
    ├── 🗃️ Database assessments
    └── ⚖️ Compliance checks
    
    📊 VULNERABILITY ASSESSMENT:
    ├── 🔍 Asset identification
    ├── 🦠 Vulnerability detection
    ├── 📊 Risk prioritization
    ├── 🏷️ CVSS scoring
    └── 📋 Remediation guidance
    
    🔄 CONTINUOUS MONITORING:
    ├── 📅 Scheduled scanning
    ├── 🎯 Event-triggered scans
    ├── 📈 Trend analysis
    ├── 🔄 Rescan validation
    └── 📊 Metrics tracking
    
    📋 REPORTING & INTEGRATION:
    ├── 👔 Executive dashboards
    ├── 🔧 Technical reports
    ├── 🎯 Remediation tracking
    ├── 📊 SIEM integration
    └── 🎫 Ticketing system feeds`
    },
    // DOMAIN 4.3: VULNERABILITY MANAGEMENT CONCEPTS
    'false-positive-vuln': {
      title: 'False Positive (Vulnerability Management)',
      explanation: 'When a vulnerability scanner incorrectly identifies a security issue that does not actually exist, leading to wasted remediation efforts.',
      examples: 'Scanner reports SQL injection in static content, flags secure configuration as vulnerable, detects outdated software version that was actually patched',
      mnemonic: '🚨 FALSE-POSITIVE = Fake Alert, Looks Suspicious Everyone, Phantom Occurrence, Scanner Incorrectly Triggered, Investigation Validates Error\nThink of smoke detector triggered by burnt toast - alarm sounds but no real fire.',
      visual: `
    🔍 VULNERABILITY SCANNER
         ↓
    🚨 ALERT: "SQL Injection Found!"
         ↓
    👨‍💻 ANALYST INVESTIGATES
         ↓
    🔍 DETAILED ANALYSIS
    ├── 📝 Code review
    ├── 🧪 Manual testing
    ├── 🔒 Security assessment
         ↓
    ❌ RESULT: No actual vulnerability
         ↓
    🏷️ MARKED AS FALSE POSITIVE`
    },
    'false-negative-vuln': {
      title: 'False Negative (Vulnerability Management)',
      explanation: 'When a vulnerability scanner fails to detect an actual security vulnerability that exists, creating a dangerous blind spot.',
      examples: 'Scanner misses zero-day exploit, fails to detect custom application vulnerabilities, overlooks misconfigured services',
      mnemonic: '🙈 FALSE-NEGATIVE = Fake Assurance, Lurking Security Exception, Not Exposed, Gets Attack Target, Invisible Vulnerability Exists\nThink of broken security camera - you think area is secure but cannot see the actual threat.',
      visual: `
    🔍 VULNERABILITY SCANNER
         ↓
    ✅ REPORT: "No vulnerabilities found"
         ↓
    😴 FALSE SENSE OF SECURITY
         ↓
    🏴‍☠️ ACTUAL VULNERABILITY EXISTS
    ├── 💀 Zero-day exploit
    ├── 🐛 Custom app bugs
    ├── ⚙️ Misconfigurations
         ↓
    💥 POTENTIAL BREACH
         ↓
    🚨 MISSED OPPORTUNITY FOR PREVENTION`
    },
    'cvss-scoring': {
      title: 'Common Vulnerability Scoring System (CVSS)',
      explanation: 'Standardized framework for rating the severity of security vulnerabilities using numerical scores from 0.0 to 10.0.',
      examples: 'CVSS 9.8 (Critical): Remote code execution, CVSS 7.2 (High): Privilege escalation, CVSS 4.3 (Medium): Information disclosure',
      mnemonic: '📊 CVSS = Common Vulnerability Scoring System\n🎯 SCORE = Severity Calculation, Overall Risk Evaluation\nThink of credit score - standardized number that everyone understands for risk assessment.',
      visual: `
    📊 CVSS SCORING SYSTEM
    
    🔴 CRITICAL (9.0-10.0)
    ├── Remote code execution
    ├── Complete system compromise
    └── Immediate patch required
    
    🟠 HIGH (7.0-8.9)
    ├── Privilege escalation
    ├── Data exfiltration risk
    └── Patch within days
    
    🟡 MEDIUM (4.0-6.9)
    ├── Information disclosure
    ├── Denial of service
    └── Patch within weeks
    
    🟢 LOW (0.1-3.9)
    ├── Minor information leak
    ├── Limited impact
    └── Patch when convenient`
    },
    'cve-enumeration': {
      title: 'Common Vulnerability Enumeration (CVE)',
      explanation: 'Standardized identifier system for publicly known cybersecurity vulnerabilities, providing unique CVE numbers for tracking.',
      examples: 'CVE-2021-44228 (Log4Shell), CVE-2020-1472 (Zerologon), CVE-2017-0144 (EternalBlue/WannaCry)',
      mnemonic: '🆔 CVE = Common Vulnerability Enumeration\n📋 TRACKING = Thorough Registry And Catalog, Keeps Individual Numbers, Global\nThink of social security number - unique identifier that follows you everywhere.',
      visual: `
    🆔 CVE IDENTIFIER SYSTEM
    
    📋 CVE-YYYY-NNNN FORMAT
    ├── CVE = Prefix
    ├── YYYY = Year discovered
    └── NNNN = Sequential number
    
    📖 EXAMPLE: CVE-2021-44228
    ├── 🗓️ Discovered in 2021
    ├── 🔢 44228th vulnerability of year
    ├── 📝 Description: Log4Shell
    ├── 📊 CVSS Score: 10.0
    └── 🌐 Global reference standard
    
    🔗 BENEFITS:
    ├── Universal tracking
    ├── Cross-platform references
    ├── Threat intelligence sharing
    └── Patch management coordination`
    },
    'vulnerability-classification': {
      title: 'Vulnerability Classification',
      explanation: 'Systematic categorization of security vulnerabilities based on type, impact, exploitability, and other characteristics.',
      examples: 'Buffer overflow, SQL injection, cross-site scripting (XSS), privilege escalation, denial of service',
      mnemonic: '🏷️ CLASSIFICATION = Categories, Labels, Assigns Security, Systems, Information, Facilitates, Information, Categories, Attack Types, Important, Organized, Networks\nThink of library classification system - organizing by type makes finding and addressing easier.',
      visual: `
    🏷️ VULNERABILITY CLASSIFICATION
    
    💻 BY VULNERABILITY TYPE:
    ├── 🗃️ Buffer overflow
    ├── 💉 Injection attacks
    ├── 🔐 Authentication bypass
    ├── 🔓 Privilege escalation
    └── 🌐 Web application flaws
    
    📊 BY IMPACT LEVEL:
    ├── 🔴 Data breach potential
    ├── 🟠 System compromise
    ├── 🟡 Service disruption
    └── 🟢 Information disclosure
    
    ⚡ BY EXPLOITABILITY:
    ├── 🎯 Remote exploitable
    ├── 🏠 Local access required
    ├── 👤 User interaction needed
    └── 🔧 Configuration dependent`
    },
    'exposure-factor': {
      title: 'Exposure Factor',
      explanation: 'Percentage of asset value that would be lost if a specific threat successfully exploits a vulnerability, used in risk calculations.',
      examples: 'Database breach: 80% exposure factor, Website defacement: 20% exposure factor, System downtime: 60% exposure factor',
      mnemonic: '📈 EXPOSURE = Expected eXposure Percentage, Outcome, Shows Unique Risk Effects\n💰 FACTOR = Financial Asset Calculation, Total Operational Risk\nThink of insurance deductible - percentage of loss you absorb when bad thing happens.',
      visual: `
    📈 EXPOSURE FACTOR CALCULATION
    
    💰 ASSET VALUE: $100,000
         ↓
    🎯 THREAT SCENARIO: Database breach
         ↓
    📊 EXPOSURE FACTOR: 80%
         ↓
    💸 POTENTIAL LOSS: $80,000
    
    📋 COMMON EXPOSURE FACTORS:
    ├── 🗃️ Data breach: 70-90%
    ├── 💻 System compromise: 50-80%
    ├── 🌐 Website defacement: 10-30%
    ├── ⏰ Service disruption: 40-70%
    └── 📱 Device theft: 20-60%
    
    🧮 RISK FORMULA:
    Risk = Asset Value × Threat Probability × Exposure Factor`
    },
    'environmental-variables': {
      title: 'Environmental Variables (Vulnerability Context)',
      explanation: 'Specific environmental factors that influence vulnerability severity and exploitability in different organizational contexts.',
      examples: 'Network segmentation, access controls, monitoring capabilities, patch management maturity, incident response readiness',
      mnemonic: '🌍 ENVIRONMENTAL = Environment Network Variables, Infrastructure, Risk, Organizational, Network, Management, External, Network, Technology, Assets, Local\nThink of climate factors - same seed grows differently in different environments.',
      visual: `
    🌍 ENVIRONMENTAL VARIABLES
    
    🏢 ORGANIZATIONAL FACTORS:
    ├── 🛡️ Security maturity
    ├── 👥 Staff expertise
    ├── 💰 Budget constraints
    ├── 📋 Compliance requirements
    └── 🔄 Change management
    
    🌐 TECHNICAL ENVIRONMENT:
    ├── 🔧 Network architecture
    ├── 🔒 Access controls
    ├── 👁️ Monitoring coverage
    ├── 🔄 Patch management
    └── 🚨 Incident response
    
    📊 IMPACT ON VULNERABILITY:
    ├── ⬆️ Increases severity
    ├── ⬇️ Reduces exploitability
    ├── 🔄 Changes prioritization
    └── 🎯 Affects remediation strategy`
    },
    'industry-organizational-impact': {
      title: 'Industry/Organizational Impact',
      explanation: 'How vulnerabilities affect different industries and organizations based on their specific operational requirements and threat landscape.',
      examples: 'Healthcare: HIPAA compliance risks, Financial: PCI DSS violations, Government: National security implications',
      mnemonic: '🏭 INDUSTRY = Impact, Network Damage, Unique, Specific, Trade, Regulatory, Yields\n🏢 ORGANIZATIONAL = Operations, Risk, Governance, Assets, Needs, Industry, Zone, Applicable, Threats, Impact, Operational, Network, Attacks, Losses\nThink of different sports - same injury affects athletes differently based on their sport.',
      visual: `
    🏭 INDUSTRY-SPECIFIC IMPACTS
    
    🏥 HEALTHCARE:
    ├── ⚖️ HIPAA violations
    ├── 👤 Patient privacy breaches
    ├── 💀 Life-critical system failures
    └── 💰 $10M+ average breach cost
    
    🏦 FINANCIAL SERVICES:
    ├── 💳 PCI DSS non-compliance
    ├── 💰 Financial fraud/theft
    ├── 📊 Market manipulation
    └── 🏛️ Regulatory penalties
    
    🏛️ GOVERNMENT:
    ├── 🔐 National security risks
    ├── 🕵️ Espionage threats
    ├── 👥 Citizen data exposure
    └── 🎯 Advanced persistent threats
    
    🏭 MANUFACTURING:
    ├── 🏭 Operational technology risks
    ├── ⚙️ Production disruption
    ├── 🔧 Industrial espionage
    └── 🚨 Safety system compromise`
    },
    'vulnerability-response-remediation': {
      title: 'Vulnerability Response and Remediation',
      explanation: 'Systematic process of addressing identified vulnerabilities through various strategies including patching, controls, and risk mitigation.',
      examples: 'Emergency patching for critical vulnerabilities, implementing compensating controls, risk acceptance for low-severity issues',
      mnemonic: '🔧 REMEDIATION = Repair, Emergency, Mitigate, Execute, Deploy, Immediate, Actions, Threat, Intervention, Operations, Networks\nThink of medical treatment - diagnose the problem, then apply appropriate treatment based on severity.',
      visual: `
    🔧 VULNERABILITY REMEDIATION PROCESS
    
    🔍 VULNERABILITY IDENTIFIED
         ↓
    📊 RISK ASSESSMENT
    ├── CVSS score analysis
    ├── Environmental factors
    ├── Business impact
    └── Exploitability review
         ↓
    🎯 REMEDIATION STRATEGY
    ├── 🩹 Patch immediately
    ├── 🛡️ Implement controls
    ├── 🔄 Temporary workaround
    ├── ⚖️ Accept risk
    └── 🚫 Remove/isolate system
         ↓
    ✅ VALIDATION & VERIFICATION
         ↓
    📋 DOCUMENTATION & REPORTING`
    },
    'patching': {
      title: 'Security Patching',
      explanation: 'Process of applying software updates to fix security vulnerabilities, requiring careful testing and deployment procedures.',
      examples: 'Emergency patches for zero-day exploits, scheduled monthly patch cycles, rollback procedures for problematic updates',
      mnemonic: '🩹 PATCHING = Patches, Apply, Technology, Correcting, Holes, Instantly, Network, Gaps\nThink of tire patch - seal the hole to prevent air leakage and maintain function.',
      visual: `
    🩹 SECURITY PATCHING PROCESS
    
    🚨 PATCH NOTIFICATION
         ↓
    📊 PRIORITY ASSESSMENT
    ├── 🔴 Critical: <24 hours
    ├── 🟠 High: <7 days
    ├── 🟡 Medium: <30 days
    └── 🟢 Low: Next cycle
         ↓
    🧪 TESTING PHASE
    ├── 🔬 Lab environment
    ├── 🧪 Compatibility testing
    ├── 📋 Rollback planning
    └── ✅ Approval process
         ↓
    🚀 DEPLOYMENT
    ├── 🎯 Pilot group
    ├── 📈 Phased rollout
    ├── 👁️ Monitoring
    └── 🔄 Rollback if needed
         ↓
    ✅ VERIFICATION & REPORTING`
    },
    'insurance': {
      title: 'Cyber Insurance',
      explanation: 'Risk transfer mechanism that provides financial protection against cyber incidents, often requiring specific security controls and practices.',
      examples: 'Data breach response coverage, business interruption insurance, regulatory fine coverage, cyber extortion protection',
      mnemonic: '🛡️ INSURANCE = In Network Security, Under Robust, Assurance, Network, Coverage, Expenses\nThink of car insurance - pay premiums for protection when accidents happen.',
      visual: `
    🛡️ CYBER INSURANCE COVERAGE
    
    💰 FINANCIAL PROTECTION:
    ├── 🚨 Incident response costs
    ├── ⚖️ Legal fees and fines
    ├── 💼 Business interruption
    ├── 🔧 System restoration
    └── 📞 Public relations
    
    📋 COVERAGE REQUIREMENTS:
    ├── 🔒 Security controls in place
    ├── 📊 Regular assessments
    ├── 🧑‍🎓 Staff training programs
    ├── 🚨 Incident response plan
    └── 📝 Documentation standards
    
    ⚖️ RISK TRANSFER MODEL:
    ├── 🏢 Organization pays premiums
    ├── 🛡️ Insurer covers losses
    ├── 🤝 Shared responsibility
    └── 💼 Business continuity`
    },
    'segmentation': {
      title: 'Network Segmentation',
      explanation: 'Dividing networks into smaller, isolated segments to limit the spread of attacks and reduce the impact of vulnerabilities.',
      examples: 'VLAN separation, subnet isolation, air-gapped critical systems, micro-segmentation for zero trust',
      mnemonic: '🧱 SEGMENTATION = Separate, Extremely, Generate, Meaningful, Effective, Network, Traffic, Allows, Threat, Isolation, Operations, Networks\nThink of watertight compartments on ship - if one floods, others stay dry.',
      visual: `
    🧱 NETWORK SEGMENTATION
    
    🌐 TRADITIONAL FLAT NETWORK:
    ├── 💥 Single breach = full access
    ├── 🔓 Lateral movement easy
    └── 🎯 High blast radius
    
    🧱 SEGMENTED NETWORK:
    ├── 🏢 DMZ (Web servers)
    ├── 💼 Internal LAN (Users)
    ├── 🗃️ Database tier
    ├── 🔧 Management network
    └── 🏭 OT/IoT segment
    
    🛡️ SEGMENTATION BENEFITS:
    ├── 🔒 Limits attack spread
    ├── 🎯 Reduces blast radius
    ├── 👁️ Improves monitoring
    ├── ⚖️ Enables compliance
    └── 🔧 Easier management`
    },
    'compensating-controls': {
      title: 'Compensating Controls',
      explanation: 'Alternative security measures implemented when primary controls cannot be applied, providing equivalent protection through different means.',
      examples: 'WAF when unable to patch web app, network monitoring when endpoint detection unavailable, manual processes when automation fails',
      mnemonic: '🔄 COMPENSATING = Controls, Operations, Mitigate, Protect, Equivalent, Network, Security, Alternative, Threat, Intelligent, Network, Guards\nThink of spare tire - different from regular tire but gets you where you need to go.',
      visual: `
    🔄 COMPENSATING CONTROLS
    
    🎯 SCENARIO: Cannot patch critical system
         ↓
    ❌ PRIMARY CONTROL: Security patch
         ↓
    🔄 COMPENSATING CONTROLS:
    ├── 🛡️ Web Application Firewall
    ├── 🌐 Network segmentation
    ├── 👁️ Enhanced monitoring
    ├── 🔒 Access restrictions
    └── 🚨 Threat detection
    
    ✅ EQUIVALENT PROTECTION ACHIEVED
    
    📋 CONTROL EXAMPLES:
    ├── 🔧 Technical controls
    ├── 📝 Administrative controls
    ├── 🏢 Physical controls
    └── 🔄 Procedural controls`
    },
    'exceptions-exemptions': {
      title: 'Security Exceptions and Exemptions',
      explanation: 'Formal documented decisions to accept specific security risks or deviate from security policies due to business requirements.',
      examples: 'Legacy system exemption from patching, temporary exception for critical business process, risk acceptance for low-impact vulnerability',
      mnemonic: '📄 EXCEPTIONS = Explicit, eXemptions, Calculated, Evaluated, Policy, Temporary, Immediate, Operations, Network, Security\nThink of medical exemption - documented reason why normal rules do not apply in specific case.',
      visual: `
    📄 SECURITY EXCEPTIONS PROCESS
    
    🚨 SECURITY REQUIREMENT
         ↓
    ❌ CANNOT COMPLY
    ├── 💰 Cost prohibitive
    ├── ⏰ Time constraints
    ├── 🔧 Technical limitations
    └── 💼 Business impact
         ↓
    📝 EXCEPTION REQUEST
    ├── 📊 Risk assessment
    ├── 🔄 Compensating controls
    ├── 📅 Time limitations
    └── 💼 Business justification
         ↓
    ⚖️ RISK ACCEPTANCE
    ├── 👥 Management approval
    ├── 📋 Documented decision
    ├── 👁️ Regular review
    └── 📅 Expiration date`
    },
    'validation-remediation': {
      title: 'Validation of Remediation',
      explanation: 'Process of confirming that implemented security fixes actually resolve vulnerabilities and do not introduce new issues.',
      examples: 'Re-scanning patched systems, penetration testing post-remediation, compliance audits, functional testing',
      mnemonic: '✅ VALIDATION = Verify, Always, Legitimate, Investigate, Deploy, Appropriate, Testing, Immediate, Operations, Networks\nThink of quality control - test the fix to make sure it actually works before calling job complete.',
      visual: `
    ✅ REMEDIATION VALIDATION PROCESS
    
    🔧 REMEDIATION APPLIED
         ↓
    📊 VALIDATION METHODS:
    ├── 🔍 Vulnerability re-scan
    ├── 🧪 Penetration testing
    ├── 📋 Configuration review
    ├── 🔧 Functional testing
    └── 📊 Compliance audit
         ↓
    📈 VALIDATION RESULTS:
    ├── ✅ Vulnerability resolved
    ├── ⚠️ Partial remediation
    ├── ❌ Fix unsuccessful
    └── 🚨 New issues introduced
         ↓
    📋 DOCUMENTATION & REPORTING
         ↓
    🔄 CONTINUOUS MONITORING`
    },
    'rescanning': {
      title: 'Vulnerability Rescanning',
      explanation: 'Follow-up security scans performed after remediation to verify that vulnerabilities have been successfully addressed.',
      examples: 'Automated rescans after patch deployment, targeted scans of specific systems, compliance verification scans',
      mnemonic: '🔄 RESCANNING = Re-examine, Everything, Security, Confirm, Applied, Network, Network, Infrastructure, Network, Guards\nThink of medical follow-up - check that treatment worked and patient is healing properly.',
      visual: `
    🔄 VULNERABILITY RESCANNING CYCLE
    
    🚨 INITIAL VULNERABILITY SCAN
         ↓
    📋 VULNERABILITIES IDENTIFIED
         ↓
    🔧 REMEDIATION APPLIED
         ↓
    🔍 RESCAN PERFORMED
    ├── 🎯 Targeted rescanning
    ├── 🌐 Full network rescan
    ├── ⏰ Scheduled intervals
    └── 🔧 On-demand rescans
         ↓
    📊 RESULTS ANALYSIS:
    ├── ✅ Vulnerability resolved
    ├── 🔄 Still present (re-remediate)
    ├── 🆕 New vulnerabilities found
    └── 📈 Risk posture improved
         ↓
    📋 UPDATED REPORTS & TRACKING`
    },
    'audit': {
      title: 'Security Audit',
      explanation: 'Systematic examination of security controls, policies, and procedures to verify compliance and effectiveness.',
      examples: 'Internal security audits, third-party compliance audits, penetration testing assessments, configuration reviews',
      mnemonic: '🔍 AUDIT = Assess, Understand, Determine, Investigate, Thoroughly\nThink of financial audit - independent verification that everything is in order and follows proper procedures.',
      visual: `
    🔍 SECURITY AUDIT PROCESS
    
    📋 AUDIT SCOPE DEFINITION
    ├── 🎯 Systems to review
    ├── ⚖️ Compliance requirements
    ├── 🔒 Security controls
    └── 📅 Time period
         ↓
    🔍 EVIDENCE COLLECTION
    ├── 📄 Documentation review
    ├── 🖥️ System configuration
    ├── 🗣️ Interviews
    └── 🧪 Technical testing
         ↓
    📊 ANALYSIS & FINDINGS
    ├── ✅ Compliant areas
    ├── ⚠️ Gaps identified
    ├── 🚨 Critical issues
    └── 💡 Recommendations
         ↓
    📋 AUDIT REPORT
         ↓
    🔧 REMEDIATION TRACKING`
    },
    'verification': {
      title: 'Security Verification',
      explanation: 'Process of confirming that security measures are working as intended and providing the expected level of protection.',
      examples: 'Control testing, security metrics validation, incident response drills, compliance verification',
      mnemonic: '✅ VERIFICATION = Validate, Examine, Risk, Investigate, Functional, Infrastructure, Confirms, Applied, Technology, Immediately, Operations, Networks\nThink of checking your work - make sure everything is correct before submitting.',
      visual: `
    ✅ SECURITY VERIFICATION METHODS
    
    🧪 TECHNICAL VERIFICATION:
    ├── 🔍 Vulnerability scanning
    ├── 🎯 Penetration testing
    ├── 📊 Log analysis
    └── 🔧 Configuration checks
    
    📋 ADMINISTRATIVE VERIFICATION:
    ├── 📝 Policy compliance
    ├── 🧑‍🎓 Training completion
    ├── 📊 Metrics review
    └── 🔄 Process validation
    
    🏢 PHYSICAL VERIFICATION:
    ├── 🚪 Access control testing
    ├── 📹 Surveillance review
    ├── 🔒 Physical security audit
    └── 🚨 Alarm system testing
    
    📈 VERIFICATION OUTCOMES:
    ├── ✅ Controls effective
    ├── 🔧 Needs adjustment
    ├── ❌ Control failure
    └── 💡 Improvement opportunities`
    },
    'reporting': {
      title: 'Vulnerability Reporting',
      explanation: 'Systematic documentation and communication of vulnerability findings, remediation status, and security metrics to stakeholders.',
      examples: 'Executive dashboards, technical vulnerability reports, compliance reports, trend analysis, risk scorecards',
      mnemonic: '📊 REPORTING = Risk, Evaluation, Prepared, Official, Records, Threat, Information, Network, Gaps\nThink of weather report - regular updates on current conditions and forecasts for decision-making.',
      visual: `
    📊 VULNERABILITY REPORTING STRUCTURE
    
    👔 EXECUTIVE LEVEL:
    ├── 📈 Risk trends
    ├── 💰 Cost impact
    ├── ⚖️ Compliance status
    └── 🎯 Strategic priorities
    
    🔧 TECHNICAL LEVEL:
    ├── 📋 Detailed findings
    ├── 🔍 Vulnerability details
    ├── 🛠️ Remediation steps
    └── ⏰ Timelines
    
    📊 OPERATIONAL LEVEL:
    ├── 📅 Patch schedules
    ├── 🎯 Priority queues
    ├── 📈 Progress tracking
    └── 🔄 Resource allocation
    
    📋 REPORT COMPONENTS:
    ├── 🎯 Current state
    ├── 📈 Trends over time
    ├── 🔄 Actions taken
    └── 💡 Recommendations`
    },
    // DOMAIN 4.8: INCIDENT RESPONSE CONCEPTS
    'incident-response-process': {
      title: 'Incident Response Process',
      explanation: 'Structured methodology for detecting, responding to, and recovering from cybersecurity incidents following established phases.',
      examples: 'NIST IR framework, preparation planning, containment strategies, eradication procedures, recovery validation',
      mnemonic: '🚨 PROCESS = Preparation, Response, Operations, Contain, Eradicate, Secure, Systems\nThink of emergency response drill - practiced steps for crisis management.',
      visual: `
    🚨 INCIDENT RESPONSE PROCESS
    
    1️⃣ PREPARATION
    ├── 📋 IR plan development
    ├── 👥 Team training
    ├── 🛠️ Tool deployment
    └── 📞 Communication setup
         ↓
    2️⃣ DETECTION & ANALYSIS
    ├── 🔍 Event identification
    ├── 📊 Impact assessment
    ├── 🏷️ Incident classification
    └── 📋 Documentation
         ↓
    3️⃣ CONTAINMENT
    ├── 🔒 Short-term containment
    ├── 🛡️ Long-term containment
    └── 🧪 Forensic imaging
         ↓
    4️⃣ ERADICATION
    ├── 🧹 Malware removal
    ├── 🔧 Vulnerability patching
    └── 🔄 System hardening
         ↓
    5️⃣ RECOVERY
    ├── 🔄 System restoration
    ├── 👁️ Enhanced monitoring
    └── ✅ Validation testing
         ↓
    6️⃣ LESSONS LEARNED
    ├── 📋 Post-incident review
    ├── 📊 Process improvement
    └── 📝 Documentation update`
    },
    'incident-response-steps': {
      title: 'Preparation, Detection, Analysis, Containment, Eradication, Recovery, Lessons Learned',
      explanation: 'The seven-phase incident response lifecycle that guides organizations through systematic incident handling.',
      examples: 'Preparation: IR team setup; Detection: SIEM alerts; Analysis: threat assessment; Containment: network isolation',
      mnemonic: '🎯 PDACREL = Prepare, Detect, Analyze, Contain, Remove, Enable, Learn\nThink of PDCA cycle extended for security incidents.',
      visual: `
    🎯 INCIDENT RESPONSE LIFECYCLE
    
    🛠️ PREPARATION PHASE:
    ├── 📋 Incident response plan
    ├── 👥 Response team roles
    ├── 🔧 Tools and resources
    ├── 📞 Communication procedures
    └── 🧑‍🎓 Training and drills
    
    🔍 DETECTION & ANALYSIS:
    ├── 🚨 Alert generation
    ├── 📊 Initial assessment
    ├── 🔍 Evidence collection
    ├── 🏷️ Incident categorization
    └── 📈 Impact evaluation
    
    🔒 CONTAINMENT:
    ├── ⚡ Short-term isolation
    ├── 🛡️ Long-term containment
    ├── 💾 System imaging
    └── 🚫 Threat neutralization
    
    🧹 ERADICATION:
    ├── 🦠 Malware removal
    ├── 🔧 Vulnerability remediation
    ├── 🔄 System cleaning
    └── 🛡️ Security hardening
    
    🔄 RECOVERY:
    ├── 🖥️ System restoration
    ├── 📊 Monitoring enhancement
    ├── ✅ Functionality testing
    └── 🔄 Gradual normalization
    
    📚 LESSONS LEARNED:
    ├── 📋 Post-incident analysis
    ├── 📊 Process evaluation
    ├── 💡 Improvement recommendations
    └── 📝 Knowledge base update`
    },
    'incident-response-training': {
      title: 'Incident Response Training',
      explanation: 'Educational programs and exercises designed to prepare incident response teams for effective security incident handling.',
      examples: 'Tabletop exercises, simulated attack scenarios, role-based training, technical skill development, communication drills',
      mnemonic: '🧑‍🎓 TRAINING = Team, Readiness, Always, Improves, Network, Intelligence, Never, Guessing\nThink of fire drill - practice makes perfect in emergency response.',
      visual: `
    🧑‍🎓 INCIDENT RESPONSE TRAINING
    
    📚 TRAINING COMPONENTS:
    ├── 📋 IR procedures knowledge
    ├── 🔧 Technical skills development
    ├── 💬 Communication protocols
    ├── ⚖️ Legal considerations
    └── 🤝 Team coordination
    
    🎯 TRAINING METHODS:
    ├── 📖 Classroom instruction
    ├── 🖥️ Online modules
    ├── 🎮 Simulation exercises
    ├── 🗣️ Tabletop discussions
    └── 🏃‍♂️ Live response drills
    
    📊 SKILL AREAS:
    ├── 🔍 Forensic analysis
    ├── 🦠 Malware analysis
    ├── 🌐 Network investigation
    ├── 📋 Documentation practices
    └── 🗣️ Stakeholder communication
    
    🔄 CONTINUOUS IMPROVEMENT:
    ├── 📈 Performance assessment
    ├── 📋 Skills gap analysis
    ├── 🎯 Targeted training
    └── 📚 Knowledge updates`
    },
    'incident-response-testing': {
      title: 'Incident Response Testing',
      explanation: 'Systematic evaluation of incident response capabilities through exercises and simulations to validate preparedness.',
      examples: 'Tabletop exercises, functional exercises, full-scale simulations, red team exercises, disaster recovery tests',
      mnemonic: '🧪 TESTING = Team, Exercises, Simulate, Threats, Improve, Never, Guessing\nThink of rehearsal before performance - practice reveals gaps.',
      visual: `
    🧪 INCIDENT RESPONSE TESTING
    
    📋 TABLETOP EXERCISES:
    ├── 🗣️ Discussion-based scenarios
    ├── 📊 Decision-making practice
    ├── 💬 Communication testing
    ├── 📋 Process validation
    └── 🤝 Team coordination
    
    🎮 SIMULATION EXERCISES:
    ├── 🖥️ Technical simulations
    ├── 🎯 Realistic scenarios
    ├── ⏰ Time-pressure testing
    ├── 🔧 Tool utilization
    └── 📊 Performance metrics
    
    🔴 RED TEAM EXERCISES:
    ├── 🎯 Actual attack simulation
    ├── 🔍 Detection capability testing
    ├── ⚡ Response time measurement
    ├── 🛡️ Defense effectiveness
    └── 📈 Realistic assessment
    
    📊 TESTING OUTCOMES:
    ├── ✅ Strengths identification
    ├── ⚠️ Gaps discovery
    ├── 💡 Improvement areas
    ├── 📋 Plan updates
    └── 🧑‍🎓 Training needs`
    },
    'root-cause-analysis': {
      title: 'Root Cause Analysis',
      explanation: 'Systematic investigation methodology to identify the fundamental cause of security incidents to prevent recurrence.',
      examples: 'Five Whys technique, fishbone diagrams, fault tree analysis, timeline reconstruction, contributing factor identification',
      mnemonic: '🔍 ROOT-CAUSE = Really, Organize, Operations, Thoroughly, Carefully, Analyze, Underlying, Source, Everything\nThink of detective work - dig deeper to find the real culprit.',
      visual: `
    🔍 ROOT CAUSE ANALYSIS PROCESS
    
    📊 INCIDENT OCCURRENCE
         ↓
    🔍 IMMEDIATE CAUSE IDENTIFICATION
    ├── 🎯 What happened?
    ├── ⏰ When did it occur?
    ├── 🏢 Where did it happen?
    └── 👤 Who was involved?
         ↓
    🧩 CONTRIBUTING FACTORS
    ├── 🔧 Technical factors
    ├── 👥 Human factors
    ├── 📋 Process factors
    ├── 🏢 Organizational factors
    └── 🌍 Environmental factors
         ↓
    🎯 ROOT CAUSE IDENTIFICATION
    ├── ❓ Five Whys technique
    ├── 🐟 Fishbone diagram
    ├── 🌳 Fault tree analysis
    └── 📊 Timeline analysis
         ↓
    💡 CORRECTIVE ACTIONS
    ├── 🔧 Technical improvements
    ├── 📋 Process changes
    ├── 🧑‍🎓 Training updates
    └── 🛡️ Control enhancements
         ↓
    🔄 PREVENTION MEASURES
    ├── 📊 Monitoring improvements
    ├── 🚨 Early warning systems
    └── 📋 Policy updates`
    },
    'threat-hunting': {
      title: 'Threat Hunting',
      explanation: 'Proactive security practice of searching through networks and datasets to detect and isolate advanced threats that evade security solutions.',
      examples: 'Hypothesis-driven hunting, IOC-based searches, behavioral analysis, network traffic analysis, endpoint investigation',
      mnemonic: '🎯 HUNTING = Hypotheses, Uncover, Network, Threats, Intelligence, Never, Guessing\nThink of detective following clues - proactive investigation vs waiting for alarms.',
      visual: `
    🎯 THREAT HUNTING PROCESS
    
    💡 HYPOTHESIS DEVELOPMENT
    ├── 🧠 Threat intelligence input
    ├── 📊 Risk assessment
    ├── 🎯 Attack scenarios
    └── 🔍 Investigation focus
         ↓
    🔍 DATA COLLECTION
    ├── 📊 Log aggregation
    ├── 🌐 Network traffic
    ├── 📱 Endpoint data
    ├── 🛡️ Security tool data
    └── ☁️ Cloud telemetry
         ↓
    📊 ANALYSIS TECHNIQUES
    ├── 📈 Statistical analysis
    ├── 🔗 Behavioral analysis
    ├── 🏷️ IOC matching
    ├── 🕐 Timeline analysis
    └── 🔗 Correlation analysis
         ↓
    🎯 THREAT IDENTIFICATION
    ├── 🚨 Suspicious activity
    ├── 🦠 Malicious indicators
    ├── 🔗 Attack patterns
    └── 📊 Anomaly detection
         ↓
    🛡️ RESPONSE ACTIONS
    ├── 🔒 Threat containment
    ├── 📋 Incident escalation
    ├── 🧹 Evidence collection
    └── 💡 Intelligence sharing`
    },
    'digital-forensics-ir': {
      title: 'Digital Forensics (Incident Response)',
      explanation: 'Application of forensic techniques during incident response to preserve evidence, analyze attack methods, and support legal proceedings.',
      examples: 'Evidence acquisition during IR, chain of custody maintenance, legal hold implementation, forensic imaging, court admissibility',
      mnemonic: '🔬 FORENSICS = Find, Official, Records, Evidence, Never, Skipping, Investigation, Chain, Security\nThink of CSI for cyber incidents - scientific evidence collection.',
      visual: `
    🔬 DIGITAL FORENSICS IN IR
    
    🚨 INCIDENT DETECTED
         ↓
    ⚖️ LEGAL HOLD ACTIVATED
    ├── 📋 Preservation notice
    ├── 🚫 Deletion prevention
    ├── 📊 Scope definition
    └── 👥 Stakeholder notification
         ↓
    🔗 CHAIN OF CUSTODY
    ├── 👤 Evidence handler ID
    ├── ⏰ Timestamp recording
    ├── 📍 Location tracking
    ├── 🔒 Integrity verification
    └── 📋 Transfer documentation
         ↓
    💾 EVIDENCE ACQUISITION
    ├── 📱 Live system imaging
    ├── 💽 Storage device cloning
    ├── 🧠 Memory capture
    ├── 🌐 Network traffic logs
    └── ☁️ Cloud data preservation
         ↓
    📋 REPORTING & DOCUMENTATION
    ├── 🔍 Analysis findings
    ├── 📊 Evidence inventory
    ├── ⚖️ Legal compliance
    └── 🏛️ Court preparation`
    },
    // DOMAIN 4.4 SPECIFIC SUB-CONCEPTS
    'log-aggregation': {
      title: 'Log Aggregation',
      explanation: 'Process of collecting log data from multiple sources into a centralized location for analysis and correlation.',
      examples: 'Syslog servers, log forwarding agents, SIEM log collection, centralized log storage, real-time log streaming',
      mnemonic: '📊 AGGREGATION = Always, Gather, Grab, Records, Everyone, Generates, All, Together, Information, Operations, Networks\nThink of collecting puzzle pieces from different boxes into one pile.',
      visual: `
    📊 LOG AGGREGATION FLOW
    
    🖥️ SYSTEMS → 📋 LOGS
    🌐 NETWORK → 📋 LOGS
    📱 APPS → 📋 LOGS
    🛡️ SECURITY → 📋 LOGS
         ↓
    🔗 COLLECTION AGENTS
         ↓
    🏢 CENTRALIZED STORAGE
    ├── 📊 Normalization
    ├── 🏷️ Tagging
    ├── 🗄️ Indexing
    └── 🔍 Search capability`
    },
    'alerting': {
      title: 'Security Alerting',
      explanation: 'Automated notification system that triggers warnings when predefined security conditions or thresholds are met.',
      examples: 'SIEM alerts, threshold-based notifications, anomaly detection alerts, compliance violation warnings, real-time security events',
      mnemonic: '🚨 ALERTING = Always, Launch, Emergency, Response, Triggers, Intelligence, Network, Guards\nThink of smoke alarm - automatic warning when danger detected.',
      visual: `
    🚨 SECURITY ALERTING SYSTEM
    
    🔍 MONITORING SENSORS
         ↓
    📊 RULE ENGINE
    ├── 🎯 Threshold checks
    ├── 🔗 Correlation rules
    ├── 🏷️ Pattern matching
    └── 🚨 Anomaly detection
         ↓
    📢 ALERT GENERATION
    ├── 📧 Email notifications
    ├── 📱 SMS alerts
    ├── 📊 Dashboard updates
    └── 🎫 Ticket creation
         ↓
    👥 RESPONSE TEAM`
    },
    'scanning': {
      title: 'Security Scanning',
      explanation: 'Automated process of examining systems, networks, and applications to identify vulnerabilities, misconfigurations, and security issues.',
      examples: 'Vulnerability scans, port scans, compliance scans, web application scans, network discovery scans',
      mnemonic: '🔍 SCANNING = Security, Checking, Always, Never, Never, Ignores, Network, Guards\nThink of airport security scanner - systematic examination for threats.',
      visual: `
    🔍 SECURITY SCANNING PROCESS
    
    🎯 SCAN TARGETS
    ├── 🖥️ Servers
    ├── 📱 Endpoints
    ├── 🌐 Network devices
    └── 📦 Applications
         ↓
    🔍 SCAN EXECUTION
    ├── 🔓 Port scanning
    ├── 🦠 Vulnerability detection
    ├── 📋 Configuration checks
    └── ⚖️ Compliance validation
         ↓
    📊 RESULTS ANALYSIS
    ├── 🎯 Risk prioritization
    ├── 📋 Remediation guidance
    └── 📈 Trend tracking`
    },
    'security-reporting': {
      title: 'Security Reporting',
      explanation: 'Process of documenting, analyzing, and communicating security findings, metrics, and status to stakeholders.',
      examples: 'Executive dashboards, compliance reports, incident summaries, vulnerability reports, security metrics',
      mnemonic: '📊 REPORTING = Records, Everyone, Produces, Official, Reports, Tracking, Intelligence, Network, Guards\nThink of news reporter - collecting facts and presenting clear information.',
      visual: `
    📊 SECURITY REPORTING HIERARCHY
    
    👔 EXECUTIVE LEVEL
    ├── 📈 Risk trends
    ├── 💰 Budget impact
    ├── ⚖️ Compliance status
    └── 🎯 Strategic metrics
    
    🔧 MANAGEMENT LEVEL
    ├── 📊 Operational metrics
    ├── 🎯 Performance KPIs
    ├── 📋 Project status
    └── 👥 Resource allocation
    
    🛠️ TECHNICAL LEVEL
    ├── 🔍 Detailed findings
    ├── 🛠️ Technical details
    ├── 📋 Action items
    └── ⏰ Implementation timelines`
    },
    'archiving': {
      title: 'Log Archiving',
      explanation: 'Long-term storage of log data for compliance, forensic analysis, and historical reference while managing storage costs.',
      examples: 'Compressed log storage, cold storage systems, compliance retention, automated archival policies, tiered storage',
      mnemonic: '🗄️ ARCHIVING = Always, Records, Carefully, Held, In, Vaults, Information, Never, Gets lost\nThink of library archive - preserving old documents for future reference.',
      visual: `
    🗄️ LOG ARCHIVING STRATEGY
    
    📊 ACTIVE LOGS (Hot Storage)
    ├── ⚡ Real-time access
    ├── 🔍 Current investigations
    └── 📅 Last 30-90 days
         ↓
    📦 NEAR-LINE (Warm Storage)
    ├── 🔍 Occasional access
    ├── 📋 Compliance queries
    └── 📅 Last 1-2 years
         ↓
    🏛️ ARCHIVE (Cold Storage)
    ├── 💰 Cost-effective
    ├── ⚖️ Long-term retention
    ├── 📅 7+ years
    └── 🔍 Rare retrieval`
    },
    // DOMAIN 4.5 SPECIFIC SUB-CONCEPTS
    'legal-hold': {
      title: 'Legal Hold',
      explanation: 'Legal process requiring preservation of electronic data that may be relevant to litigation or investigation.',
      examples: 'Litigation hold notices, data preservation orders, email retention, document freezing, compliance preservation',
      mnemonic: '⚖️ LEGAL-HOLD = Legal, Evidence, Gathered, Always, Locked, Held, Official, Legal, Documents\nThink of evidence locker - nothing gets destroyed during investigation.',
      visual: `
    ⚖️ LEGAL HOLD PROCESS
    
    📧 HOLD NOTICE ISSUED
         ↓
    🚫 STOP ALL DESTRUCTION
    ├── 📧 Email retention
    ├── 📁 Document preservation
    ├── 💾 Backup protection
    └── 📱 Mobile data hold
         ↓
    📋 SCOPE DEFINITION
    ├── 👥 Custodians identified
    ├── 📅 Date ranges set
    ├── 🏷️ Data types specified
    └── 📍 Locations mapped
         ↓
    👁️ ONGOING MONITORING
    ├── ✅ Compliance verification
    ├── 📊 Regular reporting
    └── 🔄 Hold maintenance`
    },
    'video-evidence': {
      title: 'Video Evidence',
      explanation: 'Digital video recordings used as evidence in security investigations and legal proceedings.',
      examples: 'CCTV footage, screen recordings, webcam captures, mobile video, surveillance systems',
      mnemonic: '📹 VIDEO = Visual, Images, Document, Everything, Observed\nThink of security camera - capturing visual proof of events.',
      visual: `
    📹 VIDEO EVIDENCE HANDLING
    
    📷 VIDEO CAPTURE
    ├── 🏢 Surveillance cameras
    ├── 💻 Screen recordings
    ├── 📱 Mobile devices
    └── 🌐 Network cameras
         ↓
    🔒 EVIDENCE PRESERVATION
    ├── 💾 Original format retention
    ├── 🔗 Chain of custody
    ├── 🔢 Hash verification
    └── 📅 Timestamp validation
         ↓
    🔍 ANALYSIS & ENHANCEMENT
    ├── 🔍 Frame analysis
    ├── 🎨 Image enhancement
    ├── ⏰ Timeline reconstruction
    └── 📊 Metadata extraction`
    },
    'admissibility': {
      title: 'Evidence Admissibility',
      explanation: 'Legal standards determining whether digital evidence can be accepted in court proceedings.',
      examples: 'Chain of custody documentation, proper collection procedures, authentication requirements, reliability standards',
      mnemonic: '⚖️ ADMISSIBLE = Always, Document, Methods, Integrity, Secured, Sealed, Information, Backed, Legal, Evidence\nThink of courtroom rules - evidence must meet strict standards to be accepted.',
      visual: `
    ⚖️ EVIDENCE ADMISSIBILITY CRITERIA
    
    📋 AUTHENTICITY
    ├── 🔗 Chain of custody
    ├── 👤 Witness testimony
    ├── 🔢 Hash verification
    └── 📅 Timestamp validation
    
    🔒 RELIABILITY
    ├── 🛠️ Proper tools used
    ├── 📋 Standard procedures
    ├── 👨‍🎓 Qualified personnel
    └── 🧪 Validated methods
    
    📊 RELEVANCE
    ├── 🎯 Case connection
    ├── 📅 Time relevance
    └── 💡 Probative value
    
    ⚖️ LEGAL COMPLIANCE
    ├── 📜 Procedural rules
    ├── 🔐 Privacy rights
    └── 🏛️ Jurisdiction requirements`
    },
    'chain-of-custody': {
      title: 'Chain of Custody',
      explanation: 'Documented process tracking the seizure, custody, control, transfer, analysis, and disposition of evidence.',
      examples: 'Evidence logs, transfer forms, handler signatures, timestamps, access records, custody documentation',
      mnemonic: '🔗 CHAIN = Custody, Handlers, Always, Identified, Never broken\nThink of relay race - each runner must properly hand off the baton.',
      visual: `
    🔗 CHAIN OF CUSTODY PROCESS
    
    🔍 EVIDENCE DISCOVERY
         ↓
    📋 INITIAL DOCUMENTATION
    ├── 📍 Location recorded
    ├── ⏰ Time documented
    ├── 👤 Discoverer identified
    └── 📷 Photos taken
         ↓
    📦 COLLECTION & PACKAGING
    ├── 🔒 Tamper-evident seals
    ├── 🏷️ Evidence labels
    ├── 📋 Collection forms
    └── 🔢 Hash calculations
         ↓
    📝 TRANSFER LOG
    ├── 👤 From/To handlers
    ├── ⏰ Transfer times
    ├── ✍️ Signatures required
    └── 📋 Purpose documented
         ↓
    🏢 SECURE STORAGE
    ├── 🔐 Controlled access
    ├── 📊 Environmental logs
    └── 👁️ Continuous monitoring`
    },
    'timelines': {
      title: 'Digital Timelines',
      explanation: 'Chronological reconstruction of events using digital artifacts and timestamps to understand incident sequence.',
      examples: 'Event correlation, log timeline analysis, file system timestamps, network activity timelines, user activity sequences',
      mnemonic: '⏰ TIMELINES = Time, Intelligence, Makes, Events, Linear, Information, Never, Estimates, Sequences\nThink of movie timeline - events in chronological order to tell the story.',
      visual: `
    ⏰ DIGITAL TIMELINE CONSTRUCTION
    
    📊 DATA SOURCES
    ├── 📋 System logs
    ├── 📁 File timestamps
    ├── 🌐 Network activity
    ├── 📧 Email metadata
    └── 👤 User actions
         ↓
    🔗 CORRELATION ENGINE
    ├── ⏰ Time synchronization
    ├── 🕐 Timezone normalization
    ├── 📊 Event ordering
    └── 🔗 Relationship mapping
         ↓
    📈 TIMELINE VISUALIZATION
    ├── 📅 Chronological view
    ├── 🎯 Key events highlighted
    ├── 🔍 Detailed drill-down
    └── 📊 Pattern identification
         ↓
    🧩 INCIDENT RECONSTRUCTION
    ├── 🎯 Attack progression
    ├── 💡 Causality analysis
    └── 🔍 Gap identification`
    },
    'tags': {
      title: 'Evidence Tags',
      explanation: 'Labeling system for organizing, categorizing, and tracking digital evidence throughout forensic investigations.',
      examples: 'Evidence numbers, case identifiers, content categories, priority levels, handling instructions',
      mnemonic: '🏷️ TAGS = Track, All, Generated, Sources\nThink of luggage tags - identifying and tracking items through their journey.',
      visual: `
    🏷️ EVIDENCE TAGGING SYSTEM
    
    📋 TAG CATEGORIES
    ├── 🆔 Evidence ID numbers
    ├── 📁 Case references
    ├── 🏷️ Content types
    ├── 🎯 Priority levels
    └── 📅 Date classifications
    
    🔍 SEARCH & RETRIEVAL
    ├── 🔍 Tag-based queries
    ├── 📊 Category filtering
    ├── 🗂️ Bulk operations
    └── 📈 Progress tracking
    
    📊 ORGANIZATION BENEFITS
    ├── ⚡ Quick location
    ├── 📋 Status tracking
    ├── 👥 Team coordination
    └── 📊 Case management`
    },
    'reports': {
      title: 'Forensic Reports',
      explanation: 'Comprehensive documentation of forensic analysis findings, methodologies, and conclusions.',
      examples: 'Technical analysis reports, executive summaries, expert witness reports, chain of custody reports, timeline reports',
      mnemonic: '📋 REPORTS = Records, Everything, Properly, Official, Results, Thoroughly, Summarized\nThink of lab report - detailed documentation of procedures and findings.',
      visual: `
    📋 FORENSIC REPORT STRUCTURE
    
    📊 EXECUTIVE SUMMARY
    ├── 🎯 Key findings
    ├── 💡 Conclusions
    ├── 🚨 Critical issues
    └── 💼 Business impact
    
    🔍 TECHNICAL ANALYSIS
    ├── 🛠️ Methods used
    ├── 📊 Data analyzed
    ├── 🔍 Detailed findings
    └── 📈 Supporting evidence
    
    📋 APPENDICES
    ├── 🔗 Chain of custody
    ├── 📷 Screenshots
    ├── 📊 Raw data extracts
    └── 🔢 Hash values
    
    ⚖️ LEGAL CONSIDERATIONS
    ├── 📜 Compliance notes
    ├── 🏛️ Court requirements
    └── 🔒 Confidentiality marks`
    },
    'event-logs': {
      title: 'Event Logs',
      explanation: 'System-generated records of activities, changes, and security events used for forensic analysis.',
      examples: 'Windows Event Logs, syslog entries, application logs, audit trails, security event logs',
      mnemonic: '📋 EVENT-LOGS = Everything, Valuable, Events, Never, Totally, Loses, Operations, Gets, Stored\nThink of ship\'s log - detailed record of everything that happened.',
      visual: `
    📋 EVENT LOG ANALYSIS
    
    🖥️ SYSTEM LOGS
    ├── 🔐 Authentication events
    ├── 🔧 System changes
    ├── 📊 Performance data
    └── 🚨 Error messages
    
    📱 APPLICATION LOGS
    ├── 👤 User activities
    ├── 📊 Transaction records
    ├── 🚫 Error conditions
    └── 🔧 Configuration changes
    
    🛡️ SECURITY LOGS
    ├── 🔓 Access attempts
    ├── 🔐 Permission changes
    ├── 🚨 Security violations
    └── 🦠 Threat detections
    
    🔍 FORENSIC VALUE
    ├── ⏰ Timeline reconstruction
    ├── 👤 User attribution
    ├── 🎯 Attack vectors
    └── 📊 Impact assessment`
    },
    'interviews': {
      title: 'Forensic Interviews',
      explanation: 'Structured questioning of individuals to gather information and evidence related to security incidents.',
      examples: 'Witness interviews, suspect questioning, expert consultations, victim statements, employee testimonies',
      mnemonic: '🗣️ INTERVIEWS = Information, Never, Totally, Explained, Right, Verbal, Intelligence, Everyone, Witnesses, Statements\nThink of detective questioning - gathering human intelligence to complete the picture.',
      visual: `
    🗣️ FORENSIC INTERVIEW PROCESS
    
    📋 PREPARATION
    ├── 🎯 Objectives defined
    ├── ❓ Questions prepared
    ├── 📊 Background research
    └── ⚖️ Legal considerations
    
    🗣️ INTERVIEW EXECUTION
    ├── 📝 Documentation methods
    ├── 👥 Witness rapport
    ├── ❓ Open-ended questions
    └── 🔍 Follow-up probing
    
    📊 INFORMATION TYPES
    ├── ⏰ Timeline details
    ├── 👤 Person identification
    ├── 🎯 Technical details
    └── 💡 Observational data
    
    📋 DOCUMENTATION
    ├── 📝 Written statements
    ├── 🎙️ Audio recordings
    ├── ✍️ Signed declarations
    └── 📊 Summary reports`
    },
    'order-of-volatility': {
      title: 'Order of Volatility',
      explanation: 'Prioritized sequence for collecting digital evidence based on how quickly data can be lost or changed.',
      examples: 'CPU registers, cache memory, RAM, network connections, running processes, temporary files, hard drives',
      mnemonic: '⚡ VOLATILITY = Very, Obvious, Loses, All, Things, Immediately, Loses, Information, Time, Yields\nThink of melting ice - collect the most fragile evidence first.',
      visual: `
    ⚡ ORDER OF VOLATILITY (Most to Least Volatile)
    
    1️⃣ CPU REGISTERS & CACHE
    ├── ⚡ Milliseconds lifetime
    ├── 🔧 CPU state information
    └── 🚨 Immediate collection required
    
    2️⃣ RANDOM ACCESS MEMORY (RAM)
    ├── 🧠 Running processes
    ├── 🔐 Encryption keys
    ├── 🌐 Network connections
    └── ⏰ Lost on power off
    
    3️⃣ NETWORK STATE
    ├── 🌐 Active connections
    ├── 📊 Traffic statistics
    └── 🔄 Routing tables
    
    4️⃣ RUNNING PROCESSES
    ├── 👥 User sessions
    ├── 📁 Open files
    └── 🔧 System services
    
    5️⃣ STORAGE MEDIA
    ├── 💽 Hard drives
    ├── 📱 Solid state drives
    └── 💾 Removable media
    
    📋 COLLECTION STRATEGY:
    Start with most volatile → Least volatile`
    },
    // DOMAIN 4.5: DIGITAL FORENSICS CONCEPTS (NEW UNIQUE CONCEPTS)
    'on-premises-vs-cloud': {
      title: 'On-premises vs Cloud Forensics',
      explanation: 'Different approaches and challenges when conducting digital forensics in traditional on-premises environments versus cloud-based systems.',
      examples: 'On-premises: Physical access, local imaging; Cloud: API access, jurisdiction issues, shared tenancy',
      mnemonic: '☁️ CLOUD = Challenging Location Opens Up Difficulties\n🏢 ON-PREMISES = Obvious Network, Physical Room Enables More Investigation, Simple Evidence Storage\nThink of library vs online books - physical gives you control, cloud requires cooperation.',
      visual: `
    🏢 ON-PREMISES               ☁️ CLOUD
    ├── 🔑 Physical access      ├── 🌐 Remote access only
    ├── 💾 Direct imaging       ├── 📡 API-based collection
    ├── ⏰ Immediate access     ├── ⏳ Provider dependencies
    ├── 🏠 Single jurisdiction  ├── 🌍 Multi-jurisdictional
    └── 🔒 Full control         └── 🤝 Shared responsibility`
    },
    // DOMAIN 4.6: AUTOMATION AND ORCHESTRATION CONCEPTS (NEW UNIQUE CONCEPTS)
    'other-considerations': {
      title: 'Automation Considerations',
      explanation: 'Important factors to evaluate when implementing security automation, including limitations and potential risks.',
      examples: 'False positives, system dependencies, maintenance overhead, skills requirements, integration complexity',
      mnemonic: '⚠️ CONSIDERATIONS = Carefully Observe New Systems, Including Dependencies, Expect Risks And Technical Issues, Operations Need Support\nThink of self-driving car - amazing capability but needs careful planning and oversight.',
      visual: `
    ⚠️ AUTOMATION CONSIDERATIONS

    🎯 ACCURACY ISSUES
    ├── False positives
    ├── False negatives
    └── Context understanding

    🔧 MAINTENANCE
    ├── Regular updates
    ├── Rule tuning
    └── System monitoring

    👥 HUMAN FACTORS
    ├── Skills gap
    ├── Job displacement
    └── Over-reliance`
    },
    // DOMAIN 4.7: DATA SOURCES FOR INVESTIGATION (NEW UNIQUE CONCEPTS)
    'data-sensitivity': {
      title: 'Data Sensitivity and Classification',
      explanation: 'Understanding and handling different levels of data sensitivity during investigations while maintaining privacy and compliance.',
      examples: 'Public, internal, confidential, restricted data, PII protection, legal privilege, trade secrets',
      mnemonic: '🔒 SENSITIVITY = Securing Evidence, Never Share Information, Trust Issues Vary, Information Types Yield\nThink of library sections - some books are open access, others require special permission.',
      visual: `
    🔒 DATA SENSITIVITY LEVELS

    🌍 PUBLIC
    ├── Open access
    ├── No restrictions
    └── Marketing materials

    🏢 INTERNAL
    ├── Employee access
    ├── Business operations
    └── Internal procedures

    ⚠️ CONFIDENTIAL
    ├── Limited access
    ├── Customer data
    └── Financial records

    🚫 RESTRICTED
    ├── Executive access
    ├── Trade secrets
    └── Legal privilege`
    },
    'data-sources-analysis': {
      title: 'Data Sources Analysis Methods',
      explanation: 'Techniques and approaches for analyzing different types of data sources to extract meaningful security insights.',
      examples: 'Timeline analysis, correlation analysis, statistical analysis, pattern recognition, anomaly detection',
      mnemonic: '🔬 ANALYSIS = Analytical Networks And Logic, Yields Systematic Investigation Solutions\nThink of scientist with microscope - different techniques reveal different details.',
      visual: `
    🔬 ANALYSIS METHODS

    📅 TIMELINE ANALYSIS
    ├── Event sequencing
    ├── Causality mapping
    └── Attack progression

    🔗 CORRELATION
    ├── Cross-reference events
    ├── Pattern matching
    └── Relationship mapping

    📊 STATISTICAL
    ├── Baseline comparison
    ├── Anomaly detection
    └── Trend analysis

    🎯 PATTERN RECOGNITION
    ├── Behavioral analysis
    ├── Signature matching
    └── Machine learning`
    },
    // Domain 3.3 Data Protection Concepts
    'data-types': {
      title: 'Data Types',
      explanation: 'Different categories of data that require varying levels of protection based on their sensitivity and regulatory requirements.',
      examples: 'PII (Social Security Numbers), PHI (medical records), trade secrets (proprietary formulas), financial data (credit card numbers)',
      mnemonic: '📊 DATA = Different, All, Types, Appropriately classified\nThink of library organizing books by topic.',
      visual: `
    📊 DATA TYPE HIERARCHY
    🔴 HIGHLY SENSITIVE
    ├── 🏥 REGULATED (HIPAA/PCI)
    ├── 🤐 TRADE SECRETS
    ├── 💡 INTELLECTUAL PROPERTY
    └── ⚖️ LEGAL INFORMATION
    🟡 MODERATELY SENSITIVE  
    ├── 💰 FINANCIAL INFO
    └── 👤 PERSONAL DATA
    🟢 LESS SENSITIVE
    └── 🤖 NON-HUMAN READABLE`
    },
    'data-classifications': {
      title: 'Data Classifications',
      explanation: 'Systematic categorization of data based on its level of sensitivity and the impact of unauthorized disclosure.',
      examples: 'Public (marketing materials), Internal (policies), Confidential (employee records), Restricted (trade secrets)',
      mnemonic: '🏷️ CLASSIFY = Critical, Locked, Always, Sorted, Systematically, Info, Fairly, Yours\nThink of military classification levels.',
      visual: `
    🏷️ DATA CLASSIFICATION PYRAMID
         🔴 RESTRICTED
    (Highest Security - Trade Secrets)
            ↓
      🟠 CONFIDENTIAL  
    (Internal Use - HR Records)
            ↓
        🟡 SENSITIVE
    (Limited Access - Financial)
            ↓
         🔵 PRIVATE
    (Personal Information)
            ↓
         🟢 PUBLIC
    (Marketing Materials)`
    },
    'general-data-considerations': {
      title: 'General Data Considerations',
      explanation: 'Key factors to consider when protecting data including its state, location, and sovereignty requirements.',
      examples: 'Data at rest (encrypted databases), data in transit (TLS connections), data sovereignty (GDPR compliance)',
      mnemonic: '⚖️ CONSIDER = Care, Over, Never, Stop, Inspecting, Data, Everything, Requires\nThink of data shepherd protecting flock.',
      visual: `
    ⚖️ DATA CONSIDERATIONS
    📍 DATA STATES
    ├── 🗄️ AT REST (Storage)
    ├── 🚛 IN TRANSIT (Network)
    └── 💻 IN USE (Processing)
    🌍 LOCATION FACTORS
    ├── 🏛️ DATA SOVEREIGNTY
    ├── 📍 GEOLOCATION
    ├── ⚖️ LEGAL JURISDICTION
    └── 🛂 REGULATORY COMPLIANCE`
    },
    'methods-secure-data': {
      title: 'Methods to Secure Data',
      explanation: 'Various technical and administrative approaches used to protect data confidentiality, integrity, and availability.',
      examples: 'AES encryption for data at rest, tokenization for credit cards, geographic restrictions for compliance, access controls',
      mnemonic: '🛡️ SECURE = Systems, Encryption, Control, Users, Restrict, Everything\nThink of multiple locks on treasure chest.',
      visual: `
    🛡️ DATA SECURITY METHODS
    🔐 CRYPTOGRAPHIC
    ├── 🔒 ENCRYPTION
    ├── #️⃣ HASHING  
    ├── 🎭 MASKING
    └── 🎫 TOKENIZATION
    🏗️ ARCHITECTURAL
    ├── 🧱 SEGMENTATION
    ├── 📍 GEO-RESTRICTIONS
    └── 🔑 PERMISSIONS
    🫥 OBFUSCATION
    └── 🌫️ DATA HIDING`
    },
    // Domain 4.4 Security Monitoring Concepts
    'monitoring-tools': {
      title: 'Security Monitoring Tools',
      explanation: 'Software and hardware solutions used to collect, analyze, and respond to security events and system information.',
      examples: 'SIEM platforms (Splunk, QRadar), vulnerability scanners (Nessus, OpenVAS), SCAP tools, antivirus solutions, DLP systems',
      mnemonic: '🛠️ TOOLS = Technology, Operations, Organized, Log, Systems\nThink of security analyst toolbox.',
      visual: `
    🛠️ MONITORING TOOLS
    🎯 SIEM PLATFORMS
    ├── 📊 EVENT CORRELATION
    ├── 🚨 REAL-TIME ALERTS
    └── 📈 DASHBOARDS
    🔍 VULNERABILITY SCANNERS
    ├── 🌐 NETWORK SCANNING
    ├── 🖥️ HOST SCANNING
    └── 📱 APPLICATION TESTING
    🛡️ PROTECTION TOOLS
    ├── 🦠 ANTIVIRUS
    ├── 🔒 DLP SYSTEMS
    └── 📊 SCAP COMPLIANCE`
    },
    // Domain 5.2 Risk Management Concepts
    'risk-identification': {
      title: 'Risk Identification',
      explanation: 'Process of discovering, recognizing, and documenting potential threats and vulnerabilities that could impact organizational objectives.',
      examples: 'Asset inventories, threat modeling, vulnerability assessments, business process analysis, historical incident review',
      mnemonic: '🔍 IDENTIFY = Investigation, Discovering, Everyone, Never, Thinks, It\'s, Fine, Yours\nThink of security scout finding potential dangers.',
      visual: `
    🔍 RISK IDENTIFICATION
    🎯 RISK SOURCES
    ├── 🌐 EXTERNAL THREATS
    ├── 🏢 INTERNAL THREATS
    ├── 🖥️ TECHNICAL RISKS
    └── 👤 HUMAN FACTORS
    🛠️ IDENTIFICATION METHODS
    ├── 📋 ASSET INVENTORIES
    ├── 🎭 THREAT MODELING
    ├── 🔍 VULNERABILITY SCANS
    └── 📊 BUSINESS ANALYSIS
         ↓
    📝 RISK DOCUMENTATION`
    },
    'risk-assessment': {
      title: 'Risk Assessment',
      explanation: 'Systematic evaluation of identified risks to determine their likelihood and potential impact on the organization.',
      examples: 'Qualitative assessments (High/Medium/Low), quantitative analysis (ALE calculations), compliance assessments',
      mnemonic: '📊 ASSESS = Analyze, Size, Severity, Evaluate, Systems, Systematically\nThink of doctor examining patient to determine health risks.',
      visual: `
    📊 RISK ASSESSMENT
    📝 IDENTIFIED RISKS
         ↓
    🎯 ASSESSMENT TYPES
    ├── ⏰ AD HOC
    ├── 🔄 RECURRING
    ├── 1️⃣ ONE-TIME
    └── ♾️ CONTINUOUS
         ↓
    📈 EVALUATION CRITERIA
    ├── 🎯 LIKELIHOOD
    ├── 💥 IMPACT
    └── 🎭 THREAT LANDSCAPE
         ↓
    📋 ASSESSMENT RESULTS`
    },
    'risk-analysis': {
      title: 'Risk Analysis',
      explanation: 'Detailed examination of risk components to understand their characteristics and determine appropriate treatment options.',
      examples: 'Qualitative analysis (risk matrices), quantitative analysis (SLE × ARO = ALE), probability calculations, impact assessments',
      mnemonic: '🔬 ANALYSIS = Always, Never, Assume, Look, Yield, Systems, Information, Systematically\nThink of laboratory scientist analyzing samples.',
      visual: `
    🔬 RISK ANALYSIS
    📊 QUALITATIVE ANALYSIS
    ├── 🔴 HIGH RISK
    ├── 🟡 MEDIUM RISK
    └── 🟢 LOW RISK
    💰 QUANTITATIVE ANALYSIS
    ├── 💲 SLE (Single Loss)
    ├── 🔢 ARO (Annual Rate)
    ├── 📊 ALE = SLE × ARO
    └── 🎯 EXPOSURE FACTOR
         ↓
    🎯 RISK PRIORITIZATION`
    },
    'risk-register': {
      title: 'Risk Register',
      explanation: 'Centralized repository that documents all identified risks, their characteristics, and management strategies.',
      examples: 'Risk ID numbers, descriptions, owners, likelihood ratings, impact scores, mitigation plans, status updates',
      mnemonic: '📋 REGISTER = Record, Everything, Generally, Include, Systems, Track, Everyone, Responsibly\nThink of detailed logbook for all risks.',
      visual: `
    📋 RISK REGISTER
    📝 RISK ENTRY
    ├── 🔢 RISK ID
    ├── 📄 DESCRIPTION
    ├── 👤 RISK OWNER
    └── 📅 DATE IDENTIFIED
    📊 RISK METRICS
    ├── 🎯 KEY INDICATORS
    ├── 🚨 RISK THRESHOLD
    └── 📈 RISK SCORE
    🛡️ MANAGEMENT PLAN
    ├── 🎯 MITIGATION STRATEGY
    └── 📋 ACTION ITEMS`
    },
    'risk-tolerance': {
      title: 'Risk Tolerance',
      explanation: 'The level of risk an organization is willing to accept in pursuit of its objectives before taking action.',
      examples: 'Acceptable downtime thresholds, maximum financial loss limits, compliance violation tolerance levels',
      mnemonic: '⚖️ TOLERANCE = Threshold, Organizations, Live, Everyday, Risk, Accept, Never, Complain, Everything\nThink of thermostat setting comfort range.',
      visual: `
    ⚖️ RISK TOLERANCE
    📊 TOLERANCE LEVELS
    🔴 UNACCEPTABLE RISK
    ├── 💥 IMMEDIATE ACTION
    └── 🚨 ESCALATION REQUIRED
         ↓
    🟡 TOLERABLE RISK
    ├── 👁️ MONITORING REQUIRED
    └── 📋 DOCUMENTED ACCEPTANCE
         ↓
    🟢 ACCEPTABLE RISK
    ├── ✅ NO ACTION NEEDED
    └── 📊 PERIODIC REVIEW`
    },
    'risk-appetite': {
      title: 'Risk Appetite',
      explanation: 'The amount and type of risk an organization is willing to take to achieve strategic objectives.',
      examples: 'Expansionary (high growth, high risk), Conservative (stability focused), Neutral (balanced approach)',
      mnemonic: '🍽️ APPETITE = Amount, People, Prefer, Everything, Taking, Investment, Tolerating, Everything\nThink of different dining preferences - some like spicy, others mild.',
      visual: `
    🍽️ RISK APPETITE
    🚀 EXPANSIONARY
    ├── 📈 HIGH GROWTH GOALS
    ├── 💥 HIGH RISK TOLERANCE
    └── 🎯 AGGRESSIVE STRATEGIES
    🛡️ CONSERVATIVE
    ├── 🏛️ STABILITY FOCUSED
    ├── 🔒 LOW RISK TOLERANCE
    └── 🐢 CAUTIOUS APPROACH
    ⚖️ NEUTRAL
    ├── 📊 BALANCED APPROACH
    └── 🎯 MEASURED RISKS`
    },
    'risk-reporting': {
      title: 'Risk Reporting',
      explanation: 'Communication of risk information to stakeholders through structured reports and dashboards.',
      examples: 'Executive dashboards, risk heat maps, compliance reports, incident trend analysis, KRI metrics',
      mnemonic: '📊 REPORTING = Regular, Everyone, Provided, Organizations, Risk, Transparently, Information, Never, Guessing\nThink of weather forecast updating regularly.',
      visual: `
    📊 RISK REPORTING
    📈 REPORTING TYPES
    ├── 📋 EXECUTIVE SUMMARIES
    ├── 🔥 RISK HEAT MAPS
    ├── 📊 COMPLIANCE STATUS
    └── 📈 TREND ANALYSIS
    👥 STAKEHOLDER COMMUNICATION
    ├── 🏢 BOARD OF DIRECTORS
    ├── 👔 EXECUTIVE MANAGEMENT
    ├── 🛡️ RISK COMMITTEES
    └── 🔍 AUDIT TEAMS
         ↓
    🎯 INFORMED DECISIONS`
    },
    'business-impact-analysis': {
      title: 'Business Impact Analysis (BIA)',
      explanation: 'Process that identifies and evaluates the potential effects of disruptions on business operations and critical functions.',
      examples: 'RTO/RPO calculations, MTTR/MTBF measurements, critical process mapping, financial impact assessments',
      mnemonic: '💼 BIA = Business, Impact, Analysis - understanding consequences\n⏰ TIMING = Time, Impact, Money, Impact, Never, Good when systems down',
      visual: `
    💼 BUSINESS IMPACT ANALYSIS
    🎯 KEY METRICS
    ├── ⏰ RTO (Recovery Time)
    ├── 📍 RPO (Recovery Point)
    ├── 🔧 MTTR (Mean Repair Time)
    └── ⚡ MTBF (Mean Time Between)
    📊 IMPACT ASSESSMENT
    ├── 💰 FINANCIAL IMPACT
    ├── 📈 OPERATIONAL IMPACT
    ├── 🏛️ REGULATORY IMPACT
    └── 👤 CUSTOMER IMPACT
         ↓
    🎯 RECOVERY PRIORITIES`
    },
    // Domain 5.3 Third-party Risk Management Concepts
    'vendor-assessment': {
      title: 'Vendor Assessment',
      explanation: 'Comprehensive evaluation of third-party vendors to determine their security posture and risk profile.',
      examples: 'Security questionnaires, penetration testing results, compliance certifications, financial stability reviews',
      mnemonic: '🔍 ASSESS = Always, Survey, Security, Everything, Systems, Systematically\nThink of thorough inspection before hiring contractor.',
      visual: `
    🔍 VENDOR ASSESSMENT
    📋 ASSESSMENT METHODS
    ├── 🗳️ SECURITY QUESTIONNAIRES
    ├── 🎯 PENETRATION TESTING
    ├── 🔍 RIGHT-TO-AUDIT
    ├── 📊 INTERNAL AUDIT EVIDENCE
    ├── 🏛️ INDEPENDENT ASSESSMENTS
    └── 🔗 SUPPLY CHAIN ANALYSIS
         ↓
    📊 RISK EVALUATION
    ├── 🔐 SECURITY CONTROLS
    ├── 🏛️ COMPLIANCE STATUS
    └── 💰 FINANCIAL STABILITY
         ↓
    🎯 VENDOR DECISION`
    },
    'vendor-selection': {
      title: 'Vendor Selection',
      explanation: 'Process of choosing third-party vendors based on security, business, and risk criteria.',
      examples: 'Due diligence reviews, conflict of interest assessments, competitive evaluations, reference checks',
      mnemonic: '✅ SELECT = Security, Evaluate, Legal, Everything, Carefully, Together\nThink of careful hiring process for key position.',
      visual: `
    ✅ VENDOR SELECTION
    📋 SELECTION CRITERIA
    ├── 🔐 SECURITY POSTURE
    ├── 💰 COST EFFECTIVENESS
    ├── 🏛️ COMPLIANCE RECORD
    ├── 📈 BUSINESS STABILITY
    └── 🤝 CULTURAL FIT
    🔍 DUE DILIGENCE
    ├── 📊 FINANCIAL REVIEW
    ├── 🔍 BACKGROUND CHECK
    ├── 📞 REFERENCE VERIFICATION
    └── ⚖️ CONFLICT ASSESSMENT
         ↓
    🎯 FINAL SELECTION`
    },
    'agreement-types': {
      title: 'Agreement Types',
      explanation: 'Various contractual arrangements that define the relationship, responsibilities, and obligations between organizations and vendors.',
      examples: 'SLA performance metrics, MOA collaboration terms, NDA confidentiality clauses, MSA framework agreements',
      mnemonic: '📄 AGREEMENTS = Always, Get, Rights, Everyone, Expects, Managed, Everyone, Never, Totally, Surprised\nThink of different types of promises in writing.',
      visual: `
    📄 AGREEMENT TYPES
    📊 SERVICE AGREEMENTS
    ├── 📋 SLA (Service Levels)
    ├── 📝 MSA (Master Service)
    └── 📋 SOW (Statement Work)
    🤝 PARTNERSHIP AGREEMENTS
    ├── 📄 MOA (Memorandum Agreement)
    ├── 📄 MOU (Memorandum Understanding)
    └── 🤝 BPA (Business Partners)
    🔒 PROTECTION AGREEMENTS
    └── 🤐 NDA (Non-Disclosure)
         ↓
    ⚖️ LEGAL FRAMEWORK`
    },
    'vendor-monitoring': {
      title: 'Vendor Monitoring',
      explanation: 'Ongoing oversight of vendor performance, security posture, and compliance with contractual obligations.',
      examples: 'Performance dashboards, security incident tracking, compliance audits, service level monitoring',
      mnemonic: '👁️ MONITOR = Many, Organizations, Never, Ignore, Third-party, Operations, Regularly\nThink of security guard checking vendors regularly.',
      visual: `
    👁️ VENDOR MONITORING
    📊 CONTINUOUS OVERSIGHT
    ├── 📈 PERFORMANCE METRICS
    ├── 🔐 SECURITY POSTURE
    ├── 🏛️ COMPLIANCE STATUS
    └── 📋 CONTRACT ADHERENCE
    🚨 MONITORING ACTIVITIES
    ├── 📊 REGULAR ASSESSMENTS
    ├── 🔍 AUDIT REVIEWS
    ├── 📞 INCIDENT REPORTING
    └── 📈 TREND ANALYSIS
         ↓
    🎯 REMEDIATION ACTIONS`
    },
    'vendor-questionnaires': {
      title: 'Vendor Questionnaires',
      explanation: 'Structured assessment tools used to evaluate vendor security controls, processes, and compliance status.',
      examples: 'SIG questionnaires, custom security assessments, compliance checklists, risk evaluation forms',
      mnemonic: '❓ QUESTIONS = Quality, Understanding, Everything, Security, Third-party, Information, Operations, Never, Skip\nThink of detailed interview before hiring.',
      visual: `
    ❓ VENDOR QUESTIONNAIRES
    📋 QUESTIONNAIRE TYPES
    ├── 🔐 SECURITY CONTROLS
    ├── 🏛️ COMPLIANCE STATUS
    ├── 🔄 BUSINESS PROCESSES
    └── 🚨 INCIDENT HISTORY
    📊 ASSESSMENT AREAS
    ├── 🛡️ DATA PROTECTION
    ├── 🔐 ACCESS CONTROLS  
    ├── 📊 RISK MANAGEMENT
    └── 🏛️ REGULATORY COMPLIANCE
         ↓
    📈 RISK SCORING
         ↓
    🎯 VENDOR DECISION`
    },
    'rules-of-engagement': {
      title: 'Rules of Engagement',
      explanation: 'Guidelines and protocols that define how organizations and vendors interact, communicate, and conduct business.',
      examples: 'Communication protocols, escalation procedures, incident response roles, change management processes',
      mnemonic: '⚖️ RULES = Regulations, Understanding, Legal, Everyone, Systems\nThink of game rules everyone must follow.',
      visual: `
    ⚖️ RULES OF ENGAGEMENT
    📞 COMMUNICATION RULES
    ├── 📧 CONTACT PROTOCOLS
    ├── 📋 REPORTING FREQUENCY
    ├── 🚨 ESCALATION PATHS
    └── 📅 MEETING SCHEDULES
    🔄 OPERATIONAL RULES
    ├── 🔧 CHANGE PROCEDURES
    ├── 🚨 INCIDENT RESPONSE
    ├── 🔐 ACCESS PROTOCOLS
    └── 📊 PERFORMANCE REVIEWS
         ↓
    🤝 CLEAR EXPECTATIONS
         ↓
    ✅ SUCCESSFUL PARTNERSHIP`
    },
    // Domain 5.4 Compliance Concepts  
    'consequences-non-compliance': {
      title: 'Consequences of Non-Compliance',
      explanation: 'Negative outcomes and penalties that organizations face when failing to meet regulatory, legal, or contractual requirements.',
      examples: 'GDPR fines up to 4% of revenue, SOX criminal penalties, license revocations, contract terminations',
      mnemonic: '⚠️ CONSEQUENCES = Costs, Organizations, Never, Survive, Easily, Quite, Usually, Everyone, Never, Considers, Everything, Suffers\nThink of breaking rules brings punishment.',
      visual: `
    ⚠️ NON-COMPLIANCE CONSEQUENCES
    💰 FINANCIAL PENALTIES
    ├── 💸 REGULATORY FINES
    ├── 📉 REVENUE LOSS
    ├── ⚖️ LEGAL COSTS
    └── 💰 REMEDIATION EXPENSES
    🏛️ OPERATIONAL IMPACT
    ├── 📄 LICENSE REVOCATION
    ├── 🚫 BUSINESS SANCTIONS
    ├── 📋 CONTRACT TERMINATION
    └── 🔍 INCREASED OVERSIGHT
    👤 REPUTATIONAL DAMAGE
    ├── 📰 NEGATIVE PUBLICITY
    ├── 😞 CUSTOMER LOSS
    └── 📉 STOCK IMPACT`
    },
    'compliance-monitoring': {
      title: 'Compliance Monitoring',
      explanation: 'Ongoing process of tracking, measuring, and verifying adherence to regulatory requirements and internal policies.',
      examples: 'Automated compliance scans, manual audits, attestation processes, control testing, gap analysis',
      mnemonic: '👁️ MONITOR = Many, Organizations, Never, Ignore, Tracking, Operations, Regularly\nThink of compliance officer watching all activities.',
      visual: `
    👁️ COMPLIANCE MONITORING
    🔄 MONITORING TYPES
    ├── 🤖 AUTOMATED SCANNING
    ├── 👤 MANUAL REVIEWS
    ├── 🏢 INTERNAL AUDITS
    └── 🏛️ EXTERNAL ASSESSMENTS
    📊 MONITORING ACTIVITIES
    ├── 🎯 DUE DILIGENCE
    ├── ✍️ ATTESTATION
    ├── 📋 ACKNOWLEDGMENT
    └── 🤖 AUTOMATION TOOLS
         ↓
    📈 COMPLIANCE METRICS
         ↓
    🎯 REMEDIATION ACTIONS`
    },
    'privacy-compliance': {
      title: 'Privacy Compliance',
      explanation: 'Adherence to laws and regulations governing the collection, processing, storage, and protection of personal data.',
      examples: 'GDPR compliance programs, CCPA privacy notices, data subject rights, cross-border transfer controls',
      mnemonic: '🔒 PRIVACY = Personal, Rights, Information, Very, Always, Critical, Yours\nThink of protecting personal secrets from strangers.',
      visual: `
    🔒 PRIVACY COMPLIANCE
    🌍 JURISDICTIONAL SCOPE
    ├── 🏛️ LOCAL/REGIONAL LAWS
    ├── 🇺🇸 NATIONAL REGULATIONS  
    └── 🌐 GLOBAL FRAMEWORKS
    👤 DATA SUBJECTS
    ├── 🔍 RIGHT TO ACCESS
    ├── ✏️ RIGHT TO RECTIFY
    ├── 🗑️ RIGHT TO ERASURE
    └── 📦 RIGHT TO PORTABILITY
    🏢 ROLES & RESPONSIBILITIES
    ├── 🎯 DATA CONTROLLER
    ├── ⚙️ DATA PROCESSOR
    └── 👑 DATA OWNERSHIP
         ↓
    ⚖️ LEGAL COMPLIANCE`
    },
    // Domain 4 Additional Concepts
    'mobile-deployment-models': {
      title: 'Mobile Deployment Models',
      explanation: 'Different approaches organizations use to manage and deploy mobile devices for business use.',
      examples: 'BYOD policies, COPE programs, CYOD initiatives, corporate-owned devices, hybrid models',
      mnemonic: '📱 DEPLOY = Different, Everyone, Prefers, Levels, Ownership, Yours\nThink of different ways to get mobile devices to employees.',
      visual: `
    📱 MOBILE DEPLOYMENT MODELS
    🏢 ORGANIZATION NEEDS
         ↓
    📋 DEPLOYMENT OPTIONS
    ├── 🎒 BYOD (User Owned)
    ├── 🏢 COPE (Company Owned)
    ├── 🛒 CYOD (User Chooses)
    └── 🔒 CORPORATE (Full Control)
    ⚖️ CONSIDERATIONS
    ├── 💰 COST
    ├── 🔐 SECURITY
    ├── 👤 USER EXPERIENCE
    └── 🛡️ COMPLIANCE
         ↓
    🎯 OPTIMAL MODEL`
    },
    'mobile-connection-methods': {
      title: 'Mobile Connection Methods',
      explanation: 'Various ways mobile devices connect to networks and communicate with other systems.',
      examples: 'LTE/5G cellular, Wi-Fi networks, Bluetooth pairing, NFC connections, satellite links',
      mnemonic: '📡 CONNECT = Communication, Options, Never, Never, Ending, Choices, Technology\nThink of multiple ways to reach the internet.',
      visual: `
    📡 MOBILE CONNECTIONS
    📱 MOBILE DEVICE
         ↓
    🌐 CONNECTION OPTIONS
    ├── 📶 CELLULAR (LTE/5G)
    ├── 📡 WI-FI (802.11)
    ├── 🔵 BLUETOOTH (Short Range)
    └── 💫 NFC (Very Short)
    🛡️ SECURITY CONCERNS
    ├── 🔐 ENCRYPTION
    ├── 🔑 AUTHENTICATION
    └── 👁️ MONITORING
         ↓
    🔒 SECURE CONNECTIVITY`
    },
    // Domain 4 Asset Management Concepts
    'assignment-accounting': {
      title: 'Assignment/Accounting',
      explanation: 'Process of tracking asset ownership, responsibilities, and accounting details throughout the asset lifecycle.',
      examples: 'Asset assignment records, cost center allocation, user responsibility agreements, depreciation tracking',
      mnemonic: '📊 ACCOUNT = Always, Count, Calculate, Ownership, User, Never, Transfers\nThink of keeping detailed books for all assets.',
      visual: `
    📊 ASSIGNMENT/ACCOUNTING
    📋 ASSET ASSIGNMENT
    ├── 👤 USER ASSIGNMENT
    ├── 🏢 DEPARTMENT ALLOCATION
    ├── 📍 LOCATION TRACKING
    └── 💰 COST CENTER
    📈 ACCOUNTING DETAILS
    ├── 💵 ACQUISITION COST
    ├── 📉 DEPRECIATION
    ├── 🔧 MAINTENANCE COSTS
    └── 📅 LIFECYCLE TRACKING
         ↓
    🎯 FULL ACCOUNTABILITY`
    },
    'monitoring-asset-tracking': {
      title: 'Monitoring/Asset Tracking',
      explanation: 'Continuous oversight and tracking of assets to maintain visibility and control over organizational resources.',
      examples: 'RFID tags, barcode scanning, GPS tracking, software asset management tools, automated discovery',
      mnemonic: '👁️ TRACK = Transparency, Really, Always, Counting, Knowing where everything is\nThink of GPS tracking valuable items.',
      visual: `
    👁️ ASSET TRACKING
    🏷️ IDENTIFICATION METHODS
    ├── 📱 RFID TAGS
    ├── 📊 BARCODES
    ├── 🌐 GPS TRACKING
    └── 💻 SOFTWARE AGENTS
    📋 TRACKING ACTIVITIES
    ├── 📍 LOCATION MONITORING
    ├── 👤 USER ASSIGNMENT
    ├── 🔄 STATUS UPDATES
    └── 🚨 EXCEPTION ALERTS
         ↓
    🎯 COMPLETE VISIBILITY`
    },
    'disposal-decommissioning': {
      title: 'Disposal/Decommissioning',
      explanation: 'Secure process of retiring assets at end-of-life while protecting data and meeting compliance requirements.',
      examples: 'Hard drive wiping, certificate destruction, equipment recycling, secure disposal vendors',
      mnemonic: '🗑️ DISPOSE = Data, Is, Sensitive, Properly, Obliterate, Safely, Everything\nThink of secure shredding confidential documents.',
      visual: `
    🗑️ SECURE DISPOSAL
    📱 END-OF-LIFE ASSET
         ↓
    🔐 DATA SANITIZATION
    ├── 🗑️ SECURE DELETION
    ├── 🔨 PHYSICAL DESTRUCTION
    ├── 🧲 DEGAUSSING
    └── 🔄 OVERWRITING
         ↓
    📋 CERTIFICATION
    ├── 📄 DESTRUCTION CERTIFICATE
    ├── ⚖️ COMPLIANCE VERIFICATION
    └── 📸 PHOTOGRAPHIC EVIDENCE
         ↓
    ♻️ ENVIRONMENTALLY SAFE`
    },
    // Domain 4.1 Main Section Concepts
    'secure-baselines': {
      title: 'Secure Baselines',
      explanation: 'Standardized security configurations that serve as the foundation for secure system deployment and maintenance.',
      examples: 'CIS benchmarks, NIST guidelines, vendor hardening guides, organizational security standards',
      mnemonic: '📏 BASELINE = Basic, Always, Secured, Everyone, Learns, Information, Never, Everything\nThink of foundation line for building security.',
      visual: `
    📏 SECURE BASELINES
    🏗️ SECURITY FOUNDATION
    ├── 📋 CONFIGURATION STANDARDS
    ├── 🔐 SECURITY CONTROLS
    ├── 🛡️ HARDENING GUIDELINES
    └── 📊 COMPLIANCE REQUIREMENTS
    🔄 BASELINE LIFECYCLE
    ├── 📝 ESTABLISH
    ├── 🚀 DEPLOY
    └── 🔧 MAINTAIN
         ↓
    🎯 CONSISTENT SECURITY`
    },
    'hardening-targets': {
      title: 'Hardening Targets',
      explanation: 'Various types of systems and devices that require security hardening to reduce attack surface and vulnerabilities.',
      examples: 'Mobile devices, workstations, network infrastructure, cloud systems, IoT devices, embedded systems',
      mnemonic: '🎯 TARGETS = Technology, Always, Requires, Greater, Enhanced, Trust, Systems\nThink of bulls-eye showing what needs protection.',
      visual: `
    🎯 HARDENING TARGETS
    📱 MOBILE DEVICES
    ├── 📲 SMARTPHONES
    └── 💻 TABLETS
    🖥️ WORKSTATIONS
    ├── 💻 DESKTOPS
    └── 💼 LAPTOPS
    🌐 NETWORK INFRASTRUCTURE
    ├── 🔀 SWITCHES
    ├── 📡 ROUTERS
    └── 🔥 FIREWALLS
    ☁️ CLOUD & SPECIALIZED
    ├── ☁️ CLOUD INFRASTRUCTURE
    ├── 🏭 ICS/SCADA
    └── 🌐 IOT DEVICES`
    },
    'wireless-devices': {
      title: 'Wireless Devices',
      explanation: 'Network devices and systems that communicate without physical cables, requiring specific security considerations.',
      examples: 'Wi-Fi access points, wireless routers, mobile hotspots, Bluetooth devices, wireless sensors',
      mnemonic: '📡 WIRELESS = Without, Interference, Really, Everyone, Loves, Easy, Setup, Systems\nThink of radio waves carrying data through air.',
      visual: `
    📡 WIRELESS DEVICES
    🌐 ACCESS POINTS
    ├── 📶 WI-FI ROUTERS
    ├── 🔵 BLUETOOTH HUBS
    └── 📱 MOBILE HOTSPOTS
    🛡️ SECURITY CHALLENGES
    ├── 📶 SIGNAL INTERCEPTION
    ├── 🎭 ROGUE ACCESS POINTS
    ├── 🔐 WEAK ENCRYPTION
    └── 📍 LOCATION TRACKING
         ↓
    🔒 HARDENED WIRELESS`
    },
    'wireless-security-settings': {
      title: 'Wireless Security Settings',
      explanation: 'Configuration options and protocols used to secure wireless network communications and access.',
      examples: 'WPA3 encryption, RADIUS authentication, 802.1X protocols, MAC address filtering, SSID management',
      mnemonic: '🔐 SECURITY = Strong, Encryption, Cryptographic, Utility, Really, Important, Technology, Yours\nThink of locking wireless signals with strong encryption.',
      visual: `
    🔐 WIRELESS SECURITY
    🛡️ ENCRYPTION PROTOCOLS
    ├── 🔑 WPA3 (Latest)
    ├── 🔒 WPA2 (Legacy)
    └── ❌ WEP (Deprecated)
    🔑 AUTHENTICATION
    ├── 🎯 AAA/RADIUS
    ├── 🏛️ 802.1X
    └── 📋 CRYPTOGRAPHIC PROTOCOLS
    🛠️ ADDITIONAL CONTROLS
    ├── 🎭 MAC FILTERING
    ├── 📡 SSID MANAGEMENT
    └── 🚫 GUEST ISOLATION`
    },
    'application-security': {
      title: 'Application Security',
      explanation: 'Security measures and practices implemented to protect applications from threats and vulnerabilities throughout their lifecycle.',
      examples: 'Input validation, secure coding, static analysis, penetration testing, security reviews',
      mnemonic: '🛡️ APP-SEC = Applications, Protected, Properly, Secure, Everyone, Code\nThink of bulletproof vest for software applications.',
      visual: `
    🛡️ APPLICATION SECURITY
    🔍 SECURE DEVELOPMENT
    ├── ✅ INPUT VALIDATION
    ├── 🍪 SECURE COOKIES
    ├── 🔍 STATIC CODE ANALYSIS
    └── ✍️ CODE SIGNING
    🧪 TESTING & VALIDATION
    ├── 🎯 PENETRATION TESTING
    ├── 🔍 VULNERABILITY SCANNING
    └── 👤 SECURITY REVIEWS
    🏗️ DEPLOYMENT SECURITY
    ├── 📦 SANDBOXING
    └── 👁️ MONITORING
         ↓
    🎯 SECURE APPLICATIONS`
    },
    // Domain 3.3 Data Types Detailed Concepts
    'regulated-data': {
      title: 'Regulated Data',
      explanation: 'Information subject to specific legal, regulatory, or compliance requirements that mandate particular handling, protection, and retention practices.',
      examples: 'HIPAA protected health information (PHI), PCI DSS cardholder data, GDPR personal data, SOX financial records, FERPA educational records',
      mnemonic: '⚖️ REGULATED = Rules, Everyone, Governs, Understanding, Laws, Always, Tightly, Enforced, Data\nThink of data wearing legal handcuffs.',
      visual: `
    ⚖️ REGULATED DATA
    📋 REGULATORY FRAMEWORKS
    ├── 🏥 HIPAA (Healthcare)
    ├── 💳 PCI DSS (Payment Cards)
    ├── 🇪🇺 GDPR (Privacy)
    ├── 💼 SOX (Financial)
    └── 🎓 FERPA (Education)
    🛡️ COMPLIANCE REQUIREMENTS
    ├── 🔐 SPECIFIC ENCRYPTION
    ├── 🔑 ACCESS CONTROLS
    ├── 📋 AUDIT TRAILS
    ├── 📅 RETENTION POLICIES
    └── 🚨 BREACH NOTIFICATION
         ↓
    ⚖️ LEGAL PROTECTION`
    },
    'trade-secret': {
      title: 'Trade Secret',
      explanation: 'Confidential business information that provides competitive advantage and is protected through secrecy rather than patents.',
      examples: 'Coca-Cola formula, Google search algorithm, proprietary manufacturing processes, customer lists, pricing strategies',
      mnemonic: '🤐 SECRET = Secretly, Everyone, Conceals, Recipes, Everyone, Treasures\nThink of recipe vault with combination lock.',
      visual: `
    🤐 TRADE SECRETS
    💡 INTELLECTUAL PROPERTY
    ├── 🧪 SECRET FORMULAS
    ├── ⚙️ PROPRIETARY PROCESSES
    ├── 🗂️ CUSTOMER LISTS
    ├── 💰 PRICING STRATEGIES
    └── 🔬 RESEARCH DATA
    🔒 PROTECTION METHODS
    ├── 🤐 NON-DISCLOSURE AGREEMENTS
    ├── 🔑 ACCESS RESTRICTIONS
    ├── 🏢 COMPARTMENTALIZATION
    └── 👁️ MONITORING
         ↓
    🏆 COMPETITIVE ADVANTAGE`
    },
    'intellectual-property': {
      title: 'Intellectual Property',
      explanation: 'Creations of the mind that are legally protected, including patents, trademarks, copyrights, and trade secrets.',
      examples: 'Software source code, patents, trademarks, copyrighted materials, designs, inventions, brand assets',
      mnemonic: '🧠 IP = Intellectual, Property, brain creations\n💡 CREATIVE = Copyright, Really, Excellent, Artistic, Thoughtful, Ideas, Valuable, Everyone',
      visual: `
    🧠 INTELLECTUAL PROPERTY
    📚 IP CATEGORIES
    ├── ⚖️ PATENTS (Inventions)
    ├── ™️ TRADEMARKS (Brands)
    ├── ©️ COPYRIGHTS (Creative Works)
    └── 🤐 TRADE SECRETS (Confidential)
    🛡️ PROTECTION MEASURES
    ├── ⚖️ LEGAL REGISTRATION
    ├── 🔐 ACCESS CONTROLS
    ├── 🏷️ WATERMARKING
    └── 👁️ MONITORING
         ↓
    💰 BUSINESS VALUE`
    },
    'legal-information': {
      title: 'Legal Information',
      explanation: 'Data related to legal proceedings, contracts, regulatory compliance, and attorney-client communications that require special protection.',
      examples: 'Attorney-client privileged communications, litigation documents, contracts, legal opinions, court filings, settlement agreements',
      mnemonic: '⚖️ LEGAL = Laws, Everyone, Governs, Always, Lawyers\nThink of lawyer briefcase with lock.',
      visual: `
    ⚖️ LEGAL INFORMATION
    📋 LEGAL DOCUMENTS
    ├── 📄 CONTRACTS
    ├── 🏛️ COURT FILINGS
    ├── 💼 ATTORNEY-CLIENT PRIVILEGE
    ├── 📊 LITIGATION DOCUMENTS
    └── 🤝 SETTLEMENT AGREEMENTS
    🔒 PROTECTION REQUIREMENTS
    ├── 🤐 ATTORNEY-CLIENT PRIVILEGE
    ├── 🔐 CONFIDENTIALITY
    ├── 📅 RETENTION POLICIES
    └── 🚫 DISCOVERY PROTECTION
         ↓
    ⚖️ LEGAL COMPLIANCE`
    },
    'financial-information': {
      title: 'Financial Information',
      explanation: 'Monetary data including financial statements, transactions, account information, and economic records requiring protection.',
      examples: 'Bank account numbers, credit card data, financial statements, tax records, payroll information, investment data',
      mnemonic: '💰 FINANCIAL = Funds, Income, Never, Always, Nobody, Carelessly, Ignores, Assets, Losses\nThink of bank vault protecting money.',
      visual: `
    💰 FINANCIAL INFORMATION
    💳 FINANCIAL DATA TYPES
    ├── 🏦 BANK ACCOUNTS
    ├── 💳 PAYMENT CARDS
    ├── 📊 FINANCIAL STATEMENTS
    ├── 🧾 TAX RECORDS
    └── 💼 INVESTMENT DATA
    🛡️ PROTECTION STANDARDS
    ├── 💳 PCI DSS (Payment Cards)
    ├── 💼 SOX (Public Companies)
    ├── 🔐 ENCRYPTION REQUIREMENTS
    └── 🔑 ACCESS CONTROLS
         ↓
    🏦 FINANCIAL SECURITY`
    },
    'human-non-human-readable': {
      title: 'Human- and Non-Human-Readable Data',
      explanation: 'Classification based on whether data can be directly interpreted by humans or requires machine processing for understanding.',
      examples: 'Human-readable: text documents, emails, reports. Non-human-readable: encrypted files, binary data, database records, machine code',
      mnemonic: '👤 HUMAN = Humans, Understand, Meaning, Always, Never needs translation\n🤖 MACHINE = Machines, Always, Computer, Handles, Information, Never, Everyone',
      visual: `
    👤🤖 DATA READABILITY
    👤 HUMAN-READABLE
    ├── 📄 TEXT DOCUMENTS
    ├── 📧 PLAIN TEXT EMAILS
    ├── 📊 READABLE REPORTS
    └── 📝 CONFIGURATION FILES
    🤖 NON-HUMAN-READABLE  
    ├── 🔐 ENCRYPTED DATA
    ├── 💾 BINARY FILES
    ├── 🗃️ DATABASE RECORDS
    └── ⚙️ MACHINE CODE
         ↓
    🎯 APPROPRIATE HANDLING`
    },
    // Domain 3.3 Data Classifications Detailed Concepts
    'sensitive-data': {
      title: 'Sensitive Data',
      explanation: 'Information that requires protection from unauthorized access due to potential harm if disclosed, but may not be legally regulated.',
      examples: 'Employee personal information, internal business plans, customer preferences, operational procedures, internal communications',
      mnemonic: '🔒 SENSITIVE = Special, Everyone, Never, Should, Information, Touch, Internal, Valuable, Everyone\nThink of handling delicate glass.',
      visual: `
    🔒 SENSITIVE DATA
    📋 SENSITIVITY FACTORS
    ├── 👤 PERSONAL IMPACT
    ├── 💼 BUSINESS IMPACT
    ├── 🔐 PRIVACY CONCERNS
    └── 🎯 COMPETITIVE VALUE
    🛡️ PROTECTION MEASURES
    ├── 🔑 ACCESS CONTROLS
    ├── 🏷️ DATA LABELING
    ├── 🔐 ENCRYPTION (Optional)
    └── 👁️ MONITORING
         ↓
    🎯 CONTROLLED ACCESS`
    },
    'confidential-data': {
      title: 'Confidential Data',
      explanation: 'Information intended for limited access within an organization, typically requiring formal authorization and handling procedures.',
      examples: 'Strategic business plans, HR records, financial budgets, internal policies, vendor contracts, merger discussions',
      mnemonic: '🤐 CONFIDENTIAL = Cannot, Obviously, Never, Find, Information, Disclosed, Everyone, Never, Tells, Internal, Always, Limited\nThink of sealed envelope marked confidential.',
      visual: `
    🤐 CONFIDENTIAL DATA
    🏢 ORGANIZATIONAL SCOPE
    ├── 💼 STRATEGIC PLANS
    ├── 👥 HR RECORDS
    ├── 💰 FINANCIAL BUDGETS
    ├── 📋 INTERNAL POLICIES
    └── 🤝 VENDOR CONTRACTS
    🔐 ACCESS CONTROLS
    ├── 🎫 FORMAL AUTHORIZATION
    ├── 📋 NEED-TO-KNOW BASIS
    ├── 🔑 ROLE-BASED ACCESS
    └── 📝 ACCESS LOGGING
         ↓
    🏢 INTERNAL PROTECTION`
    },
    'public-data': {
      title: 'Public Data',
      explanation: 'Information that can be freely shared and accessed without restriction, posing no risk if disclosed publicly.',
      examples: 'Marketing materials, press releases, published research, public website content, annual reports, product catalogs',
      mnemonic: '🌐 PUBLIC = People, Understanding, Basic, Library, Information, Content\nThink of newspaper available to everyone.',
      visual: `
    🌐 PUBLIC DATA
    📢 PUBLIC INFORMATION
    ├── 📰 PRESS RELEASES
    ├── 📈 ANNUAL REPORTS
    ├── 🛒 PRODUCT CATALOGS
    ├── 🌐 WEBSITE CONTENT
    └── 📚 PUBLISHED RESEARCH
    ✅ CHARACTERISTICS
    ├── 🔓 NO ACCESS RESTRICTIONS
    ├── 💰 NO FINANCIAL IMPACT
    ├── ⚖️ NO LEGAL ISSUES
    └── 🏢 NO COMPETITIVE HARM
         ↓
    🌍 FREELY SHAREABLE`
    },
    'restricted-data': {
      title: 'Restricted Data',
      explanation: 'Highly sensitive information with the strictest access controls, typically limited to specific individuals with explicit authorization.',
      examples: 'Top secret government data, executive compensation, merger negotiations, classified research, security vulnerabilities',
      mnemonic: '🚫 RESTRICTED = Really, Everyone, Strictly, Totally, Restricted, Information, Cannot, Touch, Everyone, Dangerous\nThink of maximum security prison.',
      visual: `
    🚫 RESTRICTED DATA
    🔴 HIGHEST SENSITIVITY
    ├── 🤐 CLASSIFIED INFORMATION
    ├── 💎 EXECUTIVE DECISIONS
    ├── 🔬 CLASSIFIED RESEARCH
    ├── 🛡️ SECURITY VULNERABILITIES
    └── 💰 M&A NEGOTIATIONS
    🔒 MAXIMUM CONTROLS
    ├── 🎫 EXPLICIT AUTHORIZATION
    ├── 👤 INDIVIDUAL BASIS
    ├── 🔐 STRONG ENCRYPTION
    ├── 🏰 PHYSICAL SECURITY
    └── 📊 DETAILED MONITORING
         ↓
    🔴 MAXIMUM PROTECTION`
    },
    'private-data': {
      title: 'Private Data',
      explanation: 'Personal or individually identifiable information that belongs to specific persons and requires privacy protection.',
      examples: 'Social Security numbers, personal addresses, phone numbers, medical records, personal emails, biometric data',
      mnemonic: '👤 PRIVATE = Personal, Really, Individual, Valuable, Always, Totally, Everyone\nThink of personal diary with lock.',
      visual: `
    👤 PRIVATE DATA
    🆔 PERSONAL IDENTIFIERS
    ├── 🔢 SOCIAL SECURITY NUMBERS
    ├── 🏠 HOME ADDRESSES
    ├── 📞 PHONE NUMBERS
    ├── 🏥 MEDICAL RECORDS
    └── 🧬 BIOMETRIC DATA
    🛡️ PRIVACY PROTECTION
    ├── ⚖️ LEGAL REQUIREMENTS (GDPR)
    ├── 🔐 ENCRYPTION MANDATORY
    ├── 🔑 STRICT ACCESS CONTROLS
    └── 🗑️ RIGHT TO DELETION
         ↓
    🔒 PRIVACY PRESERVED`
    },
    'critical-data': {
      title: 'Critical Data',
      explanation: 'Information essential for business operations whose loss, corruption, or unavailability would severely impact organizational functions.',
      examples: 'Production databases, financial systems data, customer records, operational control systems, core application data',
      mnemonic: '🚨 CRITICAL = Cannot, Really, Ignore, This, Information, Cannot, Always, Lose\nThink of life support system data.',
      visual: `
    🚨 CRITICAL DATA
    💼 BUSINESS ESSENTIAL
    ├── 🗃️ PRODUCTION DATABASES
    ├── 💰 FINANCIAL SYSTEMS
    ├── 👥 CUSTOMER RECORDS
    ├── ⚙️ OPERATIONAL CONTROLS
    └── 🏗️ CORE APPLICATIONS
    🛡️ PROTECTION MEASURES
    ├── 🔄 REDUNDANT BACKUPS
    ├── 🏛️ HIGH AVAILABILITY
    ├── 🔐 ENCRYPTION
    ├── 👁️ CONTINUOUS MONITORING
    └── 🚨 DISASTER RECOVERY
         ↓
    💼 BUSINESS CONTINUITY`
    },
    'data-states': {
      title: 'Data States',
      explanation: 'The three fundamental states in which data exists and requires different security protection approaches.',
      examples: 'At rest: stored files, databases. In transit: network transmissions, emails. In use: active memory, processing',
      mnemonic: '🔄 STATES = Storage, Transit, Active, Three, Everything, States\nThink of water: ice (rest), flowing (transit), steam (use).',
      visual: `
    🔄 DATA STATES
    💾 DATA AT REST
    ├── 🗃️ STORED FILES
    ├── 🗄️ DATABASES
    └── 💿 BACKUPS
    🚛 DATA IN TRANSIT
    ├── 🌐 NETWORK TRANSMISSIONS
    ├── 📧 EMAIL TRANSFERS
    └── 📡 API CALLS
    ⚡ DATA IN USE
    ├── 🧠 ACTIVE MEMORY
    ├── ⚙️ CPU PROCESSING
    └── 👤 USER INTERACTIONS
         ↓
    🛡️ STATE-SPECIFIC SECURITY`
    },
    // Domain 4 Critical Sub-concepts
    'recovery-time-objective': {
      title: 'Recovery Time Objective (RTO)',
      explanation: 'Maximum acceptable time to restore business operations after a disruption.',
      examples: 'Email: 4 hours, Financial systems: 1 hour, Website: 30 minutes',
      mnemonic: '⏰ RTO = Recovery, Time, Objective - how fast to restore\nThink of race against time clock.',
      visual: `
    ⏰ RECOVERY TIME OBJECTIVE
    🚨 INCIDENT OCCURS
         ↓
    ⏱️ RTO COUNTDOWN
    ├── 🔴 CRITICAL: 1 HOUR
    ├── 🟡 IMPORTANT: 4 HOURS  
    └── 🟢 NORMAL: 24 HOURS
         ↓
    🎯 RESTORE BY DEADLINE`
    },
    'recovery-point-objective': {
      title: 'Recovery Point Objective (RPO)',
      explanation: 'Maximum acceptable data loss measured in time during a disruption.',
      examples: 'Financial data: 0 minutes, Customer data: 15 minutes, Analytics: 24 hours',
      mnemonic: '💾 RPO = Recovery, Point, Objective - how much data loss acceptable\nThink of backup checkpoint frequency.',
      visual: `
    💾 RECOVERY POINT OBJECTIVE
    📊 DATA BACKUP POINTS
    ├── 🔴 ZERO LOSS (Real-time)
    ├── 🟡 15 MIN LOSS (Frequent backup)
    └── 🟢 24 HR LOSS (Daily backup)
         ↓
    🎯 ACCEPTABLE DATA LOSS`
    },
    'sla-service-level': {
      title: 'Service-Level Agreement (SLA)',
      explanation: 'Contract defining expected service performance levels and penalties for non-compliance.',
      examples: '99.9% uptime, <200ms response time, 24/7 support, financial penalties for breaches',
      mnemonic: '📋 SLA = Service, Level, Agreement - promises in contract\nThink of warranty with guarantees.',
      visual: `
    📋 SERVICE LEVEL AGREEMENT
    📊 PERFORMANCE METRICS
    ├── ⏱️ UPTIME: 99.9%
    ├── 🚀 RESPONSE: <200ms
    ├── 🛠️ SUPPORT: 24/7
    └── 🎯 RESOLUTION: <4hrs
    💰 PENALTIES
    ├── 📉 SERVICE CREDITS
    └── 💸 FINANCIAL COMPENSATION
         ↓
    ✅ CONTRACTUAL COMMITMENT`
    },
    'mou-memorandum': {
      title: 'Memorandum of Understanding (MOU)',
      explanation: 'Non-binding agreement outlining cooperation terms between parties.',
      examples: 'Information sharing agreements, partnership frameworks, collaboration terms',
      mnemonic: '🤝 MOU = Memorandum, Of, Understanding - handshake agreement\nThink of gentlemen\'s agreement.',
      visual: `
    🤝 MEMORANDUM OF UNDERSTANDING
    📄 NON-BINDING AGREEMENT
    ├── 🎯 COOPERATION GOALS
    ├── 📋 SHARED RESPONSIBILITIES
    ├── 🔄 INFORMATION SHARING
    └── 📅 COLLABORATION TIMELINE
    ⚖️ CHARACTERISTICS
    ├── 🤝 GOOD FAITH BASIS
    ├── 📋 FRAMEWORK ONLY
    └── 🚫 NO LEGAL ENFORCEMENT
         ↓
    🎯 MUTUAL UNDERSTANDING`
    },
    'nda-agreement': {
      title: 'Non-Disclosure Agreement (NDA)',
      explanation: 'Legal contract protecting confidential information from unauthorized disclosure.',
      examples: 'Trade secret protection, merger discussions, vendor agreements, employee confidentiality',
      mnemonic: '🤐 NDA = Never, Disclose, Anything confidential\nThink of sealed lips with legal lock.',
      visual: `
    🤐 NON-DISCLOSURE AGREEMENT
    🔒 CONFIDENTIAL INFORMATION
    ├── 🤐 TRADE SECRETS
    ├── 💰 FINANCIAL DATA
    ├── 👥 CUSTOMER LISTS
    └── 🔬 PROPRIETARY METHODS
    ⚖️ LEGAL PROTECTION
    ├── 💸 FINANCIAL DAMAGES
    ├── ⛔ INJUNCTIVE RELIEF
    └── ⚖️ LEGAL ENFORCEMENT
         ↓
    🛡️ CONFIDENTIALITY PROTECTED`
    },
    'due-diligence': {
      title: 'Due Diligence',
      explanation: 'Comprehensive investigation and analysis process to assess risks before business decisions.',
      examples: 'Vendor security assessments, merger investigations, compliance audits, financial reviews',
      mnemonic: '🔍 DILIGENCE = Detailed, Investigation, Legal, Important, Going, Everyone, Never, Carelessly, Everything\nThink of detective investigating thoroughly.',
      visual: `
    🔍 DUE DILIGENCE PROCESS
    📋 INVESTIGATION AREAS
    ├── 💰 FINANCIAL STABILITY
    ├── ⚖️ LEGAL COMPLIANCE
    ├── 🛡️ SECURITY POSTURE
    ├── 🏢 OPERATIONAL CAPABILITY
    └── 👥 REPUTATION ANALYSIS
    🔬 VERIFICATION METHODS
    ├── 📊 DOCUMENT REVIEW
    ├── 🗣️ INTERVIEWS
    ├── 🔍 BACKGROUND CHECKS
    └── 📞 REFERENCE VERIFICATION
         ↓
    ✅ INFORMED DECISION`
    },
    'internal-external-compliance': {
      title: 'Internal/External Compliance',
      explanation: 'Distinction between organizational policies and external regulatory requirements.',
      examples: 'Internal: Company security policies. External: GDPR, HIPAA, SOX, PCI DSS requirements',
      mnemonic: '🏢 INTERNAL = Inside, Never, Third-party, Everyone, Rules, Never, Always, Legal\n🌐 EXTERNAL = Everyone, eXpects, Third-party, Everyone, Rules, Never, Always, Legal',
      visual: `
    🏢🌐 COMPLIANCE TYPES
    🏢 INTERNAL COMPLIANCE
    ├── 📋 COMPANY POLICIES
    ├── 🛡️ SECURITY STANDARDS
    ├── 🎯 OPERATIONAL PROCEDURES
    └── 👥 HR GUIDELINES
    🌐 EXTERNAL COMPLIANCE
    ├── ⚖️ REGULATORY LAWS
    ├── 🏛️ GOVERNMENT STANDARDS
    ├── 🌍 INTERNATIONAL FRAMEWORKS
    └── 🏭 INDUSTRY REQUIREMENTS
         ↓
    ⚖️ COMPREHENSIVE COMPLIANCE`
    },
    // DOMAIN 4.5 ACQUISITION CONCEPTS (REMAINING)
    'disk-acquisition': {
      title: 'Disk Acquisition',
      explanation: 'Process of creating forensically sound copies of storage devices for investigation while preserving evidence integrity.',
      examples: 'Bit-for-bit imaging, write blocking, hash verification, live imaging, physical drive cloning',
      mnemonic: '💿 DISK = Duplicate Information Securely Keeping\nThink of photocopying - exact copy without touching original.',
      visual: `
    💿 DISK ACQUISITION
    🔒 WRITE BLOCKER
         ↓
    💾 SOURCE DISK
         ↓
    📋 BIT-FOR-BIT COPY
         ↓
    🔍 HASH VERIFICATION
         ↓
    ✅ FORENSIC IMAGE
         ↓
    📁 CHAIN OF CUSTODY`
    },
    'ram-acquisition': {
      title: 'RAM Acquisition',
      explanation: 'Process of capturing volatile memory contents from running systems to preserve evidence of current system state.',
      examples: 'Memory dumps, live memory imaging, hibernation files, crash dumps, VM memory snapshots',
      mnemonic: '🧠 RAM = Remember All Memory\nThink of brain scan - capturing thoughts before they fade.',
      visual: `
    🧠 RAM ACQUISITION
    💡 RUNNING SYSTEM
         ↓
    ⚡ MEMORY DUMP TOOL
         ↓
    📊 VOLATILE DATA CAPTURE
    ├── Running Processes
    ├── Network Connections
    ├── Encryption Keys
    ├── Passwords
    └── System State
         ↓
    💾 MEMORY IMAGE FILE`
    },
    'swap-pagefile-acquisition': {
      title: 'Swap/Pagefile Acquisition',
      explanation: 'Process of capturing virtual memory files that contain data swapped from RAM to disk storage.',
      examples: 'Pagefile.sys (Windows), swap partition (Linux), hibernation files, virtual memory dumps',
      mnemonic: '🔄 SWAP = System Writes All Pages\nThink of notebook overflow - extra pages stored separately.',
      visual: `
    🔄 SWAP/PAGEFILE
    🧠 RAM FULL
         ↓
    📝 WRITE TO DISK
         ↓
    💾 SWAP FILE/PARTITION
    ├── Process Memory
    ├── Application Data
    ├── System Buffers
    └── Cache Data
         ↓
    🔍 FORENSIC ANALYSIS`
    },
    'os-acquisition': {
      title: 'OS Acquisition',
      explanation: 'Process of capturing operating system artifacts, configurations, and state information for forensic analysis.',
      examples: 'Registry hives, system logs, user profiles, installed software, system configuration files',
      mnemonic: '🖥️ OS = Operating System\nThink of building blueprint - shows how everything is structured.',
      visual: `
    🖥️ OS ACQUISITION
    📋 SYSTEM REGISTRY
    📁 SYSTEM FILES
    👤 USER PROFILES
    📊 EVENT LOGS
    ⚙️ CONFIGURATION
         ↓
    🔍 FORENSIC ANALYSIS
    ├── User Activity
    ├── System Changes
    ├── Software Installation
    └── Security Events`
    },
    'device-acquisition': {
      title: 'Device Acquisition',
      explanation: 'Process of capturing data from mobile devices, IoT devices, and embedded systems for forensic investigation.',
      examples: 'Mobile phone imaging, tablet extraction, IoT device analysis, GPS data recovery, smartwatch forensics',
      mnemonic: '📱 DEVICE = Data Everyone Verifies In Criminal Evidence\nThink of digital fingerprints - unique traces on every device.',
      visual: `
    📱 DEVICE ACQUISITION
    🔌 PHYSICAL CONNECTION
         ↓
    🔓 BYPASS SECURITY
         ↓
    📊 DATA EXTRACTION
    ├── Call Logs
    ├── Text Messages
    ├── Application Data
    ├── Location History
    └── User Files
         ↓
    🔍 FORENSIC ANALYSIS`
    },
    'firmware-acquisition': {
      title: 'Firmware Acquisition',
      explanation: 'Process of extracting low-level software stored in device hardware for security analysis and forensic investigation.',
      examples: 'BIOS/UEFI extraction, router firmware analysis, embedded system firmware, microcontroller dumps',
      mnemonic: '⚙️ FIRMWARE = Fixed Instructions Running Machine Without All Resources Executed\nThink of DNA - core instructions that define how system operates.',
      visual: `
    ⚙️ FIRMWARE ACQUISITION
    🔧 HARDWARE ACCESS
         ↓
    📡 CHIP PROGRAMMING
         ↓
    💾 FIRMWARE DUMP
    ├── Boot Code
    ├── System Drivers
    ├── Configuration
    └── Security Keys
         ↓
    🔍 REVERSE ENGINEERING`
    },
    'snapshot-acquisition': {
      title: 'Snapshot Acquisition',
      explanation: 'Process of capturing point-in-time copies of system state, virtual machines, or data for forensic preservation.',
      examples: 'VM snapshots, database snapshots, filesystem snapshots, cloud instance snapshots, container images',
      mnemonic: '📸 SNAPSHOT = Save Notable And Professional System Holds One Time\nThink of crime scene photo - preserving exact moment.',
      visual: `
    📸 SNAPSHOT ACQUISITION
    ⏰ POINT IN TIME
         ↓
    🖥️ SYSTEM STATE CAPTURE
         ↓
    💾 SNAPSHOT CREATION
    ├── Memory State
    ├── Disk State
    ├── Process State
    └── Network State
         ↓
    🔒 IMMUTABLE COPY`
    },
    'cache-acquisition': {
      title: 'Cache Acquisition',
      explanation: 'Process of capturing cached data from various system components to recover temporary and frequently accessed information.',
      examples: 'Browser cache, DNS cache, ARP cache, processor cache, application cache, web proxy cache',
      mnemonic: '🗃️ CACHE = Commonly Accessed Content Held Efficiently\nThink of library reference desk - frequently used books kept handy.',
      visual: `
    🗃️ CACHE ACQUISITION
    💻 SYSTEM CACHES
    ├── 🌐 Browser Cache
    ├── 🔍 DNS Cache
    ├── 🌐 ARP Cache
    ├── 💾 Disk Cache
    └── 📱 App Cache
         ↓
    🔍 FORENSIC ANALYSIS
    ├── Web History
    ├── Network Activity
    ├── File Access
    └── User Behavior`
    },
    'network-acquisition': {
      title: 'Network Acquisition',
      explanation: 'Process of capturing network traffic, configurations, and state information for forensic investigation.',
      examples: 'Packet capture, network device configs, routing tables, firewall logs, network flow data',
      mnemonic: '🌐 NETWORK = Notable Evidence Transmitted Within Organizations Requiring Knowledge\nThink of postal system - tracking all packages and routes.',
      visual: `
    🌐 NETWORK ACQUISITION
    📡 TRAFFIC CAPTURE
         ↓
    📊 PACKET ANALYSIS
    ├── Source/Destination
    ├── Protocols Used
    ├── Data Transferred
    └── Timing Information
         ↓
    🔍 FORENSIC RECONSTRUCTION
    ├── Communication Patterns
    ├── Data Exfiltration
    └── Attack Vectors`
    },
    'artifacts-acquisition': {
      title: 'Artifacts Acquisition',
      explanation: 'Process of collecting digital artifacts and traces left by user activities and system operations for forensic analysis.',
      examples: 'Browser history, recently accessed files, temporary files, registry entries, log files, metadata',
      mnemonic: '🏺 ARTIFACTS = Always Remember To Investigate Facts And Computer Traces Successfully\nThink of archaeology - piecing together past activities from remnants.',
      visual: `
    🏺 ARTIFACTS ACQUISITION
    🔍 DIGITAL ARCHAEOLOGY
    ├── 📁 File System
    ├── 📊 Registry
    ├── 🌐 Browser Data
    ├── 📝 Log Files
    └── 📎 Metadata
         ↓
    🧩 TIMELINE RECONSTRUCTION
    ├── User Actions
    ├── System Events
    ├── Application Usage
    └── Network Activity`
    },
    'analysis-investigation': {
      title: 'Investigation Analysis Methods',
      explanation: 'Analytical techniques used to examine and interpret security data to identify threats, patterns, and anomalies.',
      examples: 'Correlation analysis, trend analysis, statistical analysis, behavioral analysis, baseline deviation detection',
      mnemonic: '🔬 ANALYSIS = Analytical, Networks, Always, Look, Yielding, Security, Intelligence, Systems\nThink of detective examining clues with magnifying glass.',
      visual: `
    🔬 INVESTIGATION ANALYSIS
    🔗 CORRELATION ANALYSIS
    ├── 📊 Event relationships
    ├── ⏰ Time-based patterns
    ├── 👤 User associations
    └── 🎯 Attack chains
    
    📈 TREND ANALYSIS
    ├── 📅 Long-term patterns
    ├── 🔄 Recurring events
    ├── 📊 Volume changes
    └── 🚨 Emerging threats
    
    📊 STATISTICAL ANALYSIS
    ├── 📉 Anomaly detection
    ├── 📈 Threshold analysis
    ├── 🎯 Probability scoring
    └── 📊 Distribution patterns
    
    👤 BEHAVIORAL ANALYSIS
    ├── 🔄 Normal patterns
    ├── 🚨 Unusual activities
    ├── 👥 User profiling
    └── 🎯 Deviation detection
    
    📏 BASELINE DEVIATION
    ├── 📊 Normal baselines
    ├── 🚨 Anomaly identification
    ├── 📈 Threshold breaches
    └── 🎯 Risk scoring`
    },
    'data-sensitivity-classification': {
      title: 'Data Sensitivity and Classification',
      explanation: 'System for categorizing data based on sensitivity levels and implementing appropriate protection measures for investigations.',
      examples: 'Public, Internal, Confidential, Restricted classifications, handling procedures, access controls',
      mnemonic: '🏷️ CLASSIFICATION = Categories, Labels, Assigns, Security, Systems, Information, Facilitates, Important, Categories, All, Types, Information, Organized, Networks\nThink of library classification - organize by sensitivity for proper handling.',
      visual: `
    🏷️ DATA SENSITIVITY CLASSIFICATION
    🔴 RESTRICTED/TOP SECRET
    ├── 🔐 Highest protection
    ├── 👤 Need-to-know basis
    ├── 🔒 Encryption required
    └── 📋 Audit trails
    
    🟠 CONFIDENTIAL
    ├── 🔒 Internal access only
    ├── 👥 Authorized personnel
    ├── 📋 Access logging
    └── 🚫 No external sharing
    
    🟡 INTERNAL/SENSITIVE
    ├── 🏢 Organization-wide
    ├── 👤 Employee access
    ├── 📊 Basic controls
    └── 🔍 Monitoring
    
    🟢 PUBLIC
    ├── 🌐 Open access
    ├── 📢 External sharing ok
    ├── 📊 Minimal controls
    └── 📋 Basic tracking
    
    🔍 INVESTIGATION IMPACT
    ├── 📋 Evidence handling
    ├── ⚖️ Legal requirements
    ├── 🔒 Access restrictions
    └── 📊 Disclosure protocols`
    },
    'security-monitoring-investigation': {
      title: 'Security Monitoring for Investigation',
      explanation: 'Continuous monitoring and analysis of security events to support investigation activities and threat detection.',
      examples: 'Flow analysis, packet analysis, protocol analysis, real-time monitoring, anomaly detection',
      mnemonic: '👁️ MONITORING = Multiple, Operations, Never, Ignore, Threats, Operations, Require, Investigation, Never, Guessing\nThink of security camera system - continuous watching and recording.',
      visual: `
    👁️ SECURITY MONITORING
    🌊 FLOW ANALYSIS
    ├── 📊 Traffic patterns
    ├── 🔗 Connection mapping
    ├── 📈 Volume analysis
    ├── ⏰ Time-based flows
    └── 🚨 Anomaly detection
    
    📦 PACKET ANALYSIS
    ├── 🔍 Deep packet inspection
    ├── 📊 Protocol analysis
    ├── 📎 Payload examination
    ├── 🏷️ Metadata extraction
    └── 🔗 Session reconstruction
    
    📡 PROTOCOL ANALYSIS
    ├── 🌐 HTTP/HTTPS analysis
    ├── 📧 Email protocols
    ├── 📁 File transfer protocols
    ├── 🔐 Encrypted traffic
    └── 🚨 Protocol violations
    
    📊 INVESTIGATION SUPPORT
    ├── 🔍 Evidence collection
    ├── ⏰ Timeline creation
    ├── 🎯 Attack reconstruction
    ├── 📋 Incident documentation
    └── 🔗 Correlation analysis`
    },
    // DOMAIN 4.8 INCIDENT RESPONSE ACTIVITIES (ADDITIONAL CONCEPTS)
    'ir-process': {
      title: 'Incident Response Process',
      explanation: 'Comprehensive seven-phase methodology for managing security incidents from preparation through lessons learned.',
      examples: 'NIST incident response lifecycle, preparation planning, detection systems, containment strategies, eradication procedures, recovery validation, post-incident review',
      mnemonic: '🚨 PROCESS = Preparation, Response, Operations, Contain, Eradicate, Secure, Systems\nThink of emergency response drill - practiced steps for crisis management.',
      visual: `
    🚨 7-PHASE IR PROCESS
    1️⃣ PREPARATION
    ├── 📋 IR plan development
    ├── 👥 Team training
    ├── 🛠️ Tool deployment
    └── 📞 Communication setup
    
    2️⃣ DETECTION & ANALYSIS
    ├── 🔍 Event identification
    ├── 📊 Impact assessment
    ├── 🏷️ Incident classification
    └── 📋 Documentation
    
    3️⃣ CONTAINMENT
    ├── 🔒 Short-term containment
    ├── 🛡️ Long-term containment
    ├── 🔍 Evidence preservation
    └── 📊 System backup
    
    4️⃣ ERADICATION
    ├── 🧹 Malware removal
    ├── 🔧 Vulnerability patching
    ├── 🔒 Access revocation
    └── 🛡️ Security hardening
    
    5️⃣ RECOVERY
    ├── 🖥️ System restoration
    ├── 📊 Monitoring enhancement
    ├── ✅ Functionality testing
    └── 🔄 Gradual normalization
    
    6️⃣ LESSONS LEARNED
    ├── 📋 Post-incident analysis
    ├── 📊 Process evaluation
    ├── 💡 Improvement recommendations
    └── 📝 Knowledge base update`
    },
    'ir-training': {
      title: 'Incident Response Training',
      explanation: 'Educational programs and exercises designed to prepare incident response teams for effective security incident handling.',
      examples: 'Tabletop exercises, simulated attack scenarios, role-based training, technical skill development, communication drills',
      mnemonic: '🧑‍🎓 TRAINING = Team, Readiness, Always, Improves, Network, Intelligence, Never, Guessing\nThink of fire drill - practice makes perfect in emergency response.',
      visual: `
    🧑‍🎓 INCIDENT RESPONSE TRAINING
    📚 TRAINING COMPONENTS
    ├── 📋 IR procedures knowledge
    ├── 🔧 Technical skills development
    ├── 💬 Communication protocols
    ├── ⚖️ Legal considerations
    └── 🤝 Team coordination
    
    🎯 TRAINING METHODS
    ├── 📖 Classroom instruction
    ├── 🖥️ Online modules
    ├── 🎮 Simulation exercises
    ├── 🗣️ Tabletop discussions
    └── 🏃‍♂️ Live response drills
    
    📊 SKILL AREAS
    ├── 🔍 Forensic analysis
    ├── 🦠 Malware analysis
    ├── 🌐 Network investigation
    ├── 📋 Documentation practices
    └── 🗣️ Stakeholder communication
    
    🔄 CONTINUOUS IMPROVEMENT
    ├── 📈 Performance assessment
    ├── 📋 Skills gap analysis
    ├── 🎯 Targeted training
    └── 📚 Knowledge updates`
    },
    'ir-testing': {
      title: 'Incident Response Testing',
      explanation: 'Structured exercises to validate incident response capabilities and identify areas for improvement.',
      examples: 'Tabletop exercises, full-scale simulations, walkthrough drills, functional testing, red team exercises',
      mnemonic: '🧪 TESTING = Teams, Exercise, Scenarios, Together, Investigate, Never, Guessing\nThink of rehearsal before performance - practice reveals weaknesses.',
      visual: `
    🧪 INCIDENT RESPONSE TESTING
    📋 TABLETOP EXERCISES
    ├── 🗣️ Discussion-based scenarios
    ├── 👥 Role-playing activities
    ├── 📊 Decision-making practice
    ├── 💬 Communication testing
    └── 📝 Process validation
    
    🎮 SIMULATION EXERCISES
    ├── 🖥️ Technical simulations
    ├── 🚨 Real-time scenarios
    ├── 🛠️ Tool utilization
    ├── ⏰ Time pressure testing
    └── 📊 Performance measurement
    
    🎯 TESTING OBJECTIVES
    ├── 📋 Plan validation
    ├── 👥 Team coordination
    ├── 🔧 Tool effectiveness
    ├── 💬 Communication flows
    └── ⏰ Response times
    
    📈 IMPROVEMENT CYCLE
    ├── 📊 Exercise evaluation
    ├── 📝 Lessons learned
    ├── 🔧 Plan updates
    ├── 🧑‍🎓 Additional training
    └── 🔄 Continuous testing`
    },
    'ir-root-cause-analysis': {
      title: 'Root Cause Analysis',
      explanation: 'Systematic investigation process to identify the fundamental causes of security incidents and prevent recurrence.',
      examples: 'Five Whys technique, fishbone diagrams, fault tree analysis, timeline analysis, contributing factor identification',
      mnemonic: '🕵️ ROOT-CAUSE = Really, Observe, Obviously, The, Core, And, Underlying, Source, Everyone\nThink of detective investigating crime - dig deep to find the real reason.',
      visual: `
    🕵️ ROOT CAUSE ANALYSIS
    🔍 INVESTIGATION METHODS
    ├── ❓ Five Whys technique
    ├── 🐟 Fishbone diagram
    ├── 🌳 Fault tree analysis
    ├── ⏰ Timeline analysis
    └── 📊 Statistical analysis
    
    🧩 CONTRIBUTING FACTORS
    ├── 🔧 Technical factors
    ├── 👥 Human factors
    ├── 📋 Process factors
    ├── 🏢 Organizational factors
    └── 🌍 Environmental factors
    
    🎯 ROOT CAUSE IDENTIFICATION
    ├── 💡 Primary cause
    ├── 🔗 Secondary causes
    ├── 🔄 Systemic issues
    ├── 📊 Pattern analysis
    └── 🧪 Hypothesis testing
    
    💡 CORRECTIVE ACTIONS
    ├── 🔧 Technical improvements
    ├── 📋 Process changes
    ├── 🧑‍🎓 Training updates
    ├── 🛡️ Control enhancements
    └── 📊 Monitoring improvements`
    },
    'ir-threat-hunting': {
      title: 'Threat Hunting',
      explanation: 'Proactive security practice of searching through networks and datasets to detect and isolate advanced threats that evade security solutions.',
      examples: 'Hypothesis-driven hunting, IOC-based searches, behavioral analysis, network traffic analysis, endpoint investigation',
      mnemonic: '🎯 HUNTING = Hypotheses, Uncover, Network, Threats, Intelligence, Never, Guessing\nThink of detective following clues - proactive investigation vs waiting for alarms.',
      visual: `
    🎯 THREAT HUNTING
    💡 HYPOTHESIS DEVELOPMENT
    ├── 🧠 Threat intelligence input
    ├── 📊 Risk assessment
    ├── 🎯 Attack scenarios
    └── 🔍 Investigation focus
    
    🔍 DATA COLLECTION
    ├── 📊 Log aggregation
    ├── 🌐 Network traffic
    ├── 📱 Endpoint data
    ├── 🛡️ Security tool data
    └── ☁️ Cloud telemetry
    
    📊 HUNTING TECHNIQUES
    ├── 📈 Statistical analysis
    ├── 🔗 Behavioral analysis
    ├── 🏷️ IOC matching
    ├── 🎯 Anomaly hunting
    └── 🔄 Pattern recognition
    
    🎯 THREAT IDENTIFICATION
    ├── 🦠 Advanced malware
    ├── 🕵️ Insider threats
    ├── 🎭 APT activities
    ├── 🔒 Lateral movement
    └── 📊 Data exfiltration`
    },
    'ir-digital-forensics': {
      title: 'Digital Forensics in IR',
      explanation: 'Application of forensic techniques during incident response to preserve evidence and support investigation and legal proceedings.',
      examples: 'Legal hold procedures, chain of custody, evidence acquisition, forensic reporting, preservation techniques, e-discovery',
      mnemonic: '🔬 FORENSICS = Find, Officially, Record, Evidence, Never, Suspect, Investigation, Captures, Systems\nThink of CSI investigation - scientifically gathering and analyzing evidence.',
      visual: `
    🔬 DIGITAL FORENSICS IN IR
    ⚖️ LEGAL HOLD
    ├── 📧 Hold notice issued
    ├── 🚫 Stop data destruction
    ├── 📋 Scope definition
    ├── 👤 Custodian notification
    └── 👁️ Ongoing monitoring
    
    🔗 CHAIN OF CUSTODY
    ├── 📋 Evidence documentation
    ├── 👤 Handler identification
    ├── ⏰ Access timestamps
    ├── 📍 Storage locations
    └── 🔒 Security measures
    
    💾 ACQUISITION
    ├── 🔒 Write-protected imaging
    ├── 🔍 Hash verification
    ├── 📊 Metadata preservation
    ├── 💾 Bit-for-bit copies
    └── 📋 Documentation
    
    📊 REPORTING
    ├── 🔍 Analysis findings
    ├── 📈 Evidence correlation
    ├── ⏰ Timeline reconstruction
    ├── 📋 Expert opinions
    └── ⚖️ Legal compliance
    
    📁 PRESERVATION
    ├── 🔒 Secure storage
    ├── 🔍 Access controls
    ├── 📊 Integrity monitoring
    ├── 📅 Retention policies
    └── 🗄️ Archive management
    
    🔍 E-DISCOVERY
    ├── 📊 Data identification
    ├── 🔍 Collection processes
    ├── 📋 Review procedures
    ├── 📤 Production formats
    └── ⚖️ Legal compliance`
    },
    // ADDITIONAL MISSING CONCEPTS
    'system-process-audit': {
      title: 'System/Process Audit',
      explanation: 'Systematic examination of systems and processes to evaluate security controls, compliance, and operational effectiveness.',
      examples: 'Security configuration reviews, process walkthroughs, control testing, compliance verification, risk assessments',
      mnemonic: '🔍 AUDIT = Assess, Understand, Determine, Investigate, Thoroughly\nThink of quality inspector - checking everything meets standards.',
      visual: `
    🔍 SYSTEM/PROCESS AUDIT
    📋 AUDIT SCOPE
    ├── 🎯 Systems reviewed
    ├── 🔄 Processes examined
    ├── ⚖️ Compliance requirements
    └── 📅 Time period
    
    🔬 EXAMINATION METHODS
    ├── 📄 Documentation review
    ├── 🖥️ System configuration
    ├── 🗣️ Personnel interviews
    ├── 🧪 Control testing
    └── 📊 Data analysis
    
    📊 FINDINGS & REPORTING
    ├── ✅ Compliance status
    ├── 🚨 Deficiencies identified
    ├── 💡 Recommendations
    └── 📋 Action plans`
    },
    'monitoring-systems': {
      title: 'Systems Monitoring',
      explanation: 'Continuous observation of computer systems, servers, and infrastructure to detect performance issues and security threats.',
      examples: 'CPU monitoring, memory usage, disk I/O, network interfaces, system processes, performance metrics',
      mnemonic: '🖥️ SYSTEMS = Servers, Your, Systems, Technology, Everyone, Monitors, Systems\nThink of vital signs monitor - watching system health continuously.',
      visual: `
    🖥️ SYSTEMS MONITORING
    💾 HARDWARE METRICS
    ├── 🔄 CPU utilization
    ├── 🧠 Memory usage
    ├── 💽 Disk I/O
    └── 🌐 Network interfaces
    
    📊 PERFORMANCE INDICATORS
    ├── ⚡ Response times
    ├── 📈 Throughput
    ├── 🔄 Process counts
    └── 🚨 Error rates
    
    🛡️ SECURITY EVENTS
    ├── 🔐 Authentication failures
    ├── 📁 File access anomalies
    ├── 🔒 Privilege changes
    └── 🌐 Network connections`
    },
    'monitoring-applications': {
      title: 'Applications Monitoring',
      explanation: 'Tracking application performance, availability, and security to ensure optimal user experience and detect threats.',
      examples: 'Application response times, error rates, transaction volumes, user sessions, database connections',
      mnemonic: '📱 APPLICATIONS = Always, Providing, Performance, Logs, Information, Continuously, All, Technology, Intelligence, Operations, Never, Stops\nThink of app store ratings - constant feedback on performance.',
      visual: `
    📱 APPLICATIONS MONITORING
    ⚡ PERFORMANCE METRICS
    ├── 🚀 Response times
    ├── 📊 Transaction volumes
    ├── 👥 Active users
    └── 🔗 Database connections
    
    🚨 ERROR TRACKING
    ├── 💥 Application crashes
    ├── 🐛 Exception handling
    ├── 🔍 Debug information
    └── 📋 Error logs
    
    🛡️ SECURITY MONITORING
    ├── 🔐 Authentication events
    ├── 🔒 Authorization failures
    ├── 📊 Data access patterns
    └── 🚨 Suspicious activities`
    },
    'monitoring-infrastructure': {
      title: 'Infrastructure Monitoring',
      explanation: 'Oversight of physical and virtual infrastructure components including networks, storage, and supporting systems.',
      examples: 'Network device monitoring, storage capacity, power systems, environmental controls, virtualization platforms',
      mnemonic: '🏗️ INFRASTRUCTURE = Infrastructure, Never, Fails, Requires, All, Systems, Tracking, Running, Under, Continuous, Technology, Unified, Reliable, Everything\nThink of building foundation - monitor what everything depends on.',
      visual: `
    🏗️ INFRASTRUCTURE MONITORING
    🌐 NETWORK INFRASTRUCTURE
    ├── 🔀 Switches
    ├── 📡 Routers
    ├── 🔥 Firewalls
    └── 📶 Wireless access points
    
    💾 STORAGE SYSTEMS
    ├── 🗄️ Storage arrays
    ├── 📊 Capacity utilization
    ├── ⚡ I/O performance
    └── 🔄 Backup systems
    
    🔌 FACILITY SYSTEMS
    ├── ⚡ Power systems
    ├── ❄️ Cooling systems
    ├── 🚨 Physical security
    └── 🌡️ Environmental sensors`
    },
    // DOMAIN 4.5 CONCEPTS FROM STUDY GUIDE
    'right-to-audit': {
      title: 'Right-to-Audit Clauses',
      explanation: 'Contractual provisions that allow an organization to audit a third-party provider\'s operations, systems, and controls to ensure compliance with security requirements and standards.',
      examples: 'Cloud service contracts allowing customer audits, vendor agreements permitting security assessments, SaaS contracts with audit rights',
      mnemonic: '📜 RIGHT = Review In-depth, Gain Honest Transparency\nThink of a backstage pass - you get to inspect behind the scenes.',
      visual: `
    📄 CONTRACT
    │
    ├── Clause: "Customer may audit provider"
    │   ├── Security controls ✓
    │   ├── Compliance checks ✓
    │   └── Data handling ✓
    │
    ▼
    🔍 AUDIT CONDUCTED
    │
    ✅ ASSURANCE GAINED`
    },
    'regulatory-jurisdiction': {
      title: 'Regulatory/Jurisdiction',
      explanation: 'The legal and geographical boundaries that determine which laws and regulations apply to digital evidence collection, handling, and use in investigations.',
      examples: 'GDPR for EU data, varying state laws in the US for evidence admissibility, international jurisdiction conflicts in cloud forensics',
      mnemonic: '⚖️ REGULATE = Rules Everywhere Govern Uniform Laws And Territories Effectively\nThink of country borders - evidence rules change when you cross them.',
      visual: `
    🌍 WORLD MAP
    ┌─────────┐  ┌─────────┐
    │ EU: GDPR │  │ US: CISA │
    └─────────┘  └─────────┘
         │            │
         └──── ⚖️ ────┘
          JURISDICTION`
    },
    'data-breach-notification': {
      title: 'Data Breach Notification Laws',
      explanation: 'Legal requirements mandating organizations to notify affected individuals, regulators, or authorities within specific timeframes after discovering a data breach.',
      examples: 'GDPR 72-hour notification, California CCPA requirements, HIPAA breach reporting to HHS',
      mnemonic: '🚨 NOTIFY = News Of Theft Immediately For You\nThink of fire alarm - you must alert everyone quickly.',
      visual: `
    💥 BREACH OCCURS
    │
    ▼
    ⏰ TIMER STARTS (e.g., 72 hours)
    │
    ▼
    📢 NOTIFICATIONS SENT
    ├── To victims
    ├── To regulators
    └── To authorities`
    },
    'forensic-hashing': {
      title: 'Hashing (Forensic)',
      explanation: 'Cryptographic process creating a unique digital fingerprint of data or files to verify integrity and detect any changes during forensic investigations.',
      examples: 'MD5 or SHA-256 hashes of disk images, hashing evidence files before and after analysis',
      mnemonic: '🔒 HASH = Hashes Assure Secure Handling\nThink of DNA fingerprint - unique identifier for data.',
      visual: `
    📄 ORIGINAL DATA
    │
    ▼
    🔑 HASH FUNCTION (SHA-256)
    │
    ▼
    🆔 UNIQUE HASH: abc123...
    │
    ▼
    🔍 VERIFY: Matches? ✅ INTEGRITY CONFIRMED`
    },
    'forensic-checksums': {
      title: 'Checksums (Forensic)',
      explanation: 'Mathematical values computed from data blocks to detect errors or changes, used in forensics to ensure evidence hasn\'t been altered during acquisition or storage.',
      examples: 'CRC32 checksums for file verification, checksum validation in forensic tools like EnCase',
      mnemonic: '✅ CHECK = Confirm Hashes Ensure Complete Knowledge\nThink of receipt total - quick check if anything changed.',
      visual: `
    🔢 DATA BLOCK
    │
    ▼
    ➕ CHECKSUM CALCULATION
    │
    ▼
    #️⃣ VALUE: 12345
    │
    ▼
    🔄 LATER: Recalculate & Compare
    ✅ NO CHANGES`
    },
    'provenance': {
      title: 'Provenance',
      explanation: 'The chronological documentation of the origin, custody, control, and disposition of digital evidence to establish its authenticity and reliability.',
      examples: 'Tracking a file\'s history from creation to court, documenting evidence chain in forensic reports',
      mnemonic: '📜 PROVEN = Past Records Of Value Ensure Nothing altered\nThink of art history - tracing ownership back to creator.',
      visual: `
    🕰️ TIMELINE
    ├── Origin: Created by User A
    ├── Transfer: To Investigator B
    ├── Analysis: By Expert C
    └── Storage: In Secure Vault
    │
    ✅ AUTHENTICITY PROVED`
    },
    'non-repudiation': {
      title: 'Non-repudiation',
      explanation: 'Security principle ensuring that parties cannot deny the authenticity of their digital transactions or communications.',
      examples: 'Digital signatures on contracts, cryptographic proof of message origin, timestamped audit trails',
      mnemonic: '🔒 NON-REPUDIATION = Never Offer No Responsibility, Everyone Proves Unique Digital Identity And Truth In Online Networks\nThink of signed receipt - can\'t deny you bought it.',
      visual: `
    🔐 DIGITAL SIGNATURE
    ├── Private key signs
    ├── Public key verifies
    ├── Timestamp included
    └── Hash of content
    │
    ✅ UNDENIABLE PROOF`
    },
    'strategic-intelligence': {
      title: 'Strategic Intelligence/Counterintelligence',
      explanation: 'Long-term analysis of threats, vulnerabilities, and adversary capabilities to inform security strategies, while counterintelligence protects against espionage and information leaks.',
      examples: 'Threat intelligence reports on nation-state actors, internal programs to detect insider threats',
      mnemonic: '🕵️ STRATEGIC = Spy Tactics Reveal Adversary Goals, Employ Counter Intelligence Carefully\nThink of chess grandmaster planning moves ahead.',
      visual: `
    ♟️ CHESS BOARD
    │
    ├── Intelligence: Know opponent's plans
    │
    ├── Counter: Block their spies
    │
    ▼
    🏆 SECURITY STRATEGY WINS`
    },
    // DOMAIN 4.6 AUTOMATION CONCEPTS FROM STUDY GUIDE
    'security-automation': {
      title: 'Security Automation',
      explanation: 'The use of technology to perform security tasks without human intervention, improving efficiency and reducing errors in processes like threat detection and response.',
      examples: 'Automated vulnerability scanning, script-based firewall rule updates, auto-quarantine of suspicious files',
      mnemonic: '🤖 AUTO = Always Use Tools Optimizing security\nThink of robot guard - works 24/7 without breaks.',
      visual: `
    👤 MANUAL TASK
    │
    ▼
    🤖 AUTOMATE
    ├── Detect threat ✓
    ├── Alert team ✓
    ├── Respond ✓
    │
    ✅ FASTER SECURITY`
    },
    'resource-provisioning': {
      title: 'Resource Provisioning',
      explanation: 'Automated allocation and configuration of computing resources like servers, storage, and networks to meet security and operational needs.',
      examples: 'Cloud auto-scaling groups, Infrastructure as Code (IaC) tools like Terraform for secure deployments',
      mnemonic: '🛠️ PROVISION = Provide Resources On-demand Very Instantly Securely In Organized Networks\nThink of vending machine - insert request, get resources instantly.',
      visual: `
    📋 REQUEST: New server
    │
    ▼
    🤖 AUTO-PROVISION
    ├── Allocate VM ✓
    ├── Configure security ✓
    ├── Deploy ✓
    │
    ✅ RESOURCE READY`
    },
    'configuration-management': {
      title: 'Configuration Management',
      explanation: 'Systematic process to maintain consistent and secure configurations across systems, often automated to prevent drift and vulnerabilities.',
      examples: 'Ansible or Puppet for enforcing security baselines, tracking changes in system settings',
      mnemonic: '⚙️ CONFIG = Control Options Now For Ideal Governance\nThink of recipe book - follow exact ingredients for consistent results.',
      visual: `
    🖥️ SYSTEMS
    │
    ▼
    ⚙️ CONFIG TOOL
    ├── Apply baseline ✓
    ├── Monitor changes ✓
    ├── Remediate drift ✓
    │
    ✅ CONSISTENT SECURITY`
    },
    'policy-enforcement': {
      title: 'Policy Enforcement',
      explanation: 'Automated application of security policies to ensure compliance, such as access controls or data protection rules.',
      examples: 'Group Policy Objects in Active Directory, automated compliance checks in cloud environments',
      mnemonic: '📜 ENFORCE = Ensure No Forbidden Operations Really Compromise Everything\nThink of traffic cop - automatically tickets violators.',
      visual: `
    📜 POLICY: "No unauthorized access"
    │
    ▼
    🚔 ENFORCEMENT ENGINE
    ├── Check request ✓
    ├── Allow/deny ✓
    ├── Log violation ✓
    │
    ✅ COMPLIANCE MAINTAINED`
    },
    'soar': {
      title: 'SOAR (Security Orchestration, Automation, and Response)',
      explanation: 'Platform that integrates security tools, automates workflows, and orchestrates responses to incidents for faster threat mitigation.',
      examples: 'Splunk SOAR, IBM Resilient, Palo Alto Cortex XSOAR for automating incident playbooks',
      mnemonic: '🚀 SOAR = Security Operations Accelerate Responses\nThink of eagle soaring - high-level coordination and quick dives on threats.',
      visual: `
    🛡️ SECURITY TOOLS
    ├── SIEM
    ├── Firewall
    ├── Endpoint
    │
    ▼
    🚀 SOAR PLATFORM
    │ Orchestrate & Automate
    ▼
    ⚡ FAST RESPONSE`
    },
    'incident-response-automation': {
      title: 'Incident Response Automation',
      explanation: 'Using scripts and tools to automatically detect, analyze, and respond to security incidents, reducing response time.',
      examples: 'Auto-isolation of compromised hosts, automated ticket creation in IR systems',
      mnemonic: '🚨 AUTO-IR = Accelerate Urgent Tasks Organizing Incident Responses\nThink of fire suppression system - activates instantly on detection.',
      visual: `
    💥 INCIDENT DETECTED
    │
    ▼
    🤖 AUTOMATE
    ├── Isolate ✓
    ├── Notify ✓
    ├── Remediate ✓
    │
    ✅ CRISIS AVERTED`
    },
    'threat-hunting-automation': {
      title: 'Threat Hunting Automation',
      explanation: 'Proactive searching for threats using automated queries and analytics to identify hidden adversaries before they cause damage.',
      examples: 'Automated IOC scans, machine learning-based anomaly detection in hunting platforms',
      mnemonic: '🕵️ HUNT = Hidden Undetected Nasties Tracked automatically\nThink of automated trap - sets itself and catches prey.',
      visual: `
    🌐 NETWORK
    │
    ▼
    🕵️ AUTO-HUNT
    ├── Scan for IOCs ✓
    ├── Analyze anomalies ✓
    ├── Alert on findings ✓
    │
    ✅ THREATS FOUND`
    },
    'compliance-reporting': {
      title: 'Compliance Reporting',
      explanation: 'Automated generation of reports demonstrating adherence to regulations and standards, using data from security tools.',
      examples: 'Automated PCI DSS reports, GDPR compliance dashboards',
      mnemonic: '📊 REPORT = Regularly Export Proof Of Regulatory Tasks\nThink of automated tax filer - gathers data and submits forms.',
      visual: `
    📜 REGULATIONS
    │
    ▼
    🤖 AUTO-REPORT
    ├── Collect data ✓
    ├── Generate report ✓
    ├── Submit ✓
    │
    ✅ COMPLIANT`
    },
    'user-provisioning': {
      title: 'User Provisioning',
      explanation: 'Automated creation, modification, and deletion of user accounts and access rights across systems.',
      examples: 'SCIM for identity federation, automated onboarding in HR systems linked to IAM',
      mnemonic: '👤 PROVISION = Provide Roles Onboarding Very Instantly For Staff In Organized Networks\nThink of hotel check-in - auto-assign room and key.',
      visual: `
    👤 NEW USER
    │
    ▼
    🤖 AUTO-PROVISION
    ├── Create account ✓
    ├── Assign roles ✓
    ├── Grant access ✓
    │
    ✅ USER READY`
    },
    'resource-provisioning-automation': {
      title: 'Resource Provisioning (Automation)',
      explanation: 'Automated allocation and configuration of computing resources like servers, storage, and networks to meet security and operational needs.',
      examples: 'Cloud auto-scaling groups, Infrastructure as Code (IaC) tools like Terraform for secure deployments',
      mnemonic: '🛠️ PROVISION = Provide Resources On-demand Very Instantly Securely In Organized Networks\nThink of vending machine - insert request, get resources instantly.',
      visual: `
    📋 REQUEST: New server
    │
    ▼
    🤖 AUTO-PROVISION
    ├── Allocate VM ✓
    ├── Configure security ✓
    ├── Deploy ✓
    │
    ✅ RESOURCE READY`
    },
    // DOMAIN 4.6 REMAINING AUTOMATION BENEFITS AND CONSIDERATIONS
    'automation-efficiency': {
      title: 'Efficiency (Automation Benefit)',
      explanation: 'Automation reduces time and resources needed for security tasks, allowing teams to focus on high-value activities.',
      examples: 'Automating patch management saves hours of manual work, scripted backups run without intervention',
      mnemonic: '⚡ EFFICIENCY = Eliminate Futile Efforts, Increase Clever Initiatives Now, Cut Yawns\nThink of assembly line - faster production with less effort.',
      visual: `
    ⏱️ MANUAL: 10 hours
    │
    ▼
    🤖 AUTOMATED: 1 hour
    │
    ▼
    💪 MORE TIME FOR STRATEGY`
    },
    'automation-consistency': {
      title: 'Consistency (Automation Benefit)',
      explanation: 'Ensures uniform application of security measures across all systems, reducing human error variations.',
      examples: 'Automated config enforcement prevents misconfigurations, standard playbooks for all incidents',
      mnemonic: '🔄 CONSIST = Create One Novel System Insuring Steady Treatment\nThink of cookie cutter - every item identical.',
      visual: `
    👥 HUMANS: Varies
    │
    ▼
    🤖 AUTOMATE: Uniform
    ├── System A ✓
    ├── System B ✓
    ├── System C ✓
    │
    ✅ NO VARIATIONS`
    },
    'automation-scalability': {
      title: 'Scalability (Automation Benefit)',
      explanation: 'Allows security processes to handle growing volumes of data, systems, or threats without proportional increase in effort.',
      examples: 'Cloud auto-scaling security groups, automated monitoring for thousands of endpoints',
      mnemonic: '📈 SCALE = Systems Can Accommodate Large Expansion\nThink of elastic band - stretches as needed.',
      visual: `
    📊 GROWTH
    │
    ▼
    🤖 AUTOMATE
    ├── Handle 10 systems ✓
    ├── Handle 1000 systems ✓
    │
    ✅ EFFORTLESS SCALING`
    },
    'response-time-improvement': {
      title: 'Response Time Improvement',
      explanation: 'Automation accelerates detection and mitigation of threats, minimizing damage from incidents.',
      examples: 'Auto-block IP on detection, instant alerts and quarantines',
      mnemonic: '⏱️ RESPOND = Rapid Execution Speeds Protection Of Networks Daily\nThink of sprinter - quick off the mark.',
      visual: `
    🚨 THREAT
    │
    ▼
    🤖 AUTO-RESPONSE: Instant
    │ vs. Manual: Delayed
    ▼
    🛡️ DAMAGE MINIMIZED`
    },
    'error-reduction': {
      title: 'Error Reduction',
      explanation: 'Minimizes human mistakes in repetitive security tasks through scripted, repeatable processes.',
      examples: 'Automated backups avoid forgetfulness, scripted deployments prevent config errors',
      mnemonic: '🚫 ERROR = Eliminate Reckless Repetitive Oversights Routinely\nThink of spell checker - catches mistakes automatically.',
      visual: `
    👤 HUMAN: Prone to errors
    │
    ▼
    🤖 AUTOMATE: Error-free
    │
    ✅ RELIABLE EXECUTION`
    },
    'automation-complexity': {
      title: 'Complexity (Automation Consideration)',
      explanation: 'Automation systems can become overly intricate, making them hard to maintain or troubleshoot.',
      examples: 'Overly scripted workflows with many dependencies, complex orchestration in SOAR platforms',
      mnemonic: '🧩 COMPLEX = Create Overly Messy Processes Leading to Extra Xeroxing (duplication of effort)\nThink of tangled wires - hard to fix when broken.',
      visual: `
    🧩 SIMPLE SCRIPT
    │
    ▼
    📈 GROWS COMPLEX
    ├── Many integrations
    ├── Dependencies
    │
    ▼
    🤯 HARD TO MAINTAIN`
    },
    'automation-cost': {
      title: 'Cost (Automation Consideration)',
      explanation: 'Initial development, tools, and maintenance of automation can be expensive, requiring ROI justification.',
      examples: 'Licensing for SOAR tools, developer time for custom scripts',
      mnemonic: '💰 COST = Cash Outlay Seems Tricky\nThink of investment - upfront spend for long-term savings.',
      visual: `
    💸 INITIAL COST
    ├── Tools ✓
    ├── Development ✓
    │
    ▼
    📉 LONG-TERM SAVINGS?
    │
    ⚖️ BALANCE ROI`
    },
    'single-point-failure': {
      title: 'Single Point of Failure',
      explanation: 'Automation relying on one component that, if fails, disrupts entire security operations.',
      examples: 'Central automation server outage halting all scripts, single API endpoint failure',
      mnemonic: '🚧 SINGLE = System Inevitably Nears Graveyard If No Alternatives Given Lonely Existence\nThink of one bridge - if it collapses, no way across.',
      visual: `
    🌉 ONE BRIDGE (Automation Hub)
    │
    ▼
    💥 FAILURE
    │
    ▼
    🛑 ALL TRAFFIC STOPPED`
    },
    'technical-debt': {
      title: 'Technical Debt',
      explanation: 'Accumulated maintenance burden from quick automation fixes that weren\'t properly designed, leading to future rework.',
      examples: 'Legacy scripts not updated, patched-together automations becoming fragile',
      mnemonic: '🛠️ DEBT = Delayed Engineering Builds Trouble\nThink of credit card - small purchases add up to big bill.',
      visual: `
    💳 QUICK FIX
    │
    ▼
    📈 DEBT ACCUMULATES
    │
    ▼
    🔧 MAJOR REWORK NEEDED`
    },
    'ongoing-supportability': {
      title: 'Ongoing Supportability',
      explanation: 'Ensuring automation systems remain maintainable, updatable, and supported over time with documentation and skilled personnel.',
      examples: 'Regular updates to scripts, training teams on automation tools',
      mnemonic: '🛡️ SUPPORT = Sustain Upkeep Providing Ongoing Reliability Today\nThink of car maintenance - regular service keeps it running.',
      visual: `
    🤖 AUTOMATION SYSTEM
    │
    ▼
    🛠️ ONGOING SUPPORT
    ├── Updates ✓
    ├── Documentation ✓
    ├── Training ✓
    │
    ✅ LONG-TERM VIABILITY`
    },
    'rest-api': {
      title: 'REST API',
      explanation: 'Representational State Transfer Application Programming Interface - a standard for web services using HTTP methods for stateless communication.',
      examples: 'AWS REST APIs for cloud management, security tool integrations via REST endpoints',
      mnemonic: '🌐 REST = Reliable Easy Stateless Transfer\nThink of postal service - send requests, get responses without connection memory.',
      visual: `
    📱 CLIENT
    │ GET /resource
    ▼
    🌐 SERVER
    │
    ▼
    📦 RESPONSE: JSON data`
    },
    'soap': {
      title: 'SOAP',
      explanation: 'Simple Object Access Protocol - XML-based protocol for exchanging structured information in web services, often with built-in security.',
      examples: 'Enterprise web services for secure transactions, WS-Security extensions',
      mnemonic: '🧼 SOAP = Structured Object Access Protocol\nThink of cleaning - structured and thorough message exchange.',
      visual: `
    📦 SOAP ENVELOPE
    ├── Header (Security)
    ├── Body (Data)
    │
    ▼
    🔄 EXCHANGE VIA XML`
    },
    'json': {
      title: 'JSON',
      explanation: 'JavaScript Object Notation - lightweight data interchange format that is easy for humans and machines to read and write.',
      examples: 'API responses in security tools, configuration files in automation',
      mnemonic: '📄 JSON = JavaScript Object Notation\nThink of dictionary - key-value pairs for data.',
      visual: `
    {
      "key": "value",
      "array": [1, 2, 3]
    }`
    },
    'xml': {
      title: 'XML',
      explanation: 'eXtensible Markup Language - markup language for encoding documents in a format readable by humans and machines.',
      examples: 'SOAP messages, security policy configurations',
      mnemonic: '🏷️ XML = eXtensible Markup Language\nThink of tagged luggage - labels describe contents.',
      visual: `
    <data>
      <item>value</item>
    </data>`
    },
    'webhooks': {
      title: 'Webhooks',
      explanation: 'Automated callbacks triggered by events, allowing real-time data push from one system to another via HTTP.',
      examples: 'GitHub webhooks for security alerts, integration hooks in SIEM',
      mnemonic: '🪝 HOOK = HTTP Operations On Known events\nThink of fishing hook - event triggers a pull.',
      visual: `
    ⚡ EVENT OCCURS
    │
    ▼
    🪝 WEBHOOK CALLBACK
    │ POST /endpoint
    ▼
    📥 DATA RECEIVED`
    },
    'scap-protocol': {
      title: 'Security Content Automation Protocol (SCAP)',
      explanation: 'NIST standard for automating vulnerability management, security measurement, and policy compliance evaluation.',
      examples: 'SCAP scanners like OpenSCAP, compliance checking in federal systems',
      mnemonic: '🔒 SCAP = Security Content Automation Protocol\nThink of security cape - protects with automated standards.',
      visual: `
    🛡️ SCAP COMPONENTS
    ├── OVAL ✓
    ├── XCCDF ✓
    ├── CVE ✓
    │
    ▼
    📊 AUTOMATED CHECKS`
    },
    'oval': {
      title: 'Open Vulnerability and Assessment Language (OVAL)',
      explanation: 'Standardized language for expressing system configurations and evaluating vulnerabilities in automation.',
      examples: 'OVAL definitions in vulnerability scanners, automated assessment scripts',
      mnemonic: '🔍 OVAL = Open Vulnerability Assessment Language\nThink of oval mirror - reflects system state clearly.',
      visual: `
    🖥️ SYSTEM STATE
    │
    ▼
    🔍 OVAL CHECK
    │
    ▼
    📋 VULN REPORT`
    },
    'stix-taxii': {
      title: 'STIX/TAXII',
      explanation: 'Structured Threat Information eXpression (STIX) for representing threat intel, Trusted Automated eXchange of Intelligence Information (TAXII) for sharing it.',
      examples: 'Cyber threat intelligence sharing platforms, ISACs using STIX for IOCs',
      mnemonic: '🕵️ STIX = Structured Threat Info eXpression; TAXII = Trusted Auto eXchange Intel Info\nThink of sticky notes (STIX) in a taxi (TAXII) - sharing intel on the move.',
      visual: `
    🕵️ THREAT INTEL
    │ STIX Format
    ▼
    🚕 TAXII SERVER
    │ Share securely
    ▼
    🤝 RECEIVERS`
    },
    // DOMAIN 4.7 DATA SOURCES - DETAILED LOG CONCEPTS FROM STUDY GUIDE
    'firewall-logs': {
      title: 'Firewall Logs',
      explanation: 'Records of network traffic allowed or blocked by firewalls, useful for investigating unauthorized access attempts.',
      examples: 'Cisco ASA logs showing denied connections, pfSense traffic reports',
      mnemonic: '🚧 FIREWALL = Filter Inspections Record Every Wall Action Logically Logged\nThink of bouncer\'s notebook - who was let in or turned away.',
      visual: `
    🌐 TRAFFIC
    │
    ▼
    🚧 FIREWALL
    ├── Allow: Log ✓
    ├── Deny: Log ✓
    │
    ▼
    📋 INVESTIGATE LOGS`
    },
    'application-logs': {
      title: 'Application Logs',
      explanation: 'Records generated by software applications detailing operations, errors, and security events.',
      examples: 'Web server access logs, database query logs showing SQL injections',
      mnemonic: '🖥️ APP = Audit Program Performance\nThink of diary - app writes what happened.',
      visual: `
    🖥️ APP RUNS
    │
    ▼
    📝 LOG EVENT
    ├── Error ✓
    ├── Access ✓
    │
    ▼
    🔍 ANALYZE`
    },
    'endpoint-logs': {
      title: 'Endpoint Logs',
      explanation: 'Security event records from individual devices like computers or mobiles, tracking processes and user activities.',
      examples: 'Windows Event Logs, macOS unified logs for malware detection',
      mnemonic: '💻 ENDPOINT = Every Network Device Provides Observable Incident Notes Timely\nThink of security camera on each device.',
      visual: `
    💻 DEVICE
    │
    ▼
    📝 LOGS
    ├── Process start ✓
    ├── File access ✓
    │
    ▼
    🕵️ INVESTIGATE`
    },
    'os-security-logs': {
      title: 'OS-Specific Security Logs',
      explanation: 'Operating system logs focused on security events like authentication attempts and privilege escalations.',
      examples: 'Linux auth.log, Windows Security Event Log',
      mnemonic: '🛡️ OS = Operating System Shields\nThink of OS as watchdog - barks (logs) at intruders.',
      visual: `
    🖥️ OS
    │
    ▼
    🛡️ SECURITY EVENT
    │
    ▼
    📝 LOGGED`
    },
    'ips-ids-logs': {
      title: 'IPS/IDS Logs',
      explanation: 'Alerts and records from Intrusion Prevention/Detection Systems about potential threats and attacks.',
      examples: 'Snort alerts on signature matches, Suricata logs of anomalies',
      mnemonic: '🚨 IPS/IDS = Intrusion Prevention/Detection Systems\nThink of alarm system - logs every trigger.',
      visual: `
    🌐 TRAFFIC
    │
    ▼
    🚨 IPS/IDS
    │ Detect & Log
    ▼
    📋 ALERTS`
    },
    'network-logs': {
      title: 'Network Logs',
      explanation: 'Records of network activities including connections, protocols, and data flows for traffic analysis.',
      examples: 'Router syslogs, NetFlow records',
      mnemonic: '🌐 NET = Note Every Transmission\nThink of traffic cam - records all passing data.',
      visual: `
    🌐 NETWORK FLOW
    │
    ▼
    📝 LOG
    ├── Source IP ✓
    ├── Destination ✓
    │
    ▼
    🔍 ANALYZE`
    },
    'vulnerability-scans': {
      title: 'Vulnerability Scans',
      explanation: 'Automated assessments identifying weaknesses in systems, providing data for investigation prioritization.',
      examples: 'Nessus scan reports, OpenVAS findings',
      mnemonic: '🔍 SCAN = Search Carefully All Networks\nThink of medical scan - finds issues before they hurt.',
      visual: `
    🖥️ SYSTEMS
    │
    ▼
    🔍 SCAN
    │
    ▼
    📋 VULNS LISTED`
    },
    'automated-reports': {
      title: 'Automated Reports',
      explanation: 'System-generated summaries of security data, trends, or incidents for quick analysis.',
      examples: 'SIEM daily summaries, compliance auto-reports',
      mnemonic: '📊 AUTO-REPORT = Assemble Useful Trends Overnight Regularly Every Period Of Required Time\nThink of newspaper - delivered summary daily.',
      visual: `
    📈 DATA
    │
    ▼
    🤖 GENERATE
    │
    ▼
    📄 REPORT`
    },
    'dashboards': {
      title: 'Dashboards',
      explanation: 'Visual interfaces displaying real-time security metrics and logs for monitoring and investigation.',
      examples: 'Splunk dashboards, Kibana visualizations',
      mnemonic: '📊 DASH = Display All Security Highlights\nThink of car dashboard - key info at a glance.',
      visual: `
    📊 DASHBOARD
    ├── Graphs ✓
    ├── Alerts ✓
    ├── Metrics ✓
    │
    ▼
    👀 QUICK INSIGHTS`
    },
    'packet-captures': {
      title: 'Packet Captures',
      explanation: 'Recorded network packets for detailed analysis of communications during investigations.',
      examples: 'Wireshark pcaps, tcpdump files',
      mnemonic: '📦 PACKET = Pick Apart Communication Knowledge Every Time\nThink of recording a conversation - replay for details.',
      visual: `
    🌐 TRAFFIC
    │
    ▼
    🎥 CAPTURE
    │
    ▼
    🔍 ANALYZE PACKETS`
    },
    'correlation': {
      title: 'Correlation',
      explanation: 'Linking related events from multiple sources to identify patterns or incidents.',
      examples: 'SIEM correlating logs from firewall and IDS for attack detection',
      mnemonic: '🔗 CORRELATE = Connect Observations Revealing Real Events Linking All Together Efficiently\nThink of connecting dots - reveals the picture.',
      visual: `
    📝 LOG A
    │
    ▼
    🔗 CORRELATE
    │ + LOG B
    ▼
    🕵️ INCIDENT PATTERN`
    },
    'trend-analysis': {
      title: 'Trend Analysis',
      explanation: 'Examining data over time to identify patterns, anomalies, or emerging threats.',
      examples: 'Increasing failed logins over weeks indicating brute-force attempts',
      mnemonic: '📈 TREND = Track Repeated Events Noting Directions\nThink of stock chart - spots rising or falling patterns.',
      visual: `
    ⏳ TIME
    │
    ▼
    📈 GRAPH TRENDS
    │
    ▼
    🔍 INSIGHTS`
    },
    'statistical-analysis': {
      title: 'Statistical Analysis',
      explanation: 'Using math to quantify risks, detect outliers, or validate findings in security data.',
      examples: 'Mean deviation for anomaly detection, probability calculations for threat likelihood',
      mnemonic: '📊 STATS = Summarize Trends And Test Significance\nThink of sports stats - numbers tell the story.',
      visual: `
    🔢 DATA
    │
    ▼
    📊 ANALYZE
    ├── Mean ✓
    ├── Variance ✓
    │
    ▼
    📉 CONCLUSIONS`
    },
    'behavioral-analysis': {
      title: 'Behavioral Analysis',
      explanation: 'Monitoring entity actions to detect deviations from normal patterns, indicating potential threats.',
      examples: 'UEBA tools flagging unusual user access times',
      mnemonic: '🧠 BEHAVE = Baseline Every Habit And Verify Exceptions\nThink of profiler - spots odd behavior.',
      visual: `
    👤 NORMAL BEHAVIOR
    │
    ▼
    🔍 MONITOR
    │
    ▼
    ⚠️ ANOMALY DETECTED`
    },
    'baseline-deviation': {
      title: 'Baseline Deviation',
      explanation: 'Identifying changes from established normal operations to spot potential security issues.',
      examples: 'Network traffic spiking above baseline indicating DDoS',
      mnemonic: '📏 BASELINE = Benchmark And Spot Every Line Item Not Expected\nThink of ruler - measures how far off.',
      visual: `
    📏 BASELINE
    │
    ▼
    📈 DATA
    │ Deviation!
    ▼
    ⚠️ ALERT`
    },
    'rule-writing': {
      title: 'Rule Writing',
      explanation: 'Creating detection rules in SIEM or IDS to identify specific threats or behaviors.',
      examples: 'SIEM rules for detecting phishing indicators, custom alerts',
      mnemonic: '📜 RULE = Refine Until Logic Encapsulates everything\nThink of recipe - specific instructions for outcome.',
      visual: `
    📜 IF condition THEN alert
    │
    ▼
    🚨 TRIGGERED`
    },
    'known-bad-indicators': {
      title: 'Known Bad Indicators',
      explanation: 'Pre-identified signs of malicious activity, like IOCs, used in detection rules.',
      examples: 'Malicious IP lists, hash signatures of malware',
      mnemonic: '🚫 KNOWN BAD = Knowledge Now Of Wicked Nasties Blocks Attacks Daily\nThink of wanted poster - recognize and act.',
      visual: `
    🚫 BAD LIST
    ├── IP: 1.2.3.4 ✓
    ├── Hash: abc123 ✓
    │
    ▼
    🔍 MATCH & BLOCK`
    },
    'unknown-suspicious-activity': {
      title: 'Unknown/Suspicious Activity',
      explanation: 'Unfamiliar or anomalous behaviors that may indicate novel threats requiring investigation.',
      examples: 'Unexpected data exfiltration, unusual process executions',
      mnemonic: '❓ UNKNOWN = Unseen Novel Knowledge Often Warrants Notable Scrutiny\nThink of stranger in neighborhood - watch closely.',
      visual: `
    ❓ ODD ACTIVITY
    │
    ▼
    🕵️ INVESTIGATE
    │
    ▼
    ⚠️ POTENTIAL THREAT`
    },
    'sentiment-analysis': {
      title: 'Sentiment Analysis',
      explanation: 'AI-based evaluation of text to determine emotional tone, useful for detecting insider threats or phishing.',
      examples: 'Analyzing employee emails for dissatisfaction, social media monitoring',
      mnemonic: '😊 SENTIMENT = Sense Emotions Now To Identify Moods Every New Text\nThink of mood ring - colors show feelings.',
      visual: `
    📧 TEXT
    │
    ▼
    😠 ANALYZE SENTIMENT
    │ Negative?
    ▼
    ⚠️ FLAG`
    },
    'communication-analysis': {
      title: 'Communication Analysis',
      explanation: 'Examining patterns in emails, chats, or calls to detect anomalies or threats like data leaks.',
      examples: 'DLP scanning outbound emails, network flow analysis for C2',
      mnemonic: '💬 COMM = Check Ongoing Messages Monitoring Unusual Niches In Communications Always\nThink of wiretap - listen for suspicious talk.',
      visual: `
    💬 MESSAGES
    │
    ▼
    🔍 ANALYZE
    ├── Patterns ✓
    ├── Anomalies ✓
    │
    ▼
    🕵️ FINDINGS`
    },
    'threat-feeds-analysis': {
      title: 'Threat Feeds',
      explanation: 'Real-time sources of threat intelligence like IOCs and vulnerability data.',
      examples: 'AlienVault OTX, MISP feeds',
      mnemonic: '📰 FEED = Fresh Enemy Exposure Data\nThink of news feed - updates on threats.',
      visual: `
    📰 THREAT FEED
    │
    ▼
    📥 INGEST
    │
    ▼
    🛡️ UPDATE DEFENSES`
    },
    'advisories': {
      title: 'Advisories',
      explanation: 'Official warnings about vulnerabilities or threats from vendors or authorities.',
      examples: 'Microsoft Security Advisories, CERT alerts',
      mnemonic: '⚠️ ADVISE = Alert Details Vulnerabilities In Systems Everywhere\nThink of weather advisory - warns of coming storm.',
      visual: `
    ⚠️ ADVISORY
    │ Vulnerability X
    ▼
    🛠️ PATCH`
    },
    'bulletins': {
      title: 'Bulletins',
      explanation: 'Periodic publications detailing security updates, patches, or threat summaries.',
      examples: 'US-CERT Technical Security Bulletins, vendor patch bulletins',
      mnemonic: '📑 BULLETIN = Brief Updates Listing Latest Emerging Threats In Networks\nThink of bulletin board - posted notices.',
      visual: `
    📑 BULLETIN
    ├── Patch info ✓
    ├── Threats ✓
    │
    ▼
    👀 REVIEW & ACT`
    },
    'flow-analysis': {
      title: 'Flow Analysis',
      explanation: 'Examining network flow data (e.g., NetFlow) for patterns without full packet content.',
      examples: 'Detecting large data transfers, anomalous ports',
      mnemonic: '🌊 FLOW = Follow Links Observing Waves\nThink of river flow - measure volume and direction.',
      visual: `
    🌊 NETWORK FLOW
    │
    ▼
    📊 ANALYZE
    │ Volume, Direction
    ▼
    ⚠️ ANOMALIES`
    },
    'packet-analysis': {
      title: 'Packet Analysis',
      explanation: 'Deep inspection of individual network packets for content and anomalies.',
      examples: 'Wireshark dissection of HTTP traffic',
      mnemonic: '📦 PACKET = Probe All Contents Knowing Every Transmission\nThink of opening packages - check what\'s inside.',
      visual: `
    📦 PACKET
    │
    ▼
    🔍 INSPECT
    ├── Header ✓
    ├── Payload ✓
    │
    ▼
    🕵️ FINDINGS`
    },
    'protocol-analysis': {
      title: 'Protocol Analysis',
      explanation: 'Examining adherence to communication protocols to detect manipulations or attacks.',
      examples: 'Analyzing TCP handshakes for SYN floods',
      mnemonic: '📡 PROTO = Probe Rules Of Transmission Observing Compliance\nThink of language grammar - check if sentences follow rules.',
      visual: `
    📡 PROTOCOL
    │
    ▼
    🔍 ANALYZE
    │
    ▼
    ⚠️ VIOLATIONS`
    },
    // DOMAIN 4.8 INCIDENT RESPONSE ACTIVITIES - DETAILED CONCEPTS FROM STUDY GUIDE
    'tabletop-exercise': {
      title: 'Tabletop Exercise',
      explanation: 'Discussion-based simulation where team members walk through incident response scenarios verbally.',
      examples: 'Simulating a ransomware attack in a meeting, discussing steps without actual systems',
      mnemonic: '🗣️ TABLETOP = Talk About Breach Levels Every Team On Paper\nThink of board game - play out scenarios on table.',
      visual: `
    👥 TEAM AROUND TABLE
    │
    ▼
    🗣️ DISCUSS SCENARIO
    ├── What if...? ✓
    ├── Response? ✓
    │
    ▼
    📚 LESSONS LEARNED`
    },
    'simulation': {
      title: 'Simulation',
      explanation: 'Hands-on practice of incident response using mock environments to test procedures and tools.',
      examples: 'Cyber range simulations, red team exercises',
      mnemonic: '🎮 SIM = Simulate Incidents Meaningfully\nThink of flight simulator - practice without real risk.',
      visual: `
    🖥️ MOCK ENVIRONMENT
    │
    ▼
    🎮 SIMULATE ATTACK
    │
    ▼
    🛡️ PRACTICE RESPONSE`
    },
    'reporting-forensics': {
      title: 'Reporting (Forensics)',
      explanation: 'Documenting findings, methods, and conclusions in a clear, admissible format for stakeholders or court.',
      examples: 'Forensic investigation reports, expert witness statements',
      mnemonic: '📄 REPORT = Record Every Pertinent Observation Reliably Today\nThink of news article - summarize facts.',
      visual: `
    🕵️ FINDINGS
    │
    ▼
    📄 REPORT
    ├── Methods ✓
    ├── Conclusions ✓
    │
    ▼
    ⚖️ PRESENT`
    },

    // DOMAIN 4.9: DATA SOURCES TO SUPPORT INVESTIGATION CONCEPTS
    'log-data-investigation': {
      title: 'Log Data',
      explanation: 'Recorded information generated by systems, applications, and network devices that document events, activities, or errors. These logs are critical for security investigations, as they provide a chronological record of system behavior, user actions, and potential security incidents, enabling analysts to reconstruct events, identify anomalies, and trace the source of attacks.',
      examples: 'A SIEM system aggregating logs from firewalls, servers, and endpoints to detect a coordinated attack; reviewing application logs to identify a user account compromise.',
      mnemonic: '📜 LOG DATA = Logged Observations Generate Detailed Audit Trails Always\nThink of a ship\'s logbook - every event, from routine operations to storms (incidents), is recorded for later analysis.',
      visual: `
  🌐 SYSTEMS/APPS/DEVICES
  │
  ▼
  📜 LOG DATA GENERATED
  │ Events | Errors | Actions
  │
  ▼
  🔍 SIEM/ANALYST
  │ Aggregate | Correlate | Investigate
  ✅ TIMELINE OF INCIDENT RECONSTRUCTED`
    },
    'firewall-logs-investigation': {
      title: 'Firewall Logs',
      explanation: 'Records of network traffic allowed or blocked by the firewall, useful for identifying unauthorized access attempts or unusual patterns during investigations.',
      examples: 'Blocked inbound connections from suspicious IPs, allowed outbound traffic to known malicious domains.',
      mnemonic: '🚧 FIRE LOG = Filter Inspections Record Every Wall action Logged\nThink of a digital bouncer\'s notebook - logs who was let in or turned away at the network gate.',
      visual: `
  🌐 TRAFFIC → 🚧 FIREWALL
                │
                ├── ALLOW: Log ✓
                ├── DENY: Log ✓
                │
                ▼
  📝 LOG FILE:
  - Time | Source | Destination | Action
  ✅ NETWORK ACTIVITY EVIDENCE`
    },
    'application-logs-investigation': {
      title: 'Application Logs',
      explanation: 'Detailed records generated by software applications, including errors, user actions, and system events, crucial for tracing application-level attacks or anomalies.',
      examples: 'Web app logs showing SQL injection attempts, authentication failures in a database application.',
      mnemonic: '📱 APP LOG = Application Performance Problems Logged\nThink of an app\'s personal diary - records daily operations, mistakes, and user interactions.',
      visual: `
  📱 APP EXECUTES
  │
  ▼
  EVENT (e.g., Error/Login)
  │
  ▼
  📝 LOG ENTRY:
  - Timestamp | Event Type | User | Details
  │
  ▼
  🔍 ANALYZE FOR PATTERNS
  ✅ APPLICATION BEHAVIOR INSIGHTS`
    },
    'endpoint-logs-investigation': {
      title: 'Endpoint Logs',
      explanation: 'Security and system event records from individual devices, helping to detect malware, unauthorized access, or suspicious processes on endpoints.',
      examples: 'Windows event logs showing malware execution, macOS logs of unusual file modifications.',
      mnemonic: '🖥️ END LOG = Endpoint Network Device Logs\nThink of a device\'s security camera - captures local events like process starts and user logins.',
      visual: `
  🖥️ ENDPOINT DEVICE
  │
  ▼
  LOCAL EVENT (e.g., Process Launch)
  │
  ▼
  📝 ENDPOINT LOG:
  - Time | Process | User | Action
  │
  ▼
  🕵️ CORRELATE WITH OTHER LOGS
  ✅ ENDPOINT COMPROMISE EVIDENCE`
    },
    'os-security-logs-investigation': {
      title: 'OS-Specific Security Logs',
      explanation: 'Operating system logs focused on security events like authentication, privilege changes, and system integrity checks.',
      examples: 'Linux audit logs of sudo commands, Windows Security logs of failed logins.',
      mnemonic: '🖥️ OS SEC LOG = Operating System Security Events Logged\nThink of the OS\'s watchdog - barks (logs) at security-relevant activities.',
      visual: `
  🖥️ OS KERNEL
  │
  ▼
  SECURITY EVENT (e.g., Privilege Escalation)
  │
  ▼
  📝 OS SECURITY LOG:
  - Time | Event ID | User | Description
  │
  ▼
  🔍 REVIEW FOR BREACHES
  ✅ SYSTEM-LEVEL SECURITY INSIGHTS`
    },
    'ips-ids-logs-investigation': {
      title: 'IPS/IDS Logs',
      explanation: 'Logs from Intrusion Prevention/Detection Systems detailing detected threats, triggered rules, and prevention actions.',
      examples: 'IPS log of blocked exploit attempt, IDS alert on anomalous traffic.',
      mnemonic: '🚨 IPS IDS LOG = Intrusion Prevention/Detection System Logs\nThink of a network guard\'s report - notes suspicious activities and actions taken.',
      visual: `
  🌐 NETWORK TRAFFIC
  │
  ▼
  🚨 IPS/IDS SENSOR
  │
  ▼
  THREAT DETECTED
  │
  ▼
  📝 LOG ENTRY:
  - Time | Rule ID | Source | Action (Alert/Block)
  ✅ INTRUSION EVIDENCE CAPTURED`
    },
    'network-logs-investigation': {
      title: 'Network Logs',
      explanation: 'Comprehensive records of network activities, including connections, data volumes, and errors, for analyzing traffic patterns and anomalies.',
      examples: 'Switch logs of port status changes, router logs of routing table updates.',
      mnemonic: '🌐 NET LOG = Network Events Tracked Logged\nThink of a network\'s flight recorder - captures all communication \'flights\' and incidents.',
      visual: `
  🌐 NETWORK DEVICES
  │
  ▼
  TRAFFIC EVENT (e.g., Connection Established)
  │
  ▼
  📝 NETWORK LOG:
  - Time | Device | Event Type | Details
  │
  ▼
  📊 CORRELATION ANALYSIS
  ✅ NETWORK-WIDE VIEW OF EVENTS`
    },
    'metadata-investigation': {
      title: 'Metadata',
      explanation: 'Descriptive information about data or events, providing context without revealing content, essential for pattern recognition in investigations.',
      examples: 'File metadata showing creation timestamps and authors, network packet metadata with source/destination IPs.',
      mnemonic: '📊 META = More Extra Traits Attached\nThink of luggage tags - provides key info about the \'package\' (data) without opening it.',
      visual: `
  📄 CORE DATA
  │
  ▼
  🏷️ METADATA TAGS
  │ - Creation Time
  │ - Author/User
  │ - Location/IP
  │ - Size/Type
  │
  ▼
  🕵️ INVESTIGATION
  ✅ CONTEXTUAL CLUES EXTRACTED`
    },
    'data-sources-investigation': {
      title: 'Data Sources',
      explanation: 'Data sources are the systems, tools, or processes that generate information used to support security investigations. They provide raw or processed data, such as logs, scan results, or visualizations, to help identify, analyze, and respond to security incidents.',
      examples: 'Using a SIEM to pull data from vulnerability scans and logs, leveraging a dashboard to monitor real-time threats across multiple sources.',
      mnemonic: '📈 DATA SRC = Data Archives Track Attacks, Supplying Reliable Context\nThink of a detective\'s evidence locker - stores various types of clues (data) to solve the case.',
      visual: `
  🔍 INCIDENT INVESTIGATION
  │
  ▼
  📈 DATA SOURCES
  │ Logs | Scans | Reports | Captures
  │
  ▼
  🕵️ ANALYST/SIEM
  │ Collect | Process | Analyze
  ✅ EVIDENCE FOR INCIDENT RESPONSE`
    },
    'vulnerability-scans-investigation': {
      title: 'Vulnerability Scans',
      explanation: 'Automated tools that probe systems for known vulnerabilities, generating reports on weaknesses that could be exploited.',
      examples: 'Regular Nessus scans identifying outdated software, ad-hoc scans after new threat disclosures.',
      mnemonic: '🔍 VULN SCAN = Vulnerabilities Uncovered Through Network Scanning\nThink of a security metal detector - scans for \'hidden weapons\' (vulnerabilities) in systems.',
      visual: `
  🖥️ TARGET SYSTEMS
  │
  ▼
  👁️ VULNERABILITY SCANNER
  │ Probe | Identify | Classify
  │
  ▼
  📋 REPORT:
  │ - Vulnerability
  │ - Severity
  │ - Affected System
  │ - Remediation Advice
  ✅ WEAKNESSES DOCUMENTED`
    },
    'automated-reports-investigation': {
      title: 'Automated Reports',
      explanation: 'Periodically generated summaries of security metrics, events, and trends, aiding in quick assessment and long-term analysis.',
      examples: 'Weekly SIEM compliance reports, monthly vulnerability summary emails.',
      mnemonic: '🤖 AUTO REP = Automated Updates To Reports On Events\nThink of an AI assistant - automatically compiles security status reports without manual intervention.',
      visual: `
  🤖 SECURITY TOOLS
  │ Gather data | Analyze | Format
  │
  ▼
  📄 AUTOMATED REPORT
  │ - Executive Summary
  │ - Key Metrics/Charts
  │ - Detailed Findings
  │ - Recommendations
  │
  ▼
  📧 DELIVERED
  ✅ ACTIONABLE INSIGHTS PROVIDED`
    },
    'dashboards-investigation': {
      title: 'Dashboards',
      explanation: 'Interactive visual displays aggregating data from multiple sources for real-time monitoring and quick decision-making in investigations.',
      examples: 'Kibana dashboard with log visualizations, Grafana security metrics overview.',
      mnemonic: '📊 DASH = Display All Security Highlights\nThink of a control center console - shows all critical security indicators in one view.',
      visual: `
  📊 SECURITY DASHBOARD
  │ Widgets:
  │ - Alert Count Gauge
  │ - Threat Trend Chart
  │ - Geo Map of Attacks
  │ - Top Vulnerabilities List
  │
  ▼
  🕵️ SECURITY ANALYST
  │ Filter | Drill down | Export
  ✅ COMPREHENSIVE OVERVIEW`
    },
    'packet-captures-investigation': {
      title: 'Packet Captures',
      explanation: 'Complete recordings of network traffic, allowing detailed reconstruction of communications and identification of malicious payloads.',
      examples: 'Tcpdump capture during a data exfiltration attempt, Wireshark analysis of encrypted traffic patterns.',
      mnemonic: '📦 PACK CAP = Packets All Captured And Preserved\nThink of a network DVR - records all \'conversations\' (packets) for later review and analysis.',
      visual: `
  🌐 NETWORK INTERFACE
  │
  ▼
  📦 PACKET STREAM
  │ Headers | Payload | Protocols
  │
  ▼
  🎥 CAPTURE TOOL (e.g., Wireshark)
  │ Filter | Record | Save
  │
  ▼
  🔍 DEEP ANALYSIS
  ✅ COMMUNICATION DETAILS REVEALED`
    },

    // Domain 5: Security Program Management and Oversight - Comprehensive Concepts from Study Guide
    'procedures': {
      title: 'Procedures',
      explanation: 'Step-by-step instructions for implementing security policies and standards, ensuring repeatable processes.',
      examples: 'Incident response procedures, patch management steps',
      mnemonic: '🛠️ PROCEDURE = Precise Routines Outlined Clearly Ensure Dependable Undertakings Regularly Executed\nThink of a recipe - follow steps for consistent results.',
      visual: `
    📋 TASK
    │
    ▼
    🛠️ PROCEDURE
    ├── Step 1 ✓
    ├── Step 2 ✓
    ├── Step 3 ✓
    │
    ▼
    ✅ REPEATABLE OUTCOME`
    },
    'change-management': {
      title: 'Change Management',
      explanation: 'Structured process to request, review, approve, and implement changes to systems or processes to minimize risks.',
      examples: 'ITIL-based change requests, approving software updates',
      mnemonic: '🔄 CHANGE = Carefully Handle Adjustments Navigating Governance Effectively\nThink of air traffic control - manages changes safely.',
      visual: `
    🔄 CHANGE REQUEST
    │
    ▼
    👥 REVIEW BOARD
    ├── Assess risk ✓
    ├── Approve/Deny ✓
    │
    ▼
    🛠️ IMPLEMENT SAFELY`
    },
    'onboarding-offboarding': {
      title: 'Onboarding/Offboarding',
      explanation: 'Processes to securely integrate new employees (onboarding) or remove access for departing ones (offboarding).',
      examples: 'Granting system access during onboarding, revoking accounts during offboarding',
      mnemonic: '🚪 ON/OFF = Open New Opportunities, Finalize Farewells\nThink of a revolving door - access granted or removed.',
      visual: `
    👤 EMPLOYEE
    │
    ▼
    🚪 ONBOARD/OFFBOARD
    ├── Grant access ✓
    ├── Revoke access ✓
    │
    ▼
    🛡️ SECURE TRANSITION`
    },
    'playbooks': {
      title: 'Playbooks',
      explanation: 'Predefined, detailed guides for responding to specific security incidents or scenarios.',
      examples: 'Ransomware response playbook, data breach playbook',
      mnemonic: '📖 PLAYBOOK = Planned Layouts And You\'re Better Organized Under Known scenarios\nThink of a sports playbook - strategies for specific plays.',
      visual: `
    🚨 INCIDENT
    │
    ▼
    📖 PLAYBOOK
    ├── Step-by-step response ✓
    ├── Roles assigned ✓
    │
    ▼
    ✅ COORDINATED RESPONSE`
    },
    'external-considerations': {
      title: 'External Considerations',
      explanation: 'Factors outside the organization that influence security governance decisions and requirements.',
      examples: 'Regulatory compliance, legal obligations, industry standards',
      mnemonic: '🌍 EXTERNAL = Extra Terms Enforced Regularly Now And Later\nThink of outside forces shaping internal decisions.',
      visual: `
    🌍 EXTERNAL FORCES
    ├── 📜 REGULATORY
    ├── ⚖️ LEGAL
    ├── 🏭 INDUSTRY
    └── 🌐 GLOBAL
         ↓
    🏢 INTERNAL POLICIES`
    },
    'regulatory': {
      title: 'Regulatory',
      explanation: 'Compliance requirements imposed by government or industry bodies to protect data and systems.',
      examples: 'GDPR for data privacy, HIPAA for healthcare',
      mnemonic: '⚖️ REG = Rules Enforce Governance\nThink of a judge - enforces legal standards.',
      visual: `
    ⚖️ LAW
    │
    ▼
    📜 REGULATION
    ├── GDPR ✓
    ├── HIPAA ✓
    │
    ▼
    ✅ COMPLIANCE`
    },
    'legal': {
      title: 'Legal',
      explanation: 'Laws and legal obligations affecting security practices, such as data breach notifications or evidence handling.',
      examples: 'Data breach notification laws, cybercrime statutes',
      mnemonic: '⚖️ LEGAL = Laws Ensure Governance And Liability\nThink of a courtroom - rules must be followed.',
      visual: `
    ⚖️ COURT
    │
    ▼
    📜 LEGAL RULES
    ├── Breach notification ✓
    ├── Evidence handling ✓
    │
    ▼
    ✅ LEGAL COMPLIANCE`
    },
    'industry': {
      title: 'Industry',
      explanation: 'Sector-specific standards or best practices influencing security governance.',
      examples: 'PCI DSS for payment card industry, NIST 800-53 for federal systems',
      mnemonic: '🏭 INDUSTRY = In-Depth Norms Define Unique Standards To Retain You\nThink of factory standards - industry-specific rules.',
      visual: `
    🏭 SECTOR
    │
    ▼
    📜 INDUSTRY STD
    ├── PCI DSS ✓
    ├── NIST 800-53 ✓
    │
    ▼
    ✅ SECTOR COMPLIANCE`
    },
    'local-regional': {
      title: 'Local/Regional',
      explanation: 'Security requirements specific to a geographic region or locality.',
      examples: 'California CCPA, EU-specific data residency rules',
      mnemonic: '🌍 LOCAL = Laws Of City And Locality\nThink of city hall - local rules apply.',
      visual: `
    🌍 REGION
    │
    ▼
    📜 LOCAL LAWS
    ├── CCPA ✓
    ├── EU rules ✓
    │
    ▼
    ✅ REGIONAL COMPLIANCE`
    },
    'national': {
      title: 'National',
      explanation: 'Country-specific laws or regulations impacting security governance.',
      examples: 'CISA regulations in the US, UK Data Protection Act',
      mnemonic: '🇺🇳 NATIONAL = Nation\'s Authoritative Terms Impact Operations Now And Long-term\nThink of national flag - country\'s laws rule.',
      visual: `
    🇺🇳 COUNTRY
    │
    ▼
    📜 NATIONAL LAWS
    ├── CISA ✓
    ├── DPA ✓
    │
    ▼
    ✅ NATIONAL COMPLIANCE`
    },
    'global': {
      title: 'Global',
      explanation: 'International standards or agreements affecting security across borders.',
      examples: 'ISO 27001, GDPR for global companies',
      mnemonic: '🌐 GLOBAL = Governance Laws Over Borders Affect All Locations\nThink of globe - rules span the world.',
      visual: `
    🌐 WORLD
    │
    ▼
    📜 GLOBAL STD
    ├── ISO 27001 ✓
    ├── GDPR ✓
    │
    ▼
    ✅ GLOBAL COMPLIANCE`
    },
    'monitoring-revision': {
      title: 'Monitoring and Revision',
      explanation: 'Continuous oversight and updating of security policies, standards, and procedures to address new threats or changes.',
      examples: 'Annual policy reviews, auditing compliance with standards',
      mnemonic: '👁️ MONITOR = Measure Ongoing Needs, Improve Through Oversight Regularly\nThink of a security camera - always watching and updating.',
      visual: `
    👁️ MONITOR
    │
    ▼
    📝 REVIEW
    ├── Policies ✓
    ├── Standards ✓
    │
    ▼
    🔄 REVISE`
    },
    'governance-structures': {
      title: 'Types of Governance Structures',
      explanation: 'Organizational frameworks that provide oversight and decision-making authority for security governance.',
      examples: 'Board of directors, security committees, government entities',
      mnemonic: '🏛️ STRUCTURE = Systems That Require Unified Control Through Unified Reporting Efforts\nThink of government branches - organized authority.',
      visual: `
    🏛️ GOVERNANCE
    ├── 🧑‍💼 BOARDS
    ├── 👥 COMMITTEES  
    ├── 🏛️ GOV ENTITIES
    └── 🔄 CENTRALIZED/DECENT`
    },
    'boards': {
      title: 'Boards',
      explanation: 'High-level groups overseeing strategic security decisions and policy approval.',
      examples: 'Board of directors, cybersecurity oversight board',
      mnemonic: '🧑‍💼 BOARD = Broad Oversight And Review Decisions\nThink of a corporate boardroom - big decisions made.',
      visual: `
    🧑‍💼 BOARD
    │
    ▼
    📜 STRATEGY
    ├── Approve policies ✓
    ├── Set goals ✓
    │
    ▼
    🛡️ GOVERNANCE`
    },
    'committees': {
      title: 'Committees',
      explanation: 'Specialized groups handling specific security governance tasks, like risk or compliance.',
      examples: 'Risk management committee, audit committee',
      mnemonic: '👥 COMM = Collaborative Oversight Making Measures Implemented Thoroughly Every time\nThink of a task force - focused teamwork.',
      visual: `
    👥 COMMITTEE
    │
    ▼
    📋 TASKS
    ├── Risk review ✓
    ├── Compliance ✓
    │
    ▼
    🛡️ SPECIALIZED GOVERNANCE`
    },
    'government-entities': {
      title: 'Government Entities',
      explanation: 'Public sector bodies setting or enforcing security regulations.',
      examples: 'NIST, CISA, ENISA in the EU',
      mnemonic: '🏛️ GOV = Governance Oversees Violations\nThink of capitol building - sets national rules.',
      visual: `
    🏛️ GOV ENTITY
    │
    ▼
    📜 REGULATIONS
    ├── NIST 800-53 ✓
    ├── CISA rules ✓
    │
    ▼
    ✅ ENFORCED`
    },
    'centralized-decentralized-governance': {
      title: 'Centralized/Decentralized',
      explanation: 'Governance models where security decisions are either centralized (single authority) or decentralized (distributed across units).',
      examples: 'Centralized IT security team, decentralized business unit security leads',
      mnemonic: '🔄 CENT/DECENT = Control Either Narrowly Tightly or Regionally Across Locations Decentralized Effectively Now Together\nThink of hub vs. branches - one control or many.',
      visual: `
    🔄 CHOOSE
    ┌─────────────┐
    │ Centralized │─── One authority
    └─────────────┘
    ┌─────────────┐
    │Decentralized│─── Many units
    └─────────────┘`
    },
    'roles-responsibilities': {
      title: 'Roles and Responsibilities for Systems and Data',
      explanation: 'Defined positions and duties for managing, protecting, and processing organizational data and systems.',
      examples: 'Data owners setting policies, controllers determining processing, custodians implementing protection',
      mnemonic: '👤 ROLE = Responsibility Over Leadership Elements\nThink of job descriptions - clear duties and accountability.',
      visual: `
    👤 ROLES & RESPONSIBILITIES
    ├── 👑 OWNERS (Strategic)
    ├── 🎮 CONTROLLERS (Tactical)
    ├── ⚙️ PROCESSORS (Operational)
    └── 🛡️ CUSTODIANS (Technical)`
    },
    'data-owners': {
      title: 'Owners',
      explanation: 'Individuals or entities responsible for the overall management and protection of systems or data.',
      examples: 'Department head owning a database, CISO for enterprise systems',
      mnemonic: '👑 OWNER = Oversees Welfare, Never Evades Responsibility\nThink of a king - owns and protects the kingdom.',
      visual: `
    👑 OWNER
    │
    ▼
    🛡️ SYSTEM/DATA
    ├── Protect ✓
    ├── Manage ✓
    │
    ▼
    ✅ ACCOUNTABLE`
    },
    'data-controllers': {
      title: 'Controllers',
      explanation: 'Entities determining the purposes and means of processing personal data.',
      examples: 'Organization deciding how customer data is used, GDPR data controller',
      mnemonic: '🎮 CONTROL = Commands Operations, Navigates Tasks Responsibly Over Lifecycles\nThink of a game controller - directs the action.',
      visual: `
    🎮 CONTROLLER
    │
    ▼
    📊 DATA
    ├── Define use ✓
    ├── Set rules ✓
    │
    ▼
    ✅ DATA GOVERNANCE`
    },
    'data-processors': {
      title: 'Processors',
      explanation: 'Entities processing data on behalf of controllers, following their instructions.',
      examples: 'Cloud provider processing data for a company, third-party analytics firm',
      mnemonic: '⚙️ PROCESS = Perform Routines On Controller\'s Explicit Specifications Strictly\nThink of a factory worker - follows orders to process.',
      visual: `
    ⚙️ PROCESSOR
    │
    ▼
    📊 DATA
    ├── Follow instructions ✓
    ├── Process securely ✓
    │
    ▼
    ✅ DATA HANDLED`
    },
    'custodians-stewards': {
      title: 'Custodians/Stewards',
      explanation: 'Individuals or teams responsible for the day-to-day management and protection of data or systems.',
      examples: 'IT team managing servers, database administrators',
      mnemonic: '🛡️ CUSTODIAN = Caretakers Undertake Secure Tasks Over Data In All Networks\nThink of a librarian - protects and manages resources.',
      visual: `
    🛡️ CUSTODIAN
    │
    ▼
    📂 DATA/SYSTEM
    ├── Maintain ✓
    ├── Protect ✓
    │
    ▼
    ✅ SAFE KEEPING`
    },
    // Domain 5.2 Risk Management Concepts
    'ad-hoc': {
      title: 'Ad Hoc',
      explanation: 'One-off risk assessments conducted as needed, often in response to specific events.',
      examples: 'Assessing risk after a new vulnerability is discovered, post-incident analysis',
      mnemonic: '🕒 AD HOC = Assess Danger Hastily On Circumstances\nThink of a pop-up tent - set up quickly when needed.',
      visual: `
    🚨 EVENT
    │
    ▼
    🕒 AD HOC ASSESSMENT
    ├── Identify risk ✓
    ├── Mitigate ✓
    │
    ▼
    ✅ RESOLVED`
    },
    'recurring': {
      title: 'Recurring',
      explanation: 'Regularly scheduled risk assessments to monitor ongoing risks.',
      examples: 'Quarterly risk reviews, annual compliance checks',
      mnemonic: '🔄 RECUR = Regularly Evaluate Conditions Under Repetition\nThink of a calendar - checks at set intervals.',
      visual: `
    📅 SCHEDULE
    │
    ▼
    🔄 RECURRING
    ├── Q1 review ✓
    ├── Q2 review ✓
    │
    ▼
    ✅ ONGOING MONITORING`
    },
    'one-time': {
      title: 'One-Time',
      explanation: 'Single, comprehensive risk assessment for a specific purpose or project.',
      examples: 'Risk assessment for a new system deployment, pre-acquisition due diligence',
      mnemonic: '🎯 ONE = Once, Not Every time\nThink of a one-shot photo - capture risk once.',
      visual: `
    🆕 PROJECT
    │
    ▼
    🎯 ONE-TIME
    ├── Assess risks ✓
    ├── Report ✓
    │
    ▼
    ✅ PROJECT READY`
    },
    'continuous': {
      title: 'Continuous',
      explanation: 'Ongoing, real-time risk monitoring to detect and respond to risks dynamically.',
      examples: 'SIEM real-time alerts, continuous compliance monitoring',
      mnemonic: '🌊 CONTINUOUS = Constant Observation Never Tires, Instantly Notifies Unusual Situations\nThink of a flowing river - always moving, always watched.',
      visual: `
    🌊 DATA STREAM
    │
    ▼
    👁️ CONTINUOUS MONITOR
    ├── Detect risk ✓
    ├── Alert ✓
    │
    ▼
    ✅ REAL-TIME RESPONSE`
    },
    'qualitative': {
      title: 'Qualitative',
      explanation: 'Subjective risk assessment based on descriptions, categories, or expert judgment.',
      examples: 'Rating risks as high/medium/low, expert risk workshops',
      mnemonic: '📊 QUAL = Quality Assessments, Understand Likelihood And Threat\nThink of a color scale - describes risk without numbers.',
      visual: `
    📊 RISK
    │
    ▼
    🌈 QUALITATIVE
    ├── High/Med/Low ✓
    ├── Expert input ✓
    │
    ▼
    ✅ PRIORITIZED`
    },
    'quantitative': {
      title: 'Quantitative',
      explanation: 'Numerical risk assessment using measurable data and calculations.',
      examples: 'Calculating financial loss from breaches, statistical risk models',
      mnemonic: '🔢 QUANT = Quantify Using Actual Numbers Thoroughly\nThink of a calculator - numbers drive decisions.',
      visual: `
    🔢 DATA
    │
    ▼
    📈 QUANTITATIVE
    ├── Calculate loss ✓
    ├── Probability ✓
    │
    ▼
    ✅ NUMERIC INSIGHTS`
    },
    // Critical Domain 5 concepts from study guide
    'sle': {
      title: 'Single Loss Expectancy (SLE)',
      explanation: 'Estimated financial loss from a single risk event.',
      examples: 'Cost of a data breach ($100,000), equipment replacement cost',
      mnemonic: '💸 SLE = Single Loss Estimated\nThink of a single bill - cost of one incident.',
      visual: `
    💥 EVENT
    │
    ▼
    💸 SLE
    │ $100,000
    ▼
    ✅ LOSS CALCULATED`
    },
    'ale': {
      title: 'Annualized Loss Expectancy (ALE)',
      explanation: 'Expected annual financial loss from a risk, calculated as SLE × ARO.',
      examples: '$100,000 SLE × 0.5 ARO = $50,000 ALE for annual breach risk',
      mnemonic: '📅 ALE = Annual Loss Expected\nThink of yearly budget - plan for annual losses.',
      visual: `
    💸 SLE: $100,000
    │
    ▼
    📅 ARO: 0.5
    │
    ▼
    ➕ ALE = $50,000`
    },
    'aro': {
      title: 'Annualized Rate of Occurrence (ARO)',
      explanation: 'Estimated frequency of a risk event occurring in a year.',
      examples: '0.5 for a breach expected once every 2 years, 2 for frequent malware incidents',
      mnemonic: '🔄 ARO = Annual Rate Observed\nThink of a clock - how often it ticks per year.',
      visual: `
    🕒 EVENT
    │
    ▼
    🔄 ARO
    │ 0.5/year
    ▼
    ✅ FREQUENCY KNOWN`
    },
    'probability': {
      title: 'Probability',
      explanation: 'Likelihood of a risk event occurring, often expressed as a percentage.',
      examples: '50% chance of phishing attack, 10% chance of server failure',
      mnemonic: '🎲 PROB = Predict Risk Occurrence By Analysis\nThink of dice - chance of rolling a number.',
      visual: `
    🎲 RISK
    │
    ▼
    📊 PROBABILITY
    │ 50%
    ▼
    ✅ LIKELIHOOD ASSESSED`
    },
    'likelihood': {
      title: 'Likelihood',
      explanation: 'General assessment of how probable a risk event is, often qualitative.',
      examples: 'High likelihood of insider threats, low likelihood of natural disasters',
      mnemonic: '📈 LIKE = Level Indicating Known Eventhood\nThink of a weather forecast - predicts chance of rain.',
      visual: `
    📈 RISK
    │
    ▼
    🌈 LIKELIHOOD
    │ High/Med/Low
    ▼
    ✅ ASSESSED`
    },
    'exposure-factor-risk': {
      title: 'Exposure Factor',
      explanation: 'Percentage of asset value lost in a risk event (used in SLE calculations).',
      examples: '40% exposure for partial data loss, 100% for total system failure',
      mnemonic: '📉 EXPOSE = Extent X Percent Of System Endangered\nThink of a flood - how much of the house is damaged.',
      visual: `
    🏠 ASSET
    │
    ▼
    📉 EXPOSURE
    │ 40% loss
    ▼
    ✅ IMPACT KNOWN`
    },
    'impact': {
      title: 'Impact',
      explanation: 'The effect of a risk event on operations, finances, or reputation.',
      examples: 'Downtime from ransomware, reputational damage from breaches',
      mnemonic: '💥 IMPACT = Incident Makes Problems, Assess Consequences Thoroughly\nThink of a meteor - measures damage caused.',
      visual: `
    💥 EVENT
    │
    ▼
    📉 IMPACT
    ├── Financial ✓
    ├── Operational ✓
    │
    ▼
    ✅ DAMAGE ASSESSED`
    },
    'penetration-testing-vendor': {
      title: 'Penetration Testing (Vendor)',
      explanation: 'Simulated attacks on third-party systems to identify vulnerabilities.',
      examples: 'Pen tests on vendor APIs, external network assessments.',
      mnemonic: '🕵️ PEN TEST = Probe Every Nook, Thoroughly Evaluate Security Threats\nThink of a hacker - tests defenses ethically.',
      visual: `
🌐 VENDOR SYSTEM
│
▼
🕵️ PEN TEST
├── Exploit attempt ✓
├── Report vulns ✓
│
▼
✅ SECURE VENDOR`
    },
    'right-to-audit-clause': {
      title: 'Right-to-Audit Clause',
      explanation: 'Contractual right to audit third-party operations for security compliance.',
      examples: 'Auditing cloud provider controls, reviewing vendor security practices.',
      mnemonic: '📜 RIGHT = Review In-depth, Gain Honest Transparency\nThink of a backstage pass - inspect behind the scenes.',
      visual: `
📄 CONTRACT
│
├── Audit clause ✓
├── Compliance check ✓
│
▼
🔍 AUDIT
✅ ASSURANCE`
    },
    'evidence-internal-audits': {
      title: 'Evidence of Internal Audits',
      explanation: 'Documentation from third parties showing their internal security audits.',
      examples: 'SOC 2 reports, internal audit summaries from vendors.',
      mnemonic: '📋 EVIDENCE = Evaluate Vendor\'s Internal Data Ensuring Notable Compliance Every time\nThink of a report card - proves they did their homework.',
      visual: `
📋 VENDOR AUDIT
│
▼
📜 EVIDENCE
├── SOC 2 ✓
├── Findings ✓
│
▼
✅ TRUST VERIFIED`
    },
    'independent-assessments': {
      title: 'Independent Assessments',
      explanation: 'Third-party evaluations of a vendor\'s security by an external entity.',
      examples: 'ISO 27001 certification, third-party pen test reports.',
      mnemonic: '🕵️ INDEPENDENT = Impartial Neutral Depth Ensures Proper Evaluation Now Daily Every New Test\nThink of a referee - unbiased judgment.',
      visual: `
🕵️ ASSESSOR
│
▼
📊 INDEPENDENT
├── Evaluate ✓
├── Certify ✓
│
▼
✅ VENDOR TRUSTED`
    },
    'supply-chain-analysis': {
      title: 'Supply Chain Analysis',
      explanation: 'Evaluating risks in a vendor\'s supply chain, including their subcontractors.',
      examples: 'Assessing cloud provider\'s third-party dependencies, reviewing supplier security.',
      mnemonic: '🔗 SUPPLY = Scrutinize Underlying Partners, Probe Links Yearly\nThink of a chain - every link must be strong.',
      visual: `
🔗 SUPPLY CHAIN
│
▼
🔍 ANALYZE
├── Vendor ✓
├── Subcontractors ✓
│
▼
✅ SECURE CHAIN`
    },
    'due-diligence-third-party': {
      title: 'Due Diligence',
      explanation: 'Thorough investigation of a third party\'s security practices before engagement.',
      examples: 'Reviewing vendor security policies, checking compliance certifications.',
      mnemonic: '🔍 DUE = Detailed Understanding Ensures safety\nThink of a background check - verify before trusting.',
      visual: `
🤝 VENDOR
│
▼
🔍 DUE DILIGENCE
├── Policies ✓
├── Certifications ✓
│
▼
✅ SAFE PARTNER`
    },
    'conflict-of-interest': {
      title: 'Conflict of Interest',
      explanation: 'Identifying and mitigating situations where a third party\'s interests may compromise security.',
      examples: 'Vendor with ties to competitors, conflicting business priorities.',
      mnemonic: '⚖️ CONFLICT = Check Opposing Needs, Find Level Integrity Compromises Taken\nThink of a scale - balance competing interests.',
      visual: `
⚖️ INTERESTS
│
▼
🔍 CHECK CONFLICT
├── Vendor ties ✓
├── Priorities ✓
│
▼
✅ FAIR RELATIONSHIP`
    },
    'sla': {
      title: 'Service-Level Agreement (SLA)',
      explanation: 'Contract defining expected service performance, including security metrics.',
      examples: 'Uptime guarantees, incident response times.',
      mnemonic: '📜 SLA = Service Level Assured\nThink of a warranty - promises performance.',
      visual: `
📜 SLA
│
▼
✅ PROMISES
├── 99.9% uptime ✓
├── Response time ✓
│
▼
🛡️ SERVICE GUARANTEED`
    },
    'moa': {
      title: 'Memorandum of Agreement (MOA)',
      explanation: 'Formal agreement outlining mutual obligations between parties, often less binding than a contract.',
      examples: 'MOA for shared incident response, joint security initiatives.',
      mnemonic: '🤝 MOA = Mutual Obligations Agreed\nThink of a handshake - mutual understanding.',
      visual: `
🤝 PARTIES
│
▼
📜 MOA
├── Obligations ✓
├── Cooperation ✓
│
▼
✅ MUTUAL COMMITMENT`
    },
    'mou': {
      title: 'Memorandum of Understanding (MOU)',
      explanation: 'Non-binding agreement expressing intent to collaborate, often for security partnerships.',
      examples: 'MOU for threat intelligence sharing, collaboration with ISACs.',
      mnemonic: '📝 MOU = Memorandum Outlines Understanding\nThink of a letter of intent - sets expectations.',
      visual: `
📝 MOU
│
▼
🤝 INTENT
├── Collaborate ✓
├── Share info ✓
│
▼
✅ PARTNERSHIP PLANNED`
    },
    'msa': {
      title: 'Master Service Agreement (MSA)',
      explanation: 'Overarching contract governing multiple services or projects with a vendor.',
      examples: 'MSA for IT services, covering security and maintenance.',
      mnemonic: '📜 MSA = Master Service Agreement\nThink of a master key - unlocks multiple services.',
      visual: `
📜 MSA
│
▼
🔑 SERVICES
├── Security ✓
├── Maintenance ✓
│
▼
✅ ALL COVERED`
    },
    'sow': {
      title: 'Work Order (WO)/Statement of Work (SOW)',
      explanation: 'Detailed document specifying tasks, deliverables, and timelines for a specific project.',
      examples: 'SOW for vendor pen testing, WO for system upgrades.',
      mnemonic: '📋 SOW = Specific Operations Work\nThink of a to-do list - details the job.',
      visual: `
📋 SOW
│
▼
✅ TASKS
├── Deliverables ✓
├── Timelines ✓
│
▼
🛠️ WORK DONE`
    },
    'nda': {
      title: 'Non-Disclosure Agreement (NDA)',
      explanation: 'Contract ensuring confidentiality of sensitive information shared with third parties.',
      examples: 'NDA for vendor access to proprietary data, protecting trade secrets.',
      mnemonic: '🔒 NDA = No Disclosure Allowed\nThink of a vault - keeps secrets safe.',
      visual: `
🔒 NDA
│
▼
📜 CONFIDENTIALITY
├── Protect data ✓
├── Enforce silence ✓
│
▼
✅ SECRETS SAFE`
    },
    'bpa': {
      title: 'Business Partners Agreement (BPA)',
      explanation: 'Contract outlining terms for ongoing business relationships, including security responsibilities.',
      examples: 'BPA with a cloud provider, partnership for joint ventures.',
      mnemonic: '🤝 BPA = Business Partners Align\nThink of a team contract - sets partnership rules.',
      visual: `
🤝 PARTNERS
│
▼
📜 BPA
├── Roles ✓
├── Security ✓
│
▼
✅ STRONG PARTNERSHIP`
    },
    'internal-compliance': {
      title: 'Internal Compliance',
      explanation: 'Adherence to an organization\'s own security policies and standards.',
      examples: 'Following internal password policies, employee training compliance.',
      mnemonic: '🏢 INTERNAL = In-house Norms, Track Every Regulation Now And Later\nThink of company rules - everyone follows them.',
      visual: `
🏢 ORGANIZATION
│
▼
📜 INTERNAL POLICY
├── Passwords ✓
├── Training ✓
│
▼
✅ COMPLIANT`
    },
    'external-compliance': {
      title: 'External Compliance',
      explanation: 'Compliance with external regulations, standards, or laws.',
      examples: 'GDPR, PCI DSS, HIPAA compliance.',
      mnemonic: '🌍 EXTERNAL = Examine Xtra Terms Enforced Regularly Now And Later\nThink of global standards - outside rules apply.',
      visual: `
🌍 REGULATIONS
│
▼
📜 EXTERNAL
├── GDPR ✓
├── PCI DSS ✓
│
▼
✅ COMPLIANT`
    },
    'fines': {
      title: 'Fines',
      explanation: 'Financial penalties for failing to comply with regulations.',
      examples: 'GDPR fines up to €20M, HIPAA penalties for data breaches.',
      mnemonic: '💸 FINE = Financial Impact Non-compliance Evokes\nThink of a parking ticket - pay for breaking rules.',
      visual: `
⚖️ VIOLATION
│
▼
💸 FINE
│ €20M
▼
✅ LESSON LEARNED`
    },
    'sanctions': {
      title: 'Sanctions',
      explanation: 'Restrictions or penalties imposed by regulators, such as bans or limitations.',
      examples: 'Trade sanctions for non-compliance, restricted operations.',
      mnemonic: '🚫 SANCTION = Stop Actions Non-compliant Companies Take In Operations Now\nThink of a ban - stops activity.',
      visual: `
⚖️ NON-COMPLIANCE
│
▼
🚫 SANCTION
│ Ban operations
▼
✅ RESTRICTED`
    },
    'reputational-damage': {
      title: 'Reputational Damage',
      explanation: 'Loss of trust or credibility due to compliance failures.',
      examples: 'Publicized data breaches, customer distrust after fines.',
      mnemonic: '😔 REPUTE = Reduced Esteem Puts Undue Trouble Everywhere\nThink of a bad review - hurts public image.',
      visual: `
📰 BREACH
│
▼
😔 REPUTATION
│ Trust lost
▼
✅ RECOVERY NEEDED`
    },
    'loss-of-license': {
      title: 'Loss of License',
      explanation: 'Revocation of operational licenses due to non-compliance.',
      examples: 'Losing medical practice license, revoked financial certifications.',
      mnemonic: '📜 LOSS = License Obliterated Stops Service\nThink of a revoked driver\'s license - can\'t operate.',
      visual: `
⚖️ VIOLATION
│
▼
📜 LICENSE
│ Revoked
▼
✅ OPERATIONS HALTED`
    },
    'contractual-impacts': {
      title: 'Contractual Impacts',
      explanation: 'Breaches of contract terms due to non-compliance, leading to penalties or termination.',
      examples: 'Losing vendor contracts, breaching SLAs.',
      mnemonic: '📄 CONTRACT = Compliance Or Negative Terms Rupture Agreements Completely Terminated\nThink of a broken contract - terms not met.',
      visual: `
📄 CONTRACT
│
▼
⚖️ VIOLATION
│ Breached
▼
✅ TERMINATED`
    },
    'due-diligence-care': {
      title: 'Due Diligence/Care',
      explanation: 'Proactive efforts to ensure compliance through research and adherence to standards.',
      examples: 'Auditing systems for GDPR, training employees on policies.',
      mnemonic: '🔍 DUE = Diligent Undertaking Ensures compliance\nThink of a checklist - verify everything\'s done.',
      visual: `
📜 REGULATION
│
▼
🔍 DUE DILIGENCE
├── Audit ✓
├── Train ✓
│
▼
✅ COMPLIANT`
    },
    'attestation': {
      title: 'Attestation and Acknowledgement',
      explanation: 'Formal confirmation that policies or standards have been understood and followed.',
      examples: 'Employee signing policy acknowledgement, vendor attestation of compliance.',
      mnemonic: '✅ ATTEST = Acknowledge Terms Thoroughly, Ensure Signed Trust\nThink of a signature - confirms agreement.',
      visual: `
📜 POLICY
│
▼
✅ ATTEST
│ Sign here
▼
✅ UNDERSTOOD`
    },
    'automation-compliance': {
      title: 'Automation',
      explanation: 'Using tools to streamline compliance tasks, such as monitoring or reporting.',
      examples: 'Automated compliance checks, SIEM for real-time monitoring.',
      mnemonic: '🤖 AUTO = Automate Undertakings To Optimize compliance\nThink of a robot - handles repetitive tasks.',
      visual: `
📜 COMPLIANCE
│
▼
🤖 AUTOMATE
├── Monitor ✓
├── Report ✓
│
▼
✅ EFFICIENT`
    },
    'data-subject': {
      title: 'Data Subject',
      explanation: 'Individual whose personal data is collected, processed, or stored.',
      examples: 'Customers under GDPR, patients under HIPAA.',
      mnemonic: '👤 SUBJECT = Someone\'s Unique Bits Just Ensure Controlled Treatment\nThink of a person - their data needs protection.',
      visual: `
👤 PERSON
│
▼
📊 DATA
├── Protect ✓
├── Rights ✓
│
▼
✅ SAFE`
    },
    'controller-vs-processor': {
      title: 'Controller vs. Processor',
      explanation: 'Controller decides how and why data is processed; processor handles data per controller\'s instructions.',
      examples: 'Company (controller) hires cloud provider (processor).',
      mnemonic: '🎮 CONT/PROC = Controller Orders, Processor Runs Operations Conscientiously\nThink of director and crew - one plans, other executes.',
      visual: `
🎮 CONTROLLER
│ Sets rules
▼
⚙️ PROCESSOR
│ Follows rules
▼
✅ DATA HANDLED`
    },
    'ownership-data': {
      title: 'Ownership (Data)',
      explanation: 'Entity responsible for managing and protecting data assets.',
      examples: 'Business unit owning customer database, IT owning servers.',
      mnemonic: '👑 OWN = Oversee Welfare Now\nThink of a property deed - who owns the asset.',
      visual: `
👑 OWNER
│
▼
📊 DATA
├── Protect ✓
├── Manage ✓
│
▼
✅ RESPONSIBLE`
    },
    'data-inventory': {
      title: 'Data Inventory and Retention',
      explanation: 'Cataloging data assets and defining how long they\'re kept based on policies or laws.',
      examples: 'GDPR data retention schedules, archiving customer records.',
      mnemonic: '📂 INVENTORY = Identify, Note, Verify Every Needed Data Or Retire Yearly\nThink of a library catalog - tracks what\'s stored.',
      visual: `
📂 DATA
│
▼
📋 INVENTORY
├── Catalog ✓
├── Retention rules ✓
│
▼
✅ ORGANIZED`
    },
    'right-to-be-forgotten': {
      title: 'Right to be Forgotten',
      explanation: 'Legal right for individuals to have their personal data erased under certain conditions.',
      examples: 'GDPR data deletion requests, removing customer records.',
      mnemonic: '🗑️ FORGET = Fully Obliterate Records, Granting Erasure Totally\nThink of a shredder - deletes data permanently.',
      visual: `
👤 REQUEST
│
▼
🗑️ ERASE
│ Delete data
▼
✅ GONE`
    },
    'campaigns': {
      title: 'Campaigns',
      explanation: 'Organized efforts to educate employees through simulated phishing attacks.',
      examples: 'Sending fake phishing emails, tracking click rates.',
      mnemonic: '📧 CAMPAIGN = Create Awareness, Mimic Phish Attacks In General Now\nThink of a training drill - practice spotting fakes.',
      visual: `
📧 FAKE EMAIL
│
▼
👥 CAMPAIGN
├── Send test ✓
├── Track clicks ✓
│
▼
✅ AWARENESS RAISED`
    },
    'recognizing-phishing-attempt': {
      title: 'Recognizing a Phishing Attempt',
      explanation: 'Training employees to identify suspicious email characteristics like misspellings or urgent requests.',
      examples: 'Spotting fake sender addresses, recognizing phishing URLs.',
      mnemonic: '👁️ RECOGNIZE = Read Emails Carefully, Observe Gimmicks Now In Zoomed Examination\nThink of a detective - spots phishing clues.',
      visual: `
📧 EMAIL
│
▼
👁️ CHECK
├── Sender ✓
├── Links ✓
│
▼
✅ PHISH SPOTTED`
    },
    'responding-to-suspicious-messages': {
      title: 'Responding to Reported Suspicious Messages',
      explanation: 'Procedures for employees to report and handle potential phishing emails.',
      examples: 'Forwarding suspicious emails to IT, not clicking links.',
      mnemonic: '🚨 RESPOND = Report Emails Swiftly, Prevent Ongoing Nasty Deceptions\nThink of a fire alarm - report danger immediately.',
      visual: `
📧 SUSPICIOUS
│
▼
🚨 REPORT
├── Forward to IT ✓
├── Avoid clicks ✓
│
▼
✅ THREAT MITIGATED`
    },
    'risky': {
      title: 'Risky Behavior',
      explanation: 'Identifying behaviors that could lead to security risks, like sharing credentials.',
      examples: 'Writing passwords on notes, using unapproved apps.',
      mnemonic: '⚠️ RISKY = Recognize Insecure, Suspicious Kinds Yearly\nThink of a red flag - signals danger.',
      visual: `
👤 BEHAVIOR
│
▼
⚠️ RISKY
├── Sharing passwords ✓
├── Unapproved apps ✓
│
▼
✅ FLAG & TRAIN`
    },
    'unexpected': {
      title: 'Unexpected Behavior',
      explanation: 'Noticing unusual actions that deviate from normal patterns.',
      examples: 'Employee accessing systems at odd hours, unusual file downloads.',
      mnemonic: '❓ UNEXPECTED = Unseen Novel Patterns Expectedly Cause Threat Evaluation Daily\nThink of a surprise - out of the ordinary.',
      visual: `
👤 ACTION
│
▼
❓ UNEXPECTED
├── Odd hours ✓
├── Strange downloads ✓
│
▼
✅ INVESTIGATE`
    },
    'unintentional': {
      title: 'Unintentional Behavior',
      explanation: 'Recognizing accidental actions that could compromise security.',
      examples: 'Clicking phishing links by mistake, misconfiguring systems.',
      mnemonic: '😬 UNINTENT = Unplanned Negligent Incidents Now Threaten Entire Network Thoroughly\nThink of a slip-up - unintended but risky.',
      visual: `
👤 MISTAKE
│
▼
😬 UNINTENTIONAL
├── Clicked link ✓
├── Misconfig ✓
│
▼
✅ CORRECT & TRAIN`
    },
    'policy-handbooks': {
      title: 'Policy/Handbooks',
      explanation: 'Documents outlining security rules and expectations for employees.',
      examples: 'Employee security handbook, acceptable use policy.',
      mnemonic: '📜 POLICY = Provide Outlined Learning In Case You\'re unsure\nThink of a guidebook - rules for all.',
      visual: `
📜 HANDBOOK
│
▼
👥 EMPLOYEES
├── Read rules ✓
├── Follow ✓
│
▼
✅ AWARE`
    },
    'situational-awareness': {
      title: 'Situational Awareness',
      explanation: 'Training to stay vigilant about security risks in daily work.',
      examples: 'Recognizing phishing, reporting suspicious activity.',
      mnemonic: '👁️ SITU = Stay Informed, Track Unusual activity\nThink of a lookout - always alert.',
      visual: `
👁️ OBSERVE
│
▼
🚨 SITUATIONAL
├── Spot phish ✓
├── Report odd ✓
│
▼
✅ VIGILANT`
    },
    'insider-threat-awareness': {
      title: 'Insider Threat (Awareness)',
      explanation: 'Educating employees about risks from within the organization.',
      examples: 'Disgruntled employees stealing data, accidental leaks.',
      mnemonic: '🕵️ INSIDER = Internal Nuisances Sabotage, Identify Danger Early Regularly\nThink of a spy - threat from within.',
      visual: `
👤 EMPLOYEE
│
▼
🕵️ INSIDER THREAT
├── Monitor ✓
├── Train ✓
│
▼
✅ PREVENTED`
    },
    'password-management': {
      title: 'Password Management',
      explanation: 'Training on creating, storing, and protecting strong passwords.',
      examples: 'Using password managers, avoiding password reuse.',
      mnemonic: '🔒 PASS = Protect Access, Securely Store\nThink of a lock - strong passwords keep it secure.',
      visual: `
🔒 PASSWORD
│
▼
✅ MANAGE
├── Strong ✓
├── Unique ✓
│
▼
✅ SECURE`
    },
    'removable-media': {
      title: 'Removable Media and Cables',
      explanation: 'Educating about risks of using untrusted USBs or cables.',
      examples: 'Avoiding unknown USB drives, using company-approved cables.',
      mnemonic: '💾 REMOVABLE = Restrict External Media, Only Valid And Blessed Links Employed\nThink of a trap - unknown devices can harm.',
      visual: `
💾 USB
│
▼
🚫 CHECK
├── Trusted? ✓
├── Approved? ✓
│
▼
✅ SAFE USE`
    },
    'social-engineering': {
      title: 'Social Engineering',
      explanation: 'Training to recognize manipulation tactics like phishing or pretexting.',
      examples: 'Identifying fake calls, spotting impersonation emails.',
      mnemonic: '🗣️ SOCIAL = Spot Odd Conversations, Identify Attempts at Lies\nThink of a con artist - tricks to watch for.',
      visual: `
🗣️ ATTACK
│
▼
👁️ RECOGNIZE
├── Fake call ✓
├── Phishing ✓
│
▼
✅ AVOIDED`
    },
    'operational-security': {
      title: 'Operational Security',
      explanation: 'Practices to protect sensitive information during daily operations.',
      examples: 'Secure disposal of documents, locking screens.',
      mnemonic: '🛡️ OPSEC = Operate Protectively, Secure Every Critical detail\nThink of a fortress - guard all secrets.',
      visual: `
🛡️ OPERATIONS
│
▼
✅ OPSEC
├── Shred docs ✓
├── Lock screen ✓
│
▼
✅ SECURE`
    },
    'hybrid-remote-work': {
      title: 'Hybrid/Remote Work Environments',
      explanation: 'Training for secure practices in remote or hybrid work settings.',
      examples: 'Using VPNs, securing home networks.',
      mnemonic: '🏠 HYBRID = Home Yet Be Rigorous In Defending systems\nThink of a home office - secure like the workplace.',
      visual: `
🏠 REMOTE
│
▼
✅ SECURE
├── VPN ✓
├── Home network ✓
│
▼
✅ SAFE WORK`
    },
    'initial': {
      title: 'Initial Training',
      explanation: 'First-time training or reporting to establish security awareness.',
      examples: 'New employee security training, initial phishing reports.',
      mnemonic: '🚀 INITIAL = Introduce New Individuals To Information Awareness Learning\nThink of a launch - first step in awareness.',
      visual: `
👤 NEW HIRE
│
▼
🚀 INITIAL
├── Train ✓
├── Report ✓
│
▼
✅ AWARE`
    },
    'recurring-training': {
      title: 'Recurring Training',
      explanation: 'Ongoing training and reporting to maintain awareness.',
      examples: 'Annual security refreshers, monthly phishing reports.',
      mnemonic: '🔄 RECUR = Regularly Enforce Continuous Understanding Routinely\nThink of a cycle - keep awareness going.',
      visual: `
📅 SCHEDULE
│
▼
🔄 RECURRING
├── Train ✓
├── Report ✓
│
▼
✅ SUSTAINED`
    },
    'development': {
      title: 'Development',
      explanation: 'Creating and updating security awareness training materials.',
      examples: 'Designing phishing simulations, updating handbooks.',
      mnemonic: '🛠️ DEVELOP = Design Educational Ventures, Ensure Learning Operates Perfectly\nThink of a blueprint - build training programs.',
      visual: `
🛠️ PROGRAM
│
▼
📚 DEVELOP
├── Create training ✓
├── Update content ✓
│
▼
✅ READY`
    },
    'execution': {
      title: 'Execution',
      explanation: 'Delivering and implementing security awareness training and practices.',
      examples: 'Conducting phishing campaigns, hosting workshops.',
      mnemonic: '🚀 EXECUTE = Engage Xtra Efforts, Carry Out Training Effectively\nThink of a mission - carry out the plan.',
      visual: `
📚 TRAINING
│
▼
🚀 EXECUTE
├── Deliver ✓
├── Engage ✓
│
▼
✅ AWARENESS BUILT`
    },
    'attestation-audit': {
      title: 'Attestation',
      explanation: 'Formal declaration of compliance.',
      examples: 'Signed statements.',
      mnemonic: '✅ ATTEST = Affirm Truth Thoroughly Every Signed Time\nThink of swear - attest to facts.',
      visual: `
✅ DECLARE
│
▼
📜 ATTESTATION
├── Sign ✓
├── Confirm ✓
│
▼
✅ VERIFIED`
    },
    'internal-audits': {
      title: 'Internal',
      explanation: 'Audits by organization itself.',
      examples: 'Self-checks.',
      mnemonic: '🏢 INT = Internal Norms Tested\nThink of self-exam - internal review.',
      visual: `
🏢 ORG
│
▼
🔍 INTERNAL
├── Self-audit ✓
├── Check ✓
│
▼
✅ ASSESSED`
    },
    'compliance-audit': {
      title: 'Compliance',
      explanation: 'Check adherence to rules.',
      examples: 'Policy compliance audit.',
      mnemonic: '📜 COMP = Check Obedience Meticulously Please\nThink of checklist - comply or not.',
      visual: `
📜 RULES
│
▼
✅ COMPLIANCE
├── Audit ✓
├── Verify ✓
│
▼
✅ CHECKED`
    },
    'audit-committee': {
      title: 'Audit Committee',
      explanation: 'Group overseeing audits.',
      examples: 'Board committee.',
      mnemonic: '👥 AUD COMM = Audit Under Direction Committee Oversees Meticulously Monthly\nThink of panel - reviews audits.',
      visual: `
👥 COMMITTEE
│
▼
🔍 AUDIT
├── Oversee ✓
├── Report ✓
│
▼
✅ GOVERNED`
    },
    'self-assessments': {
      title: 'Self-Assessments',
      explanation: 'Organization evaluates itself.',
      examples: 'Internal questionnaires.',
      mnemonic: '🪞 SELF = Self Examine Levels Frequently\nThink of mirror - self reflection.',
      visual: `
🪞 SELF
│
▼
📊 ASSESS
├── Evaluate ✓
├── Improve ✓
│
▼
✅ REFLECTED`
    },
    'external-audits': {
      title: 'External',
      explanation: 'Audits by outside parties.',
      examples: 'Third-party reviews.',
      mnemonic: '🌍 EXT = External X Tests\nThink of outsider - external view.',
      visual: `
🌍 OUTSIDE
│
▼
🔍 EXTERNAL
├── Third-party ✓
├── Objective ✓
│
▼
✅ VALIDATED`
    },
    'regulatory-audit': {
      title: 'Regulatory (Audit)',
      explanation: 'Government-mandated audits.',
      examples: 'Compliance with laws.',
      mnemonic: '⚖️ REG = Regulated Every Governance\nThink of inspector - regulatory check.',
      visual: `
⚖️ GOV
│
▼
🔍 REGULATORY
├── Inspect ✓
├── Comply ✓
│
▼
✅ PASSED`
    },
    'examinations': {
      title: 'Examinations',
      explanation: 'Formal tests or reviews.',
      examples: 'Certification exams.',
      mnemonic: '📝 EXAM = Examine X All Matters\nThink of test - examine closely.',
      visual: `
📝 TEST
│
▼
🔍 EXAMINATION
├── Review ✓
├── Score ✓
│
▼
✅ GRADED`
    },
    'assessment': {
      title: 'Assessment',
      explanation: 'Evaluation of status.',
      examples: 'Risk assessments.',
      mnemonic: '📊 ASSESS = Analyze Status See Every Single Scenario\nThink of appraisal - assess value.',
      visual: `
📊 EVAL
│
▼
🔍 ASSESSMENT
├── Measure ✓
├── Report ✓
│
▼
✅ DONE`
    },
    'independent-third-party-audit': {
      title: 'Independent Third-Party Audit',
      explanation: 'Unbiased external audit.',
      examples: 'SOC audits.',
      mnemonic: '🕵️ IND THIRD = Independent Neutral Depth Third Hand Impartial Review Daily\nThink of referee - fair judge.',
      visual: `
🕵️ THIRD
│
▼
🔍 AUDIT
├── Objective ✓
├── Report ✓
│
▼
✅ TRUSTED`
    },
    'penetration-testing-audit': {
      title: 'Penetration Testing',
      explanation: 'Simulated cyber attacks to find weaknesses.',
      examples: 'Ethical hacking.',
      mnemonic: '🕵️ PEN TEST = Probe Every Nook Thoroughly Every Security Test\nThink of intrusion - test defenses.',
      visual: `
🕵️ HACKER
│
▼
⚠️ PEN TEST
├── Simulate ✓
├── Find ✓
│
▼
✅ VULNS`
    },
    'physical-pen-test': {
      title: 'Physical',
      explanation: 'Testing physical security.',
      examples: 'Lock picking, tailgating.',
      mnemonic: '🏢 PHYS = Physical Hurdles You See\nThink of break-in - physical test.',
      visual: `
🏢 BUILDING
│
▼
🕵️ PHYSICAL
├── Access ✓
├── Test ✓
│
▼
✅ BREACHED?`
    },
    'offensive': {
      title: 'Offensive',
      explanation: 'Aggressive attack simulation.',
      examples: 'Red team exercises.',
      mnemonic: '⚔️ OFF = Offense Finds Flaws\nThink of attack - offensive mode.',
      visual: `
⚔️ ATTACK
│
▼
🕵️ OFFENSIVE
├── Simulate ✓
├── Exploit ✓
│
▼
✅ TESTED`
    },
    'defensive': {
      title: 'Defensive',
      explanation: 'Defending against simulated attacks.',
      examples: 'Blue team defense.',
      mnemonic: '🛡️ DEF = Defend Every Flank\nThink of shield - defensive stance.',
      visual: `
🛡️ DEFEND
│
▼
🕵️ DEFENSIVE
├── Block ✓
├── Respond ✓
│
▼
✅ SECURE`
    },
    'integrated': {
      title: 'Integrated',
      explanation: 'Combined offensive/defensive testing.',
      examples: 'Purple team collaboration.',
      mnemonic: '🔄 INT = Integrate Now Teams\nThink of mix - both sides.',
      visual: `
🔄 COMBINE
│
▼
🕵️ INTEGRATED
├── Offense ✓
├── Defense ✓
│
▼
✅ FULL TEST`
    },
    'known-environment': {
      title: 'Known Environment',
      explanation: 'Testing with full knowledge.',
      examples: 'White box testing.',
      mnemonic: '📖 KNOWN = Knowledge Now Of Whole Network\nThink of open book - all info known.',
      visual: `
📖 INFO
│
▼
🕵️ KNOWN
├── Full access ✓
├── Test ✓
│
▼
✅ DETAILED`
    },
    'partially-known-environment': {
      title: 'Partially Known Environment',
      explanation: 'Testing with some knowledge.',
      examples: 'Gray box testing.',
      mnemonic: '🌫️ PART = Partial Awareness Reveals Threats\nThink of foggy - some visibility.',
      visual: `
🌫️ SOME
│
▼
🕵️ PARTIAL
├── Limited info ✓
├── Test ✓
│
▼
✅ BALANCED`
    },
    'unknown-environment': {
      title: 'Unknown Environment',
      explanation: 'Testing without prior knowledge.',
      examples: 'Black box testing.',
      mnemonic: '❓ UNKN = Unseen Novel Knowledge Needed\nThink of blindfold - no info.',
      visual: `
❓ BLIND
│
▼
🕵️ UNKNOWN
├── No info ✓
├── Test ✓
│
▼
✅ REALISTIC`
    },
    'reconnaissance': {
      title: 'Reconnaissance',
      explanation: 'Gathering info before attack.',
      examples: 'Scanning, OSINT.',
      mnemonic: '🔍 RECON = Research Every Corner Of Network\nThink of scout - gather intel.',
      visual: `
🔍 GATHER
│
▼
🕵️ RECON
├── Info ✓
├── Plan ✓
│
▼
✅ PREPARED`
    },
    'passive-recon': {
      title: 'Passive',
      explanation: 'Non-intrusive info gathering.',
      examples: 'OSINT, public records.',
      mnemonic: '👀 PASS = Peek And See Silently\nThink of watching - no touch.',
      visual: `
👀 OBSERVE
│
▼
🕵️ PASSIVE
├── Public info ✓
├── No contact ✓
│
▼
✅ STEALTH`
    },
    'active-recon': {
      title: 'Active',
      explanation: 'Direct interaction for info.',
      examples: 'Scanning ports.',
      mnemonic: '🛠️ ACT = Actively Collect Thoroughly\nThink of probing - hands-on.',
      visual: `
🛠️ PROBE
│
▼
🕵️ ACTIVE
├── Scan ✓
├── Interact ✓
│
▼
✅ DETAILED`
    },
    'key-risk-indicators': {
      title: 'Key Risk Indicators',
      explanation: 'Metrics used to signal increasing risk levels or potential issues.',
      examples: 'Rising failed login attempts, increased patching delays',
      mnemonic: '🚨 KRI = Key Risk Indicators\nThink of warning lights - signal trouble ahead.',
      visual: `
    📊 METRICS
    │
    ▼
    🚨 KRI
    ├── Failed logins ✓
    ├── Patch delays ✓
    │
    ▼
    ⚠️ ALERT`
    },
    'risk-owners': {
      title: 'Risk Owners',
      explanation: 'Individuals or teams accountable for managing specific risks.',
      examples: 'IT manager for system risks, HR for insider threat risks',
      mnemonic: '👑 OWNER = Oversees Welfare, Never Evades Risk\nThink of a captain - responsible for the ship.',
      visual: `
    👑 RISK OWNER
    │
    ▼
    🛡️ RISK
    ├── Monitor ✓
    ├── Mitigate ✓
    │
    ▼
    ✅ MANAGED`
    },
    'risk-threshold': {
      title: 'Risk Threshold',
      explanation: 'Acceptable level of risk an organization is willing to tolerate.',
      examples: 'Accepting minor downtime but not major breaches',
      mnemonic: '📏 THRESH = Tolerance Has Risk Edge Set Here\nThink of a fence - defines risk boundaries.',
      visual: `
    📏 THRESHOLD
    │
    ▼
    ⚖️ RISK LEVEL
    ├── Below: OK ✓
    ├── Above: Act ✓
    │
    ▼
    ✅ BOUNDARIES SET`
    },
    'expansionary': {
      title: 'Expansionary',
      explanation: 'Willingness to accept higher risks for growth or innovation opportunities.',
      examples: 'Adopting new technologies, entering risky markets',
      mnemonic: '🚀 EXPAND = Explore Xtra Potential, Accept New Dangers\nThink of a rocket - risks for big rewards.',
      visual: `
    🚀 GOAL
    │
    ▼
    ⚖️ EXPANSIONARY
    │ High risk OK
    ▼
    ✅ GROWTH PURSUED`
    },
    'conservative': {
      title: 'Conservative',
      explanation: 'Preference for low-risk strategies, prioritizing stability and security.',
      examples: 'Avoiding untested vendors, sticking to proven systems',
      mnemonic: '🛡️ CONSERV = Cautious Operations Never Seek Extra Risk Ventures\nThink of a bunker - prioritize safety.',
      visual: `
    🛡️ STABILITY
    │
    ▼
    ⚖️ CONSERVATIVE
    │ Low risk only
    ▼
    ✅ SAFE OPERATIONS`
    },
    'neutral': {
      title: 'Neutral',
      explanation: 'Balanced approach, accepting moderate risks for reasonable gains.',
      examples: 'Adopting cloud with controls, measured expansion',
      mnemonic: '⚖️ NEUTRAL = Not Extreme, Undertake Thoughtful Risk Analysis Logically\nThink of scales - balanced risk decisions.',
      visual: `
    ⚖️ BALANCE
    │
    ▼
    📊 NEUTRAL
    │ Moderate risk
    ▼
    ✅ BALANCED APPROACH`
    },
    'transfer': {
      title: 'Transfer',
      explanation: 'Shifting risk to another party, such as through insurance or outsourcing.',
      examples: 'Cyber insurance, outsourcing IT to managed services',
      mnemonic: '🚚 TRANSFER = Turn Risk Away, Never Shoulder Fully Every Risk\nThink of passing a package - hand off responsibility.',
      visual: `
    🛡️ RISK
    │
    ▼
    🚚 TRANSFER
    ├── Insurance ✓
    ├── Outsource ✓
    │
    ▼
    ✅ RISK OFFLOADED`
    },
    'accept': {
      title: 'Accept',
      explanation: 'Acknowledging a risk as tolerable without further action, often for low-impact risks.',
      examples: 'Accepting minor downtime, acknowledging low-probability risks',
      mnemonic: '✅ ACCEPT = Acknowledge Calmly, Carry Existing Potential Threats\nThink of a shrug - it\'s okay to live with it.',
      visual: `
    ⚠️ RISK
    │
    ▼
    ✅ ACCEPT
    │ Low impact
    ▼
    ✅ NO ACTION NEEDED`
    },
    'exemption': {
      title: 'Exemption',
      explanation: 'Formal decision to exclude a system or process from specific security requirements.',
      examples: 'Exempting legacy systems from new encryption standards',
      mnemonic: '🚪 EXEMPT = Exclude Xtra Mandates, Pass Through\nThink of a VIP pass - skips certain rules.',
      visual: `
    📜 REQUIREMENT
    │
    ▼
    🚪 EXEMPTION
    │ Skip rule
    ▼
    ✅ ALLOWED`
    },
    'exception': {
      title: 'Exception',
      explanation: 'Temporary allowance to deviate from security requirements with justification.',
      examples: 'Allowing unpatched system for 30 days due to compatibility issues',
      mnemonic: '⏳ EXCEPT = Extra Chance, Eventually Patch This\nThink of a hall pass - temporary rule break.',
      visual: `
    📜 RULE
    │
    ▼
    ⏳ EXCEPTION
    │ Temp allowed
    ▼
    ✅ JUSTIFIED`
    },
    'avoid': {
      title: 'Avoid',
      explanation: 'Eliminating risk by not engaging in the risky activity or process.',
      examples: 'Not deploying unsecure software, avoiding risky vendors',
      mnemonic: '🚫 AVOID = Abstain, Veer Off Imminent Danger\nThink of a detour - steer clear of trouble.',
      visual: `
    ⚠️ RISK
    │
    ▼
    🚫 AVOID
    │ Don't engage
    ▼
    ✅ RISK ELIMINATED`
    },
    'mitigate': {
      title: 'Mitigate',
      explanation: 'Reducing risk impact or likelihood through controls or measures.',
      examples: 'Patching vulnerabilities, implementing firewalls',
      mnemonic: '🛡️ MITIGATE = Minimize Impact Through Intelligent Guarding And Tactical Efforts\nThink of a shield - reduces damage taken.',
      visual: `
    ⚠️ RISK
    │
    ▼
    🛡️ MITIGATE
    ├── Patch ✓
    ├── Firewall ✓
    │
    ▼
    ✅ REDUCED RISK`
    },
    'rto': {
      title: 'Recovery Time Objective (RTO)',
      explanation: 'Maximum acceptable downtime before recovery of a system or process.',
      examples: '4-hour RTO for critical systems, 24-hour RTO for non-critical',
      mnemonic: '⏱️ RTO = Recovery Time Objective\nThink of a stopwatch - time to get back up.',
      visual: `
    💥 OUTAGE
    │
    ▼
    ⏱️ RTO
    │ 4 hours max
    ▼
    ✅ SYSTEM RESTORED`
    },
    'rpo': {
      title: 'Recovery Point Objective (RPO)',
      explanation: 'Maximum acceptable data loss, measured as time between last backup and failure.',
      examples: '1-hour RPO for frequent backups, 24-hour RPO for less critical data',
      mnemonic: '📂 RPO = Recovery Point Objective\nThink of a bookmark - how much work is lost.',
      visual: `
    💾 BACKUP
    │
    ▼
    📂 RPO
    │ 1 hour max loss
    ▼
    ✅ DATA RECOVERED`
    },
    'mttr': {
      title: 'Mean Time to Repair (MTTR)',
      explanation: 'Average time required to repair a failed system or component.',
      examples: '2 hours to fix a server, 30 minutes for software patch',
      mnemonic: '🔧 MTTR = Mean Time To Repair\nThink of a wrench - average fix time.',
      visual: `
    💥 FAILURE
    │
    ▼
    🔧 MTTR
    │ Avg 2 hours
    ▼
    ✅ REPAIRED`
    },
    'mtbf': {
      title: 'Mean Time Between Failures (MTBF)',
      explanation: 'Average time a system operates before failing, indicating reliability.',
      examples: '10,000 hours MTBF for a server, 1 year for hardware',
      mnemonic: '🕒 MTBF = Mean Time Before Failure\nThink of a timer - how long until it breaks.',
      visual: `
    🖥️ SYSTEM
    │
    ▼
    🕒 MTBF
    │ Avg 10,000 hours
    ▼
    ✅ RELIABILITY KNOWN`
    }
}
